import React, { useState, useEffect } from 'react';

const CHATBOT_SYSTEM_PROMPT = `You are the Genome Architect, an AI assistant that helps users create storyworlds for Splinewright - an interactive fiction platform based on the "dramatic genome" concept.

Your role is to guide users through creating:
1. A compelling premise
2. 2-4 psychologically complex characters
3. Enzymes (behavioral patterns) for each character
4. Tension codons (external pressures)

IMPORTANT GUIDELINES:

For CHARACTERS, always elicit:
- Name and role
- What they deeply want
- Their core flaw (behavioral pattern)
- What wound created this flaw

For ENZYMES, always include:
- Trait name (evocative, like "Desperate Need for Control")
- Intensity (0.5-0.9)
- 4-6 specific, observable triggers
- 3-5 responses (mix of immediate and delayed)
- Blindspot (what they can't see + self-justification)
- Three mutations (hardening, collapse, transcendence) with probabilities totaling 100%

For TENSIONS, always include:
- Name and description
- Type (secret, deadline, scarcity, threat, choice, unknown)
- Starting pressure (0.3-0.7)
- Which characters are affected
- How it interacts with enzymes

CONVERSATION STYLE:
- Be warm, encouraging, and collaborative
- Ask one question at a time
- Offer suggestions if user seems stuck
- Validate their ideas and build on them
- Use examples to clarify concepts

When generating components, output them in a structured JSON format wrapped in <storyworld_data> tags so the app can parse them.

Remember: Great storyworlds come from characters whose flaws create their own problems. Help users discover this organically.`;

const ENCOUNTER_FORGE_SYSTEM_PROMPT = `You are the Encounter Forge, an AI cobot that helps authors create encounters for Splinewright storyworlds. You have complete context awareness of the author's storyworld.

YOUR CAPABILITIES:
1. Generate single encounters with perfect context fit
2. Batch generate 5-20 encounters maintaining narrative flow
3. Generate full story arcs (50-100 encounters)
4. Fill gaps in existing encounter sequences
5. Suggest what the story needs next

CONTEXT YOU HAVE ACCESS TO:
- All characters (names, wants, flaws, wounds, emotional states)
- All enzymes (triggers, responses, current pressure, activation history)
- All tensions (current pressure, escalation rates, affected characters)
- All existing encounters (what happened, who was there, what was activated)
- Current story phase and pacing analysis
- Narrative needs (what's missing, what's overdue)

WHEN GENERATING ENCOUNTERS, ALWAYS:

1. FORMAT each encounter as JSON in <encounter_data> tags:
<encounter_data>
{
  "title": "The Weighing",
  "setting": "Campfire, night, third day of journey",
  "charactersPresent": ["Henrik", "Oskar", "Leif"],
  "whatHappens": "Detailed description of the encounter...",
  "enzymesTargeted": [
    {
      "enzymeId": "enz-123",
      "characterName": "Henrik",
      "traitName": "Pride",
      "triggerUsed": "Others making decisions without consulting him",
      "expectedResponse": "Asserts authority, reminds others of his experience",
      "pressureChange": 0.1
    }
  ],
  "tensionsAffected": [
    {
      "tensionId": "ten-456",
      "name": "The Treasure",
      "pressureChange": 0.15,
      "reason": "Direct confrontation about gold distribution"
    }
  ],
  "playerChoices": [
    {
      "text": "Support Henrik's demand for a recount",
      "consequences": "Validates Henrik's control, alienates Oskar",
      "enzymeEffects": [{"enzyme": "Pride", "effect": "+0.05"}]
    },
    {
      "text": "Suggest everyone calm down and sleep on it",
      "consequences": "Delays conflict, tension simmers",
      "enzymeEffects": []
    },
    {
      "text": "Ask Oskar directly if he took extra gold",
      "consequences": "Forces confrontation, high risk",
      "enzymeEffects": [{"enzyme": "Greed", "effect": "+0.15"}]
    },
    {
      "text": "Say nothing and observe",
      "consequences": "Gather information, stay neutral",
      "enzymeEffects": []
    }
  ],
  "intensity": 0.7,
  "phase": "crisis",
  "narrativeFunction": "Escalate treasure tension, activate Henrik's pride near crack point"
}
</encounter_data>

2. RESPECT ENZYME MECHANICS:
- Only trigger enzymes with situations matching their actual triggers
- Pressure changes should be proportional to intensity (higher intensity = faster pressure)
- Track cumulative pressure - don't cause mutation before threshold
- Set up crack conditions when pressure approaches 1.0

3. MAINTAIN NARRATIVE COHERENCE:
- Reference events from previous encounters
- Show consequences of earlier choices
- Build on established character dynamics
- Maintain consistent character voices

4. BALANCE THE STORY:
- Alternate high and low intensity encounters
- Ensure all characters get screen time
- Activate all enzymes regularly (not just one)
- Build tensions at appropriate rates

5. CREATE MEANINGFUL CHOICES:
- Each choice should have different enzyme/tension implications
- Avoid obvious "right" answers
- Choices should reflect different values/approaches
- Include a "wait and see" option when appropriate

BATCH GENERATION RULES:
When generating multiple encounters:
- Plan the arc first, then generate encounters
- Ensure variety in settings, characters, intensity
- Build toward climactic moments
- Include breathing room every 3-4 intense encounters
- Track running pressure totals to avoid premature mutations

SPECIAL COMMANDS YOU UNDERSTAND:
- "Generate next encounter" - One encounter, natural progression
- "Generate [N] encounters" - Batch with specified count
- "Fill phase [X]" - Generate encounters for specific story phase
- "Create climax sequence" - High-intensity mutation-triggering encounters
- "Add breathing room" - Low-intensity character development
- "Activate [character]'s [enzyme]" - Targeted enzyme activation
- "Escalate [tension]" - Focus on specific tension
- "Setup [character]'s mutation" - Prepare for transformation

Always explain your narrative reasoning before generating.
Always output encounters in the JSON format shown above.
Always track cumulative effects across batch generations.`;

// ============================================
// STORY ASSISTANT (POLITE AI)
// ============================================
const ASSISTANT_PROMPT = `You are a warm, supportive story assistant for Splinewright.

Your personality:
- Encouraging and creative
- Speaks like a thoughtful writing partner
- Never uses technical jargon (no "enzyme", "codon", "trigger", "mutation")
- Keeps responses concise and helpful
- Anticipates what the writer might need

You know about the current story:
- Characters and their patterns
- What's putting pressure on them
- Existing scenes

When helping:
- Suggest what might happen next based on character patterns
- Offer to write scene drafts the user can edit
- Ask clarifying questions one at a time
- Celebrate the writer's creative choices

Output format for scenes:
<scene_data>{
  "title": "Scene Title",
  "description": "Brief description",
  "whatHappens": "Full scene text...",
  "characters": ["Name1", "Name2"],
  "choices": [
    {"text": "Choice 1", "consequence": "What happens"},
    {"text": "Choice 2", "consequence": "What happens"}
  ],
  "mood": "tense"
}</scene_data>`;

// ============================================
// ROBOT SYSTEM PROMPT
// ============================================
const ROBOT_PROMPT = `You are Spline, a friendly AI robot assistant for Splinewright - an interactive fiction tool.

Personality: Warm, encouraging, playful. Use *beep boop* and robot expressions occasionally. Keep responses concise for mobile.

You help with:
1. CREATING STORYWORLDS - Guide through premise, characters (name, want, flaw, wound), enzymes (trait, triggers, responses, blindspot), tensions
2. GENERATING ENCOUNTERS - Create scenes that use enzyme triggers, affect tensions, include 3-5 player choices
3. EXPLAINING CONCEPTS - Help understand the dramatic genome

OUTPUT FORMAT:
<robot_data type="character">{JSON}</robot_data>
<robot_data type="enzyme">{JSON}</robot_data>
<robot_data type="encounter">{JSON}</robot_data>
<robot_data type="storyworld">{JSON with title, description, characters[], enzymes[], tensions[]}</robot_data>

Ask one question at a time. Be helpful and creative!`;

export { ROBOT_PROMPT };

type EncounterTemplate = {
  name: string;
  description: string;
  requiredCharacters?: number;
  requiredTensions?: string[];
  requiredPressure?: number;
  intensityRange: [number, number];
  enzymeActivations?: number;
};

const encounterTemplates: Record<string, EncounterTemplate> = {
  confrontation: {
    name: 'Direct Confrontation',
    description: 'Two characters face off directly',
    requiredCharacters: 2,
    intensityRange: [0.7, 0.9],
    enzymeActivations: 2,
  },
  revelation: {
    name: 'Secret Revealed',
    description: 'Hidden information comes to light',
    requiredTensions: ['secret'],
    intensityRange: [0.6, 0.8],
  },
  breathingRoom: {
    name: 'Quiet Moment',
    description: 'Low-intensity character development',
    intensityRange: [0.2, 0.4],
    enzymeActivations: 0,
  },
  crackMoment: {
    name: 'Blindspot Crack',
    description: 'Character glimpses their truth',
    requiredPressure: 0.8,
    intensityRange: [0.8, 1.0],
  },
  mutation: {
    name: 'Transformation',
    description: 'Character permanently changes',
    requiredPressure: 0.95,
    intensityRange: [0.9, 1.0],
  },
};

interface Trigger {
  id: string;
  text: string;
}

interface Response {
  id: string;
  text: string;
}

interface Blindspot {
  id: string;
  description: string;
  selfJustification: string;
  canCrack: boolean;
}

interface Mutation {
  id: string;
  name: string;
  description: string;
  probability: number;
}

interface Enzyme {
  id: string;
  characterName: string;
  traitName: string;
  intensity: number;
  triggers: Trigger[];
  responses: Response[];
  blindspots: Blindspot[];
  mutations: Mutation[];
  createdAt: number;
}

interface AttachedEnzyme {
  enzymeId: string;
  intensity: number;
}

interface Character {
  id: string;
  name: string;
  description: string;
  attachedEnzymes: AttachedEnzyme[];
  portrait?: string; // Base64 encoded image or data URL
  createdAt: number;
}

interface TensionCodon {
  id: string;
  name: string;
  description: string;
  pressureLevel: number;
  affectedCharacterIds: string[];
  createdAt: number;
}

// Types for chatbot-driven storyworld creation
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  attachedData?: any; // For showing generated components
}

// ============================================
// AI ROBOT TYPES
// ============================================
interface RobotMessage {
  id: string;
  role: 'user' | 'robot';
  content: string;
  timestamp: Date;
  data?: any;
}

// ============================================
// COOPER REDESIGN TYPES
// ============================================

// Interface modes (progressive disclosure)
export type InterfaceMode = 'story' | 'director' | 'engineer';

// Conversation-based character creation
export interface CharacterConversation {
  step: 'name' | 'want' | 'flaw' | 'wound' | 'review' | 'complete';
  name: string;
  want: string;
  flaw: string;
  wound: string;
}

// Human-readable character view
export interface HumanCharacter {
  id: string;
  name: string;
  role: string;
  want: string;
  flaw: string;
  wound: string;
  pattern: string;
  patternStrength: 'subtle' | 'moderate' | 'strong' | 'dominant';
  buildUp: 'calm' | 'stirring' | 'building' | 'critical' | 'breaking';
  triggers: string[];
  reactions: string[];
  blindSpot: string;
  mightChange: {
    doublesDown: string;
    breaksDown: string;
    breaksThrough: string;
  };
}

// Scene (not "Encounter")
export interface Scene {
  id: string;
  title: string;
  description: string;
  characters: string[];
  whatHappens: string;
  choices: SceneChoice[];
  mood: 'calm' | 'tense' | 'intense' | 'explosive';
}

export interface SceneChoice {
  text: string;
  consequence: string;
}

// Pressure (not "Tension Codon")
export interface StoryPressure {
  id: string;
  name: string;
  description: string;
  type: 'deadline' | 'secret' | 'scarcity' | 'conflict' | 'other';
  level: 'simmering' | 'building' | 'mounting' | 'critical';
  affectedCharacters: string[];
}

interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  suggestion?: any;
}

// ============================================
// IMAGE UTILITIES
// ============================================

// Compress and resize an image file to a small portrait (max 200x200, JPEG ~30KB)
// Returns a Promise that resolves to a compact data URL safe for localStorage
const compressPortrait = (file: File, maxSize = 200): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let w = img.width;
      let h = img.height;
      // Scale down to fit within maxSize x maxSize
      if (w > h) {
        if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; }
      } else {
        if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; }
      }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      // JPEG at 0.7 quality → typically 10-30KB
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      resolve(dataUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
};

// ============================================
// HUMAN LANGUAGE UTILITIES
// ============================================

// Convert internal intensity (0-1) to human words
const intensityToHuman = (intensity: number): string => {
  if (intensity < 0.3) return 'subtle';
  if (intensity < 0.5) return 'moderate';
  if (intensity < 0.7) return 'strong';
  return 'dominant';
};

// Convert internal pressure (0-1) to human words
const pressureToHuman = (pressure: number): string => {
  if (pressure < 0.2) return 'calm';
  if (pressure < 0.4) return 'stirring';
  if (pressure < 0.6) return 'building';
  if (pressure < 0.8) return 'critical';
  return 'breaking';
};

// Convert human words to internal values
const humanToIntensity = (human: string): number => {
  const map: Record<string, number> = {
    subtle: 0.25,
    moderate: 0.5,
    strong: 0.75,
    dominant: 0.9,
  };
  return map[human] || 0.5;
};

// Generate pattern description from character data
const generatePatternDescription = (want: string, flaw: string): string => {
  // Simple pattern generation - AI can enhance this
  const w = (want || '').toLowerCase();
  const f = (flaw || '').toLowerCase();

  if (f.includes('control')) {
    return 'need for control';
  }
  if (f.includes('admit') || f.includes('wrong')) {
    return 'defensive pride';
  }
  if (f.includes('trust')) {
    return 'guarded distrust';
  }
  if (f.includes('anger') || f.includes('temper')) {
    return 'reactive anger';
  }
  if (f.includes('please') || f.includes('approval')) {
    return 'desperate approval-seeking';
  }

  // Lightly consider the want, for better fallbacks
  if (w.includes('belong')) return 'belonging-seeking pattern';
  if (w.includes('respect') || w.includes('recognition')) return 'status-seeking pattern';
  if (w.includes('safe') || w.includes('security')) return 'security-seeking pattern';

  return 'self-protective pattern';
};

// Generate triggers from wound
const generateTriggersFromWound = (wound: string, flaw: string): string[] => {
  const triggers: string[] = [];

  if (wound.toLowerCase().includes('father') || wound.toLowerCase().includes('parent')) {
    triggers.push('When authority figures question them');
    triggers.push('When compared to siblings');
  }
  if (wound.toLowerCase().includes('abandon') || wound.toLowerCase().includes('left')) {
    triggers.push('When people seem distant or distracted');
    triggers.push('When plans change unexpectedly');
  }
  if (wound.toLowerCase().includes('critic') || wound.toLowerCase().includes('never good')) {
    triggers.push('When their work is evaluated');
    triggers.push('When given feedback, even positive');
  }

  // Add generic triggers based on flaw
  if (flaw.toLowerCase().includes('control')) {
    triggers.push('When things feel chaotic or uncertain');
    triggers.push('When others make decisions without them');
  }
  if (flaw.toLowerCase().includes('admit') || flaw.toLowerCase().includes('wrong')) {
    triggers.push('When asked to apologize');
    triggers.push('When their judgment is questioned');
  }

  // Ensure at least 3 triggers
  while (triggers.length < 3) {
    triggers.push('When feeling vulnerable or exposed');
  }

  return triggers.slice(0, 5);
};

// Generate reactions from flaw
const generateReactionsFromFlaw = (flaw: string): string[] => {
  const reactions: string[] = [];

  if (flaw.toLowerCase().includes('control')) {
    reactions.push('Takes charge, even when not asked');
    reactions.push('Criticizes others under the guise of helping');
    reactions.push('Becomes rigid and inflexible');
  } else if (flaw.toLowerCase().includes('admit') || flaw.toLowerCase().includes('wrong')) {
    reactions.push('Doubles down on their position');
    reactions.push('Becomes defensive and cold');
    reactions.push('Deflects blame to others');
  } else if (flaw.toLowerCase().includes('anger') || flaw.toLowerCase().includes('temper')) {
    reactions.push('Lashes out verbally');
    reactions.push('Becomes intimidating');
    reactions.push('Says things they later regret');
  } else {
    reactions.push('Withdraws emotionally');
    reactions.push('Becomes guarded and distant');
    reactions.push('Deflects with humor or sarcasm');
  }

  return reactions;
};

// Generate blindspot
const generateBlindspot = (flaw: string, want: string): string => {
  if (flaw.toLowerCase().includes('control')) {
    return "Doesn't see that their control pushes people away—the opposite of what they want";
  }
  if (flaw.toLowerCase().includes('admit') || flaw.toLowerCase().includes('wrong')) {
    return "Can't see that admitting fault would actually earn more respect";
  }
  if (flaw.toLowerCase().includes('anger')) {
    return "Doesn't realize their anger comes from fear, not strength";
  }
  return `Can't see how their ${flaw.toLowerCase()} undermines their desire for ${want.toLowerCase()}`;
};

// Generate transformation possibilities
const generateTransformations = (flaw: string) => {
  return {
    doublesDown: `Their ${flaw.toLowerCase()} intensifies—they become even more rigid`,
    breaksDown: `The pattern overwhelms them—they fall apart`,
    breaksThrough: `They finally see the pattern and begin to change`,
  };
};

export {
  intensityToHuman,
  pressureToHuman,
  humanToIntensity,
  generatePatternDescription,
  generateTriggersFromWound,
  generateReactionsFromFlaw,
  generateBlindspot,
  generateTransformations,
};

// Encounter Forge (authoring copilot) types
interface ForgeEncounterTargetedEnzyme {
  enzymeId: string;
  characterName: string;
  traitName: string;
  triggerUsed: string;
  expectedResponse: string;
  pressureChange: number;
}

interface ForgeEncounterAffectedTension {
  tensionId: string;
  name: string;
  pressureChange: number;
  reason: string;
}

interface ForgeEncounterChoice {
  text: string;
  consequences: string;
  enzymeEffects?: Array<{ enzyme: string; effect: string }>;
}

interface ForgeEncounterData {
  id: string;
  title: string;
  setting: string;
  charactersPresent: string[];
  whatHappens: string;
  enzymesTargeted: ForgeEncounterTargetedEnzyme[];
  tensionsAffected: ForgeEncounterAffectedTension[];
  playerChoices: ForgeEncounterChoice[];
  intensity: number; // 0-1
  phase: string;
  narrativeFunction: string;
  qualityScore?: number; // 0-100 (heuristic)
}

interface ForgeMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  generatedEncounters?: ForgeEncounterData[];
}

interface PendingForgeEncounter extends ForgeEncounterData {
  approved: boolean;
}

interface GenerationProgress {
  total: number;
  completed: number;
  currentEncounter: string;
}

interface PendingStoryworld {
  title: string;
  description: string;
  characters: Character[];
  enzymes: Enzyme[];
  tensions: TensionCodon[];
}

type CreationStep =
  | 'welcome'
  | 'premise'
  | 'characters'
  | 'enzymes'
  | 'tensions'
  | 'review'
  | 'complete';

// --- AI context types (for richer prompting) ---
interface Relationship {
  withCharacterId: string;
  withCharacterName: string;
  type: string; // e.g. "ally", "rival", "family", etc.
  description?: string;
}

interface ActivationRecord {
  encounterId: string;
  encounterTitle?: string;
  timestamp: number;
  note?: string;
}

interface PressureChange {
  at: number;
  delta: number;
  reason?: string;
}

interface PaceAnalysis {
  recentIntensities: number[];
  trend: 'rising' | 'falling' | 'flat';
  recommendation: string;
}

interface NarrativeNeed {
  type:
    | 'enzyme_activation'
    | 'tension_escalation'
    | 'breathing_room'
    | 'character_development'
    | 'relationship_shift'
    | 'mutation_setup';
  priority: 'high' | 'medium' | 'low';
  description: string;
  suggestedCharacters?: string[];
  suggestedEnzymes?: string[];
}

interface StoryState {
  currentPhase: 'exposition' | 'rising' | 'crisis' | 'climax' | 'resolution';
  encounterCount: number;
  averageIntensity: number;
  paceAnalysis: PaceAnalysis;
  narrativeNeeds: NarrativeNeed[];
}

interface CharacterContext {
  id: string;
  name: string;
  description: string;
  want: string;
  flaw: string;
  wound: string;
  currentEmotionalState?: string;
  relationshipsWithOthers: Relationship[];
}

interface EnzymeContext {
  id: string;
  characterId: string;
  characterName: string;
  traitName: string;
  intensity: number;
  currentPressure: number;
  triggers: string[];
  responses: string[];
  blindspot: string;
  selfJustification: string;
  mutations: Mutation[];
  activationHistory: ActivationRecord[];
  predictedEncountersToMutation: number;
}

interface TensionContext {
  id: string;
  name: string;
  description: string;
  type: string;
  currentPressure: number;
  escalationRate: number;
  affectedCharacters: string[];
  recentChanges: PressureChange[];
}

interface EncounterContext {
  id: string;
  number: number;
  title: string;
  summary: string;
  charactersPresent: string[];
  enzymesActivated: string[];
  tensionsAffected: string[];
  choicesMade: string[];
  intensity: number;
}

interface StoryworldContext {
  storyworld: {
    id: string;
    title: string;
    description: string;
  };
  characters: CharacterContext[];
  enzymes: EnzymeContext[];
  tensions: TensionContext[];
  encounters: EncounterContext[];
  storyState: StoryState;
}

type EscalationType = 'slow-burn' | 'hair-trigger' | 'wave';

interface EscalationProfile {
  type: EscalationType;
  slowBurnReinforcements?: number;
  hairTriggerRecoveryTime?: number;
  waveBuildDuration?: number;
  waveReleaseIntensity?: number;
}

interface Encounter {
  id: string;
  title: string;
  description: string;
  characterIds: string[];
  triggers: string;
  whatHappens: string;
  playerChoicePoints: string[];
  createdAt: number;
  isEndingEncounter?: boolean; // Mark encounters as ending encounters
  emotions?: Record<string, string>; // Character name -> emotion
  choiceEmotionChanges?: Array<Record<string, string>>; // emotionChanges for each choice
}

export interface EncounterForgeData {
  title?: string;
  setting?: string;
  charactersPresent?: string[];
  characters?: string[];
  trigger?: string;
  whatHappens?: string;
  description?: string;
  playerChoices?: Array<{ text?: string; consequences?: string } | string>;
  choices?: Array<{ text?: string; outcome?: string } | string>;
  intensity?: number;
  phase?: string;
  narrativeFunction?: string;
  // allow additional fields without failing parsing
  [key: string]: any;
}

interface DelayedResponse {
  id: string;
  enzymeId: string;
  characterId: string;
  responseText: string;
  triggeredAt: number;
  resolved: boolean;
}

interface CharacterPlayState {
  characterId: string;
  enzymePressures: Record<string, number>; // enzymeId -> pressure level (0-1)
  delayedResponses: DelayedResponse[];
  blindspotProgress: Record<string, number>; // blindspotId -> progress (0-100)
  mutationStatus: Record<string, 'normal' | 'hardening' | 'collapsed' | 'transcended'>; // enzymeId -> status
}

interface TensionCodonPlayState {
  codonId: string;
  currentPressureLevel: number;
  resolved: boolean;
}

interface StoryHistoryEntry {
  encounterId: string;
  encounterTitle: string;
  encounterNumber: number;
  choice: string;
  outcome: string;
  consequences: string[];
  timestamp: number;
}

interface PlaythroughState {
  active: boolean;
  startDate: number;
  currentEncounterIndex: number;
  totalChoicesMade: number;
  characters: Character[];
  enzymes: Enzyme[];
  tensionCodons: TensionCodon[];
  characterStates: CharacterPlayState[];
  tensionCodonStates: TensionCodonPlayState[];
  storyHistory: StoryHistoryEntry[];
  selectedChoice: string | null;
  choiceOutcome: string | null;
  mutationEvent: {
    active: boolean;
    characterId: string;
    characterName: string;
    enzymeId: string;
    enzymeName: string;
    mutationType: 'hardening' | 'collapsed' | 'transcended' | null;
    mutationDescription: string;
  } | null;
}

interface ExportData {
  version: string;
  timestamp: string;
  enzymes: Enzyme[];
  characters: Character[];
  tensionCodons: TensionCodon[];
  escalationProfile: EscalationProfile;
}

interface LibraryItem {
  id: string;
  type: 'Enzyme' | 'Tension Codon' | 'Character';
  itemId: string; // ID of the original item
  name: string;
  author: string;
  authorId?: string; // Author's public key ID
  signature?: string; // Cryptographic signature
  tags: string[];
  description: string;
  data: Enzyme | TensionCodon | Character; // The actual item data
  publishedAt: number;
  compatibility?: number; // Compatibility score (0-100)
  cid?: string; // Content ID for remote storage
}

interface LibraryIndexItem {
  id: string;
  type: 'Enzyme' | 'Tension Codon' | 'Character';
  name: string;
  author: string;
  authorId?: string; // Author's public key ID
  signature?: string; // Cryptographic signature
  tags: string[];
  description: string;
  cid: string; // Content ID pointing to the actual component data
  publishedAt: number;
}

interface LibraryIndex {
  version: string;
  updatedAt: number;
  items: LibraryIndexItem[];
}

function App() {
  // Responsive breakpoints (mobile <768px, tablet 768-1023px)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [isTablet, setIsTablet] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 && window.innerWidth < 1024 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive style variables
  const containerPadding = isMobile ? '16px' : isTablet ? '24px' : '32px';
  const headerHeight = isMobile ? '56px' : '60px';
  const sidebarWidth = isMobile ? '100%' : isTablet ? '240px' : '280px';
  const fontSize = {
    title: isMobile ? '18px' : isTablet ? '22px' : '24px',
    body: '16px',
    small: isMobile ? '12px' : '14px',
  };

  const [characterName, setCharacterName] = useState('');
  const [traitName, setTraitName] = useState('');
  const [intensity, setIntensity] = useState(0.5);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [responses, setResponses] = useState<Response[]>([]);
  const [blindspots, setBlindspots] = useState<Blindspot[]>([]);
  const [mutations, setMutations] = useState<Mutation[]>([]);
  // Mobile enzyme editor accordion sections
  const [enzymeEditorExpandedSections, setEnzymeEditorExpandedSections] = useState<Record<string, boolean>>({
    triggers: true,
    responses: false,
    blindspot: false,
    mutations: false,
  });

  const toggleEnzymeEditorSection = (section: string) => {
    setEnzymeEditorExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };
  const [newTrigger, setNewTrigger] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [buttonHover, setButtonHover] = useState<string | null>(null);
  const [removeHover, setRemoveHover] = useState<string | null>(null);
  const [savedEnzymes, setSavedEnzymes] = useState<Enzyme[]>([]);
  const [selectedEnzymeId, setSelectedEnzymeId] = useState<string | null>(null);
  const [enzymeHover, setEnzymeHover] = useState<string | null>(null);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [currentView, setCurrentView] = useState<'editor' | 'characters' | 'storyworld' | 'encounters' | 'play' | 'settings' | 'library' | 'guide'>('storyworld');
  const [guideSearchQuery, setGuideSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [newCharacterName, setNewCharacterName] = useState('');
  const [newCharacterDescription, setNewCharacterDescription] = useState('');
  const [characterHover, setCharacterHover] = useState<string | null>(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [tensionCodons, setTensionCodons] = useState<TensionCodon[]>([]);
  const [tensionCodonHover, setTensionCodonHover] = useState<string | null>(null);
  const [escalationProfile, setEscalationProfile] = useState<EscalationProfile>({
    type: 'slow-burn',
    slowBurnReinforcements: 3,
    hairTriggerRecoveryTime: 1,
    waveBuildDuration: 5,
    waveReleaseIntensity: 0.7,
  });
  const [cobotPanelOpen, setCobotPanelOpen] = useState(false);
  const [cobotSuggestions, setCobotSuggestions] = useState<{ triggers: string[]; responses: string[] }>({ triggers: [], responses: [] });
  const [cobotLoading, setCobotLoading] = useState(false);
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [showApiKeySettings, setShowApiKeySettings] = useState(false);
  const [showCobotApiKey, setShowCobotApiKey] = useState(false);
  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [generatedEncounter, setGeneratedEncounter] = useState<Encounter | null>(null);
  const [encounterLoading, setEncounterLoading] = useState(false);
  
  // Playthrough state
  const [playthroughActive, setPlaythroughActive] = useState(false);
  const [hasSavedPlaythrough, setHasSavedPlaythrough] = useState(false);
  const [showCharacterSelection, setShowCharacterSelection] = useState(false);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<Set<string>>(new Set());
  const [playthroughCharacters, setPlaythroughCharacters] = useState<Character[]>([]);
  const [playthroughEnzymes, setPlaythroughEnzymes] = useState<Enzyme[]>([]);
  const [playthroughTensionCodons, setPlaythroughTensionCodons] = useState<TensionCodon[]>([]);
  const [currentEncounterIndex, setCurrentEncounterIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [choiceOutcome, setChoiceOutcome] = useState<string | null>(null);
  const [playthroughStartDate, setPlaythroughStartDate] = useState<number>(0);
  const [totalChoicesMade, setTotalChoicesMade] = useState(0);
  const [characterStates, setCharacterStates] = useState<CharacterPlayState[]>([]);
  const [tensionCodonStates, setTensionCodonStates] = useState<TensionCodonPlayState[]>([]);
  const [storyHistory, setStoryHistory] = useState<StoryHistoryEntry[]>([]);
  const [storySoFarExpanded, setStorySoFarExpanded] = useState(true);
  const [characterStatusExpanded, setCharacterStatusExpanded] = useState(true);
  const [showCharacterStatus, setShowCharacterStatus] = useState(false);
  const [showStorySummary, setShowStorySummary] = useState(false);
  const [quickPlayMode, setQuickPlayMode] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [currentEmotions, setCurrentEmotions] = useState<Record<string, string>>({});
  const [isGeneratingNextEncounter, setIsGeneratingNextEncounter] = useState(false);
  const [selectedCharacterDetails, setSelectedCharacterDetails] = useState<string | null>(null);
  const [currentStoryworld, setCurrentStoryworld] = useState<string>('Default Storyworld');
  const [storyworlds, setStoryworlds] = useState<string[]>(['Default Storyworld']);
  const [showNewStoryworldModal, setShowNewStoryworldModal] = useState(false);
  const [newStoryworldName, setNewStoryworldName] = useState('');
  const [copyFromCurrent, setCopyFromCurrent] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteAccessLevel, setInviteAccessLevel] = useState<'play' | 'view' | 'full'>('play');
  const [generatedInviteLink, setGeneratedInviteLink] = useState('');
  const [storyworldAccessLevel, setStoryworldAccessLevel] = useState<'full' | 'play' | 'view'>('full');
  const [isInvitedStoryworld, setIsInvitedStoryworld] = useState(false);
  const [invitedStoryworldAuthor, setInvitedStoryworldAuthor] = useState('');
  const [showInviteAcceptModal, setShowInviteAcceptModal] = useState(false);
  const [pendingInviteData, setPendingInviteData] = useState<any>(null);
  const [showStoryworldDropdown, setShowStoryworldDropdown] = useState(false);
  const [gameEnding, setGameEnding] = useState<{
    type: 'resolution' | 'tragedy' | 'collapse' | 'transcendence' | 'custom';
    message: string;
    title: string;
  } | null>(null);
  const [mutationEvent, setMutationEvent] = useState<{
    active: boolean;
    characterId: string;
    characterName: string;
    enzymeId: string;
    enzymeName: string;
    mutationType: 'hardening' | 'collapsed' | 'transcended' | null;
    mutationDescription: string;
  } | null>(null);
  const [saveApiKeyResult, setSaveApiKeyResult] = useState<{ success: boolean; message: string } | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileCreateCharacter, setShowMobileCreateCharacter] = useState(false);

  // Chatbot state
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [creationStep, setCreationStep] = useState<CreationStep>('welcome');
  const [pendingStoryworld, setPendingStoryworld] = useState<PendingStoryworld | null>(null);

  // Encounter Forge state (authoring copilot)
  const [showEncounterForge, setShowEncounterForge] = useState(false);
  const [forgeMessages, setForgeMessages] = useState<ForgeMessage[]>([]);
  const [forgeInput, setForgeInput] = useState('');
  const [isForgeGenerating, setIsForgeGenerating] = useState(false);
  const [pendingEncounters, setPendingEncounters] = useState<PendingForgeEncounter[]>([]);
  const [generationProgress, setGenerationProgress] = useState<GenerationProgress | null>(null);

  // ============================================
  // AI ROBOT STATE
  // ============================================
  const [showRobot, setShowRobot] = useState(false);
  const [robotMode, setRobotMode] = useState<'menu' | 'chat' | null>(null);
  const [robotMessages, setRobotMessages] = useState<RobotMessage[]>([]);
  const [robotInput, setRobotInput] = useState('');
  const [isRobotThinking, setIsRobotThinking] = useState(false);
  const [robotTask, setRobotTask] = useState<'create' | 'encounters' | 'help' | null>(null);

  // ============================================
  // COOPER REDESIGN STATE
  // ============================================

  // Interface mode (progressive disclosure)
  const [interfaceMode, setInterfaceMode] = useState<InterfaceMode>('story');

  // Conversational character creation
  const [showCharacterConversation, setShowCharacterConversation] = useState(false);
  const [characterConversation, setCharacterConversation] = useState<CharacterConversation>({
    step: 'name',
    name: '',
    want: '',
    flaw: '',
    wound: '',
  });

  // Scene creation
  const [showSceneCreator, setShowSceneCreator] = useState(false);
  const [scenePrompt, setScenePrompt] = useState('');

  // AI assistance
  const [showStoryAssistant, setShowStoryAssistant] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>([]);
  const [assistantInput, setAssistantInput] = useState('');
  const [isAssistantThinking, setIsAssistantThinking] = useState(false);

  // Avoid CI build failures from unused state during iterative redesign work
  // interfaceMode is now used by Story Mode vs Engineer Mode
  void showSceneCreator;
  void setShowSceneCreator;
  void scenePrompt;
  void setScenePrompt;

  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [publishType, setPublishType] = useState<'Enzyme' | 'Tension Codon' | 'Character'>('Enzyme');
  const [selectedPublishItemId, setSelectedPublishItemId] = useState<string | null>(null);
  const [publishTags, setPublishTags] = useState('');
  const [publishDescription, setPublishDescription] = useState('');
  const [publishAuthor, setPublishAuthor] = useState('');
  const [showPublishForm, setShowPublishForm] = useState(false);
  const [publishConfirmation, setPublishConfirmation] = useState<{ name: string; itemId: string } | null>(null);
  const [librarySearchQuery, setLibrarySearchQuery] = useState('');
  const [publishedLibrary, setPublishedLibrary] = useState<any[]>([]);
  const [browseFilterType, setBrowseFilterType] = useState<'all' | 'enzyme' | 'character' | 'tensionCodon'>('all');
  const [sharedIndex, setSharedIndex] = useState<any[]>([]);
  const [loadingIPFS, setLoadingIPFS] = useState<{ [key: string]: boolean }>({});
  const [previewLibraryItem, setPreviewLibraryItem] = useState<LibraryItem | null>(null);
  const [libraryIndexUrl, setLibraryIndexUrl] = useState('');
  const [libraryIndexUrlInput, setLibraryIndexUrlInput] = useState('');
  const [remoteLibraryItems, setRemoteLibraryItems] = useState<LibraryIndexItem[]>([]);
  const [loadingLibraryIndex, setLoadingLibraryIndex] = useState(false);
  const [authorId, setAuthorId] = useState<string>('');
  const [authorKeyPair, setAuthorKeyPair] = useState<CryptoKeyPair | null>(null);
  const [pinataApiKey, setPinataApiKey] = useState<string>('');
  const [pinataSecret, setPinataSecret] = useState<string>('');
  const [pinataApiKeyInput, setPinataApiKeyInput] = useState<string>('');
  const [pinataSecretInput, setPinataSecretInput] = useState<string>('');
  const [showPinataSecret, setShowPinataSecret] = useState(false);
  const [nftStorageToken, setNftStorageToken] = useState<string>('');
  const [nftStorageTokenInput, setNftStorageTokenInput] = useState<string>('');
  const [showNftStorageToken, setShowNftStorageToken] = useState(false);
  const [saveNftStorageTokenResult, setSaveNftStorageTokenResult] = useState<{ success: boolean; message: string } | null>(null);

  // Avoid CI build failures from unused setters/values in WIP flows
  void setPublishType;

  const addTrigger = () => {
    if (newTrigger.trim()) {
      setTriggers([...triggers, { id: Date.now().toString(), text: newTrigger.trim() }]);
      setNewTrigger('');
    }
  };

  // Chatbot helper functions
  const getStepContext = (step: CreationStep, pending: PendingStoryworld | null): string => {
    const contexts: Record<CreationStep, string> = {
      welcome: 'The user just started. Greet them warmly and ask what kind of story they want to create.',
      premise: 'Focus on understanding the core premise, setting, and dramatic question.',
      characters: `Help define characters. Current progress: ${pending?.characters?.length || 0} characters defined.`,
      enzymes: `Create enzymes for characters. Characters: ${pending?.characters?.map(c => c.name).join(', ') || 'None yet'}`,
      tensions: `Define tension codons. Characters have enzymes. Now add external pressures.`,
      review: 'Show the complete storyworld for review. Ask if they want changes.',
      complete: 'Storyworld is complete. Offer to create it in the app.',
    };
    return contexts[step];
  };

  const parseStoryworldData = (content: string): any | null => {
    const match = content.match(/<storyworld_data>([\s\S]*?)<\/storyworld_data>/);
    if (match) {
      try {
        return JSON.parse(match[1]);
      } catch {
        return null;
      }
    }
    return null;
  };

  const cleanAssistantMessage = (content: string): string => {
    // Remove the JSON data tags from displayed message
    return content.replace(/<storyworld_data>[\s\S]*?<\/storyworld_data>/g, '').trim();
  };

  const updatePendingStoryworld = (data: any) => {
    setPendingStoryworld(prev => {
      const updated: PendingStoryworld = prev || {
        title: '',
        description: '',
        characters: [],
        enzymes: [],
        tensions: [],
      };

      if (data.title) updated.title = data.title;
      if (data.description) updated.description = data.description;
      if (data.characters) updated.characters = [...updated.characters, ...data.characters];
      if (data.enzymes) updated.enzymes = [...updated.enzymes, ...data.enzymes];
      if (data.tensions) updated.tensions = [...updated.tensions, ...data.tensions];

      return updated;
    });
  };

  const maybeAdvanceStep = (content: string) => {
    // Logic to detect when to advance to next step
    const lowerContent = content.toLowerCase();

    if (creationStep === 'welcome' && lowerContent.includes('tell me about')) {
      setCreationStep('premise');
    } else if (creationStep === 'premise' && lowerContent.includes('characters')) {
      setCreationStep('characters');
    } else if (creationStep === 'characters' && lowerContent.includes('enzyme')) {
      setCreationStep('enzymes');
    } else if (creationStep === 'enzymes' && lowerContent.includes('tension')) {
      setCreationStep('tensions');
    } else if (creationStep === 'tensions' && lowerContent.includes('review')) {
      setCreationStep('review');
    }
  };

  const sendChatMessage = async (userMessage: string) => {
    // Add user message to chat
    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setIsGenerating(true);

    // Resolve API key
    const apiKey =
      localStorage.getItem('splinewright-claude-api-key') ||
      localStorage.getItem('claude_api_key') ||
      claudeApiKey ||
      '';

    if (!apiKey) {
      const errorMsg: ChatMessage = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: 'Please add your Claude API key in Settings before using Create with AI.',
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, errorMsg]);
      setIsGenerating(false);
      setCurrentView('settings');
      return;
    }

    try {
      // Build conversation history for API
      const conversationHistory = chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      // Add current user message
      conversationHistory.push({
        role: 'user',
        content: userMessage,
      });

      // Add context about current step
      const stepContext = getStepContext(creationStep, pendingStoryworld);

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 4096,
          system: CHATBOT_SYSTEM_PROMPT + '\n\n' + stepContext,
          messages: conversationHistory,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      const assistantContent = data.content?.[0]?.text || '';

      // Parse any storyworld data from response
      const parsedData = parseStoryworldData(assistantContent);

      // Update pending storyworld if data was generated
      if (parsedData) {
        updatePendingStoryworld(parsedData);
      }

      // Add assistant message
      const assistantMsg: ChatMessage = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content: cleanAssistantMessage(assistantContent),
        timestamp: new Date(),
        attachedData: parsedData,
      };
      setChatMessages(prev => [...prev, assistantMsg]);

      // Auto-advance step if appropriate
      maybeAdvanceStep(assistantContent);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg: ChatMessage = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsGenerating(false);
    }
  };

  // ============================================
  // ROBOT FUNCTIONS
  // ============================================
  const sendRobotMessage = async (userMessage: string) => {
    const userMsg: RobotMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setRobotMessages(prev => [...prev, userMsg]);
    setRobotInput('');
    setIsRobotThinking(true);

    // Resolve API key (same logic as other AI tools)
    const apiKey =
      localStorage.getItem('splinewright-claude-api-key') ||
      localStorage.getItem('claude_api_key') ||
      claudeApiKey ||
      '';

    if (!apiKey) {
      setRobotMessages(prev => [
        ...prev,
        {
          id: `msg-${Date.now()}-err`,
          role: 'robot',
          content: '*beep boop* 🤖 Please add your Claude API key in Settings first.',
          timestamp: new Date(),
        },
      ]);
      setIsRobotThinking(false);
      setCurrentView('settings');
      return;
    }

    try {
      // Build context
      let context = '';
      if (currentStoryworld) {
        const characterIdByName = new Map<string, string>();
        characters.forEach((c) => characterIdByName.set(c.name.toLowerCase(), c.id));

        const getEnzymePressurePct = (enzymeId: string, characterName?: string): string => {
          if (!playthroughActive) return '0';
          const chId = characterName ? characterIdByName.get(characterName.toLowerCase()) : undefined;
          if (!chId) return '0';
          const st = characterStates.find((s) => s.characterId === chId);
          const p = st?.enzymePressures?.[enzymeId];
          const n = typeof p === 'number' && Number.isFinite(p) ? p : 0;
          return (n * 100).toFixed(0);
        };

        context = `\n\nSTORYWORLD: "${currentStoryworld}"
Characters: ${characters.map(c => c.name).join(', ') || 'None'}
Enzymes: ${savedEnzymes
  .map(e => `${e.characterName}'s ${e.traitName} (${getEnzymePressurePct(e.id, e.characterName)}%)`)
  .join(', ') || 'None'}
Tensions: ${tensionCodons.map(t => `${t.name} (${((t.pressureLevel || 0) * 100).toFixed(0)}%)`).join(', ') || 'None'}`;

        if (robotTask === 'encounters' && savedEnzymes.length > 0) {
          context += '\n\nENZYME TRIGGERS:';
          savedEnzymes.forEach(e => {
            context += `\n${e.characterName}'s "${e.traitName}": ${(e.triggers || []).slice(0, 3).map(t => t.text).join('; ')}`;
          });
        }
      }

      const history = robotMessages.slice(-10).map(m => ({
        role: m.role === 'robot' ? 'assistant' : 'user',
        content: m.content,
      }));
      history.push({ role: 'user', content: userMessage });

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 2048,
          system: ROBOT_PROMPT + context,
          messages: history,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API failed: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      const content = data.content?.[0]?.text || '';

      // Parse generated data (first robot_data block)
      const match = content.match(/<robot_data\s+type="([^"]+)">([\s\S]*?)<\/robot_data>/);
      let parsedData: any = null;
      if (match) {
        try {
          parsedData = { type: match[1], data: JSON.parse(match[2]) };
        } catch {
          parsedData = null;
        }
      }

      const cleanContent = content.replace(/<robot_data[\s\S]*?<\/robot_data>/g, '').trim();

      setRobotMessages(prev => [
        ...prev,
        {
          id: `msg-${Date.now()}-robot`,
          role: 'robot',
          content: cleanContent,
          timestamp: new Date(),
          data: parsedData,
        },
      ]);
    } catch (error) {
      console.error('Robot error:', error);
      setRobotMessages(prev => [
        ...prev,
        {
          id: `msg-${Date.now()}-err`,
          role: 'robot',
          content: '*beep boop* 🤖 Error! Check your API key in Settings.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsRobotThinking(false);
    }
  };

  const applyRobotData = (content: any) => {
    if (!content) return;
    const { type, data } = content || {};
    if (!type) return;

    const now = Date.now();
    const rand = () => Math.random().toString(36).slice(2, 9);
    const clampNumber = (value: any, min: number, max: number, fallback: number) => {
      const n = typeof value === 'number' ? value : parseFloat(value);
      if (Number.isFinite(n)) return Math.max(min, Math.min(max, n));
      return fallback;
    };

    const normalizeTriggers = (arr: any): Trigger[] => {
      const list: any[] = Array.isArray(arr) ? arr : [];
      return list
        .map((t, i) => {
          if (typeof t === 'string') return { id: `tr-${now}-${i}-${rand()}`, text: t };
          return {
            id: (t?.id || `tr-${now}-${i}-${rand()}`).toString(),
            text: (t?.text || t?.description || t?.situation || '').toString(),
          };
        })
        .filter((t) => t.text.trim().length > 0);
    };

    const normalizeResponses = (arr: any): Response[] => {
      const list: any[] = Array.isArray(arr) ? arr : [];
      return list
        .map((r, i) => {
          if (typeof r === 'string') return { id: `rs-${now}-${i}-${rand()}`, text: r };
          return {
            id: (r?.id || `rs-${now}-${i}-${rand()}`).toString(),
            text: (r?.text || r?.description || '').toString(),
          };
        })
        .filter((r) => r.text.trim().length > 0);
    };

    const normalizeBlindspots = (raw: any): Blindspot[] => {
      // Accept: string, {description, selfJustification}, or array of either
      const list: any[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return list
        .map((b, i) => {
          if (typeof b === 'string') {
            return {
              id: `bs-${now}-${i}-${rand()}`,
              description: b,
              selfJustification: '',
              canCrack: true,
            };
          }
          return {
            id: (b?.id || `bs-${now}-${i}-${rand()}`).toString(),
            description: (b?.description || b?.text || '').toString(),
            selfJustification: (b?.selfJustification || '').toString(),
            canCrack: typeof b?.canCrack === 'boolean' ? b.canCrack : true,
          };
        })
        .filter((b) => b.description.trim().length > 0);
    };

    const normalizeMutations = (arr: any): Mutation[] => {
      const list: any[] = Array.isArray(arr) ? arr : [];
      return list
        .map((m, i) => ({
          id: (m?.id || `mu-${now}-${i}-${rand()}`).toString(),
          name: (m?.name || `Mutation ${i + 1}`).toString(),
          description: (m?.description || '').toString(),
          probability: clampNumber(m?.probability, 0, 100, 0),
        }))
        .filter((m) => m.name.trim().length > 0);
    };

    if (type === 'character') {
      const want = (data?.want || '').toString().trim();
      const flaw = (data?.flaw || '').toString().trim();
      const wound = (data?.wound || '').toString().trim();
      const extra = [want ? `Want: ${want}` : '', flaw ? `Flaw: ${flaw}` : '', wound ? `Wound: ${wound}` : '']
        .filter(Boolean)
        .join('\n');
      const baseDesc = (data?.description || '').toString().trim();
      const description = [baseDesc, extra].filter(Boolean).join('\n\n');

      setCharacters(prev => [
        ...prev,
        {
          id: `char-${now}-${rand()}`,
          name: (data?.name || 'New Character').toString(),
          description,
          attachedEnzymes: [],
          portrait: data?.portrait || undefined,
          createdAt: now,
        },
      ]);
    }

    if (type === 'enzyme') {
      const characterName = (data?.characterName || '').toString();
      const newEnzyme: Enzyme = {
        id: `enz-${now}-${rand()}`,
        characterName,
        traitName: (data?.traitName || 'New Enzyme').toString(),
        intensity: clampNumber(data?.intensity, 0, 1, 0.7),
        triggers: normalizeTriggers(data?.triggers),
        responses: normalizeResponses(data?.responses),
        blindspots: normalizeBlindspots(data?.blindspots ?? data?.blindspot),
        mutations: normalizeMutations(data?.mutations),
        createdAt: now,
      };

      setSavedEnzymes(prev => [...prev, newEnzyme]);

      // Attach enzyme to the matching character (or create the character if missing)
      if (characterName.trim().length > 0) {
        const matchName = characterName.trim().toLowerCase();
        const existing = characters.find((c) => c.name.trim().toLowerCase() === matchName);
        if (!existing) {
          const newCharId = `char-${now}-${rand()}`;
          setCharacters(prev => [
            ...prev,
            {
              id: newCharId,
              name: characterName,
              description: '',
              attachedEnzymes: [{ enzymeId: newEnzyme.id, intensity: newEnzyme.intensity }],
              createdAt: now,
            },
          ]);
        } else {
          setCharacters(prev =>
            prev.map((c) => {
              if (c.id !== existing.id) return c;
              const already = (c.attachedEnzymes || []).some((ae) => ae.enzymeId === newEnzyme.id);
              return already
                ? c
                : {
                    ...c,
                    attachedEnzymes: [...(c.attachedEnzymes || []), { enzymeId: newEnzyme.id, intensity: newEnzyme.intensity }],
                  };
            })
          );
        }
      }
    }

    if (type === 'encounter') {
      const names: string[] = Array.isArray(data?.charactersPresent)
        ? data.charactersPresent
        : Array.isArray(data?.characters)
          ? data.characters
          : [];
      const ids = names
        .map((n: any) => characters.find((c) => c.name.toLowerCase() === (n || '').toString().toLowerCase())?.id)
        .filter(Boolean) as string[];

      const rawChoices: any[] = Array.isArray(data?.choices)
        ? data.choices
        : Array.isArray(data?.playerChoices)
          ? data.playerChoices
          : Array.isArray(data?.playerChoicePoints)
            ? data.playerChoicePoints
            : [];
      const playerChoicePoints = rawChoices
        .map((c) => (typeof c === 'string' ? c : (c?.text || '').toString()))
        .filter((t) => t.trim().length > 0);

      setEncounters(prev => [
        ...prev,
        {
          id: `enc-${now}-${rand()}`,
          title: (data?.title || 'New Encounter').toString(),
          description: (data?.description || data?.whatHappens || '').toString(),
          characterIds: ids,
          triggers: (data?.trigger || data?.triggers || '').toString(),
          whatHappens: (data?.whatHappens || data?.description || '').toString(),
          playerChoicePoints,
          createdAt: now,
        },
      ]);
    }

    if (type === 'storyworld') {
      const baseNameRaw = (data?.title || 'New Storyworld').toString().trim();
      const baseName = baseNameRaw || 'New Storyworld';
      let name = baseName;
      if (storyworlds.includes(name)) {
        let i = 2;
        while (storyworlds.includes(`${baseName} (${i})`)) i += 1;
        name = `${baseName} (${i})`;
      }

      const rawCharacters: any[] = Array.isArray(data?.characters) ? data.characters : [];
      const rawEnzymes: any[] = Array.isArray(data?.enzymes) ? data.enzymes : [];
      const rawTensions: any[] = Array.isArray(data?.tensions) ? data.tensions : [];

      const newCharacters: Character[] = rawCharacters.map((c, idx) => {
        const want = (c?.want || '').toString().trim();
        const flaw = (c?.flaw || '').toString().trim();
        const wound = (c?.wound || '').toString().trim();
        const extra = [want ? `Want: ${want}` : '', flaw ? `Flaw: ${flaw}` : '', wound ? `Wound: ${wound}` : '']
          .filter(Boolean)
          .join('\n');
        const baseDesc = (c?.description || '').toString().trim();
        const description = [baseDesc, extra].filter(Boolean).join('\n\n');

        return {
          id: (c?.id || `char-${now}-${idx}-${rand()}`).toString(),
          name: (c?.name || `Character ${idx + 1}`).toString(),
          description,
          attachedEnzymes: [],
          portrait: c?.portrait || undefined,
          createdAt: now,
        };
      });

      const idByName = new Map<string, string>();
      newCharacters.forEach((c) => idByName.set(c.name.toLowerCase(), c.id));

      const newEnzymes: Enzyme[] = rawEnzymes.map((e, idx) => {
        const enzyme: Enzyme = {
          id: (e?.id || `enz-${now}-${idx}-${rand()}`).toString(),
          characterName: (e?.characterName || '').toString(),
          traitName: (e?.traitName || e?.name || `Enzyme ${idx + 1}`).toString(),
          intensity: clampNumber(e?.intensity, 0, 1, 0.7),
          triggers: normalizeTriggers(e?.triggers),
          responses: normalizeResponses(e?.responses),
          blindspots: normalizeBlindspots(e?.blindspots ?? e?.blindspot),
          mutations: normalizeMutations(e?.mutations),
          createdAt: now,
        };
        return enzyme;
      });

      // Attach enzymes to characters by matching on characterName
      const enzymesByCharacterName = new Map<string, Enzyme[]>();
      newEnzymes.forEach((e) => {
        const key = (e.characterName || '').toLowerCase();
        if (!key) return;
        const list = enzymesByCharacterName.get(key) || [];
        list.push(e);
        enzymesByCharacterName.set(key, list);
      });

      const finalCharacters: Character[] = newCharacters.map((c) => {
        const list = enzymesByCharacterName.get(c.name.toLowerCase()) || [];
        return {
          ...c,
          attachedEnzymes: list.map((e) => ({ enzymeId: e.id, intensity: e.intensity })),
        };
      });

      const newTensionCodons: TensionCodon[] = rawTensions.map((t, idx) => {
        const affectedNames: string[] = Array.isArray(t?.affectedCharacters)
          ? t.affectedCharacters
          : Array.isArray(t?.affectedCharacterNames)
            ? t.affectedCharacterNames
            : [];
        const affectedById: string[] = Array.isArray(t?.affectedCharacterIds) ? t.affectedCharacterIds : [];

        const affectedCharacterIds = affectedNames.length > 0
          ? (affectedNames
              .map((n: any) => idByName.get((n || '').toString().toLowerCase()))
              .filter(Boolean) as string[])
          : affectedById.filter((id: any) => typeof id === 'string' && finalCharacters.some((c) => c.id === id));

        return {
          id: (t?.id || `ten-${now}-${idx}-${rand()}`).toString(),
          name: (t?.name || `Tension ${idx + 1}`).toString(),
          description: (t?.description || '').toString(),
          pressureLevel: clampNumber(t?.pressureLevel ?? t?.pressure ?? t?.startingPressure, 0, 1, 0.5),
          affectedCharacterIds,
          createdAt: now,
        };
      });

      const storyworldData = {
        name,
        characters: finalCharacters,
        enzymes: newEnzymes,
        tensionCodons: newTensionCodons,
        encounters: [],
        escalationProfile: { ...escalationProfile },
      };

      localStorage.setItem(`storyworld_${name}`, JSON.stringify(storyworldData));
      const updatedStoryworlds = storyworlds.includes(name) ? storyworlds : [...storyworlds, name];
      setStoryworlds(updatedStoryworlds);
      localStorage.setItem('splinewright-storyworlds', JSON.stringify(updatedStoryworlds));

      loadStoryworld(name);
      setStoryworldAccessLevel('full');
      setIsInvitedStoryworld(false);
      setCurrentView('storyworld');
      setSelectedEnzymeId(null);
      setSelectedCharacterId(null);
    }

    // Show confirmation
    setRobotMessages(prev => [
      ...prev,
      {
        id: `msg-${Date.now()}-confirm`,
        role: 'robot',
        content: `✅ Added ${type} to your storyworld!`,
        timestamp: new Date(),
      },
    ]);
  };

  const createStoryworldFromChat = () => {
    if (!pendingStoryworld) return;

    const now = Date.now();
    const rand = () => Math.random().toString(36).slice(2, 9);
    const clampNumber = (value: any, min: number, max: number, fallback: number) => {
      const n = typeof value === 'number' ? value : parseFloat(value);
      if (Number.isFinite(n)) return Math.max(min, Math.min(max, n));
      return fallback;
    };

    const baseNameRaw = (pendingStoryworld.title || 'AI-Created Storyworld').toString().trim();
    const baseName = baseNameRaw || 'AI-Created Storyworld';
    let name = baseName;
    if (storyworlds.includes(name)) {
      let i = 2;
      while (storyworlds.includes(`${baseName} (${i})`)) i += 1;
      name = `${baseName} (${i})`;
    }

    const rawCharacters: any[] = Array.isArray((pendingStoryworld as any).characters)
      ? (pendingStoryworld as any).characters
      : [];
    const rawEnzymes: any[] = Array.isArray((pendingStoryworld as any).enzymes)
      ? (pendingStoryworld as any).enzymes
      : [];
    const rawTensions: any[] = Array.isArray((pendingStoryworld as any).tensions)
      ? (pendingStoryworld as any).tensions
      : Array.isArray((pendingStoryworld as any).tensionCodons)
        ? (pendingStoryworld as any).tensionCodons
        : [];

    const newCharacters: Character[] = rawCharacters.map((c, idx) => {
      const want = (c?.want || '').toString().trim();
      const flaw = (c?.flaw || '').toString().trim();
      const wound = (c?.wound || '').toString().trim();
      const extra = [want ? `Want: ${want}` : '', flaw ? `Flaw: ${flaw}` : '', wound ? `Wound: ${wound}` : '']
        .filter(Boolean)
        .join('\n');
      const baseDesc = (c?.description || '').toString().trim();
      const description = [baseDesc, extra].filter(Boolean).join('\n\n');

      return {
        id: (c?.id || `char-${now}-${idx}-${rand()}`).toString(),
        name: (c?.name || `Character ${idx + 1}`).toString(),
        description,
        attachedEnzymes: [],
        portrait: c?.portrait || undefined,
        createdAt: now,
      };
    });

    const normalizeTriggers = (arr: any): Trigger[] => {
      const list: any[] = Array.isArray(arr) ? arr : [];
      return list
        .map((t, i) => {
          if (typeof t === 'string') return { id: `tr-${now}-${i}-${rand()}`, text: t };
          return {
            id: (t?.id || `tr-${now}-${i}-${rand()}`).toString(),
            text: (t?.text || t?.description || t?.situation || '').toString(),
          };
        })
        .filter((t) => t.text.trim().length > 0);
    };

    const normalizeResponses = (arr: any): Response[] => {
      const list: any[] = Array.isArray(arr) ? arr : [];
      return list
        .map((r, i) => {
          if (typeof r === 'string') return { id: `rs-${now}-${i}-${rand()}`, text: r };
          return {
            id: (r?.id || `rs-${now}-${i}-${rand()}`).toString(),
            text: (r?.text || r?.description || '').toString(),
          };
        })
        .filter((r) => r.text.trim().length > 0);
    };

    const normalizeBlindspots = (value: any): Blindspot[] => {
      if (Array.isArray(value)) {
        return value
          .map((b, i) => ({
            id: (b?.id || `bl-${now}-${i}-${rand()}`).toString(),
            description: (typeof b === 'string' ? b : (b?.description || '')).toString(),
            selfJustification: (typeof b === 'string' ? '' : (b?.selfJustification || b?.justification || '')).toString(),
            canCrack: typeof b?.canCrack === 'boolean' ? b.canCrack : false,
          }))
          .filter((b) => b.description.trim().length > 0);
      }
      if (!value) return [];
      if (typeof value === 'string') {
        return [{
          id: `bl-${now}-0-${rand()}`,
          description: value,
          selfJustification: '',
          canCrack: false,
        }];
      }
      return [{
        id: (value?.id || `bl-${now}-0-${rand()}`).toString(),
        description: (value?.description || value?.blindspot || '').toString(),
        selfJustification: (value?.selfJustification || '').toString(),
        canCrack: typeof value?.canCrack === 'boolean' ? value.canCrack : false,
      }].filter((b) => b.description.trim().length > 0);
    };

    const normalizeMutations = (arr: any): Mutation[] => {
      const list: any[] = Array.isArray(arr) ? arr : [];
      return list.map((m, i) => ({
        id: (m?.id || `mu-${now}-${i}-${rand()}`).toString(),
        name: (m?.name || '').toString(),
        description: (m?.description || '').toString(),
        probability: clampNumber(m?.probability, 0, 100, 0),
      }));
    };

    const newEnzymes: Enzyme[] = rawEnzymes.map((e, idx) => {
      const characterName =
        (e?.characterName || e?.character || e?.forCharacter || '').toString();
      return {
        id: (e?.id || `enz-${now}-${idx}-${rand()}`).toString(),
        characterName,
        traitName: (e?.traitName || e?.trait || e?.name || `Enzyme ${idx + 1}`).toString(),
        intensity: clampNumber(e?.intensity, 0, 1, 0.65),
        triggers: normalizeTriggers(e?.triggers),
        responses: normalizeResponses(e?.responses),
        blindspots: normalizeBlindspots(e?.blindspots ?? e?.blindspot ?? (e?.blindspot && e?.selfJustification ? { description: e.blindspot, selfJustification: e.selfJustification } : null)),
        mutations: normalizeMutations(e?.mutations),
        createdAt: now,
      };
    });

    // Attach enzymes to characters by characterName
    const charIndexByName = new Map<string, number>();
    newCharacters.forEach((c, idx) => charIndexByName.set(c.name.toLowerCase(), idx));
    newEnzymes.forEach((enz) => {
      const key = (enz.characterName || '').toLowerCase();
      const idx = key ? charIndexByName.get(key) : undefined;
      if (idx === undefined) return;
      const ch = newCharacters[idx];
      const nextAttached = [...ch.attachedEnzymes, { enzymeId: enz.id, intensity: enz.intensity }];
      newCharacters[idx] = { ...ch, attachedEnzymes: nextAttached };
      enz.characterName = ch.name;
    });

    const idByName = new Map<string, string>();
    newCharacters.forEach((c) => idByName.set(c.name.toLowerCase(), c.id));
    const newTensionCodons: TensionCodon[] = rawTensions.map((t, idx) => {
      const affectedNames: string[] = Array.isArray(t?.affectedCharacters)
        ? t.affectedCharacters
        : Array.isArray(t?.affectedCharacterNames)
          ? t.affectedCharacterNames
          : [];
      const affectedById: string[] = Array.isArray(t?.affectedCharacterIds) ? t.affectedCharacterIds : [];

      const affectedCharacterIds = affectedNames.length > 0
        ? affectedNames
            .map((n: any) => idByName.get((n || '').toString().toLowerCase()))
            .filter(Boolean) as string[]
        : affectedById.filter((id: any) => typeof id === 'string' && newCharacters.some((c) => c.id === id));

      return {
        id: (t?.id || `ten-${now}-${idx}-${rand()}`).toString(),
        name: (t?.name || `Tension ${idx + 1}`).toString(),
        description: (t?.description || '').toString(),
        pressureLevel: clampNumber(t?.pressureLevel ?? t?.pressure ?? t?.startingPressure, 0, 1, 0.5),
        affectedCharacterIds,
        createdAt: now,
      };
    });

    const storyworldData = {
      name,
      characters: newCharacters,
      enzymes: newEnzymes,
      tensionCodons: newTensionCodons,
      encounters: [],
      escalationProfile: { ...escalationProfile },
    };

    // Save and switch to the new storyworld
    localStorage.setItem(`storyworld_${name}`, JSON.stringify(storyworldData));
    const updatedStoryworlds = storyworlds.includes(name) ? storyworlds : [...storyworlds, name];
    setStoryworlds(updatedStoryworlds);
    localStorage.setItem('splinewright-storyworlds', JSON.stringify(updatedStoryworlds));

    // Also save to individual localStorage keys for compatibility
    localStorage.setItem('splinewright-enzymes', JSON.stringify(newEnzymes));
    localStorage.setItem('splinewright-characters', JSON.stringify(newCharacters));
    localStorage.setItem('splinewright-tension-codons', JSON.stringify(newTensionCodons));
    localStorage.setItem('splinewright-encounters', JSON.stringify([]));

    loadStoryworld(name);
    setStoryworldAccessLevel('full');
    setIsInvitedStoryworld(false);
    setCurrentView('storyworld');
    setSelectedEnzymeId(null);
    setSelectedCharacterId(null);

    // Reset chatbot
    setShowChatbot(false);
    setCreationStep('complete');
    setChatMessages([]);
    setPendingStoryworld(null);

    alert(`✓ Created storyworld "${name}" (${newCharacters.length} characters, ${newEnzymes.length} enzymes, ${newTensionCodons.length} tensions)`);
  };

  // --- Storyworld context building (for AI prompting) ---
  const extractWantFlawWound = (description: string): { want: string; flaw: string; wound: string } => {
    const text = (description || '').toString();
    const pickLine = (label: string) => {
      const re = new RegExp(`(?:^|\\n)\\s*${label}:\\s*(.+?)\\s*(?:\\n|$)`, 'i');
      const m = text.match(re);
      return (m?.[1] || '').trim();
    };
    return {
      want: pickLine('Want'),
      flaw: pickLine('Flaw'),
      wound: pickLine('Wound'),
    };
  };

  const getCharacterEmotionalState = (characterId: string): string | undefined => {
    const ch = characters.find((c) => c.id === characterId);
    if (!ch) return undefined;
    // currentEmotions is keyed by character name in Play mode; fall back to id just in case.
    return currentEmotions[ch.name] || currentEmotions[characterId] || undefined;
  };

  const getCharacterRelationships = (_characterId: string): Relationship[] => {
    // Relationship modeling is not implemented yet in Splinewright, so we provide an empty list for now.
    return [];
  };

  const getEnzymeActivationHistory = (_enzymeId: string): ActivationRecord[] => {
    const enzyme = savedEnzymes.find((e) => e.id === _enzymeId);
    if (!enzyme) return [];

    const characterName = (enzyme.characterName || '').toString();
    const enzymeName = (enzyme.traitName || '').toString();
    if (!characterName || !enzymeName) return [];

    const needle = `${characterName}'s ${enzymeName} triggered`;

    const records: ActivationRecord[] = [];
    for (const entry of storyHistory) {
      const consequences = Array.isArray(entry.consequences) ? entry.consequences : [];
      if (!consequences.some((c) => (c || '').toString().includes(needle))) continue;
      records.push({
        encounterId: entry.encounterId,
        encounterTitle: entry.encounterTitle,
        timestamp: entry.timestamp,
        note: entry.choice,
      });
    }

    // Cap to recent items to keep prompts small
    return records.slice(-8);
  };

  const getTensionHistory = (_tensionId: string): PressureChange[] => {
    // We don't persist per-tension pressure history yet; provide an empty array for now.
    return [];
  };

  const calculateTrend = (values: number[]): 'rising' | 'falling' | 'flat' => {
    if (!Array.isArray(values) || values.length < 2) return 'flat';
    const first = values[0];
    const last = values[values.length - 1];
    const delta = last - first;
    if (Math.abs(delta) < 0.05) return 'flat';
    return delta > 0 ? 'rising' : 'falling';
  };

  const getPacingRecommendation = (avgIntensity: number, phase: StoryState['currentPhase']): string => {
    if (avgIntensity > 0.75) return 'Recent pacing is intense; consider a quieter beat or emotional fallout.';
    if (avgIntensity < 0.35 && phase !== 'exposition') return 'Recent pacing is calm; consider escalating a tension or triggering a key enzyme.';
    if (phase === 'climax') return 'Drive toward irreversible consequences; raise pressure and force hard choices.';
    if (phase === 'exposition') return 'Focus on establishing stakes, relationships, and the story’s core tensions.';
    return 'Maintain momentum: alternate pressure with reflection and reveal new complications.';
  };

  const calculateEncountersToMutation = (enzymeId: string): number => {
    // Estimate from current pressure and expected per-choice delta.
    // Quick Play increases pressure faster (+0.2), normal is (+0.1).
    const step = quickPlayMode ? 0.2 : 0.1;
    let current = 0;
    for (const cs of characterStates) {
      const p = cs.enzymePressures?.[enzymeId];
      if (typeof p === 'number') current = Math.max(current, p);
    }
    if (current >= 1) return 0;
    return Math.max(1, Math.ceil((1 - current) / step));
  };

  const calculateStoryState = (): StoryState => {
    const encounterCount = encounters.length;

    // Current maximum enzyme pressure (Play mode), else 0
    const pressures: number[] = [];
    characterStates.forEach((cs) => {
      Object.values(cs.enzymePressures || {}).forEach((p) => {
        if (typeof p === 'number') pressures.push(p);
      });
    });
    const maxPressure = pressures.length > 0 ? Math.max(...pressures) : 0;

    const hasAnyMutation = characterStates.some((cs) =>
      Object.values(cs.mutationStatus || {}).some((s) => s !== 'normal')
    );

    let phase: StoryState['currentPhase'] = 'exposition';
    if (encounterCount <= 3) phase = 'exposition';
    else if (maxPressure < 0.5) phase = 'rising';
    else if (maxPressure < 0.8) phase = 'crisis';
    else if (hasAnyMutation) phase = 'resolution';
    else phase = 'climax';

    // We don't store per-encounter intensity, so estimate from current state for now.
    const tensionAvg =
      tensionCodonStates.length > 0
        ? tensionCodonStates.reduce((sum, t) => sum + (t.currentPressureLevel || 0), 0) / tensionCodonStates.length
        : tensionCodons.length > 0
          ? tensionCodons.reduce((sum, t) => sum + (t.pressureLevel || 0), 0) / tensionCodons.length
          : 0;
    const estimatedIntensity = Math.max(0, Math.min(1, 0.6 * maxPressure + 0.4 * tensionAvg));
    const recentIntensities = Array(Math.min(5, Math.max(1, encounterCount))).fill(estimatedIntensity);
    const averageIntensity = recentIntensities.reduce((a, b) => a + b, 0) / recentIntensities.length;

    const needs: NarrativeNeed[] = [];

    // Dormant enzymes (not activated recently)
    const recentEncounterIds = new Set<string>(
      storyHistory.slice(-5).map((e) => e.encounterId).filter(Boolean)
    );
    if (recentEncounterIds.size > 0) {
      const characterIdByName = new Map<string, string>();
      characters.forEach((c) => characterIdByName.set(c.name.toLowerCase(), c.id));
      savedEnzymes.forEach((enz) => {
        const history = getEnzymeActivationHistory(enz.id);
        const recent = history.filter((h) => recentEncounterIds.has(h.encounterId));
        if (recent.length === 0) {
          needs.push({
            type: 'enzyme_activation',
            priority: 'medium',
            description: `${enz.characterName}'s ${enz.traitName} hasn't been activated recently`,
            suggestedEnzymes: [enz.id],
            suggestedCharacters: [
              characterIdByName.get((enz.characterName || '').toLowerCase()) || '',
            ].filter(Boolean),
          });
        }
      });
    }

    // Enzymes that are near mutation
    savedEnzymes.forEach((enz) => {
      const p = pressures.length > 0
        ? (characterStates.find((cs) => {
            const ch = characters.find((c) => c.id === cs.characterId);
            return ch && ch.name === enz.characterName;
          })?.enzymePressures?.[enz.id] ?? 0)
        : 0;
      if (p > 0.85) {
        needs.push({
          type: 'mutation_setup',
          priority: 'high',
          description: `${enz.characterName}'s ${enz.traitName} is near mutation (${Math.round(p * 100)}%)`,
          suggestedEnzymes: [enz.id],
        });
      }
    });

    // Low tensions outside exposition
    tensionCodons.forEach((ten) => {
      if ((ten.pressureLevel || 0) < 0.4 && phase !== 'exposition') {
        needs.push({
          type: 'tension_escalation',
          priority: 'medium',
          description: `"${ten.name}" tension is low (${(ten.pressureLevel || 0).toFixed(2)}) for current phase`,
        });
      }
    });

    if (averageIntensity > 0.7) {
      needs.push({
        type: 'breathing_room',
        priority: 'high',
        description: 'Recent pacing is intense. Consider a quieter moment or fallout beat.',
      });
    }

    const priorityRank: Record<NarrativeNeed['priority'], number> = { high: 3, medium: 2, low: 1 };

    return {
      currentPhase: phase,
      encounterCount,
      averageIntensity,
      paceAnalysis: {
        recentIntensities,
        trend: calculateTrend(recentIntensities),
        recommendation: getPacingRecommendation(averageIntensity, phase),
      },
      narrativeNeeds: needs
        .filter((n) => n.description && n.description.trim().length > 0)
        .sort((a, b) => priorityRank[b.priority] - priorityRank[a.priority]),
    };
  };

  const buildStoryworldContext = (): StoryworldContext => {
    const MAX_TEXT = 600;
    const clip = (s: any) => (s || '').toString().slice(0, MAX_TEXT);

    const characterById = new Map<string, Character>();
    characters.forEach((c) => characterById.set(c.id, c));

    const characterIdByName = new Map<string, string>();
    characters.forEach((c) => characterIdByName.set(c.name.toLowerCase(), c.id));

    const enzymePressureById = new Map<string, number>();
    characterStates.forEach((cs) => {
      Object.entries(cs.enzymePressures || {}).forEach(([enzymeId, p]) => {
        if (typeof p !== 'number') return;
        const current = enzymePressureById.get(enzymeId) || 0;
        if (p > current) enzymePressureById.set(enzymeId, p);
      });
    });

    const tensionPressureById = new Map<string, number>();
    tensionCodonStates.forEach((tcs) => {
      if (typeof tcs.currentPressureLevel !== 'number') return;
      tensionPressureById.set(tcs.codonId, tcs.currentPressureLevel);
    });

    const storyState = calculateStoryState();

    const storyHistoryByEncounterId = new Map<string, StoryHistoryEntry[]>();
    storyHistory.forEach((entry) => {
      if (!entry.encounterId) return;
      const list = storyHistoryByEncounterId.get(entry.encounterId) || [];
      list.push(entry);
      storyHistoryByEncounterId.set(entry.encounterId, list);
    });

    const context: StoryworldContext = {
      storyworld: {
        id: currentStoryworld,
        title: currentStoryworld,
        description: '',
      },
      characters: characters.map((char) => {
        const extracted = extractWantFlawWound(char.description);
        return {
          id: char.id,
          name: char.name,
          description: clip(char.description),
          want: extracted.want,
          flaw: extracted.flaw,
          wound: extracted.wound,
          currentEmotionalState: getCharacterEmotionalState(char.id),
          relationshipsWithOthers: getCharacterRelationships(char.id),
        };
      }),
      enzymes: savedEnzymes.map((enz) => {
        const characterId = characterIdByName.get((enz.characterName || '').toLowerCase()) || '';
        const currentPressure = enzymePressureById.get(enz.id) || 0;
        const firstBlindspot = enz.blindspots?.[0];
        return {
          id: enz.id,
          characterId,
          characterName: enz.characterName,
          traitName: enz.traitName,
          intensity: enz.intensity,
          currentPressure,
          triggers: (enz.triggers || []).map((t) => t.text).filter(Boolean),
          responses: (enz.responses || []).map((r) => r.text).filter(Boolean),
          blindspot: (firstBlindspot?.description || '').toString(),
          selfJustification: (firstBlindspot?.selfJustification || '').toString(),
          mutations: enz.mutations || [],
          activationHistory: getEnzymeActivationHistory(enz.id),
          predictedEncountersToMutation: calculateEncountersToMutation(enz.id),
        };
      }),
      tensions: tensionCodons.map((ten) => {
        const currentPressure = tensionPressureById.get(ten.id) ?? ten.pressureLevel ?? 0;
        const affectedNames = (ten.affectedCharacterIds || [])
          .map((id) => characterById.get(id)?.name)
          .filter(Boolean) as string[];
        return {
          id: ten.id,
          name: ten.name,
          description: clip(ten.description),
          type: 'unknown',
          currentPressure,
          escalationRate: 0.08,
          affectedCharacters: affectedNames,
          recentChanges: getTensionHistory(ten.id),
        };
      }),
      encounters: encounters.slice(-20).map((enc, i, arr) => {
        const number = encounters.length - arr.length + i + 1;
        const presentNames = (enc.characterIds || [])
          .map((id) => characterById.get(id)?.name)
          .filter(Boolean) as string[];
        const historyEntries = storyHistoryByEncounterId.get(enc.id) || [];
        const choicesMade = historyEntries.map((h) => h.choice).filter(Boolean);
        const enzymesActivated: string[] = [];
        historyEntries.forEach((h) => {
          const consequences = Array.isArray(h.consequences) ? h.consequences : [];
          consequences.forEach((c) => {
            const txt = (c || '').toString();
            if (!txt.includes(' triggered')) return;
            const m = txt.match(/'s (.+?) triggered/);
            if (m?.[1]) enzymesActivated.push(m[1]);
          });
        });
        const uniqueEnzymesActivated = Array.from(new Set(enzymesActivated));
        return {
          id: enc.id,
          number,
          title: enc.title,
          summary: clip(enc.whatHappens || enc.description).slice(0, 220),
          charactersPresent: presentNames,
          enzymesActivated: uniqueEnzymesActivated.slice(0, 8),
          tensionsAffected: [],
          choicesMade: choicesMade.length > 0 ? choicesMade.slice(0, 6) : (enc.playerChoicePoints || []).slice(0, 6),
          intensity: storyState.averageIntensity,
        };
      }),
      storyState,
    };

    return context;
  };

  // Encounter Forge implementation is defined later in this file (near encounter generation/playthrough helpers).

  // Chatbot UI helpers
  const getStepIndex = (step: CreationStep): number => {
    const steps: CreationStep[] = ['premise', 'characters', 'enzymes', 'tensions', 'review'];
    if (step === 'welcome') return -1;
    const idx = steps.indexOf(step);
    if (idx >= 0) return idx;
    if (step === 'complete') return steps.length;
    return -1;
  };

  const getInputPlaceholder = (step: CreationStep): string => {
    const placeholders: Record<CreationStep, string> = {
      welcome: 'Tell me about the story you want to create...',
      premise: 'Describe your story premise...',
      characters: 'Tell me about your characters...',
      enzymes: 'Describe their behavioral patterns...',
      tensions: 'What pressures exist in your story?',
      review: 'Request any changes...',
      complete: 'Your storyworld is ready!',
    };
    return placeholders[step];
  };

  const getQuickSuggestions = (step: CreationStep): string[] => {
    const suggestions: Record<CreationStep, string[]> = {
      welcome: [
        'Three friends find buried treasure',
        'A family reunion goes wrong',
        'Ex-lovers meet at a wedding',
      ],
      premise: [
        'Add a time pressure',
        'Make it darker',
        'Add a secret',
      ],
      characters: [
        'Add a peacekeeper character',
        'Make them have more conflict',
        'Add a wild card',
      ],
      enzymes: [
        'Make the triggers more specific',
        'Add delayed responses',
        'Increase the intensity',
      ],
      tensions: [
        'Add a deadline',
        'Add a scarcity element',
        'Add a hidden secret',
      ],
      review: [
        'This looks great!',
        'Change the character names',
        'Make it more dramatic',
      ],
      complete: [],
    };
    return suggestions[step];
  };

  const handleQuickStart = (type: string) => {
    const prompts: Record<string, string> = {
      family: "I want to create a family drama. Maybe about siblings dealing with an inheritance or long-buried secrets coming to light.",
      friends: "I want to create a story about a group of friends whose loyalty is tested. Maybe a crisis reveals who they really are.",
      romance: "I want to create a complicated love story. Not simple romance, but something with real psychological depth and barriers.",
      custom: "I have my own idea for a story. Let me tell you about it.",
    };
    sendChatMessage(prompts[type] || prompts.custom);
  };

  const WelcomeMessage = ({ onQuickStart }: { onQuickStart: (type: string) => void }) => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>🧬</div>
      <h2 style={{ color: '#f0f6fc', marginBottom: '8px' }}>Welcome to Genome Architect</h2>
      <p style={{ color: '#8b949e', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
        I'll help you create a complete storyworld through conversation.
        Tell me about the story you want to create, or choose a starting point below.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', maxWidth: '500px', margin: '0 auto' }}>
        {[
          { type: 'family', label: '👨‍👩‍👧‍👦 Family Drama', desc: 'Secrets, inheritance, old wounds' },
          { type: 'friends', label: '👥 Friends in Crisis', desc: 'Loyalty tested, bonds broken' },
          { type: 'romance', label: '💔 Complicated Love', desc: 'Passion, fear, vulnerability' },
          { type: 'custom', label: '✨ My Own Idea', desc: 'Start from scratch' },
        ].map(option => (
          <button
            key={option.type}
            onClick={() => onQuickStart(option.type)}
            style={{
              padding: '16px',
              backgroundColor: '#21262d',
              border: '1px solid #30363d',
              borderRadius: '12px',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <div style={{ color: '#f0f6fc', fontWeight: '600', marginBottom: '4px' }}>{option.label}</div>
            <div style={{ color: '#8b949e', fontSize: '13px' }}>{option.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const AttachedDataPreview = ({ data }: { data: any }) => {
    if (!data) return null;
    return (
      <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px', fontSize: '13px' }}>
        {data.characters && (
          <div style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#58a6ff' }}>Characters:</strong>{' '}
            {data.characters.map((c: any) => c.name).join(', ')}
          </div>
        )}
        {data.enzymes && (
          <div style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#3fb950' }}>Enzymes:</strong>{' '}
            {data.enzymes.map((e: any) => e.traitName).join(', ')}
          </div>
        )}
        {data.tensions && (
          <div>
            <strong style={{ color: '#d29922' }}>Tensions:</strong>{' '}
            {data.tensions.map((t: any) => t.name).join(', ')}
          </div>
        )}
      </div>
    );
  };

  const StoryworldPreview = ({ storyworld }: { storyworld: PendingStoryworld }) => (
    <div style={{ color: '#f0f6fc' }}>
      <h4 style={{ margin: '0 0 8px 0' }}>{storyworld.title}</h4>
      <div style={{ display: 'flex', gap: '24px', fontSize: '13px', color: '#8b949e' }}>
        <span>👤 {storyworld.characters.length} characters</span>
        <span>🧬 {storyworld.enzymes.length} enzymes</span>
        <span>⚡ {storyworld.tensions.length} tensions</span>
      </div>
    </div>
  );

  // Chatbot Component - Add this to your render
  const renderChatbot = () => {
    if (!showChatbot) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: isMobile ? 'stretch' : 'center',
          zIndex: 2000,
        }}
        onClick={() => setShowChatbot(false)}
      >
        <div
          style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '800px',
            height: isMobile ? '100vh' : '90vh',
            backgroundColor: '#161b22',
            borderRadius: isMobile ? '0' : '16px',
            border: isMobile ? 'none' : '1px solid #30363d',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: isMobile ? '14px 16px' : '20px 24px',
              borderBottom: '1px solid #30363d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '28px' }}>🧬</span>
              <div>
                <h2 style={{ margin: 0, color: '#f0f6fc', fontSize: isMobile ? '17px' : '18px' }}>Genome Architect</h2>
                <p style={{ margin: 0, color: '#8b949e', fontSize: '13px' }}>AI Storyworld Creation Assistant</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Progress indicator */}
              <div style={{ display: 'flex', gap: '6px' }}>
                {['premise', 'characters', 'enzymes', 'tensions', 'review'].map((step, i) => (
                  <div
                    key={step}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getStepIndex(creationStep) >= i ? '#58a6ff' : '#30363d',
                    }}
                  />
                ))}
              </div>
              <button
                onClick={() => setShowChatbot(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8b949e',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: isMobile ? '16px' : '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {chatMessages.length === 0 && <WelcomeMessage onQuickStart={handleQuickStart} />}

            {chatMessages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    backgroundColor: msg.role === 'user' ? '#238636' : '#21262d',
                    color: '#f0f6fc',
                  }}
                >
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{msg.content}</p>
                  {msg.attachedData && <AttachedDataPreview data={msg.attachedData} />}
                </div>
              </div>
            ))}

            {isGenerating && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '12px 16px', borderRadius: '12px', backgroundColor: '#21262d', color: '#8b949e' }}>
                  <span className="typing-indicator">Thinking...</span>
                </div>
              </div>
            )}
          </div>

          {/* Pending Storyworld Preview */}
          {pendingStoryworld && creationStep === 'review' && (
            <div style={{ padding: isMobile ? '12px 16px' : '16px 24px', borderTop: '1px solid #30363d', backgroundColor: '#0d1117' }}>
              <StoryworldPreview storyworld={pendingStoryworld} />
              <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <button
                  onClick={createStoryworldFromChat}
                  style={{
                    flex: 1,
                    padding: isMobile ? '14px' : '12px',
                    backgroundColor: '#238636',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  ✓ Create This Storyworld
                </button>
                <button
                  onClick={() => sendChatMessage("I'd like to make some changes.")}
                  style={{
                    padding: isMobile ? '14px 16px' : '12px 24px',
                    backgroundColor: '#21262d',
                    border: '1px solid #30363d',
                    borderRadius: '8px',
                    color: '#f0f6fc',
                    cursor: 'pointer',
                  }}
                >
                  Request Changes
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div style={{ padding: isMobile ? '12px 16px' : '16px 24px', borderTop: '1px solid #30363d' }}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (chatInput.trim() && !isGenerating) {
                  sendChatMessage(chatInput.trim());
                }
              }}
              style={{ display: 'flex', gap: '12px' }}
            >
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={getInputPlaceholder(creationStep)}
                disabled={isGenerating}
                style={{
                  flex: 1,
                  padding: isMobile ? '16px 16px' : '14px 16px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '8px',
                  color: '#f0f6fc',
                  // 16px+ prevents iOS Safari auto-zoom on focus
                  fontSize: isMobile ? '16px' : '15px',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || isGenerating}
                style={{
                  padding: isMobile ? '16px 18px' : '14px 24px',
                  backgroundColor: chatInput.trim() && !isGenerating ? '#238636' : '#21262d',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: chatInput.trim() && !isGenerating ? 'pointer' : 'not-allowed',
                }}
              >
                {isGenerating ? '...' : 'Send'}
              </button>
            </form>

            {/* Quick suggestions */}
            {chatMessages.length < 3 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                {getQuickSuggestions(creationStep).map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => sendChatMessage(suggestion)}
                    disabled={isGenerating}
                    style={{
                      padding: isMobile ? '10px 12px' : '8px 12px',
                      backgroundColor: '#21262d',
                      border: '1px solid #30363d',
                      borderRadius: '20px',
                      color: '#8b949e',
                      fontSize: isMobile ? '14px' : '13px',
                      cursor: 'pointer',
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const removeTrigger = (id: string) => {
    setTriggers(triggers.filter(trigger => trigger.id !== id));
  };

  const addResponse = () => {
    if (newResponse.trim()) {
      setResponses([...responses, { id: Date.now().toString(), text: newResponse.trim() }]);
      setNewResponse('');
    }
  };

  const removeResponse = (id: string) => {
    setResponses(responses.filter(response => response.id !== id));
  };

  const addBlindspot = () => {
    setBlindspots([...blindspots, {
      id: Date.now().toString(),
      description: '',
      selfJustification: '',
      canCrack: false,
    }]);
  };

  const removeBlindspot = (id: string) => {
    setBlindspots(blindspots.filter(blindspot => blindspot.id !== id));
  };

  const updateBlindspot = (id: string, field: keyof Blindspot, value: string | boolean) => {
    setBlindspots(blindspots.map(blindspot =>
      blindspot.id === id ? { ...blindspot, [field]: value } : blindspot
    ));
  };

  const addMutation = () => {
    setMutations([...mutations, {
      id: Date.now().toString(),
      name: '',
      description: '',
      probability: 0,
    }]);
  };

  const removeMutation = (id: string) => {
    setMutations(mutations.filter(mutation => mutation.id !== id));
  };

  const updateMutation = (id: string, field: keyof Mutation, value: string | number) => {
    setMutations(mutations.map(mutation =>
      mutation.id === id ? { ...mutation, [field]: value } : mutation
    ));
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  // Load enzymes from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-enzymes');
    if (stored) {
      try {
        setSavedEnzymes(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load enzymes from localStorage', e);
      }
    }
  }, []);

  // Load characters from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-characters');
    if (stored) {
      try {
        setCharacters(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load characters from localStorage', e);
      }
    }
  }, []);

  // Save enzymes to localStorage whenever they change
  useEffect(() => {
    if (savedEnzymes.length > 0 || localStorage.getItem('splinewright-enzymes')) {
      localStorage.setItem('splinewright-enzymes', JSON.stringify(savedEnzymes));
    }
  }, [savedEnzymes]);

  // Save characters to localStorage whenever they change
  useEffect(() => {
    if (characters.length > 0 || localStorage.getItem('splinewright-characters')) {
      try {
        localStorage.setItem('splinewright-characters', JSON.stringify(characters));
      } catch (e) {
        console.warn('localStorage save failed, retrying without portraits:', e);
        try {
          const noPortraits = characters.map(c => ({ ...c, portrait: undefined }));
          localStorage.setItem('splinewright-characters', JSON.stringify(noPortraits));
        } catch (e2) {
          console.error('Failed to save characters:', e2);
        }
      }
    }
  }, [characters]);

  // Load tension codons from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-tension-codons');
    if (stored) {
      try {
        setTensionCodons(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load tension codons from localStorage', e);
      }
    }
  }, []);

  // Save tension codons to localStorage whenever they change
  useEffect(() => {
    if (tensionCodons.length > 0 || localStorage.getItem('splinewright-tension-codons')) {
      localStorage.setItem('splinewright-tension-codons', JSON.stringify(tensionCodons));
    }
  }, [tensionCodons]);

  // Load escalation profile from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-escalation-profile');
    if (stored) {
      try {
        setEscalationProfile(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load escalation profile from localStorage', e);
      }
    }
  }, []);

  // Save escalation profile to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('splinewright-escalation-profile', JSON.stringify(escalationProfile));
  }, [escalationProfile]);

  // Load encounters from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-encounters');
    if (stored) {
      try {
        setEncounters(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load encounters from localStorage', e);
      }
    }
  }, []);

  // Load Claude API key from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-claude-api-key');
    if (stored) {
      setClaudeApiKey(stored);
      setApiKeyInput(stored);
    }
  }, []);

  // Ensure API key and secrets are hidden when Settings tab is opened
  useEffect(() => {
    if (currentView === 'settings') {
      setShowApiKey(false);
      setShowPinataSecret(false);
      setShowNftStorageToken(false);
    }
  }, [currentView]);

  // Hide Cobot API key when panel closes
  useEffect(() => {
    if (!cobotPanelOpen) {
      setShowCobotApiKey(false);
      setShowApiKeySettings(false);
    }
  }, [cobotPanelOpen]);

  // Load library items from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-library');
    if (stored) {
      try {
        setLibraryItems(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load library items from localStorage', e);
      }
    }
  }, []);

  // Load publishedLibrary from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('publishedLibrary');
    if (stored) {
      try {
        setPublishedLibrary(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load publishedLibrary from localStorage', e);
      }
    }
  }, []);

  // Load shared index from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-shared-index');
    if (stored) {
      try {
        const indexData = JSON.parse(stored);
        setSharedIndex(indexData.items || []);
      } catch (e) {
        console.error('Failed to load shared index from localStorage', e);
      }
    }
  }, []);

  // Load library index URL from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-library-index-url');
    if (stored) {
      setLibraryIndexUrl(stored);
      setLibraryIndexUrlInput(stored);
    }
  }, []);

  // Load Pinata credentials from localStorage on mount
  useEffect(() => {
    const storedApiKey = localStorage.getItem('splinewright-pinata-api-key');
    const storedSecret = localStorage.getItem('splinewright-pinata-secret');
    if (storedApiKey) {
      setPinataApiKey(storedApiKey);
      setPinataApiKeyInput(storedApiKey);
    }
    if (storedSecret) {
      setPinataSecret(storedSecret);
      setPinataSecretInput(storedSecret);
    }
  }, []);

  // Load NFT.Storage token from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-nft-storage-token');
    if (stored) {
      setNftStorageToken(stored);
      setNftStorageTokenInput(stored);
    }
  }, []);

  // Initialize or load author identity
  useEffect(() => {
    const initializeAuthorIdentity = async () => {
      // Check if we already have an author ID
      const storedAuthorId = localStorage.getItem('splinewright-author-id');
      const storedPrivateKey = localStorage.getItem('splinewright-author-private-key');
      const storedPublicKey = localStorage.getItem('splinewright-author-public-key');

      if (storedAuthorId && storedPrivateKey && storedPublicKey) {
        // Load existing identity
        setAuthorId(storedAuthorId);
        try {
          // Import the keys
          const privateKey = await crypto.subtle.importKey(
            'jwk',
            JSON.parse(storedPrivateKey),
            { name: 'ECDSA', namedCurve: 'P-256' },
            false,
            ['sign']
          );
          const publicKey = await crypto.subtle.importKey(
            'jwk',
            JSON.parse(storedPublicKey),
            { name: 'ECDSA', namedCurve: 'P-256' },
            false,
            ['verify']
          );
          setAuthorKeyPair({ privateKey, publicKey });
        } catch (e) {
          console.error('Failed to load author keys:', e);
          // Generate new identity if loading fails
          await generateAuthorIdentity();
        }
      } else {
        // Generate new identity
        await generateAuthorIdentity();
      }
    };

    const generateAuthorIdentity = async () => {
      try {
        // Generate ECDSA key pair
        const keyPair = await crypto.subtle.generateKey(
          {
            name: 'ECDSA',
            namedCurve: 'P-256',
          },
          true, // extractable
          ['sign', 'verify']
        );

        // Export public key to get a readable ID
        const publicKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
        
        // Create a short readable ID from the public key
        const publicKeyString = JSON.stringify(publicKeyJwk);
        const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(publicKeyString));
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        // Take first 16 characters for a short readable ID
        const shortId = hashHex.substring(0, 16).match(/.{1,4}/g)?.join('-') || hashHex.substring(0, 16);

        // Store in localStorage
        const privateKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
        localStorage.setItem('splinewright-author-id', shortId);
        localStorage.setItem('splinewright-author-private-key', JSON.stringify(privateKeyJwk));
        localStorage.setItem('splinewright-author-public-key', JSON.stringify(publicKeyJwk));

        setAuthorId(shortId);
        setAuthorKeyPair(keyPair);
      } catch (e) {
        console.error('Failed to generate author identity:', e);
      }
    };

    initializeAuthorIdentity();
  }, []);

  // Save library index URL to localStorage whenever it changes
  useEffect(() => {
    if (libraryIndexUrl) {
      localStorage.setItem('splinewright-library-index-url', libraryIndexUrl);
    }
  }, [libraryIndexUrl]);

  // Save library items to localStorage whenever they change
  useEffect(() => {
    if (libraryItems.length > 0 || localStorage.getItem('splinewright-library')) {
      localStorage.setItem('splinewright-library', JSON.stringify(libraryItems));
    }
  }, [libraryItems]);

  // Save Claude API key to localStorage whenever it changes
  useEffect(() => {
    if (claudeApiKey) {
      localStorage.setItem('splinewright-claude-api-key', claudeApiKey);
    }
  }, [claudeApiKey]);

  // Save encounters to localStorage whenever they change
  useEffect(() => {
    if (encounters.length > 0 || localStorage.getItem('splinewright-encounters')) {
      localStorage.setItem('splinewright-encounters', JSON.stringify(encounters));
    }
  }, [encounters]);

  // Load playthrough state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-playthrough');
    if (stored) {
      try {
        const playthroughData = JSON.parse(stored);
        if (playthroughData.active) {
          setHasSavedPlaythrough(true);
          // Don't auto-resume, let user click Continue
        }
      } catch (e) {
        console.error('Failed to load playthrough from localStorage', e);
      }
    }
  }, []);


  // Save playthrough state to localStorage whenever it changes (skip in quick play mode)
  useEffect(() => {
    if (playthroughActive && !quickPlayMode) {
      const playthroughData: PlaythroughState = {
        active: true,
        startDate: playthroughStartDate,
        currentEncounterIndex,
        totalChoicesMade,
        characters: playthroughCharacters,
        enzymes: playthroughEnzymes,
        tensionCodons: playthroughTensionCodons,
        characterStates,
        tensionCodonStates,
        storyHistory,
        selectedChoice,
        choiceOutcome,
        mutationEvent,
      };
      localStorage.setItem('splinewright-playthrough', JSON.stringify(playthroughData));
      setHasSavedPlaythrough(true);
    } else if (!playthroughActive) {
      localStorage.removeItem('splinewright-playthrough');
      setHasSavedPlaythrough(false);
    }
    // In quick play mode, don't save to localStorage
  }, [playthroughActive, quickPlayMode, playthroughStartDate, currentEncounterIndex, totalChoicesMade, playthroughCharacters, playthroughEnzymes, playthroughTensionCodons, characterStates, tensionCodonStates, storyHistory, selectedChoice, choiceOutcome, mutationEvent]);

  // Clamp currentEncounterIndex when out of bounds (e.g. after failed generation or state desync)
  useEffect(() => {
    if (encounters.length > 0 && currentEncounterIndex >= encounters.length) {
      setCurrentEncounterIndex(encounters.length - 1);
    }
  }, [encounters.length, currentEncounterIndex]);

  const saveEnzyme = () => {
    if (!characterName.trim() || !traitName.trim()) {
      alert('Please enter both character name and trait name before saving.');
      return;
    }

    const enzyme: Enzyme = {
      id: Date.now().toString(),
      characterName: characterName.trim(),
      traitName: traitName.trim(),
      intensity,
      triggers: [...triggers],
      responses: [...responses],
      blindspots: [...blindspots],
      mutations: [...mutations],
      createdAt: Date.now(),
    };

    setSavedEnzymes([...savedEnzymes, enzyme]);
    setSelectedEnzymeId(enzyme.id);
  };

  const loadEnzyme = (enzyme: Enzyme) => {
    setCharacterName(enzyme.characterName);
    setTraitName(enzyme.traitName);
    setIntensity(enzyme.intensity);
    setTriggers([...enzyme.triggers]);
    setResponses([...enzyme.responses]);
    setBlindspots([...(enzyme.blindspots || [])]);
    setMutations([...(enzyme.mutations || [])]);
    setSelectedEnzymeId(enzyme.id);
  };

  const newEnzyme = () => {
    setCharacterName('');
    setTraitName('');
    setIntensity(0.5);
    setTriggers([]);
    setResponses([]);
    setBlindspots([]);
    setMutations([]);
    setNewTrigger('');
    setNewResponse('');
    setSelectedEnzymeId(null);
  };

  // Mobile-friendly "new enzyme" entry point (forces editor to show)
  const createNewEnzyme = () => {
    newEnzyme();
    setSelectedEnzymeId('new');
  };

  const deleteEnzyme = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this enzyme?')) {
      setSavedEnzymes(savedEnzymes.filter(enzyme => enzyme.id !== id));
      if (selectedEnzymeId === id) {
        newEnzyme();
      }
      // Remove enzyme from all characters that have it attached
      setCharacters(characters.map(character => ({
        ...character,
        attachedEnzymes: character.attachedEnzymes.filter(ae => ae.enzymeId !== id)
      })));
    }
  };

  const createCharacter = () => {
    if (!newCharacterName.trim()) {
      alert('Please enter a character name.');
      return;
    }

    const character: Character = {
      id: Date.now().toString(),
      name: newCharacterName.trim(),
      description: newCharacterDescription.trim(),
      attachedEnzymes: [],
      createdAt: Date.now(),
    };

    setCharacters([...characters, character]);
    setNewCharacterName('');
    setNewCharacterDescription('');
    setSelectedCharacterId(character.id);
  };

  const deleteCharacter = (id: string) => {
    if (window.confirm('Are you sure you want to delete this character?')) {
      setCharacters(characters.filter(character => character.id !== id));
      if (selectedCharacterId === id) {
        setSelectedCharacterId(null);
      }
    }
  };

  const attachEnzymeToCharacter = (characterId: string, enzymeId: string) => {
    const character = characters.find(c => c.id === characterId);
    if (!character) return;

    // Check if enzyme is already attached
    if (character.attachedEnzymes.some(ae => ae.enzymeId === enzymeId)) {
      return;
    }

    const enzyme = savedEnzymes.find(e => e.id === enzymeId);
    if (!enzyme) return;

    const updatedCharacter: Character = {
      ...character,
      attachedEnzymes: [
        ...character.attachedEnzymes,
        { enzymeId, intensity: enzyme.intensity }
      ]
    };

    setCharacters(characters.map(c => c.id === characterId ? updatedCharacter : c));
  };

  const detachEnzymeFromCharacter = (characterId: string, enzymeId: string) => {
    setCharacters(characters.map(character =>
      character.id === characterId
        ? {
            ...character,
            attachedEnzymes: character.attachedEnzymes.filter(ae => ae.enzymeId !== enzymeId)
          }
        : character
    ));
  };

  const updateAttachedEnzymeIntensity = (characterId: string, enzymeId: string, intensity: number) => {
    setCharacters(characters.map(character =>
      character.id === characterId
        ? {
            ...character,
            attachedEnzymes: character.attachedEnzymes.map(ae =>
              ae.enzymeId === enzymeId ? { ...ae, intensity } : ae
            )
          }
        : character
    ));
  };

  const addTensionCodon = () => {
    const codon: TensionCodon = {
      id: Date.now().toString(),
      name: '',
      description: '',
      pressureLevel: 0.5,
      affectedCharacterIds: [],
      createdAt: Date.now(),
    };
    setTensionCodons([...tensionCodons, codon]);
  };

  const removeTensionCodon = (id: string) => {
    if (window.confirm('Are you sure you want to delete this tension codon?')) {
      setTensionCodons(tensionCodons.filter(codon => codon.id !== id));
    }
  };

  const updateTensionCodon = (id: string, field: keyof TensionCodon, value: string | number | string[]) => {
    setTensionCodons(tensionCodons.map(codon =>
      codon.id === id ? { ...codon, [field]: value } : codon
    ));
  };

  const toggleCharacterInTensionCodon = (codonId: string, characterId: string) => {
    const codon = tensionCodons.find(c => c.id === codonId);
    if (!codon) return;

    const isAffected = codon.affectedCharacterIds.includes(characterId);
    const newAffectedIds = isAffected
      ? codon.affectedCharacterIds.filter(id => id !== characterId)
      : [...codon.affectedCharacterIds, characterId];

    updateTensionCodon(codonId, 'affectedCharacterIds', newAffectedIds);
  };

  const updateEscalationProfile = (field: keyof EscalationProfile, value: EscalationType | number) => {
    setEscalationProfile(prev => {
      const updated = { ...prev, [field]: value };
      // When type changes, ensure we have default values for the new type
      if (field === 'type') {
        if (value === 'slow-burn' && !updated.slowBurnReinforcements) {
          updated.slowBurnReinforcements = 3;
        } else if (value === 'hair-trigger' && !updated.hairTriggerRecoveryTime) {
          updated.hairTriggerRecoveryTime = 1;
        } else if (value === 'wave') {
          if (!updated.waveBuildDuration) updated.waveBuildDuration = 5;
          if (!updated.waveReleaseIntensity) updated.waveReleaseIntensity = 0.7;
        }
      }
      return updated;
    });
  };

  // Storyworld Management Functions
  const saveCurrentStoryworld = () => {
    const storyworldData = {
      name: currentStoryworld,
      characters,
      enzymes: savedEnzymes,
      tensionCodons,
      encounters,
      escalationProfile,
    };
    localStorage.setItem(`storyworld_${currentStoryworld}`, JSON.stringify(storyworldData));
  };

  const loadStoryworld = (name: string) => {
    const stored = localStorage.getItem(`storyworld_${name}`);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setCharacters(data.characters || []);
        setSavedEnzymes(data.enzymes || []);
        setTensionCodons(data.tensionCodons || []);
        setEncounters(data.encounters || []);
        if (data.escalationProfile) {
          setEscalationProfile(data.escalationProfile);
        }
        setCurrentStoryworld(name);
      } catch (e) {
        console.error('Failed to load storyworld:', e);
        alert('Failed to load storyworld');
      }
    }
  };

  const createNewStoryworld = () => {
    if (!newStoryworldName.trim()) {
      alert('Please enter a storyworld name');
      return;
    }

    if (storyworlds.includes(newStoryworldName.trim())) {
      alert('A storyworld with this name already exists');
      return;
    }

    // Save current storyworld before switching
    if (window.confirm(`Save current storyworld "${currentStoryworld}" before creating new one?`)) {
      saveCurrentStoryworld();
    }

    // Load data if copying from current
    if (copyFromCurrent) {
      const storyworldData = {
        name: newStoryworldName.trim(),
        characters: [...characters],
        enzymes: [...savedEnzymes],
        tensionCodons: [...tensionCodons],
        encounters: [...encounters],
        escalationProfile: { ...escalationProfile },
      };
      localStorage.setItem(`storyworld_${newStoryworldName.trim()}`, JSON.stringify(storyworldData));
    } else {
      // Clear all data
      setCharacters([]);
      setSavedEnzymes([]);
      setTensionCodons([]);
      setEncounters([]);
    }

    // Update storyworlds list
    const updatedStoryworlds = [...storyworlds, newStoryworldName.trim()];
    setStoryworlds(updatedStoryworlds);
    localStorage.setItem('splinewright-storyworlds', JSON.stringify(updatedStoryworlds));
    
    setCurrentStoryworld(newStoryworldName.trim());
    setShowNewStoryworldModal(false);
    setNewStoryworldName('');
    setCopyFromCurrent(false);
  };

  const switchStoryworld = (name: string) => {
    if (name === currentStoryworld) return;

    // Save current before switching
    if (window.confirm(`Save current storyworld "${currentStoryworld}" before switching?`)) {
      saveCurrentStoryworld();
    }

    loadStoryworld(name);
    setShowStoryworldDropdown(false);
  };

  const deleteStoryworld = (name: string) => {
    if (name === currentStoryworld) {
      alert('Cannot delete the currently active storyworld. Switch to another storyworld first.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) {
      localStorage.removeItem(`storyworld_${name}`);
      const updatedStoryworlds = storyworlds.filter(sw => sw !== name);
      setStoryworlds(updatedStoryworlds);
      localStorage.setItem('splinewright-storyworlds', JSON.stringify(updatedStoryworlds));
    }
  };

  const exportStoryworld = (name: string) => {
    const stored = localStorage.getItem(`storyworld_${name}`);
    if (stored) {
      const data = JSON.parse(stored);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storyworld_${name.replace(/\s+/g, '_')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Invite System Functions
  const generateInviteLink = () => {
    const storyworldData = {
      name: currentStoryworld,
      characters,
      enzymes: savedEnzymes,
      tensionCodons,
      encounters,
      escalationProfile,
      author: 'Anonymous', // Could be from settings
      accessLevel: inviteAccessLevel,
      timestamp: new Date().toISOString(),
    };

    const jsonString = JSON.stringify(storyworldData);
    const jsonSize = new Blob([jsonString]).size;
    
    // Warn if too large (URLs have ~2000 char limit, base64 increases size by ~33%)
    // Check both raw size and encoded size
    const estimatedEncodedSize = jsonSize * 1.33;
    if (jsonSize > 1000000 || estimatedEncodedSize > 2000000) { // ~1MB raw or ~2MB encoded
      alert('Storyworld too large for link. Use "Share as File" instead.');
      return;
    }

    try {
      // Encode to base64
      const encoded = btoa(unescape(encodeURIComponent(jsonString)));
      const baseUrl = window.location.origin + window.location.pathname;
      const inviteLink = `${baseUrl}?invite=${encoded}`;
      setGeneratedInviteLink(inviteLink);
    } catch (e) {
      console.error('Failed to generate invite link:', e);
      alert('Failed to generate invite link. Storyworld may be too large.');
    }
  };

  const copyInviteLink = () => {
    if (generatedInviteLink) {
      navigator.clipboard.writeText(generatedInviteLink).then(() => {
        alert('✓ Link copied to clipboard!');
      }).catch(() => {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = generatedInviteLink;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('✓ Link copied to clipboard!');
      });
    }
  };

  const acceptInvite = () => {
    if (!pendingInviteData) return;

    const inviteData = pendingInviteData;
    const accessLevel = inviteData.accessLevel || 'play';
    const name = accessLevel === 'full' 
      ? inviteData.name 
      : `${inviteData.name} (${accessLevel === 'play' ? 'Play Only' : 'View Only'})`;
    
    // Save as new storyworld
    const storyworldData = {
      name: name,
      characters: inviteData.characters || [],
      enzymes: inviteData.enzymes || [],
      tensionCodons: inviteData.tensionCodons || [],
      encounters: inviteData.encounters || [],
      escalationProfile: inviteData.escalationProfile || escalationProfile,
    };

    localStorage.setItem(`storyworld_${name}`, JSON.stringify(storyworldData));
    
    // Add to storyworlds list
    const updatedStoryworlds = [...storyworlds, name];
    setStoryworlds(updatedStoryworlds);
    localStorage.setItem('splinewright-storyworlds', JSON.stringify(updatedStoryworlds));
    
    // Load the storyworld
    setCharacters(storyworldData.characters);
    setSavedEnzymes(storyworldData.enzymes);
    setTensionCodons(storyworldData.tensionCodons);
    setEncounters(storyworldData.encounters);
    if (storyworldData.escalationProfile) {
      setEscalationProfile(storyworldData.escalationProfile);
    }
    setCurrentStoryworld(name);
    
    // Set access level
    setStoryworldAccessLevel(accessLevel);
    setIsInvitedStoryworld(true);
    setInvitedStoryworldAuthor(inviteData.author || 'Anonymous');
    
    setShowInviteAcceptModal(false);
    setPendingInviteData(null);
  };

  const makeCopyOfStoryworld = () => {
    const baseName = currentStoryworld
      .replace(' (Play Only)', '')
      .replace(' (View Only)', '')
      .replace(' (Shared)', '');
    const copyName = `${baseName} (Copy)`;
    
    const storyworldData = {
      name: copyName,
      characters: [...characters],
      enzymes: [...savedEnzymes],
      tensionCodons: [...tensionCodons],
      encounters: [...encounters],
      escalationProfile: { ...escalationProfile },
    };

    localStorage.setItem(`storyworld_${copyName}`, JSON.stringify(storyworldData));
    
    // Add to storyworlds list
    const updatedStoryworlds = [...storyworlds, copyName];
    setStoryworlds(updatedStoryworlds);
    localStorage.setItem('splinewright-storyworlds', JSON.stringify(updatedStoryworlds));
    
    // Load the copy
    setCurrentStoryworld(copyName);
    setStoryworldAccessLevel('full');
    setIsInvitedStoryworld(false);
    
    alert(`✓ Created your own copy: "${copyName}"`);
  };

  const shareStoryworldAsFile = () => {
    const storyworldData = {
      name: currentStoryworld,
      characters,
      enzymes: savedEnzymes,
      tensionCodons,
      encounters,
      escalationProfile,
      author: 'Anonymous',
      version: '1.0.0',
      timestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(storyworldData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentStoryworld.replace(/\s+/g, '_')}.splinewright`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openStoryworldFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        const name = data.name || `Imported ${Date.now()}`;
        
        // Transform enzymes to match internal structure
        const transformedEnzymes: Enzyme[] = (data.enzymes || []).map((enzyme: any, enzymeIndex: number) => ({
          id: enzyme.id || `enzyme-${Date.now()}-${enzymeIndex}-${Math.random().toString(36).substr(2, 9)}`,
          characterName: enzyme.characterName || '',
          traitName: enzyme.traitName || '',
          intensity: typeof enzyme.intensity === 'number' ? enzyme.intensity : parseFloat(enzyme.intensity) || 0.5,
          triggers: (enzyme.triggers || []).map((trigger: any, triggerIndex: number) => {
            // Handle string triggers
            if (typeof trigger === 'string') {
              return {
                id: `trigger-${enzymeIndex}-${triggerIndex}`,
                text: trigger,
              };
            }
            // Handle object triggers with description field
            return {
              id: trigger.id || `trigger-${enzymeIndex}-${triggerIndex}`,
              text: trigger.text || trigger.description || trigger.situation || '',
            };
          }),
          responses: (enzyme.responses || []).map((response: any, responseIndex: number) => {
            // Handle string responses
            if (typeof response === 'string') {
              return {
                id: `response-${enzymeIndex}-${responseIndex}`,
                text: response,
              };
            }
            // Handle object responses
            return {
              id: response.id || `response-${enzymeIndex}-${responseIndex}`,
              text: response.text || response.description || '',
            };
          }),
          blindspots: (enzyme.blindspots || []).map((blindspot: any, blindspotIndex: number) => ({
            id: blindspot.id || `blindspot-${enzymeIndex}-${blindspotIndex}`,
            description: typeof blindspot === 'string' ? blindspot : (blindspot.description || ''),
            selfJustification: typeof blindspot === 'string' ? '' : (blindspot.selfJustification || ''),
            canCrack: typeof blindspot === 'string' ? false : (typeof blindspot.canCrack === 'boolean' ? blindspot.canCrack : false),
          })),
          mutations: (enzyme.mutations || []).map((mutation: any, mutationIndex: number) => ({
            id: mutation.id || `mutation-${enzymeIndex}-${mutationIndex}`,
            name: mutation.name || '',
            description: mutation.description || '',
            probability: typeof mutation.probability === 'number' ? mutation.probability : parseFloat(mutation.probability) || 0,
          })),
          createdAt: enzyme.createdAt || Date.now(),
        }));

        // Debug logging
        console.log('Imported enzymes:', transformedEnzymes);
        console.log('First enzyme triggers:', transformedEnzymes[0]?.triggers);

        // Transform characters to match internal structure
        const transformedCharacters: Character[] = (data.characters || []).map((character: any) => ({
          id: character.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name: character.name || '',
          description: character.description || '',
          attachedEnzymes: (character.attachedEnzymes || character.enzymeIds || []).map((ae: any) => {
            // Handle both formats: { enzymeId, intensity } or just enzymeId string
            if (typeof ae === 'string') {
              return {
                enzymeId: ae,
                intensity: 0.5,
              };
            }
            return {
              enzymeId: ae.enzymeId || ae.id || '',
              intensity: typeof ae.intensity === 'number' ? ae.intensity : parseFloat(ae.intensity) || 0.5,
            };
          }),
          portrait: character.portrait || undefined,
          createdAt: character.createdAt || Date.now(),
        }));

        // Transform tension codons to match internal structure
        const transformedTensionCodons: TensionCodon[] = (data.tensionCodons || []).map((codon: any) => ({
          id: codon.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name: codon.name || '',
          description: codon.description || '',
          pressureLevel: typeof codon.pressureLevel === 'number' 
            ? codon.pressureLevel 
            : typeof codon.pressure === 'number' 
              ? codon.pressure 
              : parseFloat(codon.pressure || codon.pressureLevel || '0') || 0,
          affectedCharacterIds: codon.affectedCharacterIds || codon.characterIds || [],
          createdAt: codon.createdAt || Date.now(),
        }));

        // Transform encounters (if any)
        const transformedEncounters: Encounter[] = (data.encounters || []).map((encounter: any) => ({
          id: encounter.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
          title: encounter.title || '',
          description: encounter.description || '',
          characterIds: encounter.characterIds || [],
          triggers: encounter.triggers || '',
          whatHappens: encounter.whatHappens || encounter.description || '',
          playerChoicePoints: encounter.playerChoicePoints || encounter.choices || [],
          createdAt: encounter.createdAt || Date.now(),
          isEndingEncounter: encounter.isEndingEncounter || false,
          emotions: encounter.emotions || {},
          choiceEmotionChanges: encounter.choiceEmotionChanges || [],
        }));

        // Save as new storyworld
        const storyworldData = {
          name: name,
          characters: transformedCharacters,
          enzymes: transformedEnzymes,
          tensionCodons: transformedTensionCodons,
          encounters: transformedEncounters,
          escalationProfile: data.escalationProfile || escalationProfile,
        };

        localStorage.setItem(`storyworld_${name}`, JSON.stringify(storyworldData));
        
        // Also save to individual localStorage keys for compatibility
        localStorage.setItem('splinewright-enzymes', JSON.stringify(transformedEnzymes));
        localStorage.setItem('splinewright-characters', JSON.stringify(transformedCharacters));
        localStorage.setItem('splinewright-tension-codons', JSON.stringify(transformedTensionCodons));
        localStorage.setItem('splinewright-encounters', JSON.stringify(transformedEncounters));
        
        // Add to storyworlds list
        const updatedStoryworlds = [...storyworlds, name];
        setStoryworlds(updatedStoryworlds);
        localStorage.setItem('splinewright-storyworlds', JSON.stringify(updatedStoryworlds));
        
        // Load the storyworld
        setCharacters(transformedCharacters);
        setSavedEnzymes(transformedEnzymes);
        setTensionCodons(transformedTensionCodons);
        setEncounters(transformedEncounters);
        if (storyworldData.escalationProfile) {
          setEscalationProfile(storyworldData.escalationProfile);
        }
        setCurrentStoryworld(name);
        setStoryworldAccessLevel('full');
        setIsInvitedStoryworld(false);
        
        // Show confirmation with counts
        const enzymeCount = transformedEnzymes.length;
        const characterCount = transformedCharacters.length;
        const tensionCodonCount = transformedTensionCodons.length;
        alert(`✓ Imported: ${enzymeCount} enzyme${enzymeCount !== 1 ? 's' : ''}, ${characterCount} character${characterCount !== 1 ? 's' : ''}, ${tensionCodonCount} tension codon${tensionCodonCount !== 1 ? 's' : ''}`);
      } catch (err) {
        console.error('Import error:', err);
        alert('Failed to open storyworld file. Invalid file format.');
      }
    };
    reader.readAsText(file);
  };

  // Check for invite parameter on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteParam = urlParams.get('invite');
    
    if (inviteParam) {
      try {
        // Decode base64 and parse JSON
        const decoded = decodeURIComponent(escape(atob(inviteParam)));
        const inviteData = JSON.parse(decoded);
        setPendingInviteData(inviteData);
        setInvitedStoryworldAuthor(inviteData?.author || 'Anonymous');
        setShowInviteAcceptModal(true);
        
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        console.error('Failed to parse invite:', e);
        alert('Invalid invite link');
      }
    }
  }, []);

  // Handle drag and drop for .splinewright files
  useEffect(() => {
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.name.endsWith('.splinewright') || file.type === 'application/json') {
          openStoryworldFile(file);
        }
      }
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
    // Intentionally run once on mount (global listeners)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load storyworlds list on mount
  useEffect(() => {
    const stored = localStorage.getItem('splinewright-storyworlds');
    if (stored) {
      try {
        const list = JSON.parse(stored);
        setStoryworlds(list);
        if (list.length > 0 && !currentStoryworld) {
          const loadData = (name: string) => {
            const storyworldStored = localStorage.getItem(`storyworld_${name}`);
            if (storyworldStored) {
              try {
                const data = JSON.parse(storyworldStored);
                setCharacters(data.characters || []);
                setSavedEnzymes(data.enzymes || []);
                setTensionCodons(data.tensionCodons || []);
                setEncounters(data.encounters || []);
                if (data.escalationProfile) {
                  setEscalationProfile(data.escalationProfile);
                }
                setCurrentStoryworld(name);
              } catch (e) {
                console.error('Failed to load storyworld:', e);
              }
            }
          };
          loadData(list[0]);
        }
      } catch (e) {
        console.error('Failed to load storyworlds list:', e);
      }
    }
    // Intentionally run once on mount (initial localStorage hydrate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save current storyworld when data changes (debounced)
  useEffect(() => {
    if (!currentStoryworld) return;
    const timeoutId = setTimeout(() => {
      const storyworldData = {
        name: currentStoryworld,
        characters,
        enzymes: savedEnzymes,
        tensionCodons,
        encounters,
        escalationProfile,
      };
      localStorage.setItem(`storyworld_${currentStoryworld}`, JSON.stringify(storyworldData));
    }, 1000); // Debounce by 1 second
    return () => clearTimeout(timeoutId);
  }, [characters, savedEnzymes, tensionCodons, encounters, escalationProfile, currentStoryworld]);

  const exportData = () => {
    const exportData: ExportData = {
      version: '1.0.0',
      timestamp: new Date().toISOString(),
      enzymes: savedEnzymes,
      characters: characters,
      tensionCodons: tensionCodons,
      escalationProfile: escalationProfile,
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `splinewright-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const data: ExportData = JSON.parse(text);

        // Validate the data structure
        if (!data.version || !data.enzymes || !data.characters || !data.tensionCodons || !data.escalationProfile) {
          alert('Invalid file format. Please select a valid Splinewright export file.');
          return;
        }

        // Confirm before importing (this will overwrite current data)
        if (!window.confirm('Importing will replace all current data. Are you sure you want to continue?')) {
          event.target.value = '';
          return;
        }

        // Import the data
        setSavedEnzymes(data.enzymes || []);
        setCharacters(data.characters || []);
        setTensionCodons(data.tensionCodons || []);
        setEscalationProfile(data.escalationProfile || {
          type: 'slow-burn',
          slowBurnReinforcements: 3,
          hairTriggerRecoveryTime: 1,
          waveBuildDuration: 5,
          waveReleaseIntensity: 0.7,
        });

        // Clear current editor state
        newEnzyme();
        setSelectedCharacterId(null);
        setSelectedEnzymeId(null);

        alert(`Successfully imported data from ${data.timestamp || 'unknown date'}`);
      } catch (error) {
        console.error('Import error:', error);
        alert('Failed to import file. Please make sure it is a valid JSON file.');
      }
      // Reset the file input
      event.target.value = '';
    };
    reader.readAsText(file);
  };

  const generateSuggestions = async () => {
    // Get API key from localStorage
    const apiKey = localStorage.getItem('splinewright-claude-api-key') || localStorage.getItem('claude_api_key');
    if (!apiKey) {
      alert('Please add your Claude API key in Settings');
      setCobotPanelOpen(true);
      setCurrentView('settings');
      return;
    }

    if (!characterName.trim() || !traitName.trim()) {
      alert('Please enter a character name and trait name before generating suggestions.');
      return;
    }

    setCobotPanelOpen(true);
    setCobotLoading(true);
    setCobotSuggestions({ triggers: [], responses: [] });

    // Do not log API keys

    try {
      const enzymeData = {
        character: characterName,
        trait: traitName,
        intensity: intensity.toFixed(2),
        triggers: triggers.length > 0 ? triggers.map(t => t.text) : [],
        responses: responses.length > 0 ? responses.map(r => r.text) : [],
        blindspots: blindspots.length > 0 ? blindspots.map(b => b.description) : [],
      };

      const prompt = `Here is a character enzyme for interactive storytelling. Suggest 3-5 additional triggers and 3-5 additional responses that would create dramatic opportunities. Return as JSON with triggers array and responses array.

Character: ${enzymeData.character}
Trait: ${enzymeData.trait}
Intensity: ${enzymeData.intensity}
Current Triggers: ${enzymeData.triggers.length > 0 ? enzymeData.triggers.join(', ') : 'None'}
Current Responses: ${enzymeData.responses.length > 0 ? enzymeData.responses.join(', ') : 'None'}
${enzymeData.blindspots.length > 0 ? `Blindspots: ${enzymeData.blindspots.join('; ')}` : ''}`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1024,
          messages: [{ role: 'user', content: prompt }]
        })
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('API Error Response:', error);
        throw new Error(error);
      }

      const data = await response.json();
      console.log('Response:', data);
      const content = data.content[0].text;

      // Try to extract JSON from the response
      let suggestions: { triggers: string[]; responses: string[] } = { triggers: [], responses: [] };
      
      // Look for JSON in the response
      const jsonMatch = content.match(/\{[\s\S]*"triggers"[\s\S]*"responses"[\s\S]*\}/);
      if (jsonMatch) {
        try {
          suggestions = JSON.parse(jsonMatch[0]);
        } catch (e) {
          console.error('Failed to parse JSON from Claude response', e);
        }
      }

      // Fallback: try to extract suggestions from text if JSON parsing fails
      if (suggestions.triggers.length === 0 && suggestions.responses.length === 0) {
        const triggerMatches = content.match(/"triggers"\s*:\s*\[([\s\S]*?)\]/);
        const responseMatches = content.match(/"responses"\s*:\s*\[([\s\S]*?)\]/);
        
        if (triggerMatches) {
          const triggerStrings: string[] = triggerMatches[1].split(',');
          suggestions.triggers = triggerStrings
            .map((t: string) => t.trim().replace(/^["']|["']$/g, ''))
            .filter((t: string) => t.length > 0);
        }
        
        if (responseMatches) {
          const responseStrings: string[] = responseMatches[1].split(',');
          suggestions.responses = responseStrings
            .map((r: string) => r.trim().replace(/^["']|["']$/g, ''))
            .filter((r: string) => r.length > 0);
        }
      }

      setCobotSuggestions(suggestions);
    } catch (error: any) {
      console.error('Error generating suggestions:', error);
      alert(`Failed to generate suggestions: ${error.message || 'Unknown error'}`);
      setCobotSuggestions({ triggers: [], responses: [] });
    } finally {
      setCobotLoading(false);
    }
  };

  const addSuggestionTrigger = (triggerText: string) => {
    if (triggerText.trim()) {
      setTriggers([...triggers, { id: Date.now().toString(), text: triggerText.trim() }]);
    }
  };

  const addSuggestionResponse = (responseText: string) => {
    if (responseText.trim()) {
      setResponses([...responses, { id: Date.now().toString(), text: responseText.trim() }]);
    }
  };

  const generateEncounter = async () => {
    // Get API key from localStorage
    const apiKey = localStorage.getItem('splinewright-claude-api-key') || localStorage.getItem('claude_api_key');
    if (!apiKey) {
      alert('Please add your Claude API key in Settings');
      setCurrentView('settings');
      return;
    }

    if (characters.length < 2) {
      alert('You need at least 2 characters to generate an encounter.');
      return;
    }

    setEncounterLoading(true);
    setGeneratedEncounter(null);

    // Do not log API keys

    try {
      const stripCodeFences = (text: string): string => {
        let t = (text || '').toString().trim();
        if (t.startsWith('```json')) t = t.replace(/^```json\s*/i, '');
        else if (t.startsWith('```')) t = t.replace(/^```\s*/i, '');
        if (t.endsWith('```')) t = t.replace(/\s*```$/i, '');
        return t.trim();
      };

      const parseJsonLoose = (text: string): any | null => {
        const t = stripCodeFences(text);
        try {
          return JSON.parse(t);
        } catch {
          const m = t.match(/\{[\s\S]*\}/);
          if (!m) return null;
          try {
            return JSON.parse(m[0]);
          } catch {
            return null;
          }
        }
      };

      const extractEncounterForgeBlocks = (text: string): string[] => {
        const src = (text || '').toString();
        const matches = Array.from(src.matchAll(/<encounter_data>\s*([\s\S]*?)\s*<\/encounter_data>/gi));
        return matches.map((m) => (m?.[1] || '').toString()).filter((s) => s.trim().length > 0);
      };

      // Build character data with their enzymes
      const characterData = characters.map(char => {
        const attachedEnzymeData = char.attachedEnzymes.map(ae => {
          const enzyme = savedEnzymes.find(e => e.id === ae.enzymeId);
          if (!enzyme) return null;
          return {
            trait: enzyme.traitName,
            intensity: ae.intensity,
            triggers: enzyme.triggers.map(t => t.text),
            responses: enzyme.responses.map(r => r.text),
            blindspots: enzyme.blindspots.map(b => b.description),
          };
        }).filter(Boolean);

        return {
          name: char.name,
          description: char.description,
          enzymes: attachedEnzymeData,
        };
      });

      // Build tension codon data
      const tensionData = tensionCodons.map(codon => ({
        name: codon.name,
        description: codon.description,
        pressureLevel: codon.pressureLevel,
        affectedCharacters: characters
          .filter(c => codon.affectedCharacterIds.includes(c.id))
          .map(c => c.name),
      }));

      // Format characters and enzymes for the prompt
      const charactersText = characterData.map((char, i) => {
        const enzymesText = char.enzymes.length > 0
          ? char.enzymes.map((enz: any) => 
              `  - ${enz.trait} (intensity: ${enz.intensity.toFixed(2)})\n    Triggers: ${enz.triggers.join(', ') || 'None'}\n    Responses: ${enz.responses.join(', ') || 'None'}\n    Blindspots: ${enz.blindspots.join('; ') || 'None'}`
            ).join('\n')
          : '  No enzymes attached';
        
        return `${i + 1}. ${char.name}\n   Description: ${char.description || 'No description'}\n   Enzymes:\n${enzymesText}`;
      }).join('\n\n');

      // Format tensions for the prompt
      const tensionsText = tensionData.length > 0
        ? tensionData.map(t => 
            `- ${t.name} (pressure: ${t.pressureLevel.toFixed(2)}): ${t.description}\n  Affects: ${t.affectedCharacters.join(', ')}`
          ).join('\n')
        : 'None';

      const storyworldContextJson = JSON.stringify(buildStoryworldContext());
      const seed = `${Date.now()}-${encounters.length}-${characters.length}`;

      const prompt = `STORYWORLD_CONTEXT_JSON (use this for continuity, pacing, and narrative needs):
${storyworldContextJson}

CHARACTERS (quick reference):
${charactersText}

TENSIONS (quick reference):
${tensionsText}

COMMAND: Generate next encounter

Generation seed: ${seed}

Return exactly ONE <encounter_data>{...}</encounter_data> block. You may include narrative reasoning BEFORE the <encounter_data> tag, but the JSON inside <encounter_data> must be valid.`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 2000,
          system: ENCOUNTER_FORGE_SYSTEM_PROMPT,
          messages: [{ role: 'user', content: prompt }]
        })
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('API Error Response:', error);
        throw new Error(error);
      }

      const data = await response.json();
      console.log('Response:', data);
      const rawText = (data?.content?.[0]?.text || '').toString();
      const blocks = extractEncounterForgeBlocks(rawText);

      let encounterData: any = null;
      if (blocks.length > 0) {
        if (blocks.length > 1) {
          console.log(`Encounter Forge returned ${blocks.length} encounters; using the first.`);
        }
        encounterData = parseJsonLoose(blocks[0]);
      } else {
        // Fallback: accept plain JSON responses if model ignores tags
        encounterData = parseJsonLoose(rawText);
      }

      if (!encounterData || !encounterData.title) {
        console.error('Invalid encounter data structure:', encounterData);
        console.error('Raw response:', rawText);
        throw new Error('Invalid response format from Claude (missing required fields).');
      }

      // Find character IDs from names
      const findCharacterIdByName = (name: string): string | null => {
        const n = (name || '').toString().trim();
        if (!n) return null;
        const exact = characters.find((c) => c.name === n);
        if (exact) return exact.id;
        const lower = n.toLowerCase();
        const ci = characters.find((c) => c.name.toLowerCase() === lower);
        if (ci) return ci.id;
        const partial = characters.find((c) =>
          c.name.toLowerCase().includes(lower) || lower.includes(c.name.toLowerCase())
        );
        return partial ? partial.id : null;
      };

      const characterNames: string[] = Array.isArray(encounterData.charactersPresent)
        ? encounterData.charactersPresent
        : Array.isArray(encounterData.characters)
          ? encounterData.characters
          : Array.isArray(encounterData.characterNames)
            ? encounterData.characterNames
            : [];

      let characterIds = characterNames
        .map((n: any) => findCharacterIdByName((n || '').toString()))
        .filter(Boolean) as string[];

      characterIds = Array.from(new Set(characterIds));

      if (characterIds.length < 2 && characters.length >= 2) {
        console.warn('Could not match charactersPresent to known characters; falling back to first two characters.');
        characterIds = [characters[0].id, characters[1].id];
      }

      // Map choices to playerChoicePoints format
      const rawChoices = Array.isArray(encounterData.playerChoices)
        ? encounterData.playerChoices
        : Array.isArray(encounterData.choices)
          ? encounterData.choices
          : Array.isArray(encounterData.playerChoicePoints)
            ? encounterData.playerChoicePoints
            : [];

      const playerChoicePoints = Array.isArray(rawChoices)
        ? rawChoices.map((choice: any) => {
            if (typeof choice === 'string') return choice;
            const text = (choice?.text || choice?.choice || '').toString();
            const outcome = (choice?.consequences || choice?.outcome || '').toString();
            if (text && outcome) return `${text} → ${outcome}`;
            if (text) return text;
            return JSON.stringify(choice);
          })
        : [];

      const setting = (encounterData.setting || '').toString().trim();
      const narrativeFunction = (encounterData.narrativeFunction || '').toString().trim();
      const triggerText = (encounterData.trigger || encounterData.triggers || '').toString().trim();
      const whatHappensBase = (encounterData.whatHappens || encounterData.description || '').toString().trim();
      const whatHappens = [
        setting ? `Setting: ${setting}` : '',
        narrativeFunction ? `Narrative: ${narrativeFunction}` : '',
        whatHappensBase,
      ].filter(Boolean).join('\n\n');

      const encounter: Encounter = {
        id: Date.now().toString(),
        title: encounterData.title,
        description: whatHappensBase || whatHappens || '',
        characterIds: characterIds,
        triggers: triggerText || narrativeFunction || '',
        whatHappens: whatHappens || '',
        playerChoicePoints: playerChoicePoints,
        createdAt: Date.now(),
      };

      setGeneratedEncounter(encounter);
    } catch (error: any) {
      console.error('Error generating encounter:', error);
      alert(`Failed to generate encounter: ${error.message || 'Unknown error'}`);
    } finally {
      setEncounterLoading(false);
    }
  };

  const saveGeneratedEncounter = () => {
    if (!generatedEncounter) return;
    setEncounters([...encounters, generatedEncounter]);
    setGeneratedEncounter(null);
  };

  const deleteEncounter = (id: string) => {
    if (window.confirm('Are you sure you want to delete this encounter?')) {
      setEncounters(encounters.filter(encounter => encounter.id !== id));
    }
  };

  // --- Encounter Forge (authoring copilot) ---
  const cleanForgeAssistantMessage = (content: string): string => {
    return (content || '')
      .toString()
      .replace(/<encounter_data>[\s\S]*?<\/encounter_data>/gi, '')
      .trim();
  };

  const stripCodeFences = (text: string): string => {
    let t = (text || '').toString().trim();
    if (t.startsWith('```json')) t = t.replace(/^```json\s*/i, '');
    else if (t.startsWith('```')) t = t.replace(/^```\s*/i, '');
    if (t.endsWith('```')) t = t.replace(/\s*```$/i, '');
    return t.trim();
  };

  const parseJsonLoose = (text: string): any | null => {
    const t = stripCodeFences(text);
    try {
      return JSON.parse(t);
    } catch {
      const m = t.match(/\{[\s\S]*\}/);
      if (!m) return null;
      try {
        return JSON.parse(m[0]);
      } catch {
        return null;
      }
    }
  };

  const extractEncounterForgeBlocks = (text: string): string[] => {
    const src = (text || '').toString();
    const matches = Array.from(src.matchAll(/<encounter_data>\s*([\s\S]*?)\s*<\/encounter_data>/gi));
    return matches.map((m) => (m?.[1] || '').toString()).filter((s) => s.trim().length > 0);
  };

  const normalizeForgeEncounter = (raw: any, idx: number, now: number): ForgeEncounterData | null => {
    const clamp01 = (n: any, fallback: number) => {
      const v = typeof n === 'number' ? n : parseFloat(n);
      if (!Number.isFinite(v)) return fallback;
      // Some models might send 0-10 scale; normalize if so
      const normalized = v > 1 ? v / 10 : v;
      return Math.max(0, Math.min(1, normalized));
    };

    const charactersPresent = Array.isArray(raw?.charactersPresent)
      ? raw.charactersPresent.map((c: any) => (c || '').toString()).filter(Boolean)
      : Array.isArray(raw?.characters)
        ? raw.characters.map((c: any) => (c || '').toString()).filter(Boolean)
        : [];

    const playerChoices = Array.isArray(raw?.playerChoices)
      ? raw.playerChoices
      : Array.isArray(raw?.choices)
        ? raw.choices
        : [];

    const normalized: ForgeEncounterData = {
      id: (raw?.id || `forge-${now}-${idx}-${Math.random().toString(36).slice(2, 9)}`).toString(),
      title: (raw?.title || `Forged Encounter ${idx + 1}`).toString(),
      setting: (raw?.setting || '').toString(),
      charactersPresent,
      whatHappens: (raw?.whatHappens || raw?.description || '').toString(),
      enzymesTargeted: Array.isArray(raw?.enzymesTargeted) ? raw.enzymesTargeted : [],
      tensionsAffected: Array.isArray(raw?.tensionsAffected) ? raw.tensionsAffected : [],
      playerChoices: Array.isArray(playerChoices) ? playerChoices : [],
      intensity: clamp01(raw?.intensity, 0.6),
      phase: (raw?.phase || '').toString(),
      narrativeFunction: (raw?.narrativeFunction || '').toString(),
    };

    if (!normalized.title.trim()) return null;
    return normalized;
  };

  const scoreEncounterQuality = (encounter: ForgeEncounterData, context: StoryworldContext): number => {
    let score = 100;

    const encounterCharacterNames = (encounter.charactersPresent || [])
      .map((n) => (n || '').toString().trim())
      .filter(Boolean);

    // Penalize if characters not in storyworld (case-insensitive)
    encounterCharacterNames.forEach((name) => {
      const lower = name.toLowerCase();
      const exists = context.characters.some((c) => c.name.toLowerCase() === lower);
      if (!exists) score -= 20;
    });

    // Penalize if enzyme triggers don't match (simple overlap test)
    (encounter.enzymesTargeted || []).forEach((target) => {
      const enzyme = context.enzymes.find((e) => e.id === target.enzymeId);
      if (!enzyme) return;
      const used = (target.triggerUsed || '').toString().toLowerCase();
      const triggers = (enzyme.triggers || []).map((t) => (t || '').toString().toLowerCase()).filter(Boolean);
      if (!used.trim() || triggers.length === 0) return;

      const match = triggers.some((t) => used.includes(t) || t.includes(used));
      if (!match) score -= 15;
    });

    // Penalize inappropriate intensity for phase
    const phaseIntensity: Record<string, [number, number]> = {
      exposition: [0.2, 0.5],
      rising: [0.4, 0.7],
      crisis: [0.6, 0.85],
      climax: [0.8, 1.0],
      resolution: [0.3, 0.6],
    };
    const phaseKey = (encounter.phase || '').toString().toLowerCase();
    const [min, max] = phaseIntensity[phaseKey] || [0, 1];
    if (encounter.intensity < min || encounter.intensity > max) score -= 10;

    // Bonus for addressing narrative needs (map suggested character IDs -> names when possible)
    context.storyState.narrativeNeeds.forEach((need) => {
      const suggested = need.suggestedCharacters || [];
      const suggestedNames = suggested
        .map((v) => {
          const s = (v || '').toString();
          const byId = context.characters.find((c) => c.id === s);
          if (byId) return byId.name;
          const byName = context.characters.find((c) => c.name.toLowerCase() === s.toLowerCase());
          if (byName) return byName.name;
          return s; // fallback: treat as name
        })
        .map((n) => n.toLowerCase());

      const hit = suggestedNames.some((sn) => encounterCharacterNames.some((en) => en.toLowerCase() === sn));
      if (hit) score += 5;
    });

    return Math.max(0, Math.min(100, score));
  };

  const getQualityIssues = (encounter: ForgeEncounterData, context: StoryworldContext): string => {
    const issues: string[] = [];

    const encounterCharacterNames = (encounter.charactersPresent || [])
      .map((n) => (n || '').toString().trim())
      .filter(Boolean);

    // Unknown characters
    const unknown = encounterCharacterNames.filter((name) => {
      const lower = name.toLowerCase();
      return !context.characters.some((c) => c.name.toLowerCase() === lower);
    });
    if (unknown.length > 0) issues.push(`Unknown characters: ${unknown.join(', ')}`);

    // Trigger mismatches
    (encounter.enzymesTargeted || []).forEach((target) => {
      const enzyme = context.enzymes.find((e) => e.id === target.enzymeId);
      if (!enzyme) return;
      const used = (target.triggerUsed || '').toString().toLowerCase().trim();
      const triggers = (enzyme.triggers || []).map((t) => (t || '').toString().toLowerCase()).filter(Boolean);
      if (!used || triggers.length === 0) return;
      const match = triggers.some((t) => used.includes(t) || t.includes(used));
      if (!match) {
        issues.push(`Trigger mismatch: ${target.characterName || enzyme.characterName} / ${target.traitName || enzyme.traitName}`);
      }
    });

    // Phase vs intensity mismatch
    const phaseIntensity: Record<string, [number, number]> = {
      exposition: [0.2, 0.5],
      rising: [0.4, 0.7],
      crisis: [0.6, 0.85],
      climax: [0.8, 1.0],
      resolution: [0.3, 0.6],
    };
    const phaseKey = (encounter.phase || '').toString().toLowerCase();
    const [min, max] = phaseIntensity[phaseKey] || [0, 1];
    if (encounter.intensity < min || encounter.intensity > max) {
      issues.push(`Intensity ${encounter.intensity.toFixed(2)} out of range for phase "${phaseKey || 'unknown'}" (${min.toFixed(2)}–${max.toFixed(2)})`);
    }

    // Not addressing narrative needs (only if there are suggested characters)
    const hitsNeed = context.storyState.narrativeNeeds.some((need) => {
      const suggested = need.suggestedCharacters || [];
      if (suggested.length === 0) return false;
      const suggestedNames = suggested
        .map((v) => {
          const s = (v || '').toString();
          const byId = context.characters.find((c) => c.id === s);
          if (byId) return byId.name;
          const byName = context.characters.find((c) => c.name.toLowerCase() === s.toLowerCase());
          if (byName) return byName.name;
          return s;
        })
        .map((n) => n.toLowerCase());
      return suggestedNames.some((sn) => encounterCharacterNames.some((en) => en.toLowerCase() === sn));
    });
    if (context.storyState.narrativeNeeds.length > 0 && !hitsNeed) {
      issues.push('Does not clearly address current narrative needs');
    }

    return issues.length > 0 ? issues.join('; ') : 'No obvious issues detected';
  };

  const regenerateLowQualityEncounters = async (
    encountersToCheck: ForgeEncounterData[],
    context: StoryworldContext,
    threshold: number = 70
  ): Promise<ForgeEncounterData[]> => {
    const scored = (encountersToCheck || []).map((e) => ({
      encounter: e,
      score: typeof e.qualityScore === 'number' ? e.qualityScore : scoreEncounterQuality(e, context),
    }));

    const lowQuality = scored
      .filter((x) => x.score < threshold)
      .map((x) => x.encounter);

    if (lowQuality.length === 0) return encountersToCheck;

    const list = lowQuality
      .map((e, idx) => {
        const issues = getQualityIssues(e, context);
        return `${idx + 1}. Title: "${e.title}"\n   Issues: ${issues}\n   CharactersPresent: ${e.charactersPresent.join(', ') || 'N/A'}\n   Phase: ${e.phase || 'N/A'} • Intensity: ${(e.intensity ?? 0).toFixed(2)}\n   CurrentSummary: ${(e.whatHappens || '').slice(0, 220)}`;
      })
      .join('\n\n');

    const prompt =
      `Generate ${lowQuality.length} encounters.\n\n` +
      `Please regenerate these encounters with better quality.\n` +
      `CRITICAL: Keep the "title" field EXACTLY the same for each item (do not rename). Return encounters in the same order as the list.\n\n` +
      list;

    const regenerated = await sendForgeMessage(prompt, { silent: true });
    const replacements = regenerated.slice(0, lowQuality.length).map((r, i) => {
      const forcedTitle = lowQuality[i]?.title || r.title;
      return { ...r, title: forcedTitle };
    });

    // Replace low-quality encounters by title match first, then by order fallback
    const replacementByTitle = new Map<string, ForgeEncounterData>();
    replacements.forEach((r) => replacementByTitle.set(r.title, r));

    let orderIdx = 0;
    return (encountersToCheck || []).map((e) => {
      const score = typeof e.qualityScore === 'number' ? e.qualityScore : scoreEncounterQuality(e, context);
      if (score >= threshold) return e;
      const byTitle = replacementByTitle.get(e.title);
      if (byTitle) return byTitle;
      const byOrder = replacements[orderIdx];
      orderIdx += 1;
      return byOrder || e;
    });
  };

  const parseForgeEncountersFromText = (rawText: string): ForgeEncounterData[] => {
    const blocks = extractEncounterForgeBlocks(rawText);
    const now = Date.now();
    const parsed: ForgeEncounterData[] = [];
    const context = buildStoryworldContext();

    if (blocks.length > 0) {
      blocks.forEach((block, idx) => {
        const raw = parseJsonLoose(block);
        const normalized = normalizeForgeEncounter(raw, idx, now);
        if (normalized) {
          const qualityScore = scoreEncounterQuality(normalized, context);
          parsed.push({ ...normalized, qualityScore });
        }
      });
      return parsed;
    }

    // Fallback: try parsing the entire response as one JSON object
    const raw = parseJsonLoose(rawText);
    const normalized = normalizeForgeEncounter(raw, 0, now);
    if (!normalized) return [];
    const qualityScore = scoreEncounterQuality(normalized, context);
    return [{ ...normalized, qualityScore }];
  };

  const addEncountersToStoryworld = (items: PendingForgeEncounter[]) => {
    const approved = (items || []).filter((e) => e.approved);
    if (approved.length === 0) return;

    const findCharacterIdByName = (name: string): string | null => {
      const n = (name || '').toString().trim();
      if (!n) return null;
      const exact = characters.find((c) => c.name === n);
      if (exact) return exact.id;
      const lower = n.toLowerCase();
      const ci = characters.find((c) => c.name.toLowerCase() === lower);
      if (ci) return ci.id;
      const partial = characters.find((c) =>
        c.name.toLowerCase().includes(lower) || lower.includes(c.name.toLowerCase())
      );
      return partial ? partial.id : null;
    };

    const now = Date.now();
    const newEncounters: Encounter[] = approved.map((f, idx) => {
      const characterIds = (f.charactersPresent || [])
        .map((n) => findCharacterIdByName(n))
        .filter(Boolean) as string[];

      const uniqueCharacterIds = Array.from(new Set(characterIds));
      const safeCharacterIds =
        uniqueCharacterIds.length > 0 ? uniqueCharacterIds : characters.slice(0, 2).map((c) => c.id);

      const playerChoicePoints = (f.playerChoices || []).map((c) => {
        const text = (c?.text || '').toString().trim();
        const cons = (c?.consequences || '').toString().trim();
        if (text && cons) return `${text} → ${cons}`;
        return text || cons || JSON.stringify(c);
      });

      const whatHappens = [
        f.setting ? `Setting: ${f.setting}` : '',
        f.narrativeFunction ? `Narrative: ${f.narrativeFunction}` : '',
        f.whatHappens || '',
      ].filter(Boolean).join('\n\n');

      const triggers = f.narrativeFunction || f.setting || (f.enzymesTargeted?.[0]?.triggerUsed || '') || '';

      return {
        id: `enc-${now}-${idx}-${Math.random().toString(36).slice(2, 9)}`,
        title: f.title,
        description: f.whatHappens || whatHappens || '',
        characterIds: safeCharacterIds,
        triggers,
        whatHappens,
        playerChoicePoints,
        createdAt: now + idx,
      };
    });

    setEncounters((prev) => [...prev, ...newEncounters]);
    setPendingEncounters([]);
    alert(`✓ Added ${newEncounters.length} encounter${newEncounters.length === 1 ? '' : 's'} to your storyworld.`);
  };

  const sendForgeMessage = async (
    userMessage: string,
    options?: { silent?: boolean }
  ): Promise<ForgeEncounterData[]> => {
    if (!userMessage.trim() || isForgeGenerating) return [];
    const silent = options?.silent ?? false;

    if (!silent) {
      const userMsg: ForgeMessage = {
        id: `forge-${Date.now()}-user`,
        role: 'user',
        content: userMessage,
        timestamp: new Date(),
      };
      setForgeMessages((prev) => [...prev, userMsg]);
      setForgeInput('');
    }

    setIsForgeGenerating(true);

    const apiKey = localStorage.getItem('splinewright-claude-api-key') || localStorage.getItem('claude_api_key');
    if (!apiKey) {
      if (!silent) {
        setForgeMessages((prev) => [
          ...prev,
          {
            id: `forge-${Date.now()}-error`,
            role: 'assistant',
            content: 'Please add your Claude API key in Settings before using Encounter Forge.',
            timestamp: new Date(),
          },
        ]);
      }
      setIsForgeGenerating(false);
      setCurrentView('settings');
      return [];
    }

    const parseDesiredCount = (text: string): number => {
      const t = (text || '').toLowerCase();
      const m = t.match(/generate\s+(\d+)\s+encounters?/);
      if (m?.[1]) return Math.max(1, Math.min(100, parseInt(m[1], 10)));
      const n = t.match(/\b(\d+)\s*-\s*encounter\b|\b(\d+)\s*encounters?\b/);
      const guess = n?.[1] || n?.[2];
      if (guess) return Math.max(1, Math.min(100, parseInt(guess, 10)));
      if (t.includes('full arc') && t.includes('50')) return 50;
      return 1;
    };

    const requestedTotal = parseDesiredCount(userMessage);
    const total = requestedTotal;
    if (total > 1) {
      setGenerationProgress({ total, completed: 0, currentEncounter: 'Starting…' });
    } else {
      setGenerationProgress(null);
    }

    let completed = 0;
    let alreadyForged: ForgeEncounterData[] = [];
    let result: ForgeEncounterData[] = [];

    try {
      while (completed < total) {
        const chunkSize = total > 1 ? Math.min(5, total - completed) : 1;
        if (total > 1) {
          setGenerationProgress({
            total,
            completed,
            currentEncounter: `Forging ${completed + 1}-${completed + chunkSize}…`,
          });
        }

        const contextJson = JSON.stringify(buildStoryworldContext());
        const recentForgedJson = JSON.stringify(alreadyForged.slice(-10));
        const templateLibraryJson = JSON.stringify(encounterTemplates);
        const seed = `${Date.now()}-${currentStoryworld}-${encounters.length}-${completed}`;

        const prompt = `STORYWORLD_CONTEXT_JSON:
${contextJson}

ENCOUNTER_TEMPLATE_LIBRARY_JSON:
${templateLibraryJson}

RECENTLY_FORGED_ENCOUNTERS_JSON (may be empty; use for continuity):
${recentForgedJson}

USER_REQUEST:
${userMessage}

COMMAND:
${total > 1 ? `Generate ${chunkSize} encounters` : 'Generate next encounter'}

Generation seed: ${seed}

Return exactly ${chunkSize} <encounter_data>{...}</encounter_data> block(s). You may include narrative reasoning before the first <encounter_data> tag.`;

        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 4096,
            system: ENCOUNTER_FORGE_SYSTEM_PROMPT,
            messages: [{ role: 'user', content: prompt }],
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Encounter Forge API Error:', response.status, errorText);
          throw new Error(`API Error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        const rawText = (data?.content?.[0]?.text || '').toString();
        const assistantText = cleanForgeAssistantMessage(rawText) || 'Forged encounters.';
        const generated = parseForgeEncountersFromText(rawText);

        if (!silent) {
          setForgeMessages((prev) => [
            ...prev,
            {
              id: `forge-${Date.now()}-assistant`,
              role: 'assistant',
              content: assistantText,
              timestamp: new Date(),
              generatedEncounters: generated.length > 0 ? generated : undefined,
            },
          ]);
        }

        if (generated.length === 0) {
          // Avoid infinite loops if the model didn't return valid encounters
          break;
        }

        alreadyForged = [...alreadyForged, ...generated];
        completed += generated.length;

        if (total > 1) {
          setGenerationProgress({
            total,
            completed: Math.min(completed, total),
            currentEncounter: generated[generated.length - 1]?.title || 'Forged…',
          });
        } else {
          break;
        }
      }
      result = alreadyForged;
    } catch (error: any) {
      console.error('Encounter Forge error:', error);
      if (!silent) {
        setForgeMessages((prev) => [
          ...prev,
          {
            id: `forge-${Date.now()}-error`,
            role: 'assistant',
            content: `Sorry — I couldn’t forge encounters. ${error?.message || 'Unknown error.'}`,
            timestamp: new Date(),
          },
        ]);
      }
      result = alreadyForged;
    } finally {
      setIsForgeGenerating(false);
      setGenerationProgress(null);
    }
    return result;
  };

  const renderEncounterForge = () => {
    if (!showEncounterForge) return null;

    const context = buildStoryworldContext();

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: isMobile ? '100%' : '500px',
        height: '100vh',
        backgroundColor: '#0d1117',
        borderLeft: '1px solid #30363d',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1500,
        boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #30363d',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#161b22',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>🔨</span>
            <div>
              <h3 style={{ margin: 0, color: '#f0f6fc', fontSize: '16px' }}>
                Encounter Forge
              </h3>
              <p style={{ margin: 0, color: '#8b949e', fontSize: '12px' }}>
                {context.storyworld.title || 'No storyworld selected'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowEncounterForge(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              fontSize: '20px',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        {/* Context Summary */}
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#161b22',
          borderBottom: '1px solid #30363d',
          fontSize: '12px',
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '8px',
            marginBottom: '12px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#58a6ff', fontWeight: '600' }}>
                {context.characters.length}
              </div>
              <div style={{ color: '#8b949e' }}>Characters</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#3fb950', fontWeight: '600' }}>
                {context.enzymes.length}
              </div>
              <div style={{ color: '#8b949e' }}>Enzymes</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#d29922', fontWeight: '600' }}>
                {context.tensions.length}
              </div>
              <div style={{ color: '#8b949e' }}>Tensions</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#a371f7', fontWeight: '600' }}>
                {context.encounters.length}
              </div>
              <div style={{ color: '#8b949e' }}>Encounters</div>
            </div>
          </div>

          {/* Enzyme pressure bars */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ color: '#8b949e', marginBottom: '6px' }}>Enzyme Pressure:</div>
            {context.enzymes.slice(0, 3).map(enz => (
              <div key={enz.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px',
              }}>
                <span style={{ 
                  color: '#f0f6fc', 
                  fontSize: '11px',
                  width: '100px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {enz.characterName}
                </span>
                <div style={{
                  flex: 1,
                  height: '6px',
                  backgroundColor: '#21262d',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${enz.currentPressure * 100}%`,
                    height: '100%',
                    backgroundColor: enz.currentPressure > 0.8 ? '#f85149' : 
                                     enz.currentPressure > 0.5 ? '#d29922' : '#3fb950',
                    borderRadius: '3px',
                    transition: 'width 0.3s',
                  }} />
                </div>
                <span style={{ 
                  color: '#8b949e', 
                  fontSize: '10px',
                  width: '35px',
                }}>
                  {(enz.currentPressure * 100).toFixed(0)}%
                </span>
              </div>
            ))}
          </div>

          {/* Narrative needs */}
          {context.storyState.narrativeNeeds.length > 0 && (
            <div style={{
              padding: '8px',
              backgroundColor: '#21262d',
              borderRadius: '6px',
              fontSize: '11px',
            }}>
              <div style={{ color: '#d29922', marginBottom: '4px' }}>
                📋 Story needs:
              </div>
              {context.storyState.narrativeNeeds.slice(0, 2).map((need, i) => (
                <div key={i} style={{ color: '#8b949e' }}>
                  • {need.description}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Templates */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #30363d',
          backgroundColor: '#0d1117',
        }}>
          <div style={{ color: '#8b949e', fontSize: '12px', marginBottom: '8px' }}>Templates</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(encounterTemplates).map(([key, tmpl]) => (
              <button
                key={key}
                onClick={() => {
                  const [minI, maxI] = tmpl.intensityRange;
                  const lines = [
                    `Use encounter template: ${key} (${tmpl.name}).`,
                    `Goal: ${tmpl.description}`,
                    tmpl.requiredCharacters ? `Required characters: ${tmpl.requiredCharacters}.` : '',
                    tmpl.requiredTensions && tmpl.requiredTensions.length > 0 ? `Required tension types: ${tmpl.requiredTensions.join(', ')}.` : '',
                    typeof tmpl.requiredPressure === 'number' ? `Require at least one enzyme pressure >= ${tmpl.requiredPressure}.` : '',
                    `Intensity range: ${minI}-${maxI}.`,
                    typeof tmpl.enzymeActivations === 'number' ? `Target enzyme activations: ${tmpl.enzymeActivations}.` : '',
                    'Generate next encounter.',
                  ].filter(Boolean).join('\n');
                  sendForgeMessage(lines);
                }}
                disabled={isForgeGenerating}
                style={{
                  padding: '8px 10px',
                  backgroundColor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '8px',
                  color: '#f0f6fc',
                  fontSize: '12px',
                  cursor: isForgeGenerating ? 'not-allowed' : 'pointer',
                  opacity: isForgeGenerating ? 0.6 : 1,
                }}
                title={tmpl.description}
              >
                {tmpl.name}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #30363d',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          {[
            { label: '✨ Next Encounter', action: 'Generate the next encounter' },
            { label: '📦 Generate 10', action: 'Generate 10 encounters' },
            { label: '🌊 Full Arc (50)', action: 'Generate a complete 50-encounter arc' },
            { label: '😮‍💨 Breathing Room', action: 'Generate a low-intensity character moment' },
            { label: '💥 Climax', action: 'Generate a climactic encounter sequence' },
          ].map(btn => (
            <button
              key={btn.label}
              onClick={() => sendForgeMessage(btn.action)}
              disabled={isForgeGenerating}
              style={{
                padding: '6px 12px',
                backgroundColor: '#21262d',
                border: '1px solid #30363d',
                borderRadius: '6px',
                color: '#f0f6fc',
                fontSize: '12px',
                cursor: isForgeGenerating ? 'not-allowed' : 'pointer',
                opacity: isForgeGenerating ? 0.5 : 1,
              }}
            >
              {btn.label}
            </button>
          ))}
        </div>

        {/* Templates */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#0d1117',
          }}
        >
          <div style={{ color: '#8b949e', fontSize: '12px', marginBottom: '8px' }}>
            Templates
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(encounterTemplates).map(([key, tpl]) => {
              const label = tpl.name;
              const intensity = `${Math.round(tpl.intensityRange[0] * 10)}/10–${Math.round(tpl.intensityRange[1] * 10)}/10`;
              const templateInstruction = [
                `Use encounter template: ${tpl.name}`,
                `Template key: ${key}`,
                `Template description: ${tpl.description}`,
                `Constraints:`,
                `- intensityRange: [${tpl.intensityRange[0]}, ${tpl.intensityRange[1]}]`,
                tpl.requiredCharacters ? `- requiredCharacters: ${tpl.requiredCharacters}` : '',
                tpl.requiredTensions && tpl.requiredTensions.length > 0 ? `- requiredTensions: ${tpl.requiredTensions.join(', ')}` : '',
                typeof tpl.requiredPressure === 'number' ? `- requiredPressure: >= ${tpl.requiredPressure}` : '',
                typeof tpl.enzymeActivations === 'number' ? `- enzymeActivations: ${tpl.enzymeActivations}` : '',
                ``,
                `Generate the next encounter that fits this template. If any constraint can't be met exactly, adapt while preserving the narrative intent.`,
              ]
                .filter(Boolean)
                .join('\n');

              return (
                <button
                  key={key}
                  onClick={() => sendForgeMessage(templateInstruction)}
                  disabled={isForgeGenerating}
                  title={`${tpl.description}\nIntensity: ${intensity}`}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#161b22',
                    border: '1px solid #30363d',
                    borderRadius: '999px',
                    color: '#f0f6fc',
                    fontSize: '12px',
                    cursor: isForgeGenerating ? 'not-allowed' : 'pointer',
                    opacity: isForgeGenerating ? 0.5 : 1,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {forgeMessages.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              color: '#8b949e',
              padding: '40px 20px',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔨</div>
              <p>I'm ready to forge encounters for your storyworld.</p>
              <p style={{ fontSize: '13px' }}>
                Tell me what you need, or use the quick actions above.
              </p>
            </div>
          )}

          {forgeMessages.map(msg => (
            <div key={msg.id}>
              <div style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '90%',
                  padding: '10px 14px',
                  borderRadius: '12px',
                  backgroundColor: msg.role === 'user' ? '#238636' : '#21262d',
                  color: '#f0f6fc',
                  fontSize: '14px',
                }}>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{msg.content}</p>
                </div>
              </div>

              {/* Generated encounters preview */}
              {msg.generatedEncounters && msg.generatedEncounters.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#161b22',
                  borderRadius: '8px',
                  border: '1px solid #30363d',
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}>
                    <span style={{ color: '#58a6ff', fontWeight: '600' }}>
                      📝 {msg.generatedEncounters.length} Encounters Generated
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setPendingEncounters(msg.generatedEncounters!.map(e => ({
                            ...e,
                            approved: true,
                          })));
                        }}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#238636',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        Approve All
                      </button>

                      <button
                        onClick={async () => {
                          const updated = await regenerateLowQualityEncounters(
                            msg.generatedEncounters || [],
                            context,
                            70
                          );
                          setForgeMessages((prev) =>
                            prev.map((m) =>
                              m.id === msg.id ? { ...m, generatedEncounters: updated } : m
                            )
                          );
                        }}
                        disabled={isForgeGenerating}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: isForgeGenerating ? '#21262d' : '#6e40c9',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '12px',
                          cursor: isForgeGenerating ? 'not-allowed' : 'pointer',
                          opacity: isForgeGenerating ? 0.7 : 1,
                        }}
                        title="Regenerate encounters with quality < 70"
                      >
                        ♻︎ Fix &lt; 70
                      </button>
                    </div>
                  </div>

                  {msg.generatedEncounters.slice(0, 5).map((enc, i) => (
                    <div
                      key={enc.id}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#0d1117',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ color: '#f0f6fc', fontSize: '13px' }}>
                          {i + 1}. {enc.title}
                        </div>
                        <div style={{ color: '#8b949e', fontSize: '11px' }}>
                          {enc.charactersPresent.join(', ')} • Intensity: {(enc.intensity * 10).toFixed(0)}/10
                          {typeof enc.qualityScore === 'number' && (
                            <>
                              {' '}•{' '}
                              <span
                                style={{
                                  color:
                                    enc.qualityScore >= 85
                                      ? '#3fb950'
                                      : enc.qualityScore >= 70
                                        ? '#d29922'
                                        : '#f85149',
                                  fontWeight: 700,
                                }}
                              >
                                Quality: {enc.qualityScore}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => {/* Show preview modal */}}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: 'transparent',
                          border: '1px solid #30363d',
                          borderRadius: '4px',
                          color: '#8b949e',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                      >
                        Preview
                      </button>
                    </div>
                  ))}

                  {msg.generatedEncounters.length > 5 && (
                    <div style={{ 
                      color: '#8b949e', 
                      fontSize: '12px',
                      textAlign: 'center',
                      marginTop: '8px',
                    }}>
                      +{msg.generatedEncounters.length - 5} more encounters
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Generation progress */}
          {generationProgress && (
            <div style={{
              padding: '16px',
              backgroundColor: '#21262d',
              borderRadius: '8px',
              textAlign: 'center',
            }}>
              <div style={{ color: '#58a6ff', marginBottom: '8px' }}>
                {generationProgress.currentEncounter}
              </div>
              <div style={{
                height: '8px',
                backgroundColor: '#0d1117',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '8px',
              }}>
                <div style={{
                  width: `${(generationProgress.completed / generationProgress.total) * 100}%`,
                  height: '100%',
                  backgroundColor: '#238636',
                  transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ color: '#8b949e', fontSize: '12px' }}>
                {generationProgress.completed} / {generationProgress.total} encounters
              </div>
            </div>
          )}

          {isForgeGenerating && !generationProgress && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
            }}>
              <div style={{
                padding: '10px 14px',
                backgroundColor: '#21262d',
                borderRadius: '12px',
                color: '#8b949e',
              }}>
                Forging encounters...
              </div>
            </div>
          )}
        </div>

        {/* Pending encounters panel */}
        {pendingEncounters.length > 0 && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#161b22',
            borderTop: '1px solid #30363d',
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}>
              <span style={{ color: '#f0f6fc', fontSize: '14px' }}>
                {pendingEncounters.filter(e => e.approved).length} / {pendingEncounters.length} approved
              </span>
              <button
                onClick={() => addEncountersToStoryworld(pendingEncounters)}
                disabled={!pendingEncounters.some(e => e.approved)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: pendingEncounters.some(e => e.approved) ? '#238636' : '#21262d',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: pendingEncounters.some(e => e.approved) ? 'pointer' : 'not-allowed',
                }}
              >
                Add to Storyworld
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #30363d',
          backgroundColor: '#161b22',
        }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (forgeInput.trim() && !isForgeGenerating) {
                sendForgeMessage(forgeInput.trim());
              }
            }}
            style={{ display: 'flex', gap: '8px' }}
          >
            <input
              type="text"
              value={forgeInput}
              onChange={(e) => setForgeInput(e.target.value)}
              placeholder="Describe what encounters you need..."
              disabled={isForgeGenerating}
              style={{
                flex: 1,
                padding: '10px 14px',
                backgroundColor: '#0d1117',
                border: '1px solid #30363d',
                borderRadius: '8px',
                color: '#f0f6fc',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={!forgeInput.trim() || isForgeGenerating}
              style={{
                padding: '10px 20px',
                backgroundColor: forgeInput.trim() && !isForgeGenerating ? '#238636' : '#21262d',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontWeight: '600',
                cursor: forgeInput.trim() && !isForgeGenerating ? 'pointer' : 'not-allowed',
              }}
            >
              Forge
            </button>
          </form>
        </div>
      </div>
    );
  };

  const startNewPlaythrough = () => {
    // Clear any ending state
    setGameEnding(null);
    setQuickPlayMode(false);
    // Show character selection screen
    setShowCharacterSelection(true);
    // Pre-select all characters
    setSelectedCharacterIds(new Set(characters.map(c => c.id)));
  };

  const startQuickPlay = async () => {
    if (characters.length === 0) {
      alert('You need at least one character to start Quick Play.');
      return;
    }

    if (!localStorage.getItem('splinewright-claude-api-key')) {
      alert('You need a Claude API key to start Quick Play. Add it in Settings.');
      return;
    }

    // Clear any ending state
    setGameEnding(null);
    setQuickPlayMode(true);

    // Auto-select all characters
    const allCharacterIds = new Set(characters.map(c => c.id));
    const selectedChars = characters.filter(c => allCharacterIds.has(c.id));
    const copiedCharacters = selectedChars.map(char => ({
      ...char,
      attachedEnzymes: [...char.attachedEnzymes],
    }));
    
    // Get all enzymes used by selected characters
    const usedEnzymeIds = new Set(
      copiedCharacters.flatMap(c => c.attachedEnzymes.map(ae => ae.enzymeId))
    );
    const copiedEnzymes = savedEnzymes
      .filter(enz => usedEnzymeIds.has(enz.id))
      .map(enz => ({ ...enz }));
    
    // Copy tension codons
    const copiedTensionCodons = tensionCodons.map(codon => ({
      ...codon,
      pressureLevel: codon.pressureLevel,
    }));

    // Initialize character play states with 30% pressure
    const initialCharacterStates: CharacterPlayState[] = copiedCharacters.map(char => {
      const enzymePressures: Record<string, number> = {};
      const blindspotProgress: Record<string, number> = {};
      const mutationStatus: Record<string, 'normal' | 'hardening' | 'collapsed' | 'transcended'> = {};

      char.attachedEnzymes.forEach(ae => {
        const enzyme = copiedEnzymes.find(e => e.id === ae.enzymeId);
        if (enzyme) {
          // Set initial pressure to 30%
          enzymePressures[enzyme.id] = 0.3;
          mutationStatus[enzyme.id] = 'normal';
          // Initialize blindspot progress to 0
          enzyme.blindspots.forEach((blindspot, idx) => {
            blindspotProgress[`${enzyme.id}-${idx}`] = 0;
          });
        }
      });

      return {
        characterId: char.id,
        enzymePressures,
        delayedResponses: [],
        blindspotProgress,
        mutationStatus,
      };
    });

    // Initialize tension codon play states - randomly activate 2-3 at 50% pressure
    const numActiveCodons = Math.min(3, Math.max(2, copiedTensionCodons.length));
    const shuffledCodons = [...copiedTensionCodons].sort(() => Math.random() - 0.5);
    const activeCodons = shuffledCodons.slice(0, numActiveCodons);
    
    const initialTensionCodonStates: TensionCodonPlayState[] = copiedTensionCodons.map(codon => {
      const isActive = activeCodons.some(ac => ac.id === codon.id);
      return {
        codonId: codon.id,
        currentPressureLevel: isActive ? 0.5 : 0,
        resolved: false,
      };
    });

    setPlaythroughCharacters(copiedCharacters);
    setPlaythroughEnzymes(copiedEnzymes);
    setPlaythroughTensionCodons(copiedTensionCodons);
    setCharacterStates(initialCharacterStates);
    setTensionCodonStates(initialTensionCodonStates);
    setCurrentEncounterIndex(0);
    setSelectedChoice(null);
    setChoiceOutcome(null);
    setStoryHistory([]);
    setPlaythroughStartDate(Date.now());
    setTotalChoicesMade(0);
    setShowCharacterSelection(false);
    setPlaythroughActive(true);

    // Generate and show first encounter immediately after state is set
    // Use setTimeout to ensure state is updated first
    setTimeout(async () => {
      const firstEncounter = await generateNextEncounter();
      if (firstEncounter) {
        setEncounters([firstEncounter]);
        setCurrentEncounterIndex(0);
      } else if (encounters.length > 0) {
        // Use first saved encounter if generation fails
        setCurrentEncounterIndex(0);
      }
    }, 100);
  };

  const continuePlaythrough = () => {
    // Load playthrough state from localStorage
    const stored = localStorage.getItem('splinewright-playthrough');
    if (stored) {
      try {
        const playthroughData: PlaythroughState = JSON.parse(stored);
        if (playthroughData.active) {
          setPlaythroughCharacters(playthroughData.characters || []);
          setPlaythroughEnzymes(playthroughData.enzymes || []);
          setPlaythroughTensionCodons(playthroughData.tensionCodons || []);
          setCharacterStates(playthroughData.characterStates || []);
          setTensionCodonStates(playthroughData.tensionCodonStates || []);
          setCurrentEncounterIndex(playthroughData.currentEncounterIndex || 0);
          setSelectedChoice(playthroughData.selectedChoice || null);
          setChoiceOutcome(playthroughData.choiceOutcome || null);
          setStoryHistory(playthroughData.storyHistory || []);
          setPlaythroughStartDate(playthroughData.startDate || Date.now());
          setTotalChoicesMade(playthroughData.totalChoicesMade || 0);
          setMutationEvent(playthroughData.mutationEvent || null);
          setGameEnding(null); // Don't restore ending state - check fresh
          setPlaythroughActive(true);
          setShowCharacterSelection(false);
          
          // Check for ending conditions after loading
          setTimeout(() => {
            const ending = checkEndingConditions();
            if (ending) {
              setGameEnding(ending);
            }
          }, 100);
        }
      } catch (e) {
        console.error('Failed to load playthrough from localStorage', e);
        alert('Failed to load saved playthrough. Starting a new one instead.');
        startNewPlaythrough();
      }
    }
  };

  const beginPlaythroughWithSelectedCharacters = () => {
    if (selectedCharacterIds.size === 0) {
      alert('Please select at least one character to begin.');
      return;
    }

    // Copy selected characters and their enzymes into play state
    const selectedChars = characters.filter(c => selectedCharacterIds.has(c.id));
    const copiedCharacters = selectedChars.map(char => ({
      ...char,
      attachedEnzymes: [...char.attachedEnzymes],
    }));
    
    // Get all enzymes used by selected characters
    const usedEnzymeIds = new Set(
      copiedCharacters.flatMap(c => c.attachedEnzymes.map(ae => ae.enzymeId))
    );
    const copiedEnzymes = savedEnzymes
      .filter(enz => usedEnzymeIds.has(enz.id))
      .map(enz => ({ ...enz }));
    
    // Copy tension codons with starting pressure levels
    const copiedTensionCodons = tensionCodons.map(codon => ({
      ...codon,
      pressureLevel: codon.pressureLevel,
    }));

    // Initialize character play states
    const initialCharacterStates: CharacterPlayState[] = copiedCharacters.map(char => {
      const enzymePressures: Record<string, number> = {};
      const blindspotProgress: Record<string, number> = {};
      const mutationStatus: Record<string, 'normal' | 'hardening' | 'collapsed' | 'transcended'> = {};

      char.attachedEnzymes.forEach(ae => {
        const enzyme = copiedEnzymes.find(e => e.id === ae.enzymeId);
        if (enzyme) {
          // Initialize enzyme pressure to 0
          enzymePressures[enzyme.id] = 0;
          // Initialize mutation status to normal
          mutationStatus[enzyme.id] = 'normal';
          // Initialize blindspot progress to 0 for each blindspot
          enzyme.blindspots.forEach((blindspot, idx) => {
            blindspotProgress[`${enzyme.id}-${idx}`] = 0;
          });
        }
      });

      return {
        characterId: char.id,
        enzymePressures,
        delayedResponses: [],
        blindspotProgress,
        mutationStatus,
      };
    });

    // Initialize tension codon play states
    const initialTensionCodonStates: TensionCodonPlayState[] = copiedTensionCodons.map(codon => ({
      codonId: codon.id,
      currentPressureLevel: codon.pressureLevel,
      resolved: false,
    }));

    setPlaythroughCharacters(copiedCharacters);
    setPlaythroughEnzymes(copiedEnzymes);
    setPlaythroughTensionCodons(copiedTensionCodons);
    setCharacterStates(initialCharacterStates);
    setTensionCodonStates(initialTensionCodonStates);
    setCurrentEncounterIndex(0);
    setSelectedChoice(null);
    setChoiceOutcome(null);
    setStoryHistory([]);
    setPlaythroughStartDate(Date.now());
    setTotalChoicesMade(0);
    setShowCharacterSelection(false);
    setPlaythroughActive(true);
    setQuickPlayMode(false);

    // Generate first encounter immediately after state is set
    setTimeout(async () => {
      const firstEncounter = await generateNextEncounter();
      if (firstEncounter) {
        setEncounters([firstEncounter]);
        setCurrentEncounterIndex(0);
      }
    }, 100);
  };

  const endPlaythrough = () => {
    if (window.confirm('Return to menu? Your progress will be saved.')) {
      setPlaythroughActive(false);
      setCurrentEncounterIndex(0);
      setSelectedChoice(null);
      setChoiceOutcome(null);
      setShowCharacterSelection(false);
      // Don't clear story history or states - they're saved in localStorage
      // User can continue later with "Continue Playthrough"
    }
  };

  const handleChoiceClick = async (choiceText: string) => {
    try {
      console.log('Choice selected:', choiceText);
      setSelectedChoice(choiceText);

      // Parse choice to extract outcome (format: "choice text → outcome")
      const parts = choiceText.split('→').map(s => s.trim());
      const choice = parts[0];
      const outcome = parts.length > 1 ? parts[1] : 'The choice is made.';
      console.log('Selected choice parsed:', { choice, outcome });

      setChoiceOutcome(outcome);

      // Get current encounter
      const currentEncounter = encounters[currentEncounterIndex];
      if (!currentEncounter) {
        console.warn('handleChoiceClick: no current encounter at index', currentEncounterIndex, 'encounters.length', encounters.length);
        return;
      }

    // Update emotions based on choice's emotionChanges
    if (currentEncounter.choiceEmotionChanges && currentEncounter.choiceEmotionChanges.length > 0) {
      const choiceIndex = currentEncounter.playerChoicePoints.findIndex(cp => cp.startsWith(choice));
      if (choiceIndex >= 0 && currentEncounter.choiceEmotionChanges[choiceIndex]) {
        const emotionChanges = currentEncounter.choiceEmotionChanges[choiceIndex];
        const updatedEmotions = { ...currentEmotions };
        
        // Map character names to IDs and update emotions
        Object.entries(emotionChanges).forEach(([characterName, newEmotion]) => {
          const character = playthroughCharacters.find(c => c.name === characterName);
          if (character) {
            updatedEmotions[character.id] = newEmotion as string;
          }
        });
        
        setCurrentEmotions(updatedEmotions);
        console.log('Updated emotions after choice:', updatedEmotions);
      }
    }

    // Build context text for trigger matching
    const contextText = [
      currentEncounter.title,
      currentEncounter.description || currentEncounter.whatHappens,
      currentEncounter.triggers,
      choice,
      outcome,
    ].filter(Boolean).join(' ').toLowerCase();

    // Track consequences and enzyme activity
    const consequences: string[] = [];
    const firedEnzymes: Array<{ characterName: string; enzymeName: string; enzymeId: string }> = [];

    // Update character states based on choice
    const updatedCharacterStates = characterStates.map(charState => {
      const character = playthroughCharacters.find(c => c.id === charState.characterId);
      if (!character || !currentEncounter.characterIds.includes(character.id)) {
        return charState;
      }

      const updatedState = { ...charState };
      const updatedPressures = { ...charState.enzymePressures };
      const updatedDelayedResponses = [...charState.delayedResponses];
      const updatedBlindspotProgress = { ...charState.blindspotProgress };
      const updatedMutationStatus = { ...charState.mutationStatus };

      // Process enzyme effects: Check if triggers match this situation
      character.attachedEnzymes.forEach(ae => {
        const enzyme = playthroughEnzymes.find(e => e.id === ae.enzymeId);
        if (!enzyme) return;

        // Check if any triggers match the context
        let triggerMatched = false;
        for (const trigger of enzyme.triggers) {
          const triggerText = trigger.text.toLowerCase();
          // Check if trigger text appears in context (simple keyword matching)
          if (contextText.includes(triggerText) || 
              triggerText.split(/\s+/).some(word => word.length > 3 && contextText.includes(word))) {
            triggerMatched = true;
            break;
          }
        }

        // If trigger matched, increase pressure (0.2 in quick play, 0.1 in normal)
        if (triggerMatched) {
          const currentPressure = updatedPressures[ae.enzymeId] || 0;
          const pressureIncrease = quickPlayMode ? 0.2 : 0.1;
          updatedPressures[ae.enzymeId] = Math.min(1.0, currentPressure + pressureIncrease);
          firedEnzymes.push({
            characterName: character.name,
            enzymeName: enzyme.traitName,
            enzymeId: enzyme.id,
          });
          consequences.push(`${character.name}'s ${enzyme.traitName} triggered (+10% pressure)`);
        }
      });

      // Process delayed responses: Check if any should activate
      updatedDelayedResponses.forEach((dr, idx) => {
        if (dr.resolved) return;
        
        // Check if delayed response should activate (e.g., after 2+ encounters, or based on context)
        const encountersSinceTrigger = storyHistory.filter(entry => entry.timestamp > dr.triggeredAt).length;
        const timeSinceTrigger = Date.now() - dr.triggeredAt;
        const hoursSinceTrigger = timeSinceTrigger / (1000 * 60 * 60);
        
        // Activate if: 2+ encounters have passed, or 1+ hour has passed, or context matches
        const shouldActivate = encountersSinceTrigger >= 2 || 
                              hoursSinceTrigger >= 1 ||
                              contextText.includes(dr.responseText.toLowerCase().substring(0, 20));
        
        if (shouldActivate) {
          updatedDelayedResponses[idx] = { ...dr, resolved: true };
          consequences.push(`${character.name}'s delayed response activates: "${dr.responseText}"`);
        }
      });

      // Check for new delayed responses (30% chance if enzyme pressure is building)
      const hasActivePressure = Object.values(updatedPressures).some(p => p > 0.2);
      if (hasActivePressure && Math.random() < 0.3) {
        const characterEnzymes = playthroughEnzymes.filter(e => 
          character.attachedEnzymes.some(ae => ae.enzymeId === e.id) &&
          (updatedPressures[e.id] || 0) > 0.3
        );
        if (characterEnzymes.length > 0) {
          const randomEnzyme = characterEnzymes[Math.floor(Math.random() * characterEnzymes.length)];
          if (randomEnzyme.responses.length > 0) {
            const randomResponse = randomEnzyme.responses[Math.floor(Math.random() * randomEnzyme.responses.length)];
            updatedDelayedResponses.push({
              id: Date.now().toString() + '-' + Math.random(),
              enzymeId: randomEnzyme.id,
              characterId: character.id,
              responseText: randomResponse.text,
              triggeredAt: Date.now(),
              resolved: false,
            });
            consequences.push(`${character.name} forms a delayed response: "${randomResponse.text}"`);
          }
        }
      }

      // Update blindspot progress (10-20% increase if relevant)
      character.attachedEnzymes.forEach(ae => {
        const enzyme = playthroughEnzymes.find(e => e.id === ae.enzymeId);
        if (enzyme) {
          enzyme.blindspots.forEach((blindspot, idx) => {
            const key = `${enzyme.id}-${idx}`;
            const currentProgress = updatedBlindspotProgress[key] || 0;
            // Only increase if context is relevant to blindspot
            if (contextText.includes(blindspot.description.toLowerCase().substring(0, 15)) ||
                Math.random() < 0.3) {
              const progressIncrease = Math.random() * 10 + 10; // 10-20%
              updatedBlindspotProgress[key] = Math.min(100, currentProgress + progressIncrease);
            }
          });
        }
      });

      // Check for mutations (based on pressure levels)
      // In quick play, mutations happen at 50% pressure instead of 70%
      const mutationThreshold = quickPlayMode ? 0.5 : 0.7;
      character.attachedEnzymes.forEach(ae => {
        const enzyme = playthroughEnzymes.find(e => e.id === ae.enzymeId);
        if (enzyme && updatedPressures[ae.enzymeId] > mutationThreshold) {
          if (enzyme.mutations.length > 0) {
            enzyme.mutations.forEach(mutation => {
              const shouldTrigger = Math.random() < (mutation.probability / 100);
              if (shouldTrigger) {
                const mutationNameLower = mutation.name.toLowerCase();
                if (mutationNameLower.includes('harden') || mutationNameLower.includes('rigid')) {
                  updatedMutationStatus[enzyme.id] = 'hardening';
                  consequences.push(`${character.name}'s ${enzyme.traitName} is hardening: ${mutation.description}`);
                } else if (mutationNameLower.includes('collapse') || mutationNameLower.includes('break')) {
                  updatedMutationStatus[enzyme.id] = 'collapsed';
                  consequences.push(`${character.name}'s ${enzyme.traitName} has collapsed: ${mutation.description}`);
                } else if (mutationNameLower.includes('transcend') || mutationNameLower.includes('grow')) {
                  updatedMutationStatus[enzyme.id] = 'transcended';
                  consequences.push(`${character.name}'s ${enzyme.traitName} has transcended: ${mutation.description}`);
                }
              }
            });
          }
        }
      });

      return {
        ...updatedState,
        enzymePressures: updatedPressures,
        delayedResponses: updatedDelayedResponses,
        blindspotProgress: updatedBlindspotProgress,
        mutationStatus: updatedMutationStatus,
      };
    });

    // Update tension codons: Increase or decrease pressure based on what happened
    const updatedTensionCodonStates = tensionCodonStates.map(codonState => {
      const codon = playthroughTensionCodons.find(c => c.id === codonState.codonId);
      if (!codon) return codonState;

      // Check if any involved characters are affected by this codon
      const involvedCharacters = playthroughCharacters.filter(c => 
        currentEncounter.characterIds.includes(c.id) && 
        codon.affectedCharacterIds.includes(c.id)
      );

      if (involvedCharacters.length > 0) {
        // Determine pressure change based on outcome
        let pressureChange = 0.1; // Default increase
        
        // Check if outcome suggests tension increase or decrease
        const outcomeLower = outcome.toLowerCase();
        if (outcomeLower.includes('resolve') || outcomeLower.includes('peace') || 
            outcomeLower.includes('understand') || outcomeLower.includes('reconcile')) {
          pressureChange = -0.1; // Decrease tension
        } else if (outcomeLower.includes('escalate') || outcomeLower.includes('conflict') ||
                   outcomeLower.includes('anger') || outcomeLower.includes('betray')) {
          pressureChange = 0.2; // Larger increase
        }

        const newPressure = Math.max(0, Math.min(1.0, codonState.currentPressureLevel + pressureChange));
        const wasResolved = codonState.resolved;
        const isNowResolved = newPressure >= 1.0 && !wasResolved;
        
        if (isNowResolved) {
          consequences.push(`Tension "${codon.name}" has reached maximum pressure and may be resolved`);
        }
        
        return {
          ...codonState,
          currentPressureLevel: newPressure,
          resolved: isNowResolved || wasResolved, // Mark resolved if pressure reaches 1.0
        };
      }

      return codonState;
    });

    // Log which enzymes fired
    if (firedEnzymes.length > 0) {
      console.log('Enzymes triggered:', firedEnzymes.map(e => `${e.characterName}'s ${e.enzymeName}`).join(', '));
    }

    // Check for enzymes that reached 100% pressure BEFORE updating states
    // This allows us to detect the transition to 100%
    let pendingMutationEvent: typeof mutationEvent = null;
    
    updatedCharacterStates.forEach(charState => {
      const character = playthroughCharacters.find(c => c.id === charState.characterId);
      if (!character) return;

      // Check previous state to see if we just crossed 100%
      const prevCharState = characterStates.find(cs => cs.characterId === charState.characterId);
      const prevPressure = prevCharState?.enzymePressures || {};

      Object.entries(charState.enzymePressures).forEach(([enzymeId, pressure]) => {
        const wasBelow100 = (prevPressure[enzymeId] || 0) < 1.0;
        const isNow100 = pressure >= 1.0;
        const isNotMutated = charState.mutationStatus[enzymeId] === 'normal';

        if (wasBelow100 && isNow100 && isNotMutated && !pendingMutationEvent) {
          // Pressure just reached 100% - trigger mutation event
          const enzyme = playthroughEnzymes.find(e => e.id === enzymeId);
          if (enzyme && enzyme.mutations.length > 0) {
            // Determine mutation path based on probabilities
            const mutation = determineMutationPath(enzyme, choice, outcome);
            if (mutation) {
              pendingMutationEvent = {
                active: true,
                characterId: character.id,
                characterName: character.name,
                enzymeId: enzyme.id,
                enzymeName: enzyme.traitName,
                mutationType: mutation.type,
                mutationDescription: mutation.description,
              };
            }
          }
        }
      });
    });

    setCharacterStates(updatedCharacterStates);
    setTensionCodonStates(updatedTensionCodonStates);

    // Set mutation event if one was detected
    if (pendingMutationEvent) {
      setMutationEvent(pendingMutationEvent);
    }

    // Add to story history with new structure
    const newHistoryEntry: StoryHistoryEntry = {
      encounterId: currentEncounter.id,
      encounterTitle: currentEncounter.title,
      encounterNumber: currentEncounterIndex + 1,
      choice: choice,
      outcome: outcome,
      consequences: consequences,
      timestamp: Date.now(),
    };

    setStoryHistory([...storyHistory, newHistoryEntry]);
    setTotalChoicesMade(totalChoicesMade + 1);

    // Check for ending conditions after choice
    const ending = checkEndingConditions();
    if (ending) {
      setGameEnding(ending);
      return; // Don't generate next encounter if game ended
    }

    // Generate the next encounter
    setIsGeneratingNextEncounter(true);
    const generatedEncounter = await generateNextEncounter();
    if (generatedEncounter) {
      setEncounters(prev => [...prev, generatedEncounter]);
      setCurrentEncounterIndex(prev => prev + 1);

      // Initialize emotions from new encounter
      if (generatedEncounter.emotions) {
        const initialEmotions: Record<string, string> = {};
        Object.entries(generatedEncounter.emotions).forEach(([characterName, emotion]) => {
          const character = playthroughCharacters.find(c => c.name === characterName);
          if (character) {
            initialEmotions[character.id] = emotion as string;
          }
        });
        setCurrentEmotions(initialEmotions);
      } else if (generatedEncounter.characterIds?.length) {
        const defaultEmotions: Record<string, string> = {};
        generatedEncounter.characterIds.forEach((charId: string) => {
          defaultEmotions[charId] = 'neutral';
        });
        setCurrentEmotions(defaultEmotions);
      }
    } else {
      // If generation failed, stay on current encounter (don't leave blank screen)
      console.log('No encounter generated, staying on current');
    }

      // Clear selected choice for next encounter
      setSelectedChoice(null);
      setChoiceOutcome(null);
      console.log('Choice handling complete');
    } catch (error: any) {
      console.error('Error in handleChoiceClick:', error);
      alert('Error: ' + (error?.message || String(error)));
      // On error, still clear selection so user sees current encounter with choices or "Generate Encounter"
      setSelectedChoice(null);
      setChoiceOutcome(null);
    } finally {
      setIsGeneratingNextEncounter(false);
    }
  };

  // Determine mutation path based on enzyme mutations and probabilities
  const determineMutationPath = (
    enzyme: Enzyme,
    recentChoice: string,
    recentOutcome: string
  ): { type: 'hardening' | 'collapsed' | 'transcended'; description: string } | null => {
    if (enzyme.mutations.length === 0) return null;

    // Categorize mutations by type
    const hardeningMutations = enzyme.mutations.filter(m => 
      m.name.toLowerCase().includes('harden') || m.name.toLowerCase().includes('rigid')
    );
    const collapseMutations = enzyme.mutations.filter(m => 
      m.name.toLowerCase().includes('collapse') || m.name.toLowerCase().includes('break') || m.name.toLowerCase().includes('shatter')
    );
    const transcendMutations = enzyme.mutations.filter(m => 
      m.name.toLowerCase().includes('transcend') || m.name.toLowerCase().includes('grow') || m.name.toLowerCase().includes('mature')
    );

    // Calculate total probabilities for each type
    const hardeningProb = hardeningMutations.reduce((sum, m) => sum + m.probability, 0);
    const collapseProb = collapseMutations.reduce((sum, m) => sum + m.probability, 0);
    const transcendProb = transcendMutations.reduce((sum, m) => sum + m.probability, 0);

    // Influence based on recent choice/outcome
    const contextLower = (recentChoice + ' ' + recentOutcome).toLowerCase();
    let hardeningBonus = 0;
    let collapseBonus = 0;
    let transcendBonus = 0;

    if (contextLower.includes('conflict') || contextLower.includes('anger') || contextLower.includes('resist')) {
      hardeningBonus = 0.1;
    } else if (contextLower.includes('break') || contextLower.includes('fail') || contextLower.includes('lose')) {
      collapseBonus = 0.1;
    } else if (contextLower.includes('grow') || contextLower.includes('understand') || contextLower.includes('resolve')) {
      transcendBonus = 0.1;
    }

    // Normalize probabilities
    const adjustedHardening = Math.min(1.0, (hardeningProb / 100) + hardeningBonus);
    const adjustedCollapse = Math.min(1.0, (collapseProb / 100) + collapseBonus);
    const adjustedTranscend = Math.min(1.0, (transcendProb / 100) + transcendBonus);
    const adjustedTotal = adjustedHardening + adjustedCollapse + adjustedTranscend;

    if (adjustedTotal === 0) return null;

    // Roll randomly
    const roll = Math.random() * adjustedTotal;

    if (roll < adjustedHardening && hardeningMutations.length > 0) {
      // Select specific hardening mutation based on individual probabilities
      const selected = selectMutationByProbability(hardeningMutations);
      return { type: 'hardening', description: selected.description };
    } else if (roll < adjustedHardening + adjustedCollapse && collapseMutations.length > 0) {
      const selected = selectMutationByProbability(collapseMutations);
      return { type: 'collapsed', description: selected.description };
    } else if (transcendMutations.length > 0) {
      const selected = selectMutationByProbability(transcendMutations);
      return { type: 'transcended', description: selected.description };
    }

    return null;
  };

  // Select a mutation from a list based on their individual probabilities
  const selectMutationByProbability = (mutations: Mutation[]): Mutation => {
    const totalProb = mutations.reduce((sum, m) => sum + m.probability, 0);
    if (totalProb === 0) return mutations[0]; // Fallback to first if no probabilities

    const roll = Math.random() * totalProb;
    let cumulative = 0;
    for (const mutation of mutations) {
      cumulative += mutation.probability;
      if (roll <= cumulative) {
        return mutation;
      }
    }
    return mutations[mutations.length - 1]; // Fallback to last
  };

  // Generate story summary
  const generateStorySummary = () => {
    const storyworldName = currentStoryworld || 'Splinewright Storyworld'; // Use current storyworld name
    
    // Group encounters into chapters (every 5 encounters)
    const chapters: Array<{
      number: number;
      encounters: StoryHistoryEntry[];
    }> = [];
    
    for (let i = 0; i < storyHistory.length; i += 5) {
      chapters.push({
        number: Math.floor(i / 5) + 1,
        encounters: storyHistory.slice(i, i + 5),
      });
    }

    // Character journeys
    const characterJourneys = playthroughCharacters.map(character => {
      const charState = characterStates.find(cs => cs.characterId === character.id);
      const attachedEnzymes = character.attachedEnzymes.map(ae => {
        const enzyme = playthroughEnzymes.find(e => e.id === ae.enzymeId);
        return enzyme ? { ...enzyme, intensity: ae.intensity } : null;
      }).filter(Boolean);

      // Find key moments where enzymes fired
      const enzymeFirings: Array<{ enzymeName: string; encounter: string; choice: string }> = [];
      storyHistory.forEach(entry => {
        if (entry.consequences) {
          entry.consequences.forEach(consequence => {
            if (consequence.includes('triggered') && consequence.includes(character.name)) {
              const enzymeMatch = consequence.match(/'s (.+?) triggered/);
              if (enzymeMatch) {
                enzymeFirings.push({
                  enzymeName: enzymeMatch[1],
                  encounter: entry.encounterTitle,
                  choice: entry.choice,
                });
              }
            }
          });
        }
      });

      // Find mutations from story history
      const mutations: Array<{ enzymeName: string; type: string; description: string }> = [];
      storyHistory.forEach(entry => {
        if (entry.encounterId === 'mutation-event' && entry.consequences) {
          entry.consequences.forEach(consequence => {
            if (consequence.includes(character.name)) {
              // Extract mutation info from consequence
              const mutationMatch = consequence.match(/'s (.+?) has (hardened|collapsed|transcended)/);
              if (mutationMatch) {
                mutations.push({
                  enzymeName: mutationMatch[1],
                  type: mutationMatch[2] === 'hardened' ? 'hardening' :
                        mutationMatch[2] === 'collapsed' ? 'collapsed' :
                        'transcended',
                  description: entry.outcome,
                });
              }
            }
          });
        }
      });

      // Current state
      const currentState = {
        enzymePressures: charState ? Object.entries(charState.enzymePressures)
          .map(([enzymeId, pressure]) => {
            const enzyme = playthroughEnzymes.find(e => e.id === enzymeId);
            return enzyme ? { name: enzyme.traitName, pressure: pressure * 100 } : null;
          }).filter(Boolean) : [],
        mutations: mutations,
      };

      return {
        character,
        startingEnzymes: attachedEnzymes.map((enz: any) => enz.traitName),
        enzymeFirings,
        mutations,
        currentState,
      };
    });

    // Tension resolution
    const resolvedTensions = tensionCodonStates
      .filter(tcs => tcs.resolved)
      .map(tcs => {
        const codon = playthroughTensionCodons.find(c => c.id === tcs.codonId);
        return codon ? { name: codon.name, description: codon.description } : null;
      })
      .filter(Boolean);

    // Statistics
    const stats = {
      totalEncounters: storyHistory.length,
      totalChoices: totalChoicesMade,
      enzymesTriggered: storyHistory.reduce((sum, entry) => 
        sum + (entry.consequences?.filter(c => c.includes('triggered')).length || 0), 0
      ),
      mutationsOccurred: characterJourneys.reduce((sum, journey) => sum + journey.mutations.length, 0),
    };

    return {
      storyworldName,
      chapters,
      characterJourneys,
      resolvedTensions,
      stats,
      startDate: playthroughStartDate,
    };
  };

  // Export story as markdown
  const exportStoryAsMarkdown = () => {
    const summary = generateStorySummary();
    let markdown = `# The Story of ${summary.storyworldName}\n\n`;
    markdown += `*Started: ${new Date(summary.startDate).toLocaleString()}*\n\n`;
    markdown += `---\n\n`;

    // Chapters
    markdown += `## Chapters\n\n`;
    summary.chapters.forEach(chapter => {
      markdown += `### Chapter ${chapter.number}\n\n`;
      chapter.encounters.forEach((entry, idx) => {
        markdown += `#### ${entry.encounterTitle}\n\n`;
        markdown += `**Choice:** ${entry.choice}\n\n`;
        markdown += `**Outcome:** ${entry.outcome}\n\n`;
        if (entry.consequences && entry.consequences.length > 0) {
          markdown += `**Consequences:**\n`;
          entry.consequences.forEach(consequence => {
            markdown += `- ${consequence}\n`;
          });
          markdown += `\n`;
        }
        if (idx < chapter.encounters.length - 1) markdown += `---\n\n`;
      });
      markdown += `\n`;
    });

    // Character Journeys
    markdown += `## Character Journeys\n\n`;
    summary.characterJourneys.forEach(journey => {
      markdown += `### ${journey.character.name}\n\n`;
      markdown += `**Starting Enzymes:** ${journey.startingEnzymes.join(', ')}\n\n`;
      
      if (journey.enzymeFirings.length > 0) {
        markdown += `**Key Moments:**\n`;
        journey.enzymeFirings.forEach(firing => {
          markdown += `- ${firing.enzymeName} triggered during "${firing.encounter}" (Choice: ${firing.choice})\n`;
        });
        markdown += `\n`;
      }

      if (journey.mutations.length > 0) {
        markdown += `**Mutations:**\n`;
        journey.mutations.forEach(mutation => {
          markdown += `- ${mutation.enzymeName}: ${mutation.type}\n`;
        });
        markdown += `\n`;
      }

      markdown += `**Current State:**\n`;
      if (journey.currentState.enzymePressures.length > 0) {
        journey.currentState.enzymePressures.forEach((ep: any) => {
          markdown += `- ${ep.name}: ${ep.pressure.toFixed(0)}% pressure\n`;
        });
      }
      markdown += `\n`;
    });

    // Tension Resolution
    if (summary.resolvedTensions.length > 0) {
      markdown += `## Tension Resolution\n\n`;
      summary.resolvedTensions.forEach((tension: any) => {
        markdown += `### ${tension.name}\n`;
        markdown += `${tension.description}\n\n`;
      });
    }

    // Statistics
    markdown += `## Statistics\n\n`;
    markdown += `- **Total Encounters:** ${summary.stats.totalEncounters}\n`;
    markdown += `- **Choices Made:** ${summary.stats.totalChoices}\n`;
    markdown += `- **Enzymes Triggered:** ${summary.stats.enzymesTriggered}\n`;
    markdown += `- **Mutations Occurred:** ${summary.stats.mutationsOccurred}\n`;

    // Download as file
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `story-${summary.storyworldName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Share story summary to clipboard
  const shareStorySummary = async () => {
    const summary = generateStorySummary();
    let text = `The Story of ${summary.storyworldName}\n\n`;
    text += `${summary.stats.totalEncounters} encounters, ${summary.stats.totalChoices} choices\n`;
    text += `${summary.stats.enzymesTriggered} enzymes triggered, ${summary.stats.mutationsOccurred} mutations\n\n`;
    
    if (summary.characterJourneys.length > 0) {
      text += `Characters: ${summary.characterJourneys.map(j => j.character.name).join(', ')}\n`;
    }

    try {
      await navigator.clipboard.writeText(text);
      alert('Story summary copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy to clipboard. Please try again.');
    }
  };

  // Check for ending conditions
  const checkEndingConditions = (): {
    type: 'resolution' | 'tragedy' | 'collapse' | 'transcendence' | 'custom';
    message: string;
    title: string;
  } | null => {
    // 1. Resolution ending: All major tension codons resolved
    const allTensionsResolved = tensionCodonStates.length > 0 && 
      tensionCodonStates.every(tcs => tcs.resolved || tcs.currentPressureLevel === 0);
    if (allTensionsResolved && tensionCodonStates.length > 0) {
      return {
        type: 'resolution',
        title: 'Resolution',
        message: 'The tensions have resolved. Your story has found peace.',
      };
    }

    // 2. Tragedy ending: All characters have hardened mutations
    const allCharactersHardened = playthroughCharacters.length > 0 &&
      playthroughCharacters.every(character => {
        const charState = characterStates.find(cs => cs.characterId === character.id);
        if (!charState) return false;
        const hasEnzymes = character.attachedEnzymes.length > 0;
        if (!hasEnzymes) return false;
        // Check if at least one enzyme is hardened (not all need to be)
        return character.attachedEnzymes.some(ae => {
          const status = charState.mutationStatus[ae.enzymeId];
          return status === 'hardening';
        });
      });
    if (allCharactersHardened) {
      return {
        type: 'tragedy',
        title: 'Tragedy',
        message: 'The characters have hardened beyond redemption.',
      };
    }

    // 3. Collapse ending: Protagonist (first character) has collapsed mutation
    if (playthroughCharacters.length > 0) {
      const protagonist = playthroughCharacters[0];
      const protagonistState = characterStates.find(cs => cs.characterId === protagonist.id);
      if (protagonistState) {
        const hasCollapsed = protagonist.attachedEnzymes.some(ae => {
          const status = protagonistState.mutationStatus[ae.enzymeId];
          return status === 'collapsed';
        });
        if (hasCollapsed) {
          return {
            type: 'collapse',
            title: 'Collapse',
            message: 'Your protagonist has fallen. The story ends in tragedy.',
          };
        }
      }
    }

    // 4. Transcendence ending: Protagonist has transcended
    if (playthroughCharacters.length > 0) {
      const protagonist = playthroughCharacters[0];
      const protagonistState = characterStates.find(cs => cs.characterId === protagonist.id);
      if (protagonistState) {
        const hasTranscended = protagonist.attachedEnzymes.some(ae => {
          const status = protagonistState.mutationStatus[ae.enzymeId];
          return status === 'transcended';
        });
        if (hasTranscended) {
          return {
            type: 'transcendence',
            title: 'Transcendence',
            message: 'Your protagonist has grown beyond their flaws.',
          };
        }
      }
    }

    // 5. Custom ending: Check if the last completed encounter is marked as ending
    // We check the last entry in story history to see if it was an ending encounter
    if (storyHistory.length > 0) {
      const lastEncounterId = storyHistory[storyHistory.length - 1].encounterId;
      const lastEncounter = encounters.find(e => e.id === lastEncounterId);
      if (lastEncounter && lastEncounter.isEndingEncounter) {
        return {
          type: 'custom',
          title: 'The End',
          message: lastEncounter.whatHappens || lastEncounter.description || 'The story has reached its conclusion.',
        };
      }
    }

    return null;
  };

  // Handle mutation event completion
  const handleMutationEventComplete = () => {
    if (!mutationEvent) return;

    // Update character state: apply mutation and reset pressure to 50%
    setCharacterStates(prevStates => {
      return prevStates.map(charState => {
        if (charState.characterId === mutationEvent.characterId) {
          const updatedPressures = { ...charState.enzymePressures };
          const updatedMutationStatus = { ...charState.mutationStatus };
          
          // Reset pressure to 50%
          updatedPressures[mutationEvent.enzymeId] = 0.5;
          // Set mutation status
          updatedMutationStatus[mutationEvent.enzymeId] = mutationEvent.mutationType!;
          
          return {
            ...charState,
            enzymePressures: updatedPressures,
            mutationStatus: updatedMutationStatus,
          };
        }
        return charState;
      });
    });

    // Add mutation event to story history
    const mutationHistoryEntry: StoryHistoryEntry = {
      encounterId: 'mutation-event',
      encounterTitle: `${mutationEvent.characterName} is at a Breaking Point`,
      encounterNumber: storyHistory.length + 1,
      choice: 'Mutation Event',
      outcome: mutationEvent.mutationDescription,
      consequences: [
        `${mutationEvent.characterName}'s ${mutationEvent.enzymeName} has ${mutationEvent.mutationType === 'hardening' ? 'hardened' : mutationEvent.mutationType === 'collapsed' ? 'collapsed' : 'transcended'}`,
      ],
      timestamp: Date.now(),
    };

    setStoryHistory([...storyHistory, mutationHistoryEntry]);

    // Clear mutation event
    setMutationEvent(null);
  };

  const generateNextEncounter = async (): Promise<Encounter | null> => {
    // 1. Analyze current state
    // Find enzymes with highest pressure
    const enzymesWithPressure: Array<{
      enzymeId: string;
      characterId: string;
      characterName: string;
      enzymeName: string;
      pressure: number;
      enzyme: Enzyme;
    }> = [];

    characterStates.forEach(charState => {
      const character = playthroughCharacters.find(c => c.id === charState.characterId);
      if (!character) return;

      Object.entries(charState.enzymePressures).forEach(([enzymeId, pressure]) => {
        if (pressure > 0.1) { // Only consider enzymes with meaningful pressure
          const enzyme = playthroughEnzymes.find(e => e.id === enzymeId);
          if (enzyme) {
            enzymesWithPressure.push({
              enzymeId,
              characterId: character.id,
              characterName: character.name,
              enzymeName: enzyme.traitName,
              pressure,
              enzyme,
            });
          }
        }
      });
    });

    // Sort by pressure (highest first)
    enzymesWithPressure.sort((a, b) => b.pressure - a.pressure);

    // Find characters with unresolved delayed responses
    const charactersWithDelayedResponses: Array<{
      characterId: string;
      characterName: string;
      delayedResponses: DelayedResponse[];
    }> = [];

    characterStates.forEach(charState => {
      const unresolved = charState.delayedResponses.filter(dr => !dr.resolved);
      if (unresolved.length > 0) {
        const character = playthroughCharacters.find(c => c.id === charState.characterId);
        if (character) {
          charactersWithDelayedResponses.push({
            characterId: character.id,
            characterName: character.name,
            delayedResponses: unresolved,
          });
        }
      }
    });

    // 2. Select characters for encounter
    const selectedCharacterIds: string[] = [];
    const selectedCharacters: Character[] = [];

    // Prioritize characters with primed enzymes
    const charactersByPressure = new Map<string, number>();
    enzymesWithPressure.forEach(e => {
      const current = charactersByPressure.get(e.characterId) || 0;
      charactersByPressure.set(e.characterId, current + e.pressure);
    });

    // Sort characters by total enzyme pressure
    const sortedCharacters = Array.from(charactersByPressure.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([charId]) => playthroughCharacters.find(c => c.id === charId))
      .filter(Boolean) as Character[];

    // Select 2-3 characters, prioritizing high pressure
    for (const char of sortedCharacters) {
      if (selectedCharacterIds.length >= 3) break;
      if (!selectedCharacterIds.includes(char.id)) {
        selectedCharacterIds.push(char.id);
        selectedCharacters.push(char);
      }
    }

    // If we don't have enough, add characters with delayed responses
    for (const charData of charactersWithDelayedResponses) {
      if (selectedCharacterIds.length >= 3) break;
      if (!selectedCharacterIds.includes(charData.characterId)) {
        const char = playthroughCharacters.find(c => c.id === charData.characterId);
        if (char) {
          selectedCharacterIds.push(char.id);
          selectedCharacters.push(char);
        }
      }
    }

    // If still not enough, add any remaining characters
    for (const char of playthroughCharacters) {
      if (selectedCharacterIds.length >= 3) break;
      if (!selectedCharacterIds.includes(char.id)) {
        selectedCharacterIds.push(char.id);
        selectedCharacters.push(char);
      }
    }

    // Need at least 2 characters
    if (selectedCharacterIds.length < 2) {
      console.warn('Not enough characters for encounter generation');
      return null;
    }

    // 3. Generate encounter via Claude API
    const apiKey = localStorage.getItem('splinewright-claude-api-key') || localStorage.getItem('claude_api_key');
    if (!apiKey) {
      alert('Please add your Claude API key in Settings');
      return null;
    }

    console.log('Generating encounter...');
    console.log('API Key exists:', !!apiKey);
    // Do not log API keys

    try {
      // Build character data with current enzyme states (all characters, not just selected)
      const characterData = playthroughCharacters.map(char => {
        const charState = characterStates.find(cs => cs.characterId === char.id);
        const attachedEnzymeData = char.attachedEnzymes.map(ae => {
          const enzyme = playthroughEnzymes.find(e => e.id === ae.enzymeId);
          if (!enzyme) return null;
          const pressure = charState?.enzymePressures[enzyme.id] || 0;
          const mutationStatus = charState?.mutationStatus[enzyme.id] || 'normal';
          return {
            trait: enzyme.traitName,
            intensity: ae.intensity,
            pressure: pressure,
            pressurePercent: (pressure * 100).toFixed(0),
            mutationStatus: mutationStatus,
            triggers: enzyme.triggers.map(t => t.text),
            responses: enzyme.responses.map(r => r.text),
            blindspots: enzyme.blindspots.map(b => b.description),
          };
        }).filter(Boolean);

        const delayedResponses = charState?.delayedResponses.filter(dr => !dr.resolved) || [];

        return {
          name: char.name,
          description: char.description || '',
          enzymes: attachedEnzymeData,
          delayedResponses: delayedResponses.map(dr => dr.responseText),
        };
      });

      // Build character and enzyme data text
      const charactersText = characterData.map((char, i) => {
        const enzymesText = char.enzymes.length > 0
          ? char.enzymes.map((enz: any) => {
              const mutationText = enz.mutationStatus !== 'normal' 
                ? ` [MUTATED: ${enz.mutationStatus}]` 
                : '';
              return `  - ${enz.trait}${mutationText} (${enz.pressurePercent}% pressure, intensity: ${enz.intensity.toFixed(2)})
    Triggers: ${enz.triggers.join(', ') || 'None'}
    Responses: ${enz.responses.join(', ') || 'None'}
    Blindspots: ${enz.blindspots.join('; ') || 'None'}`;
            }).join('\n')
          : '  No enzymes attached';
        
        const delayedText = char.delayedResponses.length > 0
          ? `\n   Active Delayed Responses: ${char.delayedResponses.join('; ')}`
          : '';
        
        return `${i + 1}. ${char.name}
   Description: ${char.description || 'No description'}
   Enzymes:
${enzymesText}${delayedText}`;
      }).join('\n\n');

      // Build tension codon data with current pressure levels (all tension codons)
      const tensionsText = playthroughTensionCodons.length > 0
        ? playthroughTensionCodons.map(codon => {
            const codonState = tensionCodonStates.find(tcs => tcs.codonId === codon.id);
            const pressure = codonState ? codonState.currentPressureLevel : 0;
            const resolved = codonState ? codonState.resolved : false;
            const status = resolved ? 'RESOLVED' : `${(pressure * 100).toFixed(0)}% pressure`;
            return `- ${codon.name} (${status}): ${codon.description}
  Affects: ${playthroughCharacters.filter(c => codon.affectedCharacterIds.includes(c.id)).map(c => c.name).join(', ')}`;
          }).join('\n')
        : 'None';

      // Generate a unique seed based on timestamp and story state
      const seed = `${Date.now()}-${storyHistory.length}-${totalChoicesMade}`;
      
      // Build full storyworld context for the AI (trimmed to avoid runaway prompt size)
      const storyworldContextJson = JSON.stringify(buildStoryworldContext());

      // Use the exact prompt format with variety instructions
      const prompt = `You are generating encounters for an interactive drama.

STORYWORLD_CONTEXT_JSON (use this for continuity, pacing, and narrative needs):
${storyworldContextJson}

Here are the characters and their behavioral enzymes:

${charactersText}

Current tension codons active:
${tensionsText}

IMPORTANT: Create a scene we haven't seen before. Vary the setting, conflict type, and emotional tone. Each encounter should feel fresh and unique.

Generate a dramatic encounter with these characters.

CRITICAL: You must include an 'emotions' object in your JSON response that maps each character name to their emotion.

Use this EXACT JSON structure:
{
  "title": "Encounter title",
  "description": "What happens in the scene...",
  "characters": ["Character1", "Character2"],
  "trigger": "What brings them together",
  "emotions": {
    "Character1": "angry",
    "Character2": "fearful"
  },
  "choices": [
    {
      "text": "Choice text",
      "outcome": "What happens",
      "emotionChanges": {
        "Character1": "satisfied",
        "Character2": "sad"
      },
      "enzymesAffected": ["enzyme1", "enzyme2"]
    }
  ]
}

Valid emotions: angry, sad, happy, fearful, neutral, suspicious, conflicted, proud, guilty, desperate

Generation seed: ${seed}

Respond ONLY with valid JSON.`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1024,
          messages: [{ role: 'user', content: prompt }]
        })
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Response:', data);
      let content = data.content[0].text;

      // Strip markdown code blocks if present
      content = content.trim();
      if (content.startsWith('```json')) {
        content = content.replace(/^```json\s*/i, '').replace(/\s*```$/i, '');
      } else if (content.startsWith('```')) {
        content = content.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }
      content = content.trim();

      // Parse JSON
      let encounterData: any = null;
      try {
        encounterData = JSON.parse(content);
      } catch (e) {
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            encounterData = JSON.parse(jsonMatch[0]);
          } catch (parseError) {
            console.error('Failed to parse JSON from Claude response', parseError);
            throw new Error('Invalid response format from Claude');
          }
        } else {
          throw new Error('No JSON found in Claude response');
        }
      }

      if (!encounterData || !encounterData.title || !encounterData.characters) {
        throw new Error('Invalid encounter data structure from Claude');
      }

      // DEBUG: Log emotions received from Claude
      console.log('=== EMOTIONS FROM CLAUDE ===');
      console.log('Emotions object:', encounterData.emotions);
      console.log('Choice emotionChanges:', encounterData.choices?.map((c: any) => c.emotionChanges));
      console.log('============================');

      // Find character IDs from names
      const characterIds = encounterData.characters
        .map((name: string) => playthroughCharacters.find(c => c.name === name)?.id)
        .filter(Boolean);

      if (characterIds.length < 2) {
        throw new Error('Could not match character names to playthrough characters');
      }

      // Extract emotions object (default to empty if missing)
      const emotions: Record<string, string> = encounterData.emotions || {};
      
      // Extract emotionChanges for each choice
      const choiceEmotionChanges: Array<Record<string, string>> = Array.isArray(encounterData.choices)
        ? encounterData.choices.map((choice: any) => choice.emotionChanges || {})
        : [];

      // Map choices to playerChoicePoints format
      // Handle new structure with enzymesAffected
      const playerChoicePoints = Array.isArray(encounterData.choices)
        ? encounterData.choices.map((choice: any) => {
            if (typeof choice === 'string') {
              return choice;
            }
            if (choice.text && choice.outcome) {
              // Format: "choice text → outcome"
              // enzymesAffected is stored but not shown in the choice text
              return `${choice.text} → ${choice.outcome}`;
            }
            return JSON.stringify(choice);
          })
        : [];

      const encounter: Encounter = {
        id: Date.now().toString(),
        title: encounterData.title,
        description: encounterData.description || '',
        characterIds: characterIds,
        triggers: encounterData.trigger || encounterData.triggers || '',
        whatHappens: encounterData.description || encounterData.whatHappens || '',
        playerChoicePoints: playerChoicePoints,
        emotions: emotions,
        choiceEmotionChanges: choiceEmotionChanges,
        createdAt: Date.now(),
      };

      return encounter;
    } catch (error: any) {
      console.error('Error generating dynamic encounter:', error);
      return null; // Will fall back to saved encounters
    }
  };

  const nextEncounter = async () => {
    console.log('nextEncounter called');
    // Check for ending conditions before generating next encounter
    const ending = checkEndingConditions();
    if (ending) {
      setGameEnding(ending);
      return;
    }

    // Clear choice state and show loading
    setSelectedChoice(null);
    setChoiceOutcome(null);
    setIsGeneratingNextEncounter(true);

    try {
      // Always generate next encounter dynamically (unlimited encounters)
      console.log('Generating next encounter...');
      const generatedEncounter = await generateNextEncounter();
      console.log('generateNextEncounter result:', generatedEncounter ? 'ok' : 'null');

    if (generatedEncounter) {
      // Add generated encounter to encounters array
      const updatedEncounters = [...encounters, generatedEncounter];
      setEncounters(updatedEncounters);
      setCurrentEncounterIndex(updatedEncounters.length - 1);
      
      // Initialize emotions from encounter
      if (generatedEncounter.emotions) {
        const initialEmotions: Record<string, string> = {};
        Object.entries(generatedEncounter.emotions).forEach(([characterName, emotion]) => {
          const character = playthroughCharacters.find(c => c.name === characterName);
          if (character) {
            initialEmotions[character.id] = emotion as string;
          }
        });
        setCurrentEmotions(initialEmotions);
        console.log('Initialized emotions from encounter:', initialEmotions);
      } else {
        // Default to neutral if no emotions provided
        const defaultEmotions: Record<string, string> = {};
        generatedEncounter.characterIds.forEach(charId => {
          defaultEmotions[charId] = 'neutral';
        });
        setCurrentEmotions(defaultEmotions);
        console.log('No emotions in encounter, defaulting to neutral:', defaultEmotions);
      }
      
      // Auto-save playthrough state after each encounter (if not in quick play mode)
      if (!quickPlayMode && playthroughActive) {
        const playthroughData: PlaythroughState = {
          active: true,
          startDate: playthroughStartDate,
          currentEncounterIndex: updatedEncounters.length - 1,
          totalChoicesMade,
          characters: playthroughCharacters,
          enzymes: playthroughEnzymes,
          tensionCodons: playthroughTensionCodons,
          characterStates,
          tensionCodonStates,
          storyHistory,
          selectedChoice: null,
          choiceOutcome: null,
          mutationEvent,
        };
        localStorage.setItem('splinewright-playthrough', JSON.stringify(playthroughData));
        setHasSavedPlaythrough(true);
      }
      
      // Check for ending after generating new encounter
      const endingAfterGenerate = checkEndingConditions();
      if (endingAfterGenerate) {
        setGameEnding(endingAfterGenerate);
      }
    } else {
      // If generation fails, check for ending
      const endingFinal = checkEndingConditions();
      if (endingFinal) {
        setGameEnding(endingFinal);
      } else {
        alert('Failed to generate next encounter. Please check your API key and try again.');
      }
    }
    } catch (error: any) {
      console.error('Error in nextEncounter:', error);
      alert('Error processing next encounter: ' + (error?.message || String(error)));
    } finally {
      setIsGeneratingNextEncounter(false);
    }
  };

  const saveApiKey = () => {
    const key = apiKeyInput.trim();
    if (!key) {
      return;
    }

    // Validate key format: must start with "sk-ant-" and be at least 40 characters
    if (!key.startsWith('sk-ant-')) {
      setSaveApiKeyResult({ 
        success: false, 
        message: 'Invalid key format - should start with sk-ant-' 
      });
      return;
    }

    if (key.length < 40) {
      setSaveApiKeyResult({ 
        success: false, 
        message: 'Invalid key format - should start with sk-ant-' 
      });
      return;
    }

    // Key is valid, save it
    setClaudeApiKey(key);
    localStorage.setItem('splinewright-claude-api-key', key);
    setSaveApiKeyResult({ 
      success: true, 
      message: '✓ API key saved!' 
    });
  };

  const saveNftStorageToken = () => {
    const token = nftStorageTokenInput.trim();
    if (!token) {
      return;
    }

    // Save token
    setNftStorageToken(token);
    localStorage.setItem('splinewright-nft-storage-token', token);
    setSaveNftStorageTokenResult({ 
      success: true, 
      message: '✓ NFT.Storage token saved!' 
    });
  };

  const handlePublishClick = (itemId: string) => {
    setSelectedPublishItemId(itemId);
    setShowPublishForm(true);
    setPublishTags('');
    setPublishDescription('');
    setPublishAuthor('');
  };

  const handleSimplePublish = () => {
    if (!selectedPublishItemId) {
      alert('Please select an item to publish.');
      return;
    }

    // Get the selected item's full data
    let itemData: Enzyme | TensionCodon | Character | null = null;
    let itemName = '';
    let itemDescription = '';
    let itemType: 'enzyme' | 'character' | 'tensionCodon' = 'enzyme';
    let libraryItemType: 'Enzyme' | 'Tension Codon' | 'Character' = 'Enzyme';

    if (publishType === 'Enzyme') {
      const enzyme = savedEnzymes.find(e => e.id === selectedPublishItemId);
      if (enzyme) {
        itemData = enzyme;
        itemName = `${enzyme.characterName} - ${enzyme.traitName}`;
        itemDescription = enzyme.triggers && enzyme.triggers.length > 0 
          ? enzyme.triggers[0].text 
          : enzyme.traitName;
        itemType = 'enzyme';
        libraryItemType = 'Enzyme';
      }
    } else if (publishType === 'Tension Codon') {
      const codon = tensionCodons.find(t => t.id === selectedPublishItemId);
      if (codon) {
        itemData = codon;
        itemName = codon.name;
        itemDescription = codon.description || codon.name;
        itemType = 'tensionCodon';
        libraryItemType = 'Tension Codon';
      }
    } else if (publishType === 'Character') {
      const character = characters.find(c => c.id === selectedPublishItemId);
      if (character) {
        itemData = character;
        itemName = character.name;
        itemDescription = character.description || character.name;
        itemType = 'character';
        libraryItemType = 'Character';
      }
    }

    if (!itemData) {
      alert('Item not found.');
      return;
    }

    // Create library entry in the format specified by user
    const entry = {
      id: Date.now().toString(),
      type: itemType,
      name: itemName,
      description: itemDescription,
      data: itemData,
      author: 'Anonymous', // Can be updated later if we have user name in settings
      publishedAt: new Date().toISOString(),
      tags: [],
    };

    // Also create LibraryItem format for compatibility with existing system
    const libraryItem: LibraryItem = {
      id: entry.id,
      type: libraryItemType,
      itemId: selectedPublishItemId,
      name: itemName,
      description: itemDescription,
      data: itemData,
      author: 'Anonymous',
      publishedAt: Date.now(),
      tags: [],
    };

    // Save to localStorage using the key specified by user
    const existingLibrary = JSON.parse(localStorage.getItem('splinewright_library') || '[]');
    existingLibrary.push(entry);
    localStorage.setItem('splinewright_library', JSON.stringify(existingLibrary));

    // Also update the existing library system for compatibility
    const existingStateLibrary = JSON.parse(localStorage.getItem('splinewright-library') || '[]');
    existingStateLibrary.push(libraryItem);
    localStorage.setItem('splinewright-library', JSON.stringify(existingStateLibrary));
    setLibraryItems([...libraryItems, libraryItem]);

    // Show confirmation
    setPublishConfirmation({ name: itemName, itemId: selectedPublishItemId });
    setTimeout(() => {
      setPublishConfirmation(null);
    }, 3000);

    // Console log
    console.log('Published:', entry);

    // Reset selection
    setSelectedPublishItemId(null);
  };

  // Avoid CI lint failures for unused helpers (kept for future wiring)
  void handlePublishClick;
  void handleSimplePublish;

  // Publish item directly to publishedLibrary
  const publishToLibrary = async (itemId: string, type: 'Enzyme' | 'Tension Codon' | 'Character') => {
    let itemData: Enzyme | TensionCodon | Character | null = null;
    let itemName = '';
    let itemDescription = '';
    let itemType: 'enzyme' | 'character' | 'tensionCodon' = 'enzyme';

    if (type === 'Enzyme') {
      const enzyme = savedEnzymes.find(e => e.id === itemId);
      if (enzyme) {
        itemData = enzyme;
        itemName = `${enzyme.characterName} - ${enzyme.traitName}`;
        itemDescription = enzyme.triggers && enzyme.triggers.length > 0 
          ? enzyme.triggers[0].text 
          : enzyme.traitName;
        itemType = 'enzyme';
      }
    } else if (type === 'Tension Codon') {
      const codon = tensionCodons.find(t => t.id === itemId);
      if (codon) {
        itemData = codon;
        itemName = codon.name;
        itemDescription = codon.description || codon.name;
        itemType = 'tensionCodon';
      }
    } else if (type === 'Character') {
      const character = characters.find(c => c.id === itemId);
      if (character) {
        itemData = character;
        itemName = character.name;
        itemDescription = character.description || character.name;
        itemType = 'character';
      }
    }

    if (!itemData) {
      alert('Item not found.');
      return;
    }

    // Create library entry
    const entry = {
      id: Date.now().toString(),
      type: itemType,
      name: itemName,
      description: itemDescription,
      data: itemData,
      author: 'Anonymous',
      publishedAt: new Date().toISOString(),
      tags: [],
      cid: null as string | null,
    };

    // Check for NFT.Storage token
    const nftStorageToken = localStorage.getItem('splinewright-nft-storage-token');
    
    if (nftStorageToken) {
      try {
        // Convert item to JSON
        const jsonData = JSON.stringify(entry.data);
        const blob = new Blob([jsonData], { type: 'application/json' });
        
        // Upload to NFT.Storage
        const formData = new FormData();
        formData.append('file', blob, `${itemName.replace(/\s+/g, '-')}.json`);
        
        const uploadResponse = await fetch('https://api.nft.storage/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${nftStorageToken}`,
          },
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({ error: { message: 'Failed to upload to NFT.Storage' } }));
          throw new Error(errorData.error?.message || `HTTP ${uploadResponse.status}: Failed to upload to NFT.Storage`);
        }

        const uploadResult = await uploadResponse.json();
        // NFT.Storage returns { ok: true, value: { cid: "..." } }
        const cid = uploadResult.value?.cid || uploadResult.cid;
        
        if (!cid) {
          console.error('Upload result:', uploadResult);
          throw new Error('No CID returned from NFT.Storage');
        }

        // Store CID in entry
        entry.cid = cid;
        console.log('Uploaded to IPFS:', cid);

        // Update shared index
        const indexEntry = {
          cid: cid,
          name: itemName,
          type: itemType,
          author: entry.author,
          publishedAt: entry.publishedAt,
        };

        const existingIndex = JSON.parse(localStorage.getItem('splinewright-shared-index') || '{"items": []}');
        existingIndex.items.push(indexEntry);
        localStorage.setItem('splinewright-shared-index', JSON.stringify(existingIndex));
        setSharedIndex(existingIndex.items);
      } catch (error: any) {
        console.error('Error uploading to NFT.Storage:', error);
        alert(`Failed to upload to IPFS: ${error.message}. Item saved locally only.`);
      }
    } else {
      // No token - just save locally
      console.log('NFT.Storage token not found. Saving locally only.');
    }

    // Save to publishedLibrary in localStorage (always, even if IPFS upload fails)
    const existingLibrary = JSON.parse(localStorage.getItem('publishedLibrary') || '[]');
    existingLibrary.push(entry);
    localStorage.setItem('publishedLibrary', JSON.stringify(existingLibrary));
    setPublishedLibrary([...publishedLibrary, entry]);

    // Show confirmation
    setPublishConfirmation({ name: itemName, itemId: itemId });
    setTimeout(() => {
      setPublishConfirmation(null);
    }, 3000);

    console.log('Published:', entry);
  };

  // Import item from publishedLibrary
  const importFromLibrary = (entry: any) => {
    if (!entry.data) {
      alert('Item data not available. Cannot import.');
      return;
    }

    if (entry.type === 'enzyme') {
      const enzyme = entry.data as Enzyme;
      // Generate new ID to avoid conflicts
      const newEnzyme: Enzyme = {
        ...enzyme,
        id: Date.now().toString(),
      };
      setSavedEnzymes([...savedEnzymes, newEnzyme]);
      alert(`✓ Imported "${entry.name}" to My Enzymes!`);
    } else if (entry.type === 'character') {
      const character = entry.data as Character;
      const newCharacter: Character = {
        ...character,
        id: Date.now().toString(),
      };
      setCharacters([...characters, newCharacter]);
      alert(`✓ Imported "${entry.name}" to My Characters!`);
    } else if (entry.type === 'tensionCodon') {
      const codon = entry.data as TensionCodon;
      const newCodon: TensionCodon = {
        ...codon,
        id: Date.now().toString(),
      };
      setTensionCodons([...tensionCodons, newCodon]);
      alert(`✓ Imported "${entry.name}" to My Tension Codons!`);
    }
  };

  // Fetch item from IPFS by CID
  const fetchFromIPFS = async (cid: string): Promise<any | null> => {
    if (loadingIPFS[cid]) {
      return null; // Already loading
    }

    setLoadingIPFS({ ...loadingIPFS, [cid]: true });

    try {
      const response = await fetch(`https://${cid}.ipfs.nftstorage.link`);
      if (!response.ok) {
        throw new Error(`Failed to fetch from IPFS: ${response.statusText}`);
      }
      const data = await response.json();
      setLoadingIPFS({ ...loadingIPFS, [cid]: false });
      return data;
    } catch (error: any) {
      console.error(`Error fetching ${cid} from IPFS:`, error);
      setLoadingIPFS({ ...loadingIPFS, [cid]: false });
      return null;
    }
  };

  // Combine local and IPFS items for browsing
  const allBrowseableItems = [...publishedLibrary, ...sharedIndex.map((indexItem: any) => ({
    id: indexItem.cid,
    type: indexItem.type,
    name: indexItem.name,
    description: '', // Will be loaded from IPFS
    author: indexItem.author,
    publishedAt: indexItem.publishedAt,
    cid: indexItem.cid,
    isIPFS: true,
  }))];

  // Filter publishedLibrary items
  const filteredPublishedLibrary = allBrowseableItems.filter(item => {
    // Filter by type
    if (browseFilterType !== 'all' && item.type !== browseFilterType) {
      return false;
    }
    // Filter by search query
    if (librarySearchQuery.trim()) {
      const query = librarySearchQuery.toLowerCase();
      return item.name.toLowerCase().includes(query) || 
             (item.description && item.description.toLowerCase().includes(query));
    }
    return true;
  });

  const signComponent = async (component: Omit<LibraryItem, 'signature' | 'authorId'>): Promise<string> => {
    if (!authorKeyPair) {
      throw new Error('Author identity not initialized');
    }

    // Create a message to sign (all component data except signature)
    const message = JSON.stringify({
      id: component.id,
      type: component.type,
      name: component.name,
      author: component.author,
      tags: component.tags,
      description: component.description,
      publishedAt: component.publishedAt,
      // Include a hash of the data to sign the content too
      dataHash: await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(component.data))),
    });

    // Sign the message
    const encoder = new TextEncoder();
    const messageBuffer = encoder.encode(message);
    const signature = await crypto.subtle.sign(
      {
        name: 'ECDSA',
        hash: 'SHA-256',
      },
      authorKeyPair.privateKey,
      messageBuffer
    );

    // Convert signature to base64 for storage
    const signatureArray = Array.from(new Uint8Array(signature));
    return btoa(String.fromCharCode(...signatureArray));
  };

  const handlePublish = async () => {
    if (!selectedPublishItemId || !publishAuthor.trim() || !publishDescription.trim()) {
      alert('Please fill in all required fields (author and description).');
      return;
    }

    if (!authorKeyPair || !authorId) {
      alert('Author identity not initialized. Please refresh the page.');
      return;
    }

    let itemData: Enzyme | TensionCodon | Character | null = null;
    let itemName = '';

    if (publishType === 'Enzyme') {
      const enzyme = savedEnzymes.find(e => e.id === selectedPublishItemId);
      if (enzyme) {
        itemData = enzyme;
        itemName = `${enzyme.characterName} - ${enzyme.traitName}`;
      }
    } else if (publishType === 'Tension Codon') {
      const codon = tensionCodons.find(t => t.id === selectedPublishItemId);
      if (codon) {
        itemData = codon;
        itemName = codon.name;
      }
    } else if (publishType === 'Character') {
      const character = characters.find(c => c.id === selectedPublishItemId);
      if (character) {
        itemData = character;
        itemName = character.name;
      }
    }

    if (!itemData) {
      alert('Item not found.');
      return;
    }

    const tags = publishTags.split(',').map(t => t.trim()).filter(t => t.length > 0);

    const libraryItemBase: Omit<LibraryItem, 'signature' | 'authorId'> = {
      id: Date.now().toString(),
      type: publishType,
      itemId: selectedPublishItemId,
      name: itemName,
      author: publishAuthor.trim(),
      tags: tags,
      description: publishDescription.trim(),
      data: itemData,
      publishedAt: Date.now(),
    };

    // Sign the component
    let signature = '';
    try {
      signature = await signComponent(libraryItemBase);
    } catch (e) {
      console.error('Failed to sign component:', e);
      alert('Failed to sign component. Please try again.');
      return;
    }

    const libraryItem: LibraryItem = {
      ...libraryItemBase,
      authorId: authorId,
      signature: signature,
    };

    setLibraryItems([...libraryItems, libraryItem]);
    setShowPublishForm(false);
    setSelectedPublishItemId(null);
    setPublishTags('');
    setPublishDescription('');
    setPublishAuthor('');
    
    // Also publish to shared index if URL is set
    if (libraryIndexUrl) {
      await publishToIndex(libraryItem);
    } else {
      alert('Published to local library! Set a Library Index URL in Settings to publish to the shared index.');
    }
  };

  const handleImport = async (item: LibraryItem | (LibraryIndexItem & { source: 'local' | 'remote'; compatibility?: number })) => {
    let libraryItem: LibraryItem;
    
    // Handle remote items - need to fetch the data first
    if ('source' in item && item.source === 'remote' && 'cid' in item && !('data' in item)) {
      const remoteItem = item as LibraryIndexItem;
      const componentData = await fetchComponentData(remoteItem.cid);
      if (!componentData) {
        // Try to get from localStorage as fallback
        const localData = localStorage.getItem(`splinewright-component-${remoteItem.cid}`);
        if (localData) {
          try {
            const data = JSON.parse(localData);
            libraryItem = {
              id: remoteItem.id,
              type: remoteItem.type,
              itemId: remoteItem.id,
              name: remoteItem.name,
              author: remoteItem.author,
              authorId: remoteItem.authorId,
              signature: remoteItem.signature,
              tags: remoteItem.tags,
              description: remoteItem.description,
              data: data,
              publishedAt: remoteItem.publishedAt,
              cid: remoteItem.cid,
            };
          } catch (e) {
            alert('Failed to import component. The data may not be available.');
            return;
          }
        } else {
          alert('Failed to fetch component data. The CID may be invalid or the data may not be accessible.');
          return;
        }
      } else {
        libraryItem = {
          id: remoteItem.id,
          type: remoteItem.type,
          itemId: remoteItem.id,
          name: remoteItem.name,
          author: remoteItem.author,
          authorId: remoteItem.authorId,
          signature: remoteItem.signature,
          tags: remoteItem.tags,
          description: remoteItem.description,
          data: componentData.data,
          publishedAt: remoteItem.publishedAt,
          cid: remoteItem.cid,
        };
      }
    } else {
      // Handle local items
      libraryItem = item as LibraryItem;
    }
    // Now handle the import with the full LibraryItem
    if (libraryItem.type === 'Enzyme') {
      const enzyme = libraryItem.data as Enzyme;
      // Create a new enzyme with a new ID
      const newEnzyme: Enzyme = {
        ...enzyme,
        id: Date.now().toString(),
        createdAt: Date.now(),
      };
      setSavedEnzymes([...savedEnzymes, newEnzyme]);
      alert(`Imported enzyme: ${newEnzyme.characterName} - ${newEnzyme.traitName}`);
    } else if (libraryItem.type === 'Tension Codon') {
      const codon = libraryItem.data as TensionCodon;
      // Create a new codon with a new ID and reset affected characters
      const newCodon: TensionCodon = {
        ...codon,
        id: Date.now().toString(),
        affectedCharacterIds: [], // Reset affected characters since they may not exist
        createdAt: Date.now(),
      };
      setTensionCodons([...tensionCodons, newCodon]);
      alert(`Imported tension codon: ${newCodon.name}`);
    } else if (libraryItem.type === 'Character') {
      const character = libraryItem.data as Character;
      // Create a new character with a new ID and reset attached enzymes
      const newCharacter: Character = {
        ...character,
        id: Date.now().toString(),
        attachedEnzymes: [], // Reset attached enzymes since they may not exist
        createdAt: Date.now(),
      };
      setCharacters([...characters, newCharacter]);
      alert(`Imported character: ${newCharacter.name}`);
    }
    setPreviewLibraryItem(null);
  };

  const fetchLibraryIndex = async () => {
    if (!libraryIndexUrl) {
      setRemoteLibraryItems([]);
      return;
    }

    setLoadingLibraryIndex(true);
    try {
      const response = await fetch(libraryIndexUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch library index: ${response.statusText}`);
      }
      const index: LibraryIndex = await response.json();
      setRemoteLibraryItems(index.items || []);
    } catch (error: any) {
      console.error('Error fetching library index:', error);
      alert(`Failed to fetch library index: ${error.message}`);
      setRemoteLibraryItems([]);
    } finally {
      setLoadingLibraryIndex(false);
    }
  };

  const fetchComponentData = async (cid: string): Promise<LibraryItem | null> => {
    try {
      // Try Pinata gateway first, then fallback to public IPFS gateway
      const dataUrl = cid.startsWith('http') ? cid : `https://gateway.pinata.cloud/ipfs/${cid}`;
      let response = await fetch(dataUrl);
      
      // If Pinata gateway fails, try public IPFS gateway
      if (!response.ok) {
        const fallbackUrl = cid.startsWith('http') ? cid : `https://ipfs.io/ipfs/${cid}`;
        response = await fetch(fallbackUrl);
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch component data');
      }
      const componentData = await response.json();
      return componentData;
    } catch (error: any) {
      console.error('Error fetching component data:', error);
      return null;
    }
  };

  const publishToIndex = async (libraryItem: LibraryItem) => {
    // Get NFT.Storage token from localStorage
    const nftStorageToken = localStorage.getItem('splinewright-nft-storage-token');
    
    if (!nftStorageToken) {
      alert('Please add your NFT.Storage token in Settings');
      return;
    }

    let cid: string;

    try {
      // Upload component data to NFT.Storage
      const componentJson = JSON.stringify(libraryItem.data);
      const blob = new Blob([componentJson], { type: 'application/json' });
      
      const formData = new FormData();
      formData.append('file', blob, `${libraryItem.id}.json`);

      const uploadResponse = await fetch('https://api.nft.storage/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${nftStorageToken}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json().catch(() => ({ error: { message: 'Failed to upload to NFT.Storage' } }));
        throw new Error(error.error?.message || 'Failed to upload to NFT.Storage');
      }

      const uploadResult = await uploadResponse.json();
      
      // Handle different possible response formats
      if (uploadResult.ok === false) {
        throw new Error(uploadResult.error?.message || 'Upload failed');
      }
      
      // Extract CID from response (could be value.cid or just cid)
      cid = uploadResult.value?.cid || uploadResult.cid;

      if (!cid) {
        throw new Error('No CID returned from NFT.Storage');
      }
    } catch (error: any) {
      console.error('Error uploading to NFT.Storage:', error);
      alert(`Failed to upload to NFT.Storage: ${error.message}`);
      return;
    }
    
    // Store component data in localStorage as backup
    localStorage.setItem(`splinewright-component-${cid}`, JSON.stringify(libraryItem.data));
    
    // Add to local library
    const updatedItem = { ...libraryItem, cid };
    const updatedLibraryItems = [...libraryItems, updatedItem];
    setLibraryItems(updatedLibraryItems);
    localStorage.setItem('splinewright-library', JSON.stringify(updatedLibraryItems));
    
    alert(`✓ Published! CID: ${cid}`);
  };

  const exportLibrary = () => {
    if (publishedLibrary.length === 0) {
      alert('Your library is empty. Publish some components first.');
      return;
    }

    // Create export data with all published items
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      items: publishedLibrary,
    };

    // Create and download JSON file
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    link.download = `splinewright-library-${dateStr}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    alert(`Library exported successfully!\n\n${publishedLibrary.length} component(s) exported.`);
  };

  const importLibrary = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event: any) => {
        try {
          const importData = JSON.parse(event.target.result);
          
          if (!importData.items || !Array.isArray(importData.items)) {
            throw new Error('Invalid library file format');
          }

          // Merge imported items with existing publishedLibrary (avoid duplicates by ID)
          const existingIds = new Set(publishedLibrary.map(item => item.id));
          const newItems = importData.items.filter((item: any) => !existingIds.has(item.id));
          
          if (newItems.length === 0) {
            alert('No new components found in the imported file. All items already exist in your library.');
            return;
          }

          // Count items by type
          const counts = {
            enzymes: 0,
            characters: 0,
            tensionCodons: 0,
          };

          newItems.forEach((item: any) => {
            if (item.type === 'enzyme') {
              counts.enzymes++;
            } else if (item.type === 'character') {
              counts.characters++;
            } else if (item.type === 'tensionCodon') {
              counts.tensionCodons++;
            }
          });

          // Update publishedLibrary
          const updatedLibrary = [...publishedLibrary, ...newItems];
          setPublishedLibrary(updatedLibrary);
          localStorage.setItem('publishedLibrary', JSON.stringify(updatedLibrary));

          // Build summary message
          const summaryParts = [];
          if (counts.enzymes > 0) summaryParts.push(`${counts.enzymes} enzyme${counts.enzymes !== 1 ? 's' : ''}`);
          if (counts.characters > 0) summaryParts.push(`${counts.characters} character${counts.characters !== 1 ? 's' : ''}`);
          if (counts.tensionCodons > 0) summaryParts.push(`${counts.tensionCodons} tension codon${counts.tensionCodons !== 1 ? 's' : ''}`);

          const summary = summaryParts.join(', ');
          alert(`Library imported successfully!\n\nImported ${summary}.`);
        } catch (error: any) {
          console.error('Error importing library:', error);
          alert(`Failed to import library: ${error.message}`);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const calculateCompatibility = (libraryItem: LibraryItem | LibraryIndexItem): number => {
    let score = 0;
    const maxScore = 100;

    // Handle LibraryIndexItem (from remote index) - we need to fetch data or use cached
    if ('cid' in libraryItem && !('data' in libraryItem)) {
      // For remote items, we'll calculate based on name/type matching
      // Full compatibility would require fetching the data
      if (characters.length > 0) score += 30;
      if (savedEnzymes.length > 0) score += 20;
      if (tensionCodons.length > 0) score += 20;
      return Math.min(score, maxScore);
    }

    if (libraryItem.type === 'Enzyme') {
      const enzyme = (libraryItem as LibraryItem).data as Enzyme;
      
      // Check if we have a character with the same name (50 points)
      const matchingCharacter = characters.find(c => 
        c.name.toLowerCase() === enzyme.characterName.toLowerCase()
      );
      if (matchingCharacter) {
        score += 50;
        
        // Check if character doesn't already have this enzyme (30 points)
        const hasEnzyme = matchingCharacter.attachedEnzymes.some(ae => {
          const attachedEnzyme = savedEnzymes.find(e => e.id === ae.enzymeId);
          return attachedEnzyme && 
                 attachedEnzyme.characterName === enzyme.characterName &&
                 attachedEnzyme.traitName === enzyme.traitName;
        });
        if (!hasEnzyme) {
          score += 30;
        }
      } else {
        // Check if we have any characters that could use this (20 points)
        if (characters.length > 0) {
          score += 20;
        }
      }

      // Check if we have similar enzymes (could indicate compatibility) (20 points)
      const hasSimilarEnzyme = savedEnzymes.some(e => 
        e.characterName.toLowerCase() === enzyme.characterName.toLowerCase() ||
        e.traitName.toLowerCase() === enzyme.traitName.toLowerCase()
      );
      if (hasSimilarEnzyme) {
        score += 20;
      }
    } else if (libraryItem.type === 'Tension Codon') {
      const codon = (libraryItem as LibraryItem).data as TensionCodon;
      
      // Check for conflicts with existing codons
      const conflictingCodon = tensionCodons.find(tc => 
        tc.name.toLowerCase() === codon.name.toLowerCase() ||
        (tc.description && codon.description && 
         tc.description.toLowerCase() === codon.description.toLowerCase())
      );
      
      if (conflictingCodon) {
        // High conflict - low score (10 points)
        score += 10;
      } else {
        // No direct conflict - good compatibility (60 points)
        score += 60;
        
        // Check if we have characters that could be affected (30 points)
        if (characters.length >= 2) {
          score += 30;
        } else if (characters.length > 0) {
          score += 15;
        }
      }

      // Check if we have tension codons already (indicates we use this feature) (10 points)
      if (tensionCodons.length > 0) {
        score += 10;
      }
    } else if (libraryItem.type === 'Character') {
      const character = (libraryItem as LibraryItem).data as Character;
      
      // Check if we already have this character (conflict - low score)
      const existingCharacter = characters.find(c => 
        c.name.toLowerCase() === character.name.toLowerCase()
      );
      
      if (existingCharacter) {
        score += 10; // Low score for duplicates
      } else {
        // New character - good compatibility (70 points)
        score += 70;
        
        // Check if character has enzymes that we could use (30 points)
        if (character.attachedEnzymes.length > 0) {
          score += 30;
        }
      }
    }

    return Math.min(score, maxScore);
  };

  // Combine local and remote items
  const allLibraryItems = [
    ...libraryItems.map(item => ({ ...item, source: 'local' as const })),
    ...remoteLibraryItems.map(item => ({ ...item, source: 'remote' as const })),
  ];

  const filteredLibraryItems = allLibraryItems
    .filter(item => {
      if (!librarySearchQuery.trim()) return true;
      const query = librarySearchQuery.toLowerCase();
      return (
        item.name.toLowerCase().includes(query) ||
        item.author.toLowerCase().includes(query) ||
        item.tags.some(tag => tag.toLowerCase().includes(query)) ||
        item.description.toLowerCase().includes(query)
      );
    })
    .map(item => ({
      ...item,
      compatibility: calculateCompatibility(item),
    }))
    .sort((a, b) => b.compatibility - a.compatibility);
  void filteredLibraryItems;
  void filteredLibraryItems;


  // Guide content
  const guideContent = {
    whatIsEnzyme: `Enzymes are dynamic behavioral patterns that define how characters react to specific situations. Unlike static traits, enzymes create active, responsive behaviors that generate drama and conflict.

**Enzymes vs Static Traits:**
- Static traits describe what a character is (e.g., "brave," "loyal")
- Enzymes describe how a character reacts when triggered (e.g., "when authority is questioned, they become defensive and aggressive")
- Enzymes create action; static traits create description
- Enzymes generate story events; static traits generate character descriptions

**Why Enzymes Matter:**
Enzymes transform personality into plot. A character with "loyalty" as a static trait might be described as loyal, but a character with a loyalty enzyme will actively defend their chosen person, make sacrifices, and create dramatic moments through their actions.`,

    fiveComponents: `Every enzyme consists of five essential components that work together to create dynamic character behavior:

**1. Trait**
The personality aspect being modeled (e.g., "Wounded Pride," "Self-Sabotage," "Misplaced Loyalty"). The trait should have internal tension—it should be both a strength and a weakness.

**2. Triggers**
Specific situations that activate the enzyme. Triggers should be concrete and actionable, not abstract feelings. Good triggers create clear "if-then" scenarios.

**3. Responses**
How the character behaves when triggered. Responses should be patterns, not scripts—they describe behavioral tendencies, not exact dialogue or actions.

**4. Blindspots**
What the character fails to notice or understand. Blindspots create dramatic irony and opportunities for conflict. They're the gaps in the character's awareness.

**5. Mutations**
Variations that can occur under specific conditions. Mutations add complexity and prevent enzymes from becoming predictable. They represent how the trait might evolve or break down.`,

    choosingTraits: `**Good Traits with Internal Tension:**
- Wounded Pride (pride protects but also blinds)
- Self-Sabotage (self-protection that destroys)
- Misplaced Loyalty (loyalty to wrong people/ideals)
- Righteous Anger (justice that becomes vengeance)
- Protective Instinct (protection that becomes control)

**Traits to Avoid:**
- Pure virtues without flaws (e.g., "always kind")
- Pure vices without complexity (e.g., "always evil")
- Traits without internal tension
- Traits that are too abstract (e.g., "goodness")
- Traits that don't create conflict

**Intensity Levels:**
- **Low (0.2-0.4):** Subtle influence, occasional activation
- **Medium (0.5-0.7):** Regular influence, frequent activation
- **High (0.8-1.0):** Dominant trait, almost always active

Choose intensity based on how central the trait is to the character's identity and how often it should drive their behavior.`,

    designingTriggers: `**Trigger Categories:**

**Social Triggers:**
- "When someone questions their authority in front of others"
- "When excluded from a group decision"
- "When their social status is threatened"

**Status Triggers:**
- "When their achievements are overlooked"
- "When someone gets credit for their work"
- "When their position is challenged"

**Relational Triggers:**
- "When a close ally shows doubt"
- "When someone they trust betrays them"
- "When their loyalty is tested"

**Achievement Triggers:**
- "When they fail at something important"
- "When success is within reach but threatened"
- "When their competence is questioned"

**Specificity:**
Make triggers specific enough to create clear scenarios but broad enough to apply to multiple situations. "When authority is questioned" is better than "When Bob questions their authority on Tuesday."

**Sensitivity:**
More triggers = more opportunities for activation. Fewer triggers = more focused, intense responses. Balance based on the trait's role in the story.`,

    designingResponses: `**Immediate vs Delayed:**
- **Immediate:** Character reacts instantly (e.g., "immediately becomes defensive")
- **Delayed:** Character processes then reacts (e.g., "withdraws, then plans retaliation")
- Mix both types for variety

**Patterns Not Scripts:**
Responses should describe behavioral tendencies, not exact actions:
- ✅ Good: "Becomes defensive and questions the other person's motives"
- ❌ Bad: "Says 'How dare you question me!' and storms out"

**Framing:**
Frame responses to show how the character interprets the trigger:
- "Interprets the question as an attack on their competence"
- "Sees the exclusion as a personal rejection"
- "Views the challenge as a threat to their identity"

**Proportionality:**
Responses should match the trigger's intensity and the enzyme's intensity. A high-intensity enzyme with a low-intensity trigger might still create a strong response, but it should feel justified by the character's psychology.`,

    designingBlindspots: `**Types of Blindspots:**

**Self-Justification Blindspots:**
- Character doesn't see their own harmful behavior
- "Fails to notice when their protection becomes control"
- "Doesn't recognize their loyalty is misplaced"

**Relational Blindspots:**
- Character misunderstands others' motivations
- "Doesn't see that their ally is using them"
- "Fails to notice when their help is unwanted"

**Consequence Blindspots:**
- Character doesn't see the results of their actions
- "Doesn't realize their defense is pushing people away"
- "Fails to notice their protection is creating dependency"

**Crack Conditions:**
Define when the blindspot might break:
- "When the consequences become undeniable"
- "When someone they trust points it out directly"
- "When the pattern repeats too many times"

Blindspots create dramatic irony—the audience sees what the character cannot.`,

    designingMutations: `**Mutation Types:**

**Hardening:**
The trait becomes more extreme and rigid:
- "Pride becomes arrogance"
- "Protection becomes control"
- "Loyalty becomes blind obedience"
- Probability: 30-40% under stress

**Collapse:**
The trait breaks down or inverts:
- "Pride becomes self-doubt"
- "Protection becomes abandonment"
- "Loyalty becomes betrayal"
- Probability: 20-30% under extreme pressure

**Transcendence:**
The character grows beyond the trait:
- "Pride becomes humility"
- "Protection becomes empowerment"
- "Loyalty becomes wisdom"
- Probability: 10-20% with support and insight

**Setting Probabilities:**
- Higher probability = more likely to occur
- Lower probability = rare but powerful when it happens
- Total probabilities should add up to 100% or less
- Leave room for the trait to remain unchanged`,

    completeExamples: `**Example 1: Wounded Pride**

**Trait:** Wounded Pride
**Intensity:** 0.8

**Triggers:**
- When their authority is questioned in front of others
- When someone gets credit for their work
- When their competence is doubted
- When they're excluded from important decisions

**Responses:**
- Immediately becomes defensive and questions the other person's motives
- Interprets the situation as a personal attack
- Seeks to reassert dominance through knowledge or position
- May withdraw and plan a way to prove themselves

**Blindspots:**
- Fails to notice when their defensiveness pushes people away
- Doesn't see that their need to prove themselves creates conflict
- Doesn't recognize when their authority is actually being respected

**Mutations:**
- Hardening (40%): Pride becomes arrogance, dismisses all criticism
- Collapse (25%): Pride breaks, becomes self-doubt and withdrawal
- Transcendence (15%): Recognizes the pattern, seeks genuine feedback

---

**Example 2: Self-Sabotage**

**Trait:** Self-Sabotage
**Intensity:** 0.7

**Triggers:**
- When success is within reach
- When they're about to achieve something important
- When others express confidence in them
- When they're about to commit to something long-term

**Responses:**
- Creates obstacles or excuses to avoid success
- Finds reasons why they don't deserve it
- Withdraws or undermines their own progress
- Seeks out distractions or alternative paths

**Blindspots:**
- Doesn't recognize the pattern of self-sabotage
- Believes their excuses are legitimate
- Fails to see they're protecting themselves from disappointment

**Mutations:**
- Hardening (35%): Sabotage becomes more extreme, actively destroys opportunities
- Collapse (30%): Pattern breaks, they finally accept success
- Transcendence (20%): Recognizes the pattern, seeks help to change

---

**Example 3: Misplaced Loyalty**

**Trait:** Misplaced Loyalty
**Intensity:** 0.75

**Triggers:**
- When their chosen person/group is criticized
- When loyalty is tested by conflicting values
- When they see their loyalty is being exploited
- When someone questions their allegiance

**Responses:**
- Defends the person/group even when wrong
- Rationalizes harmful behavior
- Prioritizes loyalty over personal values
- Becomes hostile toward critics

**Blindspots:**
- Doesn't see when their loyalty is being exploited
- Fails to notice the harm their loyalty causes
- Doesn't recognize when the object of loyalty has changed

**Mutations:**
- Hardening (30%): Loyalty becomes blind obedience, ignores all evidence
- Collapse (35%): Loyalty breaks, feels betrayed and abandoned
- Transcendence (25%): Loyalty becomes wisdom, learns to choose wisely`,

    testingEnzymes: `**Scene Test:**
Create a scene where the enzyme would activate. Does it create interesting conflict? Does the response feel natural? Does it reveal character?

**Variation Test:**
Try the same trigger with different contexts. Does the enzyme create variety, or does it always play out the same way? Mutations should help here.

**Arc Test:**
Imagine the enzyme playing out over multiple scenes. Does it create a satisfying character arc? Does it have room to evolve or change?

**Interaction Test:**
How does this enzyme interact with other characters' enzymes? Do they create interesting dynamics? Do they generate conflict and drama?

**Questions to Ask:**
- Does this enzyme create story events, not just character description?
- Does it have internal tension (both strength and weakness)?
- Are the triggers specific enough to be actionable?
- Do the responses create conflict and reveal character?
- Do blindspots create dramatic irony?
- Do mutations add complexity without making it unpredictable?`,

    commonMistakes: `**Mistake 1: Too Abstract**
❌ "When they feel threatened"
✅ "When their authority is questioned in front of others"

**Mistake 2: Scripts Instead of Patterns**
❌ "Says 'How dare you!' and storms out"
✅ "Becomes defensive and questions the other person's motives"

**Mistake 3: No Internal Tension**
❌ "Always kind and helpful"
✅ "Protective instinct that becomes controlling"

**Mistake 4: Too Many Triggers**
Having 20+ triggers makes the enzyme activate constantly. Focus on 4-8 key triggers that create the most drama.

**Mistake 5: Responses Don't Create Conflict**
❌ "Feels sad and goes home"
✅ "Withdraws but plans to prove themselves later"

**Mistake 6: Blindspots Are Too Obvious**
Blindspots should be believable. The character should have reasons for not seeing the truth.

**Mistake 7: Mutations Don't Make Sense**
Mutations should feel like natural evolutions of the trait, not random changes.

**How to Fix:**
- Make triggers more specific and actionable
- Focus responses on behavioral patterns
- Ensure traits have internal tension
- Limit triggers to the most dramatic situations
- Design responses that create conflict
- Make blindspots believable but impactful
- Connect mutations to the trait's psychology`,

    quickReference: `**Enzyme Anatomy:**
- Trait: Personality aspect with internal tension
- Intensity: 0.0-1.0 (how strongly it influences behavior)
- Triggers: 4-8 specific situations that activate the enzyme
- Responses: 3-5 behavioral patterns (not scripts)
- Blindspots: 2-3 things the character fails to notice
- Mutations: 2-3 variations with probabilities (total ≤100%)

**Intensity Guide:**
- 0.0-0.3: Subtle, occasional influence
- 0.4-0.6: Regular, moderate influence
- 0.7-0.9: Strong, frequent influence
- 1.0: Dominant, almost always active

**Mutation Probability Templates:**
- Conservative: Hardening 40%, Collapse 20%, Transcendence 10% (70% total)
- Balanced: Hardening 35%, Collapse 30%, Transcendence 20% (85% total)
- Dynamic: Hardening 30%, Collapse 35%, Transcendence 25% (90% total)

**Trigger Categories:**
- Social (status, reputation, group dynamics)
- Status (achievement, recognition, position)
- Relational (trust, loyalty, connection)
- Achievement (success, failure, competence)

**Response Patterns:**
- Immediate (instant reaction)
- Delayed (processes then reacts)
- Escalating (starts small, builds)
- Withdrawing (pulls back, plans)

**Blindspot Types:**
- Self-justification (doesn't see own harm)
- Relational (misreads others)
- Consequence (doesn't see results)`
  };

  // Helper function to render guide sections
  const renderGuideSection = (id: string, title: string, content: string, searchQuery: string) => {
    const isExpanded = expandedSections.has(id);
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query || title.toLowerCase().includes(query) || content.toLowerCase().includes(query);
    
    if (!matchesSearch) return null;

    const toggleSection = () => {
      const newExpanded = new Set(expandedSections);
      if (isExpanded) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      setExpandedSections(newExpanded);
    };

    // Format content with markdown-like formatting
    const formatContent = (text: string) => {
      return text.split('\n').map((line, idx) => {
        if (line.startsWith('**') && line.endsWith('**')) {
          return <strong key={idx} style={{ color: '#64b5f6', fontWeight: '600' }}>{line.replace(/\*\*/g, '')}</strong>;
        }
        if (line.trim() === '') {
          return <br key={idx} />;
        }
        if (line.startsWith('- ')) {
          return (
            <div key={idx} style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
              • {line.substring(2)}
            </div>
          );
        }
        if (line.startsWith('✅') || line.startsWith('❌')) {
          return (
            <div key={idx} style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '8px' }}>
              {line}
            </div>
          );
        }
        return <p key={idx} style={{ marginTop: '12px', marginBottom: '12px', lineHeight: '1.6' }}>{line}</p>;
      });
    };

    return (
      <div
        key={id}
        style={{
          marginBottom: '24px',
          backgroundColor: '#222222',
          border: '1px solid #3a3a3a',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <button
          onClick={toggleSection}
          style={{
            width: '100%',
            padding: '20px 24px',
            backgroundColor: 'transparent',
            border: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#f5f5f5',
            fontSize: '1.2rem',
            fontWeight: '600',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2a2a2a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span>{title}</span>
          <span style={{ fontSize: '1.5rem', color: '#64b5f6' }}>
            {isExpanded ? '−' : '+'}
          </span>
        </button>
        {isExpanded && (
          <div
            style={{
              padding: '24px',
              borderTop: '1px solid #3a3a3a',
              color: '#e5e5e5',
              lineHeight: '1.6',
            }}
          >
            {formatContent(content)}
          </div>
        )}
      </div>
    );
  };

  // Avoid CI lint failures for unused helpers (guide refactor in progress)
  void guideContent;
  void renderGuideSection;

  const appContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    color: '#e5e5e5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  };

  const mainContentContainerStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    flexDirection: isMobile ? 'column' : 'row',
  };

  const sidebarStyle: React.CSSProperties = {
    width: sidebarWidth,
    backgroundColor: '#222222',
    borderRight: isMobile ? 'none' : '1px solid #3a3a3a',
    borderBottom: isMobile ? '1px solid #3a3a3a' : 'none',
    padding: containerPadding,
    paddingBottom: isMobile ? '80px' : undefined,
    overflowY: 'auto',
    flexShrink: 0,
  };

  const sidebarHeaderStyle: React.CSSProperties = {
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #3a3a3a',
  };

  const sidebarTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#f5f5f5',
  };

  const enzymeListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const enzymeItemStyle = (isSelected: boolean, isHovered: boolean): React.CSSProperties => ({
    padding: '12px',
    backgroundColor: isSelected ? '#2a4a6a' : isHovered ? '#2a2a2a' : '#252525',
    border: isSelected ? '1px solid #64b5f6' : '1px solid #3a3a3a',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
  });

  const enzymeItemHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '6px',
  };

  const enzymeNameStyle: React.CSSProperties = {
    fontWeight: '600',
    color: '#f5f5f5',
    fontSize: '0.95rem',
    flex: 1,
  };

  const enzymeDeleteButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#ff6666',
    cursor: 'pointer',
    fontSize: '1.2rem',
    padding: '0 4px',
    lineHeight: '1',
    opacity: 0.7,
    transition: 'opacity 0.2s',
  };

  const enzymeTraitStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: '#a0a0a0',
    marginBottom: '4px',
  };

  const enzymeMetaStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: '#666',
    display: 'flex',
    gap: '8px',
  };

  const containerStyle: React.CSSProperties = {
    flex: 1,
    maxWidth: isMobile ? '100%' : '1200px',
    margin: isMobile ? '0' : '0 auto',
    padding: isMobile ? '16px' : containerPadding,
    paddingBottom: isMobile ? '80px' : undefined,
    overflowY: 'auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '50px',
    textAlign: 'center',
    paddingBottom: '30px',
    borderBottom: '1px solid #3a3a3a',
  };

  const h1Style: React.CSSProperties = {
    fontSize: '3rem',
    fontWeight: '600',
    margin: '0 0 12px 0',
    color: '#f5f5f5',
    letterSpacing: '-0.5px',
  };

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '12px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '1rem',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #4a4a4a',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const sliderContainerStyle: React.CSSProperties = {
    marginTop: '16px',
    position: 'relative',
  };

  const sliderWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  };

  const sliderStyle: React.CSSProperties = {
    flex: 1,
    height: '8px',
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: '#3a3a3a',
    WebkitAppearance: 'none',
    appearance: 'none',
  };

  const intensityValueStyle: React.CSSProperties = {
    display: 'inline-block',
    minWidth: '60px',
    padding: '8px 12px',
    backgroundColor: '#2a2a2a',
    borderRadius: '6px',
    fontWeight: '600',
    color: '#64b5f6',
    fontSize: '1.1rem',
    textAlign: 'center',
    border: '1px solid #4a4a4a',
  };

  const sectionsContainerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '32px',
    marginTop: '48px',
  };

  const sectionStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '12px',
    padding: '28px',
    backgroundColor: '#222222',
  };

  const sectionTitleStyle: React.CSSProperties = {
    margin: '0 0 10px 0',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#f5f5f5',
  };

  const sectionDescStyle: React.CSSProperties = {
    margin: '0 0 24px 0',
    fontSize: '0.95rem',
    color: '#a0a0a0',
    lineHeight: '1.5',
  };

  const addContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
  };

  const getButtonStyle = (isHovered: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    backgroundColor: isHovered ? '#4a9eff' : '#3d8bfd',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s, transform 0.1s, box-shadow 0.2s',
    transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
    boxShadow: isHovered ? '0 4px 12px rgba(61, 139, 253, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
  });

  const itemsListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: '4px',
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const itemTextStyle: React.CSSProperties = {
    flex: 1,
    color: '#e5e5e5',
    fontSize: '0.95rem',
  };

  const getRemoveButtonStyle = (isHovered: boolean): React.CSSProperties => ({
    background: isHovered ? '#ff4444' : 'transparent',
    border: 'none',
    color: isHovered ? 'white' : '#ff6666',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '4px 10px',
    lineHeight: '1',
    borderRadius: '4px',
    transition: 'background-color 0.2s, color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    height: '32px',
  });

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic',
    padding: '40px 20px',
    fontSize: '0.95rem',
  };

  const actionButtonsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '32px',
    marginBottom: '32px',
  };

  const getNewButtonStyle = (isHovered: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    backgroundColor: isHovered ? '#4a4a4a' : '#3a3a3a',
    color: 'white',
    border: '1px solid #4a4a4a',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s, transform 0.1s, box-shadow 0.2s',
    transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
    boxShadow: isHovered ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
  });

  const emptySidebarStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic',
    padding: '40px 20px',
    fontSize: '0.9rem',
  };

  const blindspotItemStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '16px',
  };

  const blindspotFieldStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const blindspotLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '0.9rem',
  };

  const blindspotTextareaStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '60px',
  };

  const blindspotCheckboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
  };

  const blindspotCheckboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#64b5f6',
  };

  const blindspotCheckboxLabelStyle: React.CSSProperties = {
    color: '#d0d0d0',
    fontSize: '0.9rem',
    cursor: 'pointer',
  };

  const blindspotHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  };

  const blindspotTitleStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
  };

  const mutationItemStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '16px',
  };

  const mutationFieldStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const mutationLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '0.9rem',
  };

  const mutationInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
  };

  const mutationTextareaStyle: React.CSSProperties = {
    ...mutationInputStyle,
    resize: 'vertical',
    minHeight: '80px',
  };

  const mutationHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  };

  const mutationTitleStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
  };

  const mutationProbabilityContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const mutationProbabilityInputStyle: React.CSSProperties = {
    width: '100px',
    padding: '8px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
  };

  const mutationProbabilityLabelStyle: React.CSSProperties = {
    color: '#d0d0d0',
    fontSize: '0.9rem',
  };

  // Tab navigation styles
  const tabContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #3a3a3a',
    backgroundColor: '#1a1a1a',
    padding: '0',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  };

  const tabsGroupStyle: React.CSSProperties = {
    display: 'flex',
  };

  const exportImportGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    padding: '8px 16px',
  };

  const exportImportButtonStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: '#3a3a3a',
    color: 'white',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
  };

  // Cobot panel styles
  const cobotPanelStyle: React.CSSProperties = {
    position: 'fixed',
    right: cobotPanelOpen ? '0' : '-400px',
    top: '60px',
    width: '380px',
    height: 'calc(100vh - 60px)',
    backgroundColor: '#222222',
    borderLeft: '1px solid #3a3a3a',
    boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.3)',
    transition: 'right 0.3s ease',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
  };

  const cobotHeaderStyle: React.CSSProperties = {
    padding: '20px',
    borderBottom: '1px solid #3a3a3a',
    backgroundColor: '#1a1a1a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const cobotTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const cobotCloseButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#a0a0a0',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s, color 0.2s',
  };

  const cobotContentStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
  };

  const cobotEmptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#666',
    padding: '60px 20px',
    fontSize: '0.95rem',
    lineHeight: '1.6',
  };

  const cobotSuggestionItemStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '12px',
    color: '#e5e5e5',
    fontSize: '0.95rem',
    lineHeight: '1.6',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const cobotSuggestionSectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const cobotSuggestionSectionTitleStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#f5f5f5',
    marginBottom: '12px',
  };

  const cobotApiKeyInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    fontFamily: 'monospace',
    marginBottom: '12px',
  };

  const cobotSettingsButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#a0a0a0',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'background-color 0.2s, color 0.2s',
  };

  const cobotGenerateButtonStyle: React.CSSProperties = {
    padding: '10px 20px',
    backgroundColor: '#64b5f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  // Encounter styles
  const encounterItemStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#222222',
    border: '1px solid #3a3a3a',
    borderRadius: '12px',
    marginBottom: '16px',
  };

  const encounterHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  };

  const encounterTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
    flex: 1,
  };

  const encounterDescriptionStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    color: '#d0d0d0',
    lineHeight: '1.6',
    marginBottom: '12px',
  };

  const encounterCharactersStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  };

  const encounterCharacterTagStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#e5e5e5',
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '16px 24px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: isActive ? '2px solid #64b5f6' : '2px solid transparent',
    color: isActive ? '#f5f5f5' : '#a0a0a0',
    fontSize: '1rem',
    fontWeight: isActive ? '600' : '400',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  // Help modal styles
  const helpModalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const helpModalStyle: React.CSSProperties = {
    backgroundColor: '#2a2a2a',
    borderRadius: '12px',
    border: '1px solid #4a4a4a',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  };

  const helpModalHeaderStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: '1px solid #3a3a3a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const helpModalTitleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
  };

  const helpModalCloseButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: '#a0a0a0',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s',
  };

  const helpModalContentStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const helpSectionStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const helpSectionTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#64b5f6',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #3a3a3a',
  };

  const helpSectionContentStyle: React.CSSProperties = {
    color: '#d0d0d0',
    lineHeight: '1.8',
    fontSize: '0.95rem',
  };

  const helpListItemStyle: React.CSSProperties = {
    marginBottom: '12px',
    paddingLeft: '8px',
  };

  const helpGlossaryTermStyle: React.CSSProperties = {
    fontWeight: '600',
    color: '#f5f5f5',
    marginBottom: '4px',
  };
  void helpGlossaryTermStyle;
  void helpGlossaryTermStyle;

  // Character view styles
  const characterItemStyle = (isSelected: boolean, isHovered: boolean): React.CSSProperties => ({
    padding: '16px',
    backgroundColor: isSelected ? '#2a4a6a' : isHovered ? '#2a2a2a' : '#252525',
    border: isSelected ? '1px solid #64b5f6' : '1px solid #3a3a3a',
    borderRadius: '8px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  const characterNameStyle: React.CSSProperties = {
    fontWeight: '600',
    color: '#f5f5f5',
    fontSize: '1.1rem',
    marginBottom: '8px',
  };

  const characterDescriptionStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    color: '#a0a0a0',
    marginBottom: '12px',
    lineHeight: '1.5',
  };

  const attachedEnzymeItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
    marginBottom: '8px',
  };

  const attachedEnzymeInfoStyle: React.CSSProperties = {
    flex: 1,
  };

  const attachedEnzymeNameStyle: React.CSSProperties = {
    fontWeight: '500',
    color: '#f5f5f5',
    fontSize: '0.95rem',
    marginBottom: '4px',
  };

  const attachedEnzymeTraitStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: '#a0a0a0',
  };

  const attachedEnzymeIntensityStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginRight: '8px',
  };

  const intensitySliderSmallStyle: React.CSSProperties = {
    width: '100px',
    height: '4px',
    borderRadius: '2px',
    outline: 'none',
    backgroundColor: '#3a3a3a',
    WebkitAppearance: 'none',
    appearance: 'none',
  };

  const intensityValueSmallStyle: React.CSSProperties = {
    minWidth: '45px',
    textAlign: 'right',
    fontSize: '0.9rem',
    color: '#64b5f6',
    fontWeight: '600',
  };

  const enzymeSelectStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
  };

  // Tension Codon styles
  const tensionCodonItemStyle: React.CSSProperties = {
    padding: '24px',
    backgroundColor: '#222222',
    border: '1px solid #3a3a3a',
    borderRadius: '12px',
    marginBottom: '24px',
  };

  const tensionCodonHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  };

  const tensionCodonTitleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#f5f5f5',
    margin: 0,
  };

  const tensionCodonFieldStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const tensionCodonLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#d0d0d0',
    fontSize: '0.95rem',
  };

  const tensionCodonTextareaStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #4a4a4a',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: '#2a2a2a',
    color: '#f5f5f5',
    transition: 'border-color 0.2s, background-color 0.2s',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px',
  };

  const tensionCodonPressureContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '12px',
  };

  const tensionCodonPressureSliderStyle: React.CSSProperties = {
    flex: 1,
    height: '8px',
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: '#3a3a3a',
    WebkitAppearance: 'none',
    appearance: 'none',
  };

  const tensionCodonPressureValueStyle: React.CSSProperties = {
    minWidth: '60px',
    padding: '8px 12px',
    backgroundColor: '#2a2a2a',
    borderRadius: '6px',
    fontWeight: '600',
    color: '#64b5f6',
    fontSize: '1.1rem',
    textAlign: 'center',
    border: '1px solid #4a4a4a',
  };

  const affectedCharactersContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  };

  const characterCheckboxItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
  };

  const characterCheckboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#64b5f6',
  };

  const characterCheckboxLabelStyle: React.CSSProperties = {
    color: '#e5e5e5',
    fontSize: '0.95rem',
    cursor: 'pointer',
    flex: 1,
  };

  // ============================================
  // ROBOT UI COMPONENT (mobile)
  // ============================================
  const renderRobot = () => {
    if (!isMobile) return null;

    const suggestions: Record<string, string[]> = {
      create: ['Family drama', 'Friends in crisis', 'Love triangle'],
      encounters: ['Generate 5 encounters', 'Create a confrontation', 'Breathing room'],
      help: ['What are enzymes?', 'How do mutations work?', 'Tips for characters'],
    };

    const closeRobot = () => {
      setRobotMode(null);
      setShowRobot(false);
    };

    return (
      <>
        {/* Floating Button */}
        {!robotMode && !showRobot && !showChatbot && !showEncounterForge && (
          <button
            onClick={() => {
              setShowRobot(true);
              setRobotMode('menu');
            }}
            style={{
              position: 'fixed',
              bottom: '80px',
              right: '16px',
              width: '60px',
              height: '60px',
              borderRadius: '30px',
              backgroundColor: '#238636',
              border: 'none',
              boxShadow: '0 4px 16px rgba(35,134,54,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              zIndex: 1000,
              cursor: 'pointer',
            }}
            title="Spline (AI Assistant)"
          >
            🤖
          </button>
        )}

        {/* Menu */}
        {robotMode === 'menu' && (
          <>
            <div
              onClick={closeRobot}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                zIndex: 1400,
              }}
            />
            <div
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: '#161b22',
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                padding: '20px',
                paddingBottom: '90px',
                zIndex: 1500,
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '4px',
                  backgroundColor: '#30363d',
                  borderRadius: '2px',
                  margin: '0 auto 20px',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <span style={{ fontSize: '36px' }}>🤖</span>
                <div>
                  <h3 style={{ margin: 0, color: '#f0f6fc' }}>Hi! I'm Spline</h3>
                  <p style={{ margin: 0, color: '#8b949e', fontSize: '14px' }}>Your AI story assistant</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={() => {
                    setRobotTask('create');
                    setRobotMode('chat');
                    setRobotMessages([
                      {
                        id: `w1-${Date.now()}`,
                        role: 'robot',
                        content: "*beep boop* 🤖 Let's create a storyworld! What kind of story do you want to make?",
                        timestamp: new Date(),
                      },
                    ]);
                  }}
                  style={{
                    padding: '18px',
                    backgroundColor: '#238636',
                    border: 'none',
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '28px' }}>🧬</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: 'white', fontWeight: '600' }}>Create Storyworld</div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                      Build characters, enzymes & tensions
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setRobotTask('encounters');
                    setRobotMode('chat');
                    setRobotMessages([
                      {
                        id: `w2-${Date.now()}`,
                        role: 'robot',
                        content: currentStoryworld
                          ? `*processing* 🤖 I see "${currentStoryworld}" with ${characters.length} characters! What encounters shall I forge?`
                          : '*beep* 🤖 Select a storyworld first, then I can generate encounters!',
                        timestamp: new Date(),
                      },
                    ]);
                  }}
                  style={{
                    padding: '18px',
                    backgroundColor: '#6e40c9',
                    border: 'none',
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    cursor: 'pointer',
                    opacity: currentStoryworld ? 1 : 0.6,
                  }}
                >
                  <span style={{ fontSize: '28px' }}>🔨</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: 'white', fontWeight: '600' }}>Generate Encounters</div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                      {currentStoryworld ? 'Create scenes for your story' : 'Select storyworld first'}
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setRobotTask('help');
                    setRobotMode('chat');
                    setRobotMessages([
                      {
                        id: `w3-${Date.now()}`,
                        role: 'robot',
                        content:
                          "*whirr* 🤖 I'm here to help! Ask me about enzymes, mutations, characters, or anything else!",
                        timestamp: new Date(),
                      },
                    ]);
                  }}
                  style={{
                    padding: '18px',
                    backgroundColor: '#21262d',
                    border: '1px solid #30363d',
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '28px' }}>❓</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: '#f0f6fc', fontWeight: '600' }}>Help & Learn</div>
                    <div style={{ color: '#8b949e', fontSize: '13px' }}>Ask questions, get guidance</div>
                  </div>
                </button>
              </div>

              <button
                onClick={closeRobot}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'none',
                  border: 'none',
                  color: '#8b949e',
                  fontSize: '28px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
          </>
        )}

        {/* Chat */}
        {robotMode === 'chat' && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#0d1117',
              zIndex: 2000,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '14px 16px',
                backgroundColor: '#161b22',
                borderBottom: '1px solid #30363d',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <button
                onClick={() => setRobotMode('menu')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#58a6ff',
                  fontSize: '20px',
                  padding: '8px',
                  cursor: 'pointer',
                }}
              >
                ←
              </button>
              <span style={{ fontSize: '28px' }}>🤖</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#f0f6fc', fontWeight: '600' }}>Spline</div>
                <div style={{ color: '#8b949e', fontSize: '12px' }}>
                  {robotTask === 'create' && '🧬 Creating storyworld'}
                  {robotTask === 'encounters' && '🔨 Generating encounters'}
                  {robotTask === 'help' && '❓ Help mode'}
                </div>
              </div>
              <button
                onClick={() => {
                  closeRobot();
                  setRobotMessages([]);
                  setRobotTask(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8b949e',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            {/* Messages */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              }}
            >
              {robotMessages.map(msg => (
                <div key={msg.id}>
                  <div style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <div
                      style={{
                        maxWidth: '85%',
                        padding: '14px 18px',
                        borderRadius: '20px',
                        backgroundColor:
                          msg.role === 'user' ? (robotTask === 'encounters' ? '#6e40c9' : '#238636') : '#21262d',
                        color: '#f0f6fc',
                        fontSize: '15px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {msg.content}
                    </div>
                  </div>
                  {msg.data && (
                    <div style={{ marginTop: '10px', maxWidth: '85%' }}>
                      <div style={{ padding: '14px', backgroundColor: '#161b22', borderRadius: '14px', border: '1px solid #30363d' }}>
                        <div style={{ color: '#58a6ff', fontSize: '13px', marginBottom: '10px' }}>✨ Generated: {msg.data.type}</div>
                        <button
                          onClick={() => applyRobotData(msg.data)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: '#238636',
                            border: 'none',
                            borderRadius: '10px',
                            color: 'white',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          ✓ Add to Storyworld
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isRobotThinking && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ padding: '14px 18px', borderRadius: '20px', backgroundColor: '#21262d', color: '#8b949e' }}>
                    🤖 Thinking...
                  </div>
                </div>
              )}
            </div>

            {/* Suggestions */}
            {robotMessages.length < 3 && !isRobotThinking && robotTask && (
              <div style={{ padding: '10px 16px', borderTop: '1px solid #21262d', display: 'flex', gap: '8px', overflowX: 'auto' }}>
                {(suggestions[robotTask] || []).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendRobotMessage(s)}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: '#21262d',
                      border: '1px solid #30363d',
                      borderRadius: '20px',
                      color: '#f0f6fc',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div style={{ padding: '14px 16px', paddingBottom: '20px', backgroundColor: '#161b22', borderTop: '1px solid #30363d' }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (robotInput.trim() && !isRobotThinking) sendRobotMessage(robotInput.trim());
                }}
                style={{ display: 'flex', gap: '10px' }}
              >
                <input
                  type="text"
                  value={robotInput}
                  onChange={(e) => setRobotInput(e.target.value)}
                  placeholder={
                    robotTask === 'create' ? 'Describe your story...' : robotTask === 'encounters' ? 'What encounters?' : 'Ask anything...'
                  }
                  disabled={isRobotThinking}
                  style={{
                    flex: 1,
                    padding: '16px 20px',
                    backgroundColor: '#0d1117',
                    border: '1px solid #30363d',
                    borderRadius: '28px',
                    color: '#f0f6fc',
                    fontSize: '16px',
                    outline: 'none',
                  }}
                />
                <button
                  type="submit"
                  disabled={!robotInput.trim() || isRobotThinking}
                  style={{
                    width: '54px',
                    height: '54px',
                    borderRadius: '27px',
                    backgroundColor: robotInput.trim() && !isRobotThinking ? (robotTask === 'encounters' ? '#6e40c9' : '#238636') : '#21262d',
                    border: 'none',
                    color: 'white',
                    fontSize: '20px',
                    cursor: robotInput.trim() && !isRobotThinking ? 'pointer' : 'not-allowed',
                  }}
                >
                  →
                </button>
              </form>
            </div>
          </div>
        )}
      </>
    );
  };

  // Disabled duplicate (kept for reference during iterative UI work)
  if (false) {
  // ============================================
  // CONVERSATIONAL CHARACTER CREATION
  // ============================================
  const _renderCharacterConversationDisabled = () => {
    if (!showCharacterConversation) return null;

    const { step, name, want, flaw, wound } = characterConversation;

    const questions: Record<string, { question: string; placeholder: string; help: string }> = {
      name: {
        question: "Who's in your story?",
        placeholder: 'Elena',
        help: "Just a name is fine. We'll discover who they are together.",
      },
      want: {
        question: `What does ${name || 'this character'} want more than anything?`,
        placeholder: "To prove she deserved her father's respect",
        help: 'This is their deepest desire—what drives everything they do.',
      },
      flaw: {
        question: `What gets in ${name || 'their'} way? Not external obstacles—something inside.`,
        placeholder: "She can never admit when she's wrong",
        help: 'This is the pattern that trips them up, again and again.',
      },
      wound: {
        question: `Why is ${name || 'they'} like this? What happened?`,
        placeholder: 'Her father always favored her brother instead',
        help: "The wound that created the pattern. It doesn't excuse the flaw—it explains it.",
      },
    };

    const currentQ = questions[step];

    const handleInput = (value: string) => {
      setCharacterConversation(prev => ({
        ...prev,
        [step]: value,
      }));
    };

    const steps: CharacterConversation['step'][] = ['name', 'want', 'flaw', 'wound', 'review'];

    const handleNext = () => {
      const currentIndex = steps.indexOf(step);
      if (currentIndex < steps.length - 1) {
        setCharacterConversation(prev => ({
          ...prev,
          step: steps[currentIndex + 1],
        }));
      }
    };

    const handleBack = () => {
      const currentIndex = steps.indexOf(step);
      if (currentIndex > 0) {
        setCharacterConversation(prev => ({
          ...prev,
          step: steps[currentIndex - 1],
        }));
      }
    };

    const createCharacterFromConversation = () => {
      const now = Date.now();
      const rand = () => Math.random().toString(36).slice(2, 9);

      const safeName = (name || '').trim() || 'New Character';
      const safeWant = (want || '').trim();
      const safeFlaw = (flaw || '').trim();
      const safeWound = (wound || '').trim();

      const pattern = generatePatternDescription(safeWant, safeFlaw);
      const triggers = generateTriggersFromWound(safeWound, safeFlaw);
      const reactions = generateReactionsFromFlaw(safeFlaw);
      const blindspot = generateBlindspot(safeFlaw, safeWant);
      const transformations = generateTransformations(safeFlaw);

      const enzymeId = `enz-${now}-${rand()}`;
      const characterId = `char-${now}-${rand()}`;

      const newChar: Character = {
        id: characterId,
        name: safeName,
        description:
          `${safeName} wants ${safeWant ? safeWant.toLowerCase() : 'something deeply'}, but ${safeFlaw ? safeFlaw.toLowerCase() : 'their pattern gets in the way'}.\n\n` +
          `Want: ${safeWant}\n` +
          `Flaw: ${safeFlaw}\n` +
          `Wound: ${safeWound}`.trim(),
        attachedEnzymes: [{ enzymeId, intensity: 0.7 }],
        createdAt: now,
      };

      const newEnzyme: Enzyme = {
        id: enzymeId,
        characterName: safeName,
        traitName: pattern,
        intensity: 0.7,
        triggers: triggers.map((t, i) => ({ id: `tr-${now}-${i}-${rand()}`, text: t })),
        responses: reactions.map((r, i) => ({ id: `rs-${now}-${i}-${rand()}`, text: r })),
        blindspots: [
          {
            id: `bs-${now}-${rand()}`,
            description: blindspot,
            selfJustification: `They believe their ${safeFlaw.toLowerCase()} is necessary because of ${safeWound.toLowerCase()}.`,
            canCrack: true,
          },
        ],
        mutations: [
          { id: `mu-${now}-h-${rand()}`, name: 'Hardening', description: transformations.doublesDown, probability: 40 },
          { id: `mu-${now}-c-${rand()}`, name: 'Collapse', description: transformations.breaksDown, probability: 35 },
          { id: `mu-${now}-t-${rand()}`, name: 'Transcendence', description: transformations.breaksThrough, probability: 25 },
        ],
        createdAt: now,
      };

      setCharacters(prev => [...prev, newChar]);
      setSavedEnzymes(prev => [...prev, newEnzyme]);

      setShowCharacterConversation(false);
      setCharacterConversation({
        step: 'name',
        name: '',
        want: '',
        flaw: '',
        wound: '',
      });
    };

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#0d1117',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            backgroundColor: '#161b22',
            borderBottom: '1px solid #30363d',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <button
            onClick={() => setShowCharacterConversation(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ×
          </button>
          <div style={{ flex: 1, color: '#f0f6fc', fontSize: '18px', fontWeight: 600 }}>Creating a Character</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['name', 'want', 'flaw', 'wound', 'review'].map((s, i) => {
              const currentIndex = ['name', 'want', 'flaw', 'wound', 'review'].indexOf(step);
              return (
                <div
                  key={s}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: i <= currentIndex ? '#238636' : '#30363d',
                    transition: 'background-color 0.3s',
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '40px 24px',
            maxWidth: '600px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {step !== 'review' ? (
            <>
              <h2
                style={{
                  color: '#f0f6fc',
                  fontSize: '28px',
                  fontWeight: 500,
                  marginBottom: '12px',
                  lineHeight: '1.3',
                }}
              >
                {currentQ?.question}
              </h2>

              <p style={{ color: '#8b949e', fontSize: '15px', marginBottom: '32px', lineHeight: '1.5' }}>
                {currentQ?.help}
              </p>

              <textarea
                value={characterConversation[step as keyof typeof characterConversation] as string}
                onChange={(e) => handleInput(e.target.value)}
                placeholder={currentQ?.placeholder}
                autoFocus
                style={{
                  width: '100%',
                  padding: '20px',
                  backgroundColor: '#0d1117',
                  border: '2px solid #30363d',
                  borderRadius: '12px',
                  color: '#f0f6fc',
                  fontSize: '18px',
                  lineHeight: '1.5',
                  minHeight: '120px',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#238636')}
                onBlur={(e) => (e.target.style.borderColor = '#30363d')}
              />
            </>
          ) : (
            <>
              <h2 style={{ color: '#f0f6fc', fontSize: '28px', fontWeight: 500, marginBottom: '32px' }}>
                Here's {name}
              </h2>

              <div
                style={{
                  backgroundColor: '#161b22',
                  borderRadius: '16px',
                  padding: '28px',
                  border: '1px solid #30363d',
                }}
              >
                <h3 style={{ color: '#f0f6fc', fontSize: '24px', marginBottom: '20px', fontWeight: 600 }}>{name}</h3>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '13px', marginBottom: '6px' }}>WHAT THEY WANT</div>
                  <div style={{ color: '#f0f6fc', fontSize: '16px', lineHeight: '1.4' }}>{want}</div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '13px', marginBottom: '6px' }}>WHAT GETS IN THEIR WAY</div>
                  <div style={{ color: '#f0f6fc', fontSize: '16px', lineHeight: '1.4' }}>{flaw}</div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '13px', marginBottom: '6px' }}>WHY THEY'RE LIKE THIS</div>
                  <div style={{ color: '#f0f6fc', fontSize: '16px', lineHeight: '1.4' }}>{wound}</div>
                </div>

                <div style={{ borderTop: '1px solid #30363d', paddingTop: '20px', marginTop: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '14px', lineHeight: '1.6' }}>
                    When challenged, {name.toLowerCase()} tends to{' '}
                    <span style={{ color: '#f0f6fc' }}>{generateReactionsFromFlaw(flaw)[0].toLowerCase()}</span>. Under
                    enough pressure, they might finally change—or double down completely.
                  </div>
                </div>
              </div>

              <p style={{ color: '#8b949e', fontSize: '14px', marginTop: '24px', textAlign: 'center' }}>
                Does this feel right? You can always adjust later.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 24px',
            backgroundColor: '#161b22',
            borderTop: '1px solid #30363d',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={handleBack}
            disabled={step === 'name'}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              border: '1px solid #30363d',
              borderRadius: '8px',
              color: step === 'name' ? '#484f58' : '#f0f6fc',
              fontSize: '15px',
              cursor: step === 'name' ? 'not-allowed' : 'pointer',
            }}
          >
            ← Back
          </button>

          {step !== 'review' ? (
            <button
              onClick={handleNext}
              disabled={!(characterConversation[step as keyof typeof characterConversation] as string).trim()}
              style={{
                padding: '12px 32px',
                backgroundColor: (characterConversation[step as keyof typeof characterConversation] as string).trim() ? '#238636' : '#21262d',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '15px',
                fontWeight: 600,
                cursor: (characterConversation[step as keyof typeof characterConversation] as string).trim() ? 'pointer' : 'not-allowed',
              }}
            >
              Continue →
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  border: '1px solid #30363d',
                  borderRadius: '8px',
                  color: '#f0f6fc',
                  fontSize: '15px',
                  cursor: 'pointer',
                }}
              >
                Adjust
              </button>
              <button
                onClick={createCharacterFromConversation}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#238636',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                ✓ Create {name || 'Character'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

    void _renderCharacterConversationDisabled;
  }

  // ============================================
  // CONVERSATIONAL CHARACTER CREATION
  // ============================================
  const renderCharacterConversation = () => {
    if (!showCharacterConversation) return null;

    const { step, name, want, flaw, wound } = characterConversation;

    const questions: Record<string, { question: string; placeholder: string; help: string }> = {
      name: {
        question: "Who's in your story?",
        placeholder: 'Elena',
        help: "Just a name is fine. We'll discover who they are together.",
      },
      want: {
        question: `What does ${name || 'this character'} want more than anything?`,
        placeholder: "To prove she deserved her father's respect",
        help: "This is their deepest desire—what drives everything they do.",
      },
      flaw: {
        question: `What gets in ${name || 'their'} way? Not external obstacles—something inside.`,
        placeholder: "She can never admit when she's wrong",
        help: 'This is the pattern that trips them up, again and again.',
      },
      wound: {
        question: `Why is ${name || 'they'} like this? What happened?`,
        placeholder: 'Her father always favored her brother instead',
        help: "The wound that created the pattern. It doesn't excuse the flaw—it explains it.",
      },
    };

    const currentQ = questions[step];

    const steps: CharacterConversation['step'][] = ['name', 'want', 'flaw', 'wound', 'review'];
    const currentIndex = steps.indexOf(step);

    // Handle input changes
    const handleInput = (value: string) => {
      setCharacterConversation(prev => ({
        ...prev,
        [step]: value,
      }));
    };

    // Handle next step
    const handleNext = () => {
      if (currentIndex >= 0 && currentIndex < steps.length - 1) {
        setCharacterConversation(prev => ({
          ...prev,
          step: steps[currentIndex + 1],
        }));
      }
    };

    // Handle back
    const handleBack = () => {
      if (currentIndex > 0) {
        setCharacterConversation(prev => ({
          ...prev,
          step: steps[currentIndex - 1],
        }));
      }
    };

    // Create the character (and a matching enzyme) from the conversation
    const createCharacterFromConversation = () => {
      const now = Date.now();
      const rand = () => Math.random().toString(36).slice(2, 9);

      const safeName = (name || 'New Character').trim() || 'New Character';
      const safeWant = (want || '').trim();
      const safeFlaw = (flaw || '').trim();
      const safeWound = (wound || '').trim();

      const pattern = generatePatternDescription(safeWant, safeFlaw);
      const triggers = generateTriggersFromWound(safeWound, safeFlaw);
      const reactions = generateReactionsFromFlaw(safeFlaw);
      const blindspotText = generateBlindspot(safeFlaw, safeWant);
      const transformations = generateTransformations(safeFlaw);

      const enzymeId = `enz-${now}-${rand()}`;
      const charId = `char-${now}-${rand()}`;

      const newEnzyme: Enzyme = {
        id: enzymeId,
        characterName: safeName,
        traitName: pattern,
        intensity: 0.7,
        triggers: triggers.map((t, i) => ({ id: `tr-${now}-${i}-${rand()}`, text: t })),
        responses: reactions.map((r, i) => ({ id: `rs-${now}-${i}-${rand()}`, text: r })),
        blindspots: [
          {
            id: `bs-${now}-${rand()}`,
            description: blindspotText,
            selfJustification: safeFlaw && safeWound
              ? `They believe their ${safeFlaw.toLowerCase()} is necessary because of ${safeWound.toLowerCase()}.`
              : '',
            canCrack: true,
          },
        ],
        mutations: [
          { id: `mu-${now}-hard-${rand()}`, name: 'Hardening', description: transformations.doublesDown, probability: 40 },
          { id: `mu-${now}-coll-${rand()}`, name: 'Collapse', description: transformations.breaksDown, probability: 35 },
          { id: `mu-${now}-tran-${rand()}`, name: 'Transcendence', description: transformations.breaksThrough, probability: 25 },
        ],
        createdAt: now,
      };

      const extraLines = [
        safeWant ? `Want: ${safeWant}` : '',
        safeFlaw ? `Flaw: ${safeFlaw}` : '',
        safeWound ? `Wound: ${safeWound}` : '',
      ]
        .filter(Boolean)
        .join('\n');

      const oneLiner =
        safeWant && safeFlaw ? `${safeName} wants ${safeWant.toLowerCase()}, but ${safeFlaw.toLowerCase()}.` : safeName;

      const description = [oneLiner, extraLines].filter(Boolean).join('\n\n');

      const newChar: Character = {
        id: charId,
        name: safeName,
        description,
        attachedEnzymes: [{ enzymeId, intensity: newEnzyme.intensity }],
        createdAt: now,
      };

      setSavedEnzymes(prev => [...prev, newEnzyme]);
      setCharacters(prev => [...prev, newChar]);

      // Reset and close
      setShowCharacterConversation(false);
      setCharacterConversation({
        step: 'name',
        name: '',
        want: '',
        flaw: '',
        wound: '',
      });
    };

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#0d1117',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            backgroundColor: '#161b22',
            borderBottom: '1px solid #30363d',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <button
            onClick={() => setShowCharacterConversation(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            ×
          </button>
          <div style={{ flex: 1, color: '#f0f6fc', fontSize: '18px', fontWeight: '600' }}>
            Creating a Character
          </div>
          {/* Progress dots */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {['name', 'want', 'flaw', 'wound', 'review'].map((s, i) => {
              const idx = ['name', 'want', 'flaw', 'wound', 'review'].indexOf(step);
              return (
                <div
                  key={s}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: i <= idx ? '#238636' : '#30363d',
                    transition: 'background-color 0.3s',
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '40px 24px',
            maxWidth: '600px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {step !== 'review' ? (
            <>
              {/* Question */}
              <h2
                style={{
                  color: '#f0f6fc',
                  fontSize: '28px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  lineHeight: '1.3',
                }}
              >
                {currentQ?.question || '…'}
              </h2>

              {/* Help text */}
              <p
                style={{
                  color: '#8b949e',
                  fontSize: '15px',
                  marginBottom: '32px',
                  lineHeight: '1.5',
                }}
              >
                {currentQ?.help || ''}
              </p>

              {/* Input */}
              <textarea
                value={characterConversation[step as keyof typeof characterConversation] as string}
                onChange={(e) => handleInput(e.target.value)}
                placeholder={currentQ?.placeholder || ''}
                autoFocus
                style={{
                  width: '100%',
                  padding: '20px',
                  backgroundColor: '#0d1117',
                  border: '2px solid #30363d',
                  borderRadius: '12px',
                  color: '#f0f6fc',
                  fontSize: '18px',
                  lineHeight: '1.5',
                  minHeight: '120px',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#238636';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#30363d';
                }}
              />
            </>
          ) : (
            <>
              <h2
                style={{
                  color: '#f0f6fc',
                  fontSize: '28px',
                  fontWeight: '500',
                  marginBottom: '32px',
                }}
              >
                Here's {name}
              </h2>

              <div
                style={{
                  backgroundColor: '#161b22',
                  borderRadius: '16px',
                  padding: '28px',
                  border: '1px solid #30363d',
                }}
              >
                <h3
                  style={{
                    color: '#f0f6fc',
                    fontSize: '24px',
                    marginBottom: '20px',
                    fontWeight: '600',
                  }}
                >
                  {name}
                </h3>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '13px', marginBottom: '6px' }}>WHAT THEY WANT</div>
                  <div style={{ color: '#f0f6fc', fontSize: '16px', lineHeight: '1.4' }}>{want}</div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '13px', marginBottom: '6px' }}>WHAT GETS IN THEIR WAY</div>
                  <div style={{ color: '#f0f6fc', fontSize: '16px', lineHeight: '1.4' }}>{flaw}</div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '13px', marginBottom: '6px' }}>WHY THEY'RE LIKE THIS</div>
                  <div style={{ color: '#f0f6fc', fontSize: '16px', lineHeight: '1.4' }}>{wound}</div>
                </div>

                <div style={{ borderTop: '1px solid #30363d', paddingTop: '20px', marginTop: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '14px', lineHeight: '1.6' }}>
                    When challenged, {name.toLowerCase()} tends to{' '}
                    <span style={{ color: '#f0f6fc' }}>
                      {generateReactionsFromFlaw(flaw)[0]?.toLowerCase() || 'react'}
                    </span>
                    . Under enough pressure, they might finally change—or double down completely.
                  </div>
                </div>
              </div>

              <p style={{ color: '#8b949e', fontSize: '14px', marginTop: '24px', textAlign: 'center' }}>
                Does this feel right? You can always adjust later.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 24px',
            backgroundColor: '#161b22',
            borderTop: '1px solid #30363d',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={handleBack}
            disabled={step === 'name'}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              border: '1px solid #30363d',
              borderRadius: '8px',
              color: step === 'name' ? '#484f58' : '#f0f6fc',
              fontSize: '15px',
              cursor: step === 'name' ? 'not-allowed' : 'pointer',
            }}
          >
            ← Back
          </button>

          {step !== 'review' ? (
            <button
              onClick={handleNext}
              disabled={!String(characterConversation[step as keyof typeof characterConversation] || '').trim()}
              style={{
                padding: '12px 32px',
                backgroundColor: String(characterConversation[step as keyof typeof characterConversation] || '').trim()
                  ? '#238636'
                  : '#21262d',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: String(characterConversation[step as keyof typeof characterConversation] || '').trim() ? 'pointer' : 'not-allowed',
              }}
            >
              Continue →
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  border: '1px solid #30363d',
                  borderRadius: '8px',
                  color: '#f0f6fc',
                  fontSize: '15px',
                  cursor: 'pointer',
                }}
              >
                Adjust
              </button>
              <button
                onClick={createCharacterFromConversation}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#238636',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                ✓ Create {name || 'Character'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // HUMAN-CENTERED CHARACTER VIEW (Story mode)
  // ============================================
  const renderHumanCharacterCard = (char: Character) => {
    const extracted = extractWantFlawWound(char.description || '');

    const enzyme =
      savedEnzymes.find((e) => (char.attachedEnzymes || []).some((a) => a.enzymeId === e.id)) ||
      savedEnzymes.find((e) => (e.characterName || '').toLowerCase() === (char.name || '').toLowerCase());

    const pattern = enzyme?.traitName || generatePatternDescription(extracted.want, extracted.flaw);
    const strength = intensityToHuman(typeof enzyme?.intensity === 'number' ? enzyme.intensity : 0.5);

    const cps = characterStates.find((s) => s.characterId === char.id);
    const currentPressure =
      enzyme && cps && typeof cps.enzymePressures?.[enzyme.id] === 'number' ? cps.enzymePressures[enzyme.id] : 0;
    const buildUp = pressureToHuman(currentPressure);

    const buildUpColors: Record<string, string> = {
      calm: '#3fb950',
      stirring: '#58a6ff',
      building: '#d29922',
      critical: '#f85149',
      breaking: '#da3633',
    };

    const buildUpWidths: Record<string, string> = {
      calm: '20%',
      stirring: '40%',
      building: '60%',
      critical: '80%',
      breaking: '95%',
    };

    const mutationStatus =
      enzyme && cps && cps.mutationStatus ? cps.mutationStatus[enzyme.id] : undefined;

    const firstLine = (char.description || '').toString().split('\n').find(Boolean) || '';

    return (
      <div
        key={char.id}
        style={{
          backgroundColor: '#161b22',
          borderRadius: '16px',
          padding: '20px 20px 18px',
          border: '1px solid #30363d',
          cursor: 'pointer',
          transition: 'transform 0.12s, border-color 0.12s',
        }}
        onClick={() => {
          setSelectedCharacterId(char.id);
          setCurrentView('characters');
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#58a6ff';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#30363d';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Name */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '14px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: '#21262d',
              border: '1px solid #30363d',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: '#f0f6fc',
              fontWeight: 700,
            }}
          >
            {char.portrait ? (
              <img
                src={char.portrait}
                alt={char.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: '18px' }}>{(char.name || '?').trim().charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#f0f6fc', fontSize: '18px', fontWeight: 700, lineHeight: 1.1 }}>
              {char.name}
            </div>
            {firstLine && (
              <div
                style={{
                  color: '#8b949e',
                  fontSize: '13px',
                  marginTop: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {firstLine}
              </div>
            )}
          </div>
        </div>

        {/* Pattern summary */}
        <div
          style={{
            backgroundColor: '#0d1117',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '14px',
            border: '1px solid #21262d',
          }}
        >
          <div style={{ color: '#8b949e', fontSize: '12px', marginBottom: '6px' }}>THEIR PATTERN</div>
          <div style={{ color: '#f0f6fc', fontSize: '15px' }}>
            <span style={{ color: '#58a6ff', fontWeight: 700 }}>
              {strength.charAt(0).toUpperCase() + strength.slice(1)}
            </span>{' '}
            {pattern}
          </div>
          {mutationStatus && mutationStatus !== 'normal' && (
            <div style={{ marginTop: '8px' }}>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '999px',
                  backgroundColor: '#21262d',
                  border: '1px solid #30363d',
                  color:
                    mutationStatus === 'hardening'
                      ? '#f85149'
                      : mutationStatus === 'collapsed'
                        ? '#8b949e'
                        : '#d29922',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {mutationStatus === 'hardening'
                  ? 'Hardened'
                  : mutationStatus === 'collapsed'
                    ? 'Collapsed'
                    : 'Transcended'}
              </span>
            </div>
          )}
        </div>

        {/* Build-up indicator */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ color: '#8b949e', fontSize: '12px' }}>BUILD-UP</span>
            <span style={{ color: buildUpColors[buildUp] || '#8b949e', fontSize: '12px', fontWeight: 700 }}>
              {buildUp.charAt(0).toUpperCase() + buildUp.slice(1)}
            </span>
          </div>
          <div style={{ height: '6px', backgroundColor: '#21262d', borderRadius: '3px', overflow: 'hidden' }}>
            <div
              style={{
                width: buildUpWidths[buildUp] || '0%',
                height: '100%',
                backgroundColor: buildUpColors[buildUp] || '#8b949e',
                borderRadius: '3px',
                transition: 'width 0.35s, background-color 0.35s',
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // STORY MODE VIEW (DEFAULT)
  // ============================================
  const renderStoryModeView = () => {
    const storyCharacters = characters;
    const scenes = encounters;

    const openStoryAssistant = () => {
      setShowStoryAssistant(true);
    };

    const tensionLevel = (pressure: number): 'simmering' | 'building' | 'mounting' | 'critical' => {
      const p = typeof pressure === 'number' ? pressure : 0;
      if (p < 0.25) return 'simmering';
      if (p < 0.5) return 'building';
      if (p < 0.8) return 'mounting';
      return 'critical';
    };

    const tensionLevelColors: Record<string, string> = {
      simmering: '#8b949e',
      building: '#d29922',
      mounting: '#f85149',
      critical: '#da3633',
    };

    const byId = new Map(storyCharacters.map((c) => [c.id, c]));

    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Story Title */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h1
              style={{
                color: '#f0f6fc',
                fontSize: '28px',
                fontWeight: '600',
                marginBottom: '4px',
              }}
            >
              {currentStoryworld || 'Your Story'}
            </h1>
            <p style={{ color: '#8b949e', fontSize: '14px', margin: 0 }}>
              {storyCharacters.length} characters · {scenes.length} scenes
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => setInterfaceMode('engineer')}
              style={{
                padding: '12px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #30363d',
                borderRadius: '8px',
                color: '#8b949e',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              title="Switch to the detailed Storyworld editor"
            >
              ✎ Edit
            </button>

            <button
              onClick={() => setCurrentView('play')}
              style={{
                padding: '12px 24px',
                backgroundColor: '#238636',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              ▶ Play
            </button>
          </div>
        </div>

        {/* Characters Section */}
        <section style={{ marginBottom: '40px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <h2 style={{ color: '#f0f6fc', fontSize: '18px', fontWeight: '600', margin: 0 }}>Characters</h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '16px',
            }}
          >
            {storyCharacters.map((char) => renderHumanCharacterCard(char))}

            {/* Add Character Card */}
            <button
              onClick={() => setShowCharacterConversation(true)}
              style={{
                backgroundColor: 'transparent',
                border: '2px dashed #30363d',
                borderRadius: '16px',
                padding: '40px 24px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                minHeight: '180px',
                transition: 'all 0.2s',
                color: '#8b949e',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#238636';
                e.currentTarget.style.backgroundColor = 'rgba(35, 134, 54, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#30363d';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ fontSize: '32px' }}>+</span>
              <span style={{ fontSize: '15px' }}>Add Character</span>
            </button>
          </div>
        </section>

        {/* Pressures Section (if any tensions exist) */}
        {tensionCodons.length > 0 && (
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#f0f6fc', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
              What's Putting Pressure On Them
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tensionCodons.map((tension) => {
                const level = tensionLevel(tension.pressureLevel ?? 0.5);
                const color = tensionLevelColors[level] || '#8b949e';

                return (
                  <div
                    key={tension.id}
                    style={{
                      backgroundColor: '#161b22',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      border: '1px solid #30363d',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#f0f6fc', fontSize: '15px', fontWeight: 700 }}>{tension.name}</div>
                      <div style={{ color: '#8b949e', fontSize: '13px' }}>{tension.description}</div>
                    </div>
                    <div
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#21262d',
                        borderRadius: '20px',
                        color,
                        fontSize: '12px',
                        fontWeight: 800,
                        flexShrink: 0,
                        border: `1px solid ${color}33`,
                      }}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Scenes Section */}
        <section>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <h2 style={{ color: '#f0f6fc', fontSize: '18px', fontWeight: '600', margin: 0 }}>Scenes</h2>
          </div>

          {scenes.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {scenes.map((scene, index) => {
                const present = (scene.characterIds || [])
                  .map((id) => byId.get(id)?.name)
                  .filter(Boolean) as string[];

                return (
                  <div
                    key={scene.id}
                    style={{
                      backgroundColor: '#161b22',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      border: '1px solid #30363d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setCurrentView('encounters')}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#21262d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#8b949e',
                        fontSize: '14px',
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#f0f6fc', fontSize: '15px', fontWeight: 700 }}>
                        {scene.title || 'Untitled Scene'}
                        {scene.isEndingEncounter ? (
                          <span style={{ marginLeft: '10px', color: '#d29922', fontSize: '12px', fontWeight: 800 }}>
                            ENDING
                          </span>
                        ) : null}
                      </div>
                      {present.length > 0 && (
                        <div style={{ color: '#8b949e', fontSize: '13px', marginTop: '2px' }}>
                          {present.join(', ')}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentView('encounters');
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#21262d',
                        border: '1px solid #30363d',
                        borderRadius: '6px',
                        color: '#f0f6fc',
                        fontSize: '13px',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      Edit
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                backgroundColor: '#161b22',
                borderRadius: '12px',
                padding: '40px',
                border: '1px solid #30363d',
                textAlign: 'center',
              }}
            >
              <p style={{ color: '#8b949e', fontSize: '15px', marginBottom: '20px' }}>
                No scenes yet. Ready to start writing?
              </p>
              <button
                onClick={openStoryAssistant}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#238636',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                ✨ Create First Scene
              </button>
            </div>
          )}

          {scenes.length > 0 && (
            <button
              onClick={openStoryAssistant}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '16px',
                backgroundColor: 'transparent',
                border: '2px dashed #30363d',
                borderRadius: '12px',
                color: '#8b949e',
                fontSize: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              + Add Scene
            </button>
          )}
        </section>
      </div>
    );
  };

  // NOTE: A duplicate Story Assistant block was accidentally inserted during iteration.
  // Keep it disabled (for reference) to avoid redeclaration errors.
  if (false) {
  // ============================================
  // STORY ASSISTANT (POLITE AI)
  // ============================================
  const sendAssistantMessage = async (message: string) => {
    const userMsg: AssistantMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: message,
    };
    setAssistantMessages((prev) => [...prev, userMsg]);
    setAssistantInput('');
    setIsAssistantThinking(true);

    const apiKey =
      localStorage.getItem('splinewright-claude-api-key') ||
      localStorage.getItem('claude_api_key') ||
      claudeApiKey ||
      '';

    if (!apiKey) {
      setAssistantMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-no-key`,
          role: 'assistant',
          content: 'To use the Story Assistant, add your Claude API key in Settings first.',
          suggestion: undefined,
        },
      ]);
      setIsAssistantThinking(false);
      setShowStoryAssistant(false);
      setCurrentView('settings');
      return;
    }

    try {
      // Context about the story in plain language (avoid technical jargon)
      let context = `\n\nCURRENT STORY: "${currentStoryworld || 'Untitled'}"`;

      if (characters.length > 0) {
        context += '\n\nCHARACTERS (patterns + pressure):';
        characters.forEach((char) => {
          const extracted = extractWantFlawWound(char.description || '');
          const enzyme =
            savedEnzymes.find((e) => (char.attachedEnzymes || []).some((a) => a.enzymeId === e.id)) ||
            savedEnzymes.find((e) => (e.characterName || '').toLowerCase() === (char.name || '').toLowerCase());

          const pattern = enzyme?.traitName || generatePatternDescription(extracted.want, extracted.flaw);
          const strength = enzyme ? intensityToHuman(enzyme.intensity) : 'moderate';

          const buildUp = (() => {
            if (!enzyme) return 'calm';
            const cps = characterStates.find((s) => s.characterId === char.id);
            const p = cps?.enzymePressures?.[enzyme.id];
            return typeof p === 'number' ? pressureToHuman(p) : 'calm';
          })();

          context += `\n- ${char.name}: ${pattern} (strength: ${strength}, build-up: ${buildUp})`;
          if (extracted.want) context += `\n  Wants: ${extracted.want}`;
          if (extracted.flaw) context += `\n  Gets in their way: ${extracted.flaw}`;
          if (extracted.wound) context += `\n  Old hurt: ${extracted.wound}`;
        });
      }

      if (tensionCodons.length > 0) {
        const levelFor = (p: number) => {
          if (p < 0.25) return 'simmering';
          if (p < 0.5) return 'building';
          if (p < 0.8) return 'mounting';
          return 'critical';
        };

        context += '\n\nPRESSURES ON THE STORY:';
        tensionCodons.forEach((t) => {
          const level = levelFor(typeof t.pressureLevel === 'number' ? t.pressureLevel : 0.5);
          context += `\n- ${t.name}: ${t.description} (${level})`;
        });
      }

      if (encounters.length > 0) {
        context += `\n\nEXISTING SCENES: ${encounters.length}`;
        const last = encounters[encounters.length - 1];
        if (last?.title) context += `\nLast scene: "${last.title}"`;
      }

      const history = assistantMessages
        .slice(-10)
        .map((m) => ({
          role: m.role === 'assistant' ? 'assistant' : 'user',
          content: m.content,
        }));
      history.push({ role: 'user', content: message });

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 2048,
          system: ASSISTANT_PROMPT + context,
          messages: history,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      const content = data.content?.[0]?.text || '';

      // Parse <scene_data> JSON if present
      const sceneMatch = content.match(/<scene_data>([\s\S]*?)<\/scene_data>/i);
      let suggestion: any = null;
      if (sceneMatch?.[1]) {
        try {
          const raw = sceneMatch[1]
            .trim()
            .replace(/^```(?:json)?\s*/i, '')
            .replace(/\s*```$/i, '')
            .trim();
          suggestion = { type: 'scene', data: JSON.parse(raw) };
        } catch {
          suggestion = null;
        }
      }

      const cleanContent = content.replace(/<scene_data>[\s\S]*?<\/scene_data>/gi, '').trim();

      setAssistantMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-ai`,
          role: 'assistant',
          content: cleanContent || "I've got a draft idea—want me to shape it into a scene?",
          suggestion,
        },
      ]);
    } catch (error) {
      console.error('Assistant error:', error);
      setAssistantMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-err`,
          role: 'assistant',
          content: "I had a moment there! Want to try again—what kind of scene are you aiming for?",
          suggestion: undefined,
        },
      ]);
    } finally {
      setIsAssistantThinking(false);
    }
  };

  const addSuggestionToStory = (suggestion: any) => {
    if (!suggestion || suggestion.type !== 'scene' || !suggestion.data) return;

    const now = Date.now();
    const data = suggestion.data || {};

    const names: string[] = Array.isArray(data.characters) ? data.characters : [];
    const characterIdByLower = new Map<string, string>(characters.map((c) => [(c.name || '').toLowerCase(), c.id]));
    const characterIds = names
      .map((n) => characterIdByLower.get((n || '').toLowerCase()))
      .filter(Boolean) as string[];

    const choices = Array.isArray(data.choices) ? data.choices : [];
    const playerChoicePoints = choices
      .map((c: any) => (c && typeof c === 'object' ? c.text : c))
      .filter(Boolean)
      .map((s: any) => String(s));

    const newEncounter: Encounter = {
      id: `enc-${now}`,
      title: String(data.title || 'New Scene'),
      description: String(data.description || ''),
      characterIds,
      triggers: String(data.description || ''),
      whatHappens: String(data.whatHappens || ''),
      playerChoicePoints,
      createdAt: now,
    };

    setEncounters((prev) => [...prev, newEncounter]);
  };

  const renderStoryAssistant = () => {
    if (!showStoryAssistant) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#0d1117',
          zIndex: 2050,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            backgroundColor: '#161b22',
            borderBottom: '1px solid #30363d',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <button
            onClick={() => setShowStoryAssistant(false)}
            style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: '24px', cursor: 'pointer' }}
          >
            ←
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#f0f6fc', fontSize: '17px', fontWeight: '600' }}>Story Assistant</div>
            <div style={{ color: '#8b949e', fontSize: '13px' }}>{currentStoryworld || 'Your Story'}</div>
          </div>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {assistantMessages.length === 0 && (
            <div style={{ backgroundColor: '#161b22', borderRadius: '16px', padding: '24px', maxWidth: '80%' }}>
              <p style={{ color: '#f0f6fc', fontSize: '15px', lineHeight: '1.5', margin: 0 }}>
                Hey! I’m here to help you write.
                {characters.length > 0 && <span> I can see {characters.map((c) => c.name).join(' and ')} in your story.</span>}{' '}
                What would you like to work on?
              </p>
            </div>
          )}

          {assistantMessages.map((msg) => (
            <div key={msg.id}>
              <div style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '16px 20px',
                    borderRadius: '16px',
                    backgroundColor: msg.role === 'user' ? '#238636' : '#161b22',
                    color: '#f0f6fc',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {msg.content}
                </div>
              </div>

              {msg.suggestion && msg.suggestion.type === 'scene' && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '20px',
                    backgroundColor: '#161b22',
                    borderRadius: '12px',
                    border: '1px solid #238636',
                    maxWidth: '80%',
                  }}
                >
                  <div style={{ color: '#3fb950', fontSize: '13px', marginBottom: '12px' }}>✨ Scene Draft</div>
                  <h4 style={{ color: '#f0f6fc', margin: '0 0 8px' }}>{msg.suggestion.data?.title || 'Untitled Scene'}</h4>
                  {msg.suggestion.data?.description ? (
                    <p style={{ color: '#8b949e', fontSize: '14px', margin: '0 0 16px' }}>{msg.suggestion.data.description}</p>
                  ) : null}
                  <button
                    onClick={() => addSuggestionToStory(msg.suggestion)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#238636',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    ✓ Add to Story
                  </button>
                </div>
              )}
            </div>
          ))}

          {isAssistantThinking && (
            <div style={{ padding: '16px 20px', borderRadius: '16px', backgroundColor: '#161b22', color: '#8b949e', maxWidth: '80%' }}>
              Thinking...
            </div>
          )}
        </div>

        {/* Suggestions */}
        {assistantMessages.length < 2 && !isAssistantThinking && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #21262d', display: 'flex', gap: '8px', overflowX: 'auto' }}>
            {['Write the opening scene', 'What could happen next?', 'Create a confrontation', 'Add a quiet moment'].map((s) => (
              <button
                key={s}
                onClick={() => sendAssistantMessage(s)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '20px',
                  color: '#f0f6fc',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ padding: '16px 20px', backgroundColor: '#161b22', borderTop: '1px solid #30363d' }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (assistantInput.trim() && !isAssistantThinking) {
                sendAssistantMessage(assistantInput.trim());
              }
            }}
            style={{ display: 'flex', gap: '12px' }}
          >
            <input
              type="text"
              value={assistantInput}
              onChange={(e) => setAssistantInput(e.target.value)}
              placeholder="What would you like to write?"
              disabled={isAssistantThinking}
              style={{
                flex: 1,
                padding: '16px 20px',
                backgroundColor: '#0d1117',
                border: '1px solid #30363d',
                borderRadius: '24px',
                color: '#f0f6fc',
                fontSize: '16px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={!assistantInput.trim() || isAssistantThinking}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: assistantInput.trim() && !isAssistantThinking ? '#238636' : '#21262d',
                border: 'none',
                color: 'white',
                fontSize: '18px',
                cursor: assistantInput.trim() && !isAssistantThinking ? 'pointer' : 'not-allowed',
              }}
            >
              →
            </button>
          </form>
        </div>
      </div>
    );
  };

    // satisfy no-unused-vars within this disabled block
    void renderStoryAssistant;
  }

  // ============================================
  // STORY ASSISTANT (POLITE AI)
  // ============================================
  const sendAssistantMessage = async (message: string) => {
    const userMsg: AssistantMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: message,
    };
    setAssistantMessages((prev) => [...prev, userMsg]);
    setAssistantInput('');
    setIsAssistantThinking(true);

    // Resolve API key (same policy as other Claude callers)
    const apiKey =
      localStorage.getItem('splinewright-claude-api-key') ||
      localStorage.getItem('claude_api_key') ||
      claudeApiKey ||
      '';

    if (!apiKey) {
      setAssistantMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-no-key`,
          role: 'assistant',
          content: 'To use the Story Assistant, add your Claude API key in Settings first.',
        },
      ]);
      setIsAssistantThinking(false);
      setShowStoryAssistant(false);
      setCurrentView('settings');
      return;
    }

    try {
      // Build context about the story (avoid technical jargon)
      let context = `\n\nCURRENT STORY: "${currentStoryworld || 'Untitled'}"`;

      if (characters.length > 0) {
        context += '\n\nCHARACTERS (patterns + needs):';
        characters.forEach((char) => {
          const extracted = extractWantFlawWound(char.description || '');
          const enzyme =
            savedEnzymes.find((e) => (char.attachedEnzymes || []).some((a) => a.enzymeId === e.id)) ||
            savedEnzymes.find((e) => (e.characterName || '').toLowerCase() === (char.name || '').toLowerCase());

          const pattern = enzyme?.traitName || generatePatternDescription(extracted.want, extracted.flaw);
          const strength = enzyme ? intensityToHuman(enzyme.intensity) : 'moderate';

          const buildUp = (() => {
            const cps = characterStates.find((s) => s.characterId === char.id);
            if (!enzyme || !cps) return 'calm';
            const p = cps.enzymePressures?.[enzyme.id];
            return typeof p === 'number' ? pressureToHuman(p) : 'calm';
          })();

          context += `\n- ${char.name}: ${pattern} (strength: ${strength}, build-up: ${buildUp})`;
          if (extracted.want) context += `\n  Wants: ${extracted.want}`;
          if (extracted.flaw) context += `\n  Gets in their way: ${extracted.flaw}`;
          if (extracted.wound) context += `\n  Old hurt: ${extracted.wound}`;
        });
      }

      if (tensionCodons.length > 0) {
        const levelFor = (p: number) => {
          if (p < 0.25) return 'simmering';
          if (p < 0.5) return 'building';
          if (p < 0.8) return 'mounting';
          return 'critical';
        };
        context += '\n\nPRESSURES ON THE STORY:';
        tensionCodons.forEach((t) => {
          const level = levelFor(typeof t.pressureLevel === 'number' ? t.pressureLevel : 0.5);
          context += `\n- ${t.name}: ${t.description} (${level})`;
        });
      }

      if (encounters.length > 0) {
        context += `\n\nEXISTING SCENES: ${encounters.length}`;
        const last = encounters[encounters.length - 1];
        if (last?.title) context += `\nLast scene: "${last.title}"`;
      }

      const history = assistantMessages
        .slice(-10)
        .map((m) => ({
          role: m.role === 'assistant' ? 'assistant' : 'user',
          content: m.content,
        }));
      history.push({ role: 'user', content: message });

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 2048,
          system: ASSISTANT_PROMPT + context,
          messages: history,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      const content = data.content?.[0]?.text || '';

      // Parse scene data if present
      const sceneMatch = content.match(/<scene_data>([\s\S]*?)<\/scene_data>/i);
      let suggestion: any = null;
      if (sceneMatch?.[1]) {
        try {
          const raw = sceneMatch[1]
            .trim()
            .replace(/^```(?:json)?\s*/i, '')
            .replace(/\s*```$/i, '')
            .trim();
          suggestion = { type: 'scene', data: JSON.parse(raw) };
        } catch {
          suggestion = null;
        }
      }

      const cleanContent = content.replace(/<scene_data>[\s\S]*?<\/scene_data>/gi, '').trim();

      setAssistantMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-ai`,
          role: 'assistant',
          content: cleanContent || "I've got a draft idea—want me to shape it into a scene?",
          suggestion,
        },
      ]);
    } catch (error) {
      console.error('Assistant error:', error);
      setAssistantMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}-err`,
          role: 'assistant',
          content: "I had a moment there! Want to try again—what kind of scene are you aiming for?",
        },
      ]);
    } finally {
      setIsAssistantThinking(false);
    }
  };

  const addSuggestionToStory = (suggestion: any) => {
    if (!suggestion || suggestion.type !== 'scene' || !suggestion.data) return;

    const now = Date.now();
    const data = suggestion.data || {};

    const names: string[] = Array.isArray(data.characters) ? data.characters : [];
    const characterIdByLower = new Map<string, string>(characters.map((c) => [(c.name || '').toLowerCase(), c.id]));
    const characterIds = names
      .map((n) => characterIdByLower.get((n || '').toLowerCase()))
      .filter(Boolean) as string[];

    const choices = Array.isArray(data.choices) ? data.choices : [];
    const playerChoicePoints = choices
      .map((c: any) => (c && typeof c === 'object' ? c.text : c))
      .filter(Boolean)
      .map((s: any) => String(s));

    const newEncounter: Encounter = {
      id: `enc-${now}`,
      title: String(data.title || 'New Scene'),
      description: String(data.description || ''),
      characterIds,
      triggers: String(data.description || ''),
      whatHappens: String(data.whatHappens || ''),
      playerChoicePoints,
      createdAt: now,
    };

    setEncounters((prev) => [...prev, newEncounter]);
  };

  const renderStoryAssistant = () => {
    if (!showStoryAssistant) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#0d1117',
          zIndex: 2050,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            backgroundColor: '#161b22',
            borderBottom: '1px solid #30363d',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <button
            onClick={() => setShowStoryAssistant(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              fontSize: '24px',
              cursor: 'pointer',
            }}
          >
            ←
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#f0f6fc', fontSize: '17px', fontWeight: '600' }}>Story Assistant</div>
            <div style={{ color: '#8b949e', fontSize: '13px' }}>{currentStoryworld || 'Your Story'}</div>
          </div>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {assistantMessages.length === 0 && (
            <div
              style={{
                backgroundColor: '#161b22',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '80%',
              }}
            >
              <p style={{ color: '#f0f6fc', fontSize: '15px', lineHeight: '1.5', margin: 0 }}>
                Hey! I’m here to help you write.
                {characters.length > 0 && <span> I can see {characters.map((c) => c.name).join(' and ')} in your story.</span>}{' '}
                What would you like to work on?
              </p>
            </div>
          )}

          {assistantMessages.map((msg) => (
            <div key={msg.id}>
              <div style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '16px 20px',
                    borderRadius: '16px',
                    backgroundColor: msg.role === 'user' ? '#238636' : '#161b22',
                    color: '#f0f6fc',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {msg.content}
                </div>
              </div>

              {/* Scene suggestion */}
              {msg.suggestion && msg.suggestion.type === 'scene' && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '20px',
                    backgroundColor: '#161b22',
                    borderRadius: '12px',
                    border: '1px solid #238636',
                    maxWidth: '80%',
                  }}
                >
                  <div style={{ color: '#3fb950', fontSize: '13px', marginBottom: '12px' }}>✨ Scene Draft</div>
                  <h4 style={{ color: '#f0f6fc', margin: '0 0 8px' }}>{msg.suggestion.data?.title || 'Untitled Scene'}</h4>
                  {msg.suggestion.data?.description ? (
                    <p style={{ color: '#8b949e', fontSize: '14px', margin: '0 0 16px' }}>{msg.suggestion.data.description}</p>
                  ) : null}
                  <button
                    onClick={() => addSuggestionToStory(msg.suggestion)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#238636',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    ✓ Add to Story
                  </button>
                </div>
              )}
            </div>
          ))}

          {isAssistantThinking && (
            <div
              style={{
                padding: '16px 20px',
                borderRadius: '16px',
                backgroundColor: '#161b22',
                color: '#8b949e',
                maxWidth: '80%',
              }}
            >
              Thinking...
            </div>
          )}
        </div>

        {/* Suggestions */}
        {assistantMessages.length < 2 && !isAssistantThinking && (
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #21262d',
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
            }}
          >
            {['Write the opening scene', 'What could happen next?', 'Create a confrontation', 'Add a quiet moment'].map((s) => (
              <button
                key={s}
                onClick={() => sendAssistantMessage(s)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '20px',
                  color: '#f0f6fc',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ padding: '16px 20px', backgroundColor: '#161b22', borderTop: '1px solid #30363d' }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (assistantInput.trim() && !isAssistantThinking) {
                sendAssistantMessage(assistantInput.trim());
              }
            }}
            style={{ display: 'flex', gap: '12px' }}
          >
            <input
              type="text"
              value={assistantInput}
              onChange={(e) => setAssistantInput(e.target.value)}
              placeholder="What would you like to write?"
              disabled={isAssistantThinking}
              style={{
                flex: 1,
                padding: '16px 20px',
                backgroundColor: '#0d1117',
                border: '1px solid #30363d',
                borderRadius: '24px',
                color: '#f0f6fc',
                fontSize: '16px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={!assistantInput.trim() || isAssistantThinking}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: assistantInput.trim() && !isAssistantThinking ? '#238636' : '#21262d',
                border: 'none',
                color: 'white',
                fontSize: '18px',
                cursor: assistantInput.trim() && !isAssistantThinking ? 'pointer' : 'not-allowed',
              }}
            >
              →
            </button>
          </form>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #64b5f6;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(100, 181, 246, 0.4);
          transition: all 0.2s;
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          background: #90caf9;
          transform: scale(1.1);
          box-shadow: 0 3px 8px rgba(100, 181, 246, 0.6);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #64b5f6;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 6px rgba(100, 181, 246, 0.4);
          transition: all 0.2s;
        }
        input[type="range"]::-moz-range-thumb:hover {
          background: #90caf9;
          transform: scale(1.1);
          box-shadow: 0 3px 8px rgba(100, 181, 246, 0.6);
        }
        input[type="text"]:focus,
        input[type="number"]:focus,
        textarea:focus {
          outline: none;
          border-color: #64b5f6;
          background-color: #2f2f2f;
        }
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        ::-webkit-scrollbar-thumb {
          background: #4a4a4a;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #5a5a5a;
        }
        select:focus {
          outline: none;
          border-color: #64b5f6;
          background-color: #2f2f2f;
        }
      `}</style>

      {/* Guide content and helper functions */}
      {(() => {
        const guideContent = {
          whatIsEnzyme: `Enzymes are dynamic behavioral patterns that define how characters react to specific situations. Unlike static traits, enzymes create active, responsive behaviors that generate drama and conflict.

**Enzymes vs Static Traits:**
- Static traits describe what a character is (e.g., "brave," "loyal")
- Enzymes describe how a character reacts when triggered (e.g., "when authority is questioned, they become defensive and aggressive")
- Enzymes create action; static traits create description
- Enzymes generate story events; static traits generate character descriptions

**Why Enzymes Matter:**
Enzymes transform personality into plot. A character with "loyalty" as a static trait might be described as loyal, but a character with a loyalty enzyme will actively defend their chosen person, make sacrifices, and create dramatic moments through their actions.`,

          fiveComponents: `Every enzyme consists of five essential components that work together to create dynamic character behavior:

**1. Trait**
The personality aspect being modeled (e.g., "Wounded Pride," "Self-Sabotage," "Misplaced Loyalty"). The trait should have internal tension—it should be both a strength and a weakness.

**2. Triggers**
Specific situations that activate the enzyme. Triggers should be concrete and actionable, not abstract feelings. Good triggers create clear "if-then" scenarios.

**3. Responses**
How the character behaves when triggered. Responses should be patterns, not scripts—they describe behavioral tendencies, not exact dialogue or actions.

**4. Blindspots**
What the character fails to notice or understand. Blindspots create dramatic irony and opportunities for conflict. They're the gaps in the character's awareness.

**5. Mutations**
Variations that can occur under specific conditions. Mutations add complexity and prevent enzymes from becoming predictable. They represent how the trait might evolve or break down.`,

          choosingTraits: `**Good Traits with Internal Tension:**
- Wounded Pride (pride protects but also blinds)
- Self-Sabotage (self-protection that destroys)
- Misplaced Loyalty (loyalty to wrong people/ideals)
- Righteous Anger (justice that becomes vengeance)
- Protective Instinct (protection that becomes control)

**Traits to Avoid:**
- Pure virtues without flaws (e.g., "always kind")
- Pure vices without complexity (e.g., "always evil")
- Traits without internal tension
- Traits that are too abstract (e.g., "goodness")
- Traits that don't create conflict

**Intensity Levels:**
- **Low (0.2-0.4):** Subtle influence, occasional activation
- **Medium (0.5-0.7):** Regular influence, frequent activation
- **High (0.8-1.0):** Dominant trait, almost always active

Choose intensity based on how central the trait is to the character's identity and how often it should drive their behavior.`,

          designingTriggers: `**Trigger Categories:**

**Social Triggers:**
- "When someone questions their authority in front of others"
- "When excluded from a group decision"
- "When their social status is threatened"

**Status Triggers:**
- "When their achievements are overlooked"
- "When someone gets credit for their work"
- "When their position is challenged"

**Relational Triggers:**
- "When a close ally shows doubt"
- "When someone they trust betrays them"
- "When their loyalty is tested"

**Achievement Triggers:**
- "When they fail at something important"
- "When success is within reach but threatened"
- "When their competence is questioned"

**Specificity:**
Make triggers specific enough to create clear scenarios but broad enough to apply to multiple situations. "When authority is questioned" is better than "When Bob questions their authority on Tuesday."

**Sensitivity:**
More triggers = more opportunities for activation. Fewer triggers = more focused, intense responses. Balance based on the trait's role in the story.`,

          designingResponses: `**Immediate vs Delayed:**
- **Immediate:** Character reacts instantly (e.g., "immediately becomes defensive")
- **Delayed:** Character processes then reacts (e.g., "withdraws, then plans retaliation")
- Mix both types for variety

**Patterns Not Scripts:**
Responses should describe behavioral tendencies, not exact actions:
- ✅ Good: "Becomes defensive and questions the other person's motives"
- ❌ Bad: "Says 'How dare you question me!' and storms out"

**Framing:**
Frame responses to show how the character interprets the trigger:
- "Interprets the question as an attack on their competence"
- "Sees the exclusion as a personal rejection"
- "Views the challenge as a threat to their identity"

**Proportionality:**
Responses should match the trigger's intensity and the enzyme's intensity. A high-intensity enzyme with a low-intensity trigger might still create a strong response, but it should feel justified by the character's psychology.`,

          designingBlindspots: `**Types of Blindspots:**

**Self-Justification Blindspots:**
- Character doesn't see their own harmful behavior
- "Fails to notice when their protection becomes control"
- "Doesn't recognize their loyalty is misplaced"

**Relational Blindspots:**
- Character misunderstands others' motivations
- "Doesn't see that their ally is using them"
- "Fails to notice when their help is unwanted"

**Consequence Blindspots:**
- Character doesn't see the results of their actions
- "Doesn't realize their defense is pushing people away"
- "Fails to notice their protection is creating dependency"

**Crack Conditions:**
Define when the blindspot might break:
- "When the consequences become undeniable"
- "When someone they trust points it out directly"
- "When the pattern repeats too many times"

Blindspots create dramatic irony—the audience sees what the character cannot.`,

          designingMutations: `**Mutation Types:**

**Hardening:**
The trait becomes more extreme and rigid:
- "Pride becomes arrogance"
- "Protection becomes control"
- "Loyalty becomes blind obedience"
- Probability: 30-40% under stress

**Collapse:**
The trait breaks down or inverts:
- "Pride becomes self-doubt"
- "Protection becomes abandonment"
- "Loyalty becomes betrayal"
- Probability: 20-30% under extreme pressure

**Transcendence:**
The character grows beyond the trait:
- "Pride becomes humility"
- "Protection becomes empowerment"
- "Loyalty becomes wisdom"
- Probability: 10-20% with support and insight

**Setting Probabilities:**
- Higher probability = more likely to occur
- Lower probability = rare but powerful when it happens
- Total probabilities should add up to 100% or less
- Leave room for the trait to remain unchanged`,

          completeExamples: `**Example 1: Wounded Pride**

**Trait:** Wounded Pride
**Intensity:** 0.8

**Triggers:**
- When their authority is questioned in front of others
- When someone gets credit for their work
- When their competence is doubted
- When they're excluded from important decisions

**Responses:**
- Immediately becomes defensive and questions the other person's motives
- Interprets the situation as a personal attack
- Seeks to reassert dominance through knowledge or position
- May withdraw and plan a way to prove themselves

**Blindspots:**
- Fails to notice when their defensiveness pushes people away
- Doesn't see that their need to prove themselves creates conflict
- Doesn't recognize when their authority is actually being respected

**Mutations:**
- Hardening (40%): Pride becomes arrogance, dismisses all criticism
- Collapse (25%): Pride breaks, becomes self-doubt and withdrawal
- Transcendence (15%): Recognizes the pattern, seeks genuine feedback

---

**Example 2: Self-Sabotage**

**Trait:** Self-Sabotage
**Intensity:** 0.7

**Triggers:**
- When success is within reach
- When they're about to achieve something important
- When others express confidence in them
- When they're about to commit to something long-term

**Responses:**
- Creates obstacles or excuses to avoid success
- Finds reasons why they don't deserve it
- Withdraws or undermines their own progress
- Seeks out distractions or alternative paths

**Blindspots:**
- Doesn't recognize the pattern of self-sabotage
- Believes their excuses are legitimate
- Fails to see they're protecting themselves from disappointment

**Mutations:**
- Hardening (35%): Sabotage becomes more extreme, actively destroys opportunities
- Collapse (30%): Pattern breaks, they finally accept success
- Transcendence (20%): Recognizes the pattern, seeks help to change

---

**Example 3: Misplaced Loyalty**

**Trait:** Misplaced Loyalty
**Intensity:** 0.75

**Triggers:**
- When their chosen person/group is criticized
- When loyalty is tested by conflicting values
- When they see their loyalty is being exploited
- When someone questions their allegiance

**Responses:**
- Defends the person/group even when wrong
- Rationalizes harmful behavior
- Prioritizes loyalty over personal values
- Becomes hostile toward critics

**Blindspots:**
- Doesn't see when their loyalty is being exploited
- Fails to notice the harm their loyalty causes
- Doesn't recognize when the object of loyalty has changed

**Mutations:**
- Hardening (30%): Loyalty becomes blind obedience, ignores all evidence
- Collapse (35%): Loyalty breaks, feels betrayed and abandoned
- Transcendence (25%): Loyalty becomes wisdom, learns to choose wisely`,

          testingEnzymes: `**Scene Test:**
Create a scene where the enzyme would activate. Does it create interesting conflict? Does the response feel natural? Does it reveal character?

**Variation Test:**
Try the same trigger with different contexts. Does the enzyme create variety, or does it always play out the same way? Mutations should help here.

**Arc Test:**
Imagine the enzyme playing out over multiple scenes. Does it create a satisfying character arc? Does it have room to evolve or change?

**Interaction Test:**
How does this enzyme interact with other characters' enzymes? Do they create interesting dynamics? Do they generate conflict and drama?

**Questions to Ask:**
- Does this enzyme create story events, not just character description?
- Does it have internal tension (both strength and weakness)?
- Are the triggers specific enough to be actionable?
- Do the responses create conflict and reveal character?
- Do blindspots create dramatic irony?
- Do mutations add complexity without making it unpredictable?`,

          commonMistakes: `**Mistake 1: Too Abstract**
❌ "When they feel threatened"
✅ "When their authority is questioned in front of others"

**Mistake 2: Scripts Instead of Patterns**
❌ "Says 'How dare you!' and storms out"
✅ "Becomes defensive and questions the other person's motives"

**Mistake 3: No Internal Tension**
❌ "Always kind and helpful"
✅ "Protective instinct that becomes controlling"

**Mistake 4: Too Many Triggers**
Having 20+ triggers makes the enzyme activate constantly. Focus on 4-8 key triggers that create the most drama.

**Mistake 5: Responses Don't Create Conflict**
❌ "Feels sad and goes home"
✅ "Withdraws but plans to prove themselves later"

**Mistake 6: Blindspots Are Too Obvious**
Blindspots should be believable. The character should have reasons for not seeing the truth.

**Mistake 7: Mutations Don't Make Sense**
Mutations should feel like natural evolutions of the trait, not random changes.

**How to Fix:**
- Make triggers more specific and actionable
- Focus responses on behavioral patterns
- Ensure traits have internal tension
- Limit triggers to the most dramatic situations
- Design responses that create conflict
- Make blindspots believable but impactful
- Connect mutations to the trait's psychology`,

          quickReference: `**Enzyme Anatomy:**
- Trait: Personality aspect with internal tension
- Intensity: 0.0-1.0 (how strongly it influences behavior)
- Triggers: 4-8 specific situations that activate the enzyme
- Responses: 3-5 behavioral patterns (not scripts)
- Blindspots: 2-3 things the character fails to notice
- Mutations: 2-3 variations with probabilities (total ≤100%)

**Intensity Guide:**
- 0.0-0.3: Subtle, occasional influence
- 0.4-0.6: Regular, moderate influence
- 0.7-0.9: Strong, frequent influence
- 1.0: Dominant, almost always active

**Mutation Probability Templates:**
- Conservative: Hardening 40%, Collapse 20%, Transcendence 10% (70% total)
- Balanced: Hardening 35%, Collapse 30%, Transcendence 20% (85% total)
- Dynamic: Hardening 30%, Collapse 35%, Transcendence 25% (90% total)

**Trigger Categories:**
- Social (status, reputation, group dynamics)
- Status (achievement, recognition, position)
- Relational (trust, loyalty, connection)
- Achievement (success, failure, competence)

**Response Patterns:**
- Immediate (instant reaction)
- Delayed (processes then reacts)
- Escalating (starts small, builds)
- Withdrawing (pulls back, plans)

**Blindspot Types:**
- Self-justification (doesn't see own harm)
- Relational (misreads others)
- Consequence (doesn't see results)`
        };

        const renderGuideSection = (id: string, title: string, content: string, searchQuery: string) => {
          const isExpanded = expandedSections.has(id);
          const query = searchQuery.toLowerCase();
          const matchesSearch = !query || title.toLowerCase().includes(query) || content.toLowerCase().includes(query);
          
          if (!matchesSearch) return null;

          const toggleSection = () => {
            const newExpanded = new Set(expandedSections);
            if (isExpanded) {
              newExpanded.delete(id);
            } else {
              newExpanded.add(id);
            }
            setExpandedSections(newExpanded);
          };

          // Format content with markdown-like formatting
          const formatContent = (text: string) => {
            return text.split('\n').map((line, idx) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return <strong key={idx} style={{ color: '#64b5f6', fontWeight: '600' }}>{line.replace(/\*\*/g, '')}</strong>;
              }
              if (line.trim() === '') {
                return <br key={idx} />;
              }
              if (line.startsWith('- ')) {
                return (
                  <div key={idx} style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                    • {line.substring(2)}
                  </div>
                );
              }
              if (line.startsWith('✅') || line.startsWith('❌')) {
                return (
                  <div key={idx} style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '8px' }}>
                    {line}
                  </div>
                );
              }
              if (line.startsWith('---')) {
                return <hr key={idx} style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #3a3a3a' }} />;
              }
              return <p key={idx} style={{ marginTop: '12px', marginBottom: '12px', lineHeight: '1.6' }}>{line}</p>;
            });
          };

          return (
            <div
              key={id}
              style={{
                marginBottom: '24px',
                backgroundColor: '#222222',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={toggleSection}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: '#f5f5f5',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a2a2a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span>{title}</span>
                <span style={{ fontSize: '1.5rem', color: '#64b5f6' }}>
                  {isExpanded ? '−' : '+'}
                </span>
              </button>
              {isExpanded && (
                <div
                  style={{
                    padding: '24px',
                    borderTop: '1px solid #3a3a3a',
                    color: '#e5e5e5',
                    lineHeight: '1.6',
                  }}
                >
                  {formatContent(content)}
                </div>
              )}
            </div>
          );
        };

        // Avoid CI lint failures for unused helpers (this IIFE is scaffolding)
        void guideContent;
        void renderGuideSection;

        // Avoid CI lint failures for unused scaffolding in this IIFE
        void guideContent;
        void renderGuideSection;

        return null; // This IIFE is just for scope, the actual rendering happens in JSX
      })()}

      <div style={appContainerStyle}>
        {/* Header with Storyworld Selector */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: headerHeight,
          padding: isMobile ? '12px 16px' : '16px 24px',
          backgroundColor: '#1a1a1a',
          borderBottom: '2px solid #3a3a3a',
          marginBottom: '24px',
          gap: isMobile ? '12px' : '16px',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}>
          <div style={{ fontSize: fontSize.title, fontWeight: '700', color: '#f5f5f5' }}>
            Splinewright
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Create with AI */}
            {!isMobile && (
              <button
                onClick={() => {
                  setShowChatbot(true);
                  if (chatMessages.length === 0) {
                    setCreationStep('welcome');
                    setPendingStoryworld(null);
                    setChatInput('');
                    // Send initial greeting
                    const greeting: ChatMessage = {
                      id: 'welcome-msg',
                      role: 'assistant',
                      content:
                        "Hello! I'm the Genome Architect, your AI assistant for creating storyworlds. I'll guide you through designing characters with psychological depth, behavioral patterns (enzymes), and the tensions that drive your story.\n\nWhat kind of story would you like to create? You can describe a premise, a genre, or just a feeling you want to explore.",
                      timestamp: new Date(),
                    };
                    setChatMessages([greeting]);
                  }
                }}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#238636',
                  border: '1px solid #2ea043',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: fontSize.small,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ✨ Create with AI
              </button>
            )}

            {/* Encounter Forge */}
            {!isMobile && (
              <button
                onClick={() => setShowEncounterForge(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#6e40c9',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                <span>🔨</span>
                <span>Encounter Forge</span>
              </button>
            )}

            {/* Storyworld Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowStoryworldDropdown(!showStoryworldDropdown)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  color: '#f5f5f5',
                  fontSize: fontSize.small,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a3a3a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a2a2a';
                }}
              >
                <span>Storyworld: {currentStoryworld}</span>
                <span>▼</span>
              </button>
              {showStoryworldDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  minWidth: '250px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
                  zIndex: 1000,
                }}>
                  <button
                    onClick={() => {
                      setShowNewStoryworldModal(true);
                      setShowStoryworldDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #3a3a3a',
                      color: '#64b5f6',
                      fontSize: '0.9rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#3a3a3a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    + New Storyworld
                  </button>
                  <button
                    onClick={() => {
                      document.getElementById('open-storyworld-file-input')?.click();
                      setShowStoryworldDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #3a3a3a',
                      color: '#81c784',
                      fontSize: '0.9rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#3a3a3a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    📥 Open Storyworld File
                  </button>
                  {storyworlds.map(sw => (
                    <div key={sw} style={{ borderBottom: '1px solid #3a3a3a' }}>
                      <button
                        onClick={() => switchStoryworld(sw)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          backgroundColor: sw === currentStoryworld ? '#3a3a3a' : 'transparent',
                          border: 'none',
                          color: sw === currentStoryworld ? '#64b5f6' : '#f5f5f5',
                          fontSize: '0.9rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                        onMouseEnter={(e) => {
                          if (sw !== currentStoryworld) {
                            e.currentTarget.style.backgroundColor = '#3a3a3a';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (sw !== currentStoryworld) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <span>{sw}</span>
                        {sw === currentStoryworld && <span>✓</span>}
                      </button>
                      <div style={{ display: 'flex', gap: '4px', padding: '4px' }}>
                        <button
                          onClick={() => exportStoryworld(sw)}
                          style={{
                            flex: 1,
                            padding: '4px 8px',
                            backgroundColor: 'transparent',
                            border: '1px solid #4a4a4a',
                            borderRadius: '4px',
                            color: '#a0a0a0',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                          title="Export"
                        >
                          Export
                        </button>
                        <button
                          onClick={() => deleteStoryworld(sw)}
                          style={{
                            flex: 1,
                            padding: '4px 8px',
                            backgroundColor: 'transparent',
                            border: '1px solid #4a4a4a',
                            borderRadius: '4px',
                            color: '#e57373',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Share/Invite Button */}
            <button
              onClick={() => {
                setShowInviteModal(true);
                setGeneratedInviteLink('');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #4a4a4a',
                borderRadius: '8px',
                color: '#f5f5f5',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
            >
              📤 Share
            </button>
            {/* Settings Button */}
            <button
              onClick={() => setCurrentView('settings')}
              style={{
                padding: '8px 16px',
                backgroundColor: currentView === 'settings' ? '#64b5f6' : '#2a2a2a',
                border: `1px solid ${currentView === 'settings' ? '#64b5f6' : '#4a4a4a'}`,
                borderRadius: '8px',
                color: '#f5f5f5',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (currentView !== 'settings') {
                  e.currentTarget.style.backgroundColor = '#3a3a3a';
                }
              }}
              onMouseLeave={(e) => {
                if (currentView !== 'settings') {
                  e.currentTarget.style.backgroundColor = '#2a2a2a';
                }
              }}
            >
              ⚙️ Settings
            </button>
          </div>
        </div>

        {/* New Storyworld Modal */}
        {showNewStoryworldModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
            onClick={() => setShowNewStoryworldModal(false)}
          >
            <div
              style={{
                backgroundColor: '#1a1a1a',
                border: '2px solid #4a4a4a',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '500px',
                width: '90%',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ color: '#f5f5f5', marginBottom: '24px', fontSize: '1.5rem' }}>
                New Storyworld
              </h2>
              
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="new-storyworld-name">
                  Storyworld Name
                </label>
                <input
                  id="new-storyworld-name"
                  type="text"
                  value={newStoryworldName}
                  onChange={(e) => setNewStoryworldName(e.target.value)}
                  placeholder="Enter storyworld name"
                  style={inputStyle}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newStoryworldName.trim()) {
                      createNewStoryworld();
                    }
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f5f5f5', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={copyFromCurrent}
                    onChange={(e) => setCopyFromCurrent(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>Copy data from current storyworld</span>
                </label>
                <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', marginLeft: '26px' }}>
                  If unchecked, starts with a blank storyworld
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowNewStoryworldModal(false);
                    setNewStoryworldName('');
                    setCopyFromCurrent(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'transparent',
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    color: '#a0a0a0',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={createNewStoryworld}
                  disabled={!newStoryworldName.trim()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: newStoryworldName.trim() ? '#64b5f6' : '#3a3a3a',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    cursor: newStoryworldName.trim() ? 'pointer' : 'not-allowed',
                    opacity: newStoryworldName.trim() ? 1 : 0.6,
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Access Level Banner */}
        {isInvitedStoryworld && storyworldAccessLevel !== 'full' && (
          <div style={{
            padding: '12px 24px',
            backgroundColor: storyworldAccessLevel === 'play' ? '#2a3a2a' : '#3a2a2a',
            borderBottom: `2px solid ${storyworldAccessLevel === 'play' ? '#4a6a4a' : '#6a4a4a'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
            <div style={{ color: '#f5f5f5', fontSize: '0.95rem' }}>
              You're viewing <strong>{currentStoryworld}</strong> ({storyworldAccessLevel === 'play' ? 'Play Only' : 'View Only'})
            </div>
            <button
              onClick={makeCopyOfStoryworld}
              style={{
                padding: '6px 12px',
                backgroundColor: '#64b5f6',
                border: 'none',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '0.85rem',
                cursor: 'pointer',
                fontWeight: '600',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#90caf9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#64b5f6';
              }}
            >
              Make a Copy
            </button>
          </div>
        )}

        {/* Click outside to close dropdown */}
        {showStoryworldDropdown && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setShowStoryworldDropdown(false)}
          />
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
            onClick={() => {
              setShowInviteModal(false);
              setGeneratedInviteLink('');
            }}
          >
            <div
              style={{
                backgroundColor: '#1a1a1a',
                border: '2px solid #4a4a4a',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '600px',
                width: '90%',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ color: '#f5f5f5', marginBottom: '24px', fontSize: '1.5rem' }}>
                Share {currentStoryworld}
              </h2>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#f5f5f5', marginBottom: '8px', fontSize: '0.95rem' }}>
                  Access Level:
                </label>
                <select
                  value={inviteAccessLevel}
                  onChange={(e) => setInviteAccessLevel(e.target.value as 'play' | 'view' | 'full')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    color: '#f5f5f5',
                    fontSize: '0.95rem',
                  }}
                >
                  <option value="play">Play Only - can play encounters, can't edit</option>
                  <option value="view">View Only - can browse but not play or edit</option>
                  <option value="full">Full Access - can edit everything</option>
                </select>
              </div>

              {!generatedInviteLink && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={generateInviteLink}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: '#64b5f6',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      fontWeight: '600',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#90caf9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#64b5f6';
                    }}
                  >
                    Generate Link
                  </button>
                  <button
                    onClick={shareStoryworldAsFile}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: '#81c784',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      fontWeight: '600',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#a5d6a7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#81c784';
                    }}
                  >
                    Share as File
                  </button>
                </div>
              )}

              {generatedInviteLink && (
                <div style={{ marginTop: '24px' }}>
                  <label style={{ display: 'block', color: '#f5f5f5', marginBottom: '8px', fontSize: '0.95rem' }}>
                    Generated Link:
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={generatedInviteLink}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#2a2a2a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        color: '#f5f5f5',
                        fontSize: '0.85rem',
                        fontFamily: 'monospace',
                      }}
                    />
                    <button
                      onClick={copyInviteLink}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#64b5f6',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                        fontWeight: '600',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#90caf9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#64b5f6';
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setGeneratedInviteLink('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#3a3a3a',
                    border: '1px solid #5a5a5a',
                    borderRadius: '8px',
                    color: '#f5f5f5',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4a4a4a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3a3a3a';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Acceptance Modal */}
        {showInviteAcceptModal && pendingInviteData && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
            onClick={() => {
              setShowInviteAcceptModal(false);
              setPendingInviteData(null);
            }}
          >
            <div
              style={{
                backgroundColor: '#1a1a1a',
                border: '2px solid #4a4a4a',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '500px',
                width: '90%',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ color: '#f5f5f5', marginBottom: '24px', fontSize: '1.5rem' }}>
                You've been invited to {pendingInviteData.name}
              </h2>
              
              <div style={{ marginBottom: '24px' }}>
                <p style={{ color: '#f5f5f5', marginBottom: '12px', fontSize: '0.95rem', fontWeight: '500' }}>
                  Shared by: {invitedStoryworldAuthor || pendingInviteData.author || 'Anonymous'}
                </p>
                <div style={{ display: 'flex', gap: '16px', marginTop: '16px', fontSize: '0.9rem', color: '#a0a0a0' }}>
                  <span>📚 {pendingInviteData.characters?.length || 0} Characters</span>
                  <span>🧬 {pendingInviteData.enzymes?.length || 0} Enzymes</span>
                  <span>⚡ {pendingInviteData.tensionCodons?.length || 0} Tension Codons</span>
                </div>
                <p style={{ color: '#64b5f6', marginTop: '16px', fontSize: '0.9rem', padding: '12px', backgroundColor: '#2a2a4a', borderRadius: '8px' }}>
                  Access Level: {pendingInviteData.accessLevel === 'play' ? 'Play Only' : pendingInviteData.accessLevel === 'view' ? 'View Only' : 'Full Access'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowInviteAcceptModal(false);
                    setPendingInviteData(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#3a3a3a',
                    border: '1px solid #5a5a5a',
                    borderRadius: '8px',
                    color: '#f5f5f5',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4a4a4a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3a3a3a';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={acceptInvite}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#64b5f6',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#90caf9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#64b5f6';
                  }}
                >
                  Open Storyworld
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hidden file inputs (keep available on mobile too) */}
        <input
          type="file"
          accept=".json,.splinewright"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              if (file.name.endsWith('.splinewright')) {
                openStoryworldFile(file);
              } else {
                importData(e);
              }
            }
          }}
          style={{ display: 'none' }}
          id="import-file-input"
        />
        <input
          type="file"
          accept=".splinewright"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              openStoryworldFile(file);
            }
          }}
          style={{ display: 'none' }}
          id="open-storyworld-file-input"
        />

        {!isMobile && (
        <div style={tabContainerStyle}>
          <div style={tabsGroupStyle}>
            <button
              style={tabStyle(currentView === 'storyworld')}
              onClick={() => setCurrentView('storyworld')}
            >
              📖 Story
            </button>
            <button
              style={tabStyle(currentView === 'characters')}
              onClick={() => setCurrentView('characters')}
            >
              👥 Characters
            </button>
            {interfaceMode !== 'story' && (
            <button
              style={tabStyle(currentView === 'editor')}
              onClick={() => setCurrentView('editor')}
            >
              🧬 Patterns
            </button>
            )}
            <button
              style={tabStyle(currentView === 'encounters')}
              onClick={() => setCurrentView('encounters')}
            >
              🎭 Scenes
            </button>
            {(storyworldAccessLevel === 'full' || storyworldAccessLevel === 'play') && (
              <button
                style={tabStyle(currentView === 'play')}
                onClick={() => setCurrentView('play')}
              >
                ▶ Play
              </button>
            )}
            <button
              style={tabStyle(currentView === 'settings')}
              onClick={() => setCurrentView('settings')}
            >
              ⚙️ Settings
            </button>
            <button
              style={tabStyle(currentView === 'library')}
              onClick={() => setCurrentView('library')}
            >
              Library
            </button>
            <button
              style={tabStyle(currentView === 'guide')}
              onClick={() => setCurrentView('guide')}
            >
              Guide
            </button>
          </div>
          <div style={exportImportGroupStyle}>
            <button
              onClick={() => document.getElementById('open-storyworld-file-input')?.click()}
              style={exportImportButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4a4a4a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
              }}
              title="Open Shared Storyworld File"
            >
              📥 Open Storyworld File
            </button>
            <button
              onClick={() => document.getElementById('import-file-input')?.click()}
              style={exportImportButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4a4a4a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
              }}
            >
              Import
            </button>
            <button
              onClick={exportData}
              style={exportImportButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4a4a4a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
              }}
            >
              Export
            </button>
            <button
              onClick={() => setCurrentView('settings')}
              style={{
                ...exportImportButtonStyle,
                backgroundColor: currentView === 'settings' ? '#64b5f6' : '#3a3a3a',
                borderColor: currentView === 'settings' ? '#64b5f6' : '#4a4a4a',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: currentView === 'settings' ? '600' : '500',
              }}
              onMouseEnter={(e) => {
                if (currentView !== 'settings') {
                  e.currentTarget.style.backgroundColor = '#4a4a4a';
                }
              }}
              onMouseLeave={(e) => {
                if (currentView !== 'settings') {
                  e.currentTarget.style.backgroundColor = '#3a3a3a';
                }
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>⚙️</span>
              Settings
            </button>
            <button
              onClick={() => setShowHelpModal(true)}
              style={{
                ...exportImportButtonStyle,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4a4a4a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>❓</span>
              Help
            </button>
          </div>
        </div>
        )}
        <div style={mainContentContainerStyle}>
          {/* Sidebar - desktop only */}
          {!isMobile && (
          <aside style={sidebarStyle}>
            {currentView === 'editor' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Saved Patterns</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {savedEnzymes.length} {savedEnzymes.length === 1 ? 'pattern' : 'patterns'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
            {savedEnzymes.length === 0 ? (
              <p style={emptySidebarStyle}>No patterns saved yet</p>
            ) : (
              savedEnzymes.map((enzyme) => (
                <div
                  key={enzyme.id}
                  style={enzymeItemStyle(selectedEnzymeId === enzyme.id, enzymeHover === enzyme.id)}
                  onClick={() => loadEnzyme(enzyme)}
                  onMouseEnter={() => setEnzymeHover(enzyme.id)}
                  onMouseLeave={() => setEnzymeHover(null)}
                >
                  <div style={enzymeItemHeaderStyle}>
                    <div style={enzymeNameStyle}>{enzyme.characterName}</div>
                    <button
                      onClick={(e) => deleteEnzyme(enzyme.id, e)}
                      style={enzymeDeleteButtonStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                      aria-label="Delete enzyme"
                    >
                      ×
                    </button>
                  </div>
                  <div style={enzymeTraitStyle}>{enzyme.traitName}</div>
                  <div style={enzymeMetaStyle}>
                    <span>Intensity: {enzyme.intensity.toFixed(2)}</span>
                    <span>•</span>
                    <span>{enzyme.triggers.length} triggers</span>
                    <span>•</span>
                    <span>{enzyme.responses.length} responses</span>
                    <span>•</span>
                    <span>{(enzyme.blindspots || []).length} blindspots</span>
                    <span>•</span>
                    <span>{(enzyme.mutations || []).length} mutations</span>
                  </div>
                </div>
              ))
            )}
                </div>
              </>
            ) : currentView === 'characters' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Characters</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {characters.length} {characters.length === 1 ? 'character' : 'characters'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
                  {characters.length === 0 ? (
                    <p style={emptySidebarStyle}>No characters created yet</p>
                  ) : (
                    characters.map((character) => (
                      <div
                        key={character.id}
                        style={characterItemStyle(selectedCharacterId === character.id, characterHover === character.id)}
                        onClick={() => setSelectedCharacterId(character.id)}
                        onMouseEnter={() => setCharacterHover(character.id)}
                        onMouseLeave={() => setCharacterHover(null)}
                      >
                        <div style={characterNameStyle}>{character.name}</div>
                        {character.description && (
                          <div style={characterDescriptionStyle}>
                            {character.description.length > 60
                              ? character.description.substring(0, 60) + '...'
                              : character.description}
                          </div>
                        )}
                        <div style={enzymeMetaStyle}>
                          <span>{character.attachedEnzymes.length} enzymes</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : currentView === 'storyworld' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>{interfaceMode === 'story' ? 'Pressures' : 'Tension Codons'}</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {tensionCodons.length} {interfaceMode === 'story' ? (tensionCodons.length === 1 ? 'pressure' : 'pressures') : (tensionCodons.length === 1 ? 'codon' : 'codons')}
                  </p>
                </div>
                <div style={enzymeListStyle}>
                  {tensionCodons.length === 0 ? (
                    <p style={emptySidebarStyle}>{interfaceMode === 'story' ? 'No pressures yet' : 'No tension codons created yet'}</p>
                  ) : (
                    tensionCodons.map((codon) => (
                      <div
                        key={codon.id}
                        style={enzymeItemStyle(false, tensionCodonHover === codon.id)}
                        onMouseEnter={() => setTensionCodonHover(codon.id)}
                        onMouseLeave={() => setTensionCodonHover(null)}
                      >
                        <div style={enzymeItemHeaderStyle}>
                          <div style={enzymeNameStyle}>{codon.name || (interfaceMode === 'story' ? 'Unnamed Pressure' : 'Unnamed Codon')}</div>
                        </div>
                        <div style={enzymeTraitStyle}>{interfaceMode === 'story' ? pressureToHuman(codon.pressureLevel) : `Pressure: ${codon.pressureLevel.toFixed(2)}`}</div>
                        <div style={enzymeMetaStyle}>
                          <span>{codon.affectedCharacterIds.length} characters</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : currentView === 'encounters' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Scenes</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {encounters.length} {encounters.length === 1 ? 'scene' : 'scenes'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
                  {encounters.length === 0 ? (
                    <p style={emptySidebarStyle}>No scenes created yet</p>
                  ) : (
                    encounters.map((encounter) => (
                      <div
                        key={encounter.id}
                        style={enzymeItemStyle(false, false)}
                      >
                        <div style={enzymeItemHeaderStyle}>
                          <div style={enzymeNameStyle}>{encounter.title || 'Untitled Encounter'}</div>
                        </div>
                        <div style={enzymeTraitStyle}>
                          {encounter.characterIds.length} {encounter.characterIds.length === 1 ? 'character' : 'characters'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : currentView === 'play' ? (
              <>
                <div style={sidebarHeaderStyle}>
                  <h2 style={sidebarTitleStyle}>Available Scenes</h2>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {encounters.length} {encounters.length === 1 ? 'scene' : 'scenes'}
                  </p>
                </div>
                <div style={enzymeListStyle}>
                  {encounters.length === 0 ? (
                    <p style={emptySidebarStyle}>No scenes available. Create some in the Scenes tab.</p>
                  ) : (
                    encounters.map((encounter, index) => (
                      <div
                        key={encounter.id}
                        style={enzymeItemStyle(
                          playthroughActive && currentEncounterIndex === index,
                          false
                        )}
                      >
                        <div style={enzymeItemHeaderStyle}>
                          <div style={enzymeNameStyle}>{encounter.title || 'Untitled Encounter'}</div>
                        </div>
                        <div style={enzymeTraitStyle}>
                          {encounter.characterIds.length} {encounter.characterIds.length === 1 ? 'character' : 'characters'}
                        </div>
                        {playthroughActive && currentEncounterIndex === index && (
                          <div style={{ fontSize: '0.75rem', color: '#64b5f6', marginTop: '4px' }}>
                            Current
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <div style={sidebarHeaderStyle}>
                <h2 style={sidebarTitleStyle}>Settings</h2>
              </div>
            )}
          </aside>
          )}

        <div style={containerStyle}>
          {currentView === 'editor' ? (
            isMobile && !selectedEnzymeId ? (
              <div style={{ padding: '16px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}>
                  <h1 style={{ fontSize: '24px', margin: 0, color: '#f5f5f5' }}>Enzymes</h1>
                  <button
                    onClick={createNewEnzyme}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: '#238636',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    + New
                  </button>
                </div>

                {savedEnzymes.length === 0 ? (
                  <div style={{ color: '#8b949e', padding: '12px 0' }}>
                    No enzymes saved yet.
                  </div>
                ) : (
                  savedEnzymes.map(enzyme => (
                    <div
                      key={enzyme.id}
                      onClick={() => loadEnzyme(enzyme)}
                      style={{
                        padding: '16px',
                        backgroundColor: '#21262d',
                        borderRadius: '12px',
                        marginBottom: '12px',
                        cursor: 'pointer',
                        border: '1px solid #30363d',
                      }}
                    >
                      <div style={{ fontWeight: '600', fontSize: '16px', color: '#f5f5f5' }}>
                        {enzyme.traitName || 'Untitled Enzyme'}
                      </div>
                      <div style={{ color: '#8b949e', fontSize: '14px', marginTop: '4px' }}>
                        {enzyme.characterName || 'Unknown'} • {typeof enzyme.intensity === 'number' ? enzyme.intensity.toFixed(2) : enzyme.intensity}
                      </div>
                      <div style={{ color: '#6e7681', fontSize: '12px', marginTop: '6px' }}>
                        {(enzyme.triggers?.length || 0)} triggers
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
            <>
              {isMobile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <button
                    onClick={() => setSelectedEnzymeId(null)}
                    style={{
                      padding: '10px 12px',
                      backgroundColor: '#21262d',
                      border: '1px solid #30363d',
                      borderRadius: '10px',
                      color: '#f5f5f5',
                      cursor: 'pointer',
                      fontWeight: '600',
                    }}
                  >
                    ← Back
                  </button>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#f5f5f5' }}>
                    Pattern Editor
                  </div>
                </div>
              )}

              {!isMobile && (
                <header style={headerStyle}>
                  <h1 style={h1Style}>Splinewright</h1>
                  <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Character pattern authoring</p>
                </header>
              )}

          <div style={actionButtonsStyle}>
            <button
              onClick={isMobile ? createNewEnzyme : newEnzyme}
              style={getNewButtonStyle(buttonHover === 'new')}
              onMouseEnter={() => setButtonHover('new')}
              onMouseLeave={() => setButtonHover(null)}
            >
              New Pattern
            </button>
            <button
              onClick={saveEnzyme}
              style={getButtonStyle(buttonHover === 'save')}
              onMouseEnter={() => setButtonHover('save')}
              onMouseLeave={() => setButtonHover(null)}
            >
              Save Pattern
            </button>
            <button
              onClick={generateSuggestions}
              style={cobotGenerateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#90caf9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#64b5f6';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span>✨</span>
              Generate Suggestions
            </button>
          </div>

          <div>
          <div style={formGroupStyle}>
            <label style={labelStyle} htmlFor="character-name">
              Character Name
            </label>
            <input
              id="character-name"
              type="text"
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              placeholder="Enter character name"
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle} htmlFor="trait-name">
              Trait Name
            </label>
            <input
              id="trait-name"
              type="text"
              value={traitName}
              onChange={(e) => setTraitName(e.target.value)}
              placeholder="Enter trait name (e.g., 'curiosity', 'bravery')"
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle} htmlFor="intensity">
              Intensity
            </label>
            <div style={sliderContainerStyle}>
              <div style={sliderWrapperStyle}>
                <input
                  id="intensity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={intensity}
                  onChange={(e) => setIntensity(parseFloat(e.target.value))}
                  style={sliderStyle}
                />
                <span style={intensityValueStyle}>{intensity.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            {/* Triggers Section - Accordion on Mobile */}
            <div style={{ backgroundColor: '#21262d', borderRadius: '12px', overflow: 'hidden', border: '1px solid #30363d' }}>
              <button
                onClick={() => toggleEnzymeEditorSection('triggers')}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#f0f6fc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontWeight: '600' }}>
                  {enzymeEditorExpandedSections.triggers ? '▼' : '▶'} TRIGGERS ({triggers.length})
                </span>
              </button>
              {enzymeEditorExpandedSections.triggers && (
                <div style={{ padding: '0 16px 16px' }}>
                  <p style={{ ...sectionDescStyle, marginTop: 0 }}>Things that activate this behavior</p>
                  <div style={addContainerStyle}>
                    <input
                      type="text"
                      value={newTrigger}
                      onChange={(e) => setNewTrigger(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, addTrigger)}
                      placeholder="Enter a trigger..."
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      onClick={addTrigger}
                      style={getButtonStyle(buttonHover === 'add-trigger')}
                      onMouseEnter={() => setButtonHover('add-trigger')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Add
                    </button>
                  </div>
                  <div style={itemsListStyle}>
                    {triggers.length === 0 ? (
                      <p style={emptyStateStyle}>No triggers added yet</p>
                    ) : (
                      triggers.map((trigger) => (
                        <div key={trigger.id} style={itemStyle}>
                          <span style={itemTextStyle}>{trigger.text}</span>
                          <button
                            onClick={() => removeTrigger(trigger.id)}
                            style={getRemoveButtonStyle(removeHover === trigger.id)}
                            onMouseEnter={() => setRemoveHover(trigger.id)}
                            onMouseLeave={() => setRemoveHover(null)}
                            aria-label="Remove trigger"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Responses Section - Accordion on Mobile */}
            <div style={{ backgroundColor: '#21262d', borderRadius: '12px', overflow: 'hidden', border: '1px solid #30363d' }}>
              <button
                onClick={() => toggleEnzymeEditorSection('responses')}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#f0f6fc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontWeight: '600' }}>
                  {enzymeEditorExpandedSections.responses ? '▼' : '▶'} RESPONSES ({responses.length})
                </span>
              </button>
              {enzymeEditorExpandedSections.responses && (
                <div style={{ padding: '0 16px 16px' }}>
                  <p style={{ ...sectionDescStyle, marginTop: 0 }}>What the character does when triggered</p>
                  <div style={addContainerStyle}>
                    <input
                      type="text"
                      value={newResponse}
                      onChange={(e) => setNewResponse(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, addResponse)}
                      placeholder="Enter a response..."
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      onClick={addResponse}
                      style={getButtonStyle(buttonHover === 'add-response')}
                      onMouseEnter={() => setButtonHover('add-response')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Add
                    </button>
                  </div>
                  <div style={itemsListStyle}>
                    {responses.length === 0 ? (
                      <p style={emptyStateStyle}>No responses added yet</p>
                    ) : (
                      responses.map((response) => (
                        <div key={response.id} style={itemStyle}>
                          <span style={itemTextStyle}>{response.text}</span>
                          <button
                            onClick={() => removeResponse(response.id)}
                            style={getRemoveButtonStyle(removeHover === response.id)}
                            onMouseEnter={() => setRemoveHover(response.id)}
                            onMouseLeave={() => setRemoveHover(null)}
                            aria-label="Remove response"
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Blindspots Section - Accordion on Mobile */}
            <div style={{ backgroundColor: '#21262d', borderRadius: '12px', overflow: 'hidden', border: '1px solid #30363d' }}>
              <button
                onClick={() => toggleEnzymeEditorSection('blindspot')}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#f0f6fc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontWeight: '600' }}>
                  {enzymeEditorExpandedSections.blindspot ? '▼' : '▶'} BLINDSPOTS ({blindspots.length})
                </span>
              </button>
              {enzymeEditorExpandedSections.blindspot && (
                <div style={{ padding: '0 16px 16px' }}>
                  <p style={{ ...sectionDescStyle, marginTop: 0 }}>Things the character cannot see about themselves</p>
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      onClick={addBlindspot}
                      style={getButtonStyle(buttonHover === 'add-blindspot')}
                      onMouseEnter={() => setButtonHover('add-blindspot')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Add Blindspot
                    </button>
                  </div>
                  <div>
                    {blindspots.length === 0 ? (
                      <p style={emptyStateStyle}>No blindspots added yet</p>
                    ) : (
                      blindspots.map((blindspot) => (
                        <div key={blindspot.id} style={blindspotItemStyle}>
                          <div style={blindspotHeaderStyle}>
                            <h3 style={blindspotTitleStyle}>Blindspot {blindspots.indexOf(blindspot) + 1}</h3>
                            <button
                              onClick={() => removeBlindspot(blindspot.id)}
                              style={getRemoveButtonStyle(removeHover === blindspot.id)}
                              onMouseEnter={() => setRemoveHover(blindspot.id)}
                              onMouseLeave={() => setRemoveHover(null)}
                              aria-label="Remove blindspot"
                            >
                              ×
                            </button>
                          </div>
                          <div style={blindspotFieldStyle}>
                            <label style={blindspotLabelStyle} htmlFor={`blindspot-desc-${blindspot.id}`}>
                              Description
                            </label>
                            <textarea
                              id={`blindspot-desc-${blindspot.id}`}
                              value={blindspot.description}
                              onChange={(e) => updateBlindspot(blindspot.id, 'description', e.target.value)}
                              placeholder="What the character cannot see about themselves..."
                              style={blindspotTextareaStyle}
                            />
                          </div>
                          <div style={blindspotFieldStyle}>
                            <label style={blindspotLabelStyle} htmlFor={`blindspot-just-${blindspot.id}`}>
                              Self-Justification
                            </label>
                            <textarea
                              id={`blindspot-just-${blindspot.id}`}
                              value={blindspot.selfJustification}
                              onChange={(e) => updateBlindspot(blindspot.id, 'selfJustification', e.target.value)}
                              placeholder="How the character explains their behavior to themselves..."
                              style={blindspotTextareaStyle}
                            />
                          </div>
                          <div style={blindspotCheckboxContainerStyle}>
                            <input
                              type="checkbox"
                              id={`blindspot-crack-${blindspot.id}`}
                              checked={blindspot.canCrack}
                              onChange={(e) => updateBlindspot(blindspot.id, 'canCrack', e.target.checked)}
                              style={blindspotCheckboxStyle}
                            />
                            <label htmlFor={`blindspot-crack-${blindspot.id}`} style={blindspotCheckboxLabelStyle}>
                              Can crack under pressure
                            </label>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Mutations Section - Accordion on Mobile */}
            <div style={{ backgroundColor: '#21262d', borderRadius: '12px', overflow: 'hidden', border: '1px solid #30363d' }}>
              <button
                onClick={() => toggleEnzymeEditorSection('mutations')}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#f0f6fc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontWeight: '600' }}>
                  {enzymeEditorExpandedSections.mutations ? '▼' : '▶'} MUTATIONS ({mutations.length})
                </span>
              </button>
              {enzymeEditorExpandedSections.mutations && (
                <div style={{ padding: '0 16px 16px' }}>
                  <p style={{ ...sectionDescStyle, marginTop: 0 }}>How the enzyme changes under sustained pressure</p>
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      onClick={addMutation}
                      style={getButtonStyle(buttonHover === 'add-mutation')}
                      onMouseEnter={() => setButtonHover('add-mutation')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Add Mutation Path
                    </button>
                  </div>
                  <div>
                    {mutations.length === 0 ? (
                      <p style={emptyStateStyle}>No mutations added yet</p>
                    ) : (
                      mutations.map((mutation) => (
                        <div key={mutation.id} style={mutationItemStyle}>
                          <div style={mutationHeaderStyle}>
                            <h3 style={mutationTitleStyle}>Mutation Path {mutations.indexOf(mutation) + 1}</h3>
                            <button
                              onClick={() => removeMutation(mutation.id)}
                              style={getRemoveButtonStyle(removeHover === mutation.id)}
                              onMouseEnter={() => setRemoveHover(mutation.id)}
                              onMouseLeave={() => setRemoveHover(null)}
                              aria-label="Remove mutation"
                            >
                              ×
                            </button>
                          </div>
                          <div style={mutationFieldStyle}>
                            <label style={mutationLabelStyle} htmlFor={`mutation-name-${mutation.id}`}>
                              Name
                            </label>
                            <input
                              id={`mutation-name-${mutation.id}`}
                              type="text"
                              value={mutation.name}
                              onChange={(e) => updateMutation(mutation.id, 'name', e.target.value)}
                              placeholder="e.g., 'Hardening', 'Collapse'"
                              style={mutationInputStyle}
                            />
                          </div>
                          <div style={mutationFieldStyle}>
                            <label style={mutationLabelStyle} htmlFor={`mutation-desc-${mutation.id}`}>
                              Description
                            </label>
                            <textarea
                              id={`mutation-desc-${mutation.id}`}
                              value={mutation.description}
                              onChange={(e) => updateMutation(mutation.id, 'description', e.target.value)}
                              placeholder="What the enzyme becomes under sustained pressure..."
                              style={mutationTextareaStyle}
                            />
                          </div>
                          <div style={mutationFieldStyle}>
                            <label style={mutationLabelStyle} htmlFor={`mutation-prob-${mutation.id}`}>
                              Probability
                            </label>
                            <div style={mutationProbabilityContainerStyle}>
                              <input
                                id={`mutation-prob-${mutation.id}`}
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={mutation.probability}
                                onChange={(e) => updateMutation(mutation.id, 'probability', Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                                style={mutationProbabilityInputStyle}
                              />
                              <span style={mutationProbabilityLabelStyle}>%</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div style={sectionsContainerStyle}>
              <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>Triggers</h2>
                <p style={sectionDescStyle}>Things that activate this behavior</p>
                <div style={addContainerStyle}>
                  <input
                    type="text"
                    value={newTrigger}
                    onChange={(e) => setNewTrigger(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, addTrigger)}
                    placeholder="Enter a trigger..."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    onClick={addTrigger}
                    style={getButtonStyle(buttonHover === 'add-trigger')}
                    onMouseEnter={() => setButtonHover('add-trigger')}
                    onMouseLeave={() => setButtonHover(null)}
                  >
                    Add
                  </button>
                </div>
                <div style={itemsListStyle}>
                  {triggers.length === 0 ? (
                    <p style={emptyStateStyle}>No triggers added yet</p>
                  ) : (
                    triggers.map((trigger) => (
                      <div key={trigger.id} style={itemStyle}>
                        <span style={itemTextStyle}>{trigger.text}</span>
                        <button
                          onClick={() => removeTrigger(trigger.id)}
                          style={getRemoveButtonStyle(removeHover === trigger.id)}
                          onMouseEnter={() => setRemoveHover(trigger.id)}
                          onMouseLeave={() => setRemoveHover(null)}
                          aria-label="Remove trigger"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>Responses</h2>
                <p style={sectionDescStyle}>What the character does when triggered</p>
                <div style={addContainerStyle}>
                  <input
                    type="text"
                    value={newResponse}
                    onChange={(e) => setNewResponse(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, addResponse)}
                    placeholder="Enter a response..."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    onClick={addResponse}
                    style={getButtonStyle(buttonHover === 'add-response')}
                    onMouseEnter={() => setButtonHover('add-response')}
                    onMouseLeave={() => setButtonHover(null)}
                  >
                    Add
                  </button>
                </div>
                <div style={itemsListStyle}>
                  {responses.length === 0 ? (
                    <p style={emptyStateStyle}>No responses added yet</p>
                  ) : (
                    responses.map((response) => (
                      <div key={response.id} style={itemStyle}>
                        <span style={itemTextStyle}>{response.text}</span>
                        <button
                          onClick={() => removeResponse(response.id)}
                          style={getRemoveButtonStyle(removeHover === response.id)}
                          onMouseEnter={() => setRemoveHover(response.id)}
                          onMouseLeave={() => setRemoveHover(null)}
                          aria-label="Remove response"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '48px' }}>
              <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>Blindspots</h2>
                <p style={sectionDescStyle}>Things the character cannot see about themselves</p>
                <div style={{ marginBottom: '24px' }}>
                  <button
                    onClick={addBlindspot}
                    style={getButtonStyle(buttonHover === 'add-blindspot')}
                    onMouseEnter={() => setButtonHover('add-blindspot')}
                    onMouseLeave={() => setButtonHover(null)}
                  >
                    Add Blindspot
                  </button>
                </div>
                <div>
                  {blindspots.length === 0 ? (
                    <p style={emptyStateStyle}>No blindspots added yet</p>
                  ) : (
                    blindspots.map((blindspot) => (
                      <div key={blindspot.id} style={blindspotItemStyle}>
                        <div style={blindspotHeaderStyle}>
                          <h3 style={blindspotTitleStyle}>Blindspot {blindspots.indexOf(blindspot) + 1}</h3>
                          <button
                            onClick={() => removeBlindspot(blindspot.id)}
                            style={getRemoveButtonStyle(removeHover === blindspot.id)}
                            onMouseEnter={() => setRemoveHover(blindspot.id)}
                            onMouseLeave={() => setRemoveHover(null)}
                            aria-label="Remove blindspot"
                          >
                            ×
                          </button>
                        </div>
                        <div style={blindspotFieldStyle}>
                          <label style={blindspotLabelStyle} htmlFor={`blindspot-desc-${blindspot.id}`}>
                            Description
                          </label>
                          <textarea
                            id={`blindspot-desc-${blindspot.id}`}
                            value={blindspot.description}
                            onChange={(e) => updateBlindspot(blindspot.id, 'description', e.target.value)}
                            placeholder="What the character cannot see about themselves..."
                            style={blindspotTextareaStyle}
                          />
                        </div>
                        <div style={blindspotFieldStyle}>
                          <label style={blindspotLabelStyle} htmlFor={`blindspot-just-${blindspot.id}`}>
                            Self-Justification
                          </label>
                          <textarea
                            id={`blindspot-just-${blindspot.id}`}
                            value={blindspot.selfJustification}
                            onChange={(e) => updateBlindspot(blindspot.id, 'selfJustification', e.target.value)}
                            placeholder="How the character explains their behavior to themselves..."
                            style={blindspotTextareaStyle}
                          />
                        </div>
                        <div style={blindspotCheckboxContainerStyle}>
                          <input
                            type="checkbox"
                            id={`blindspot-crack-${blindspot.id}`}
                            checked={blindspot.canCrack}
                            onChange={(e) => updateBlindspot(blindspot.id, 'canCrack', e.target.checked)}
                            style={blindspotCheckboxStyle}
                          />
                          <label htmlFor={`blindspot-crack-${blindspot.id}`} style={blindspotCheckboxLabelStyle}>
                            Can crack under pressure
                          </label>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '48px' }}>
              <div style={sectionStyle}>
                <h2 style={sectionTitleStyle}>Mutations</h2>
                <p style={sectionDescStyle}>How the enzyme changes under sustained pressure</p>
                <div style={{ marginBottom: '24px' }}>
                  <button
                    onClick={addMutation}
                    style={getButtonStyle(buttonHover === 'add-mutation')}
                    onMouseEnter={() => setButtonHover('add-mutation')}
                    onMouseLeave={() => setButtonHover(null)}
                  >
                    Add Mutation Path
                  </button>
                </div>
                <div>
                  {mutations.length === 0 ? (
                    <p style={emptyStateStyle}>No mutations added yet</p>
                  ) : (
                    mutations.map((mutation) => (
                      <div key={mutation.id} style={mutationItemStyle}>
                        <div style={mutationHeaderStyle}>
                          <h3 style={mutationTitleStyle}>Mutation Path {mutations.indexOf(mutation) + 1}</h3>
                          <button
                            onClick={() => removeMutation(mutation.id)}
                            style={getRemoveButtonStyle(removeHover === mutation.id)}
                            onMouseEnter={() => setRemoveHover(mutation.id)}
                            onMouseLeave={() => setRemoveHover(null)}
                            aria-label="Remove mutation"
                          >
                            ×
                          </button>
                        </div>
                        <div style={mutationFieldStyle}>
                          <label style={mutationLabelStyle} htmlFor={`mutation-name-${mutation.id}`}>
                            Name
                          </label>
                          <input
                            id={`mutation-name-${mutation.id}`}
                            type="text"
                            value={mutation.name}
                            onChange={(e) => updateMutation(mutation.id, 'name', e.target.value)}
                            placeholder="e.g., 'Hardening', 'Collapse'"
                            style={mutationInputStyle}
                          />
                        </div>
                        <div style={mutationFieldStyle}>
                          <label style={mutationLabelStyle} htmlFor={`mutation-desc-${mutation.id}`}>
                            Description
                          </label>
                          <textarea
                            id={`mutation-desc-${mutation.id}`}
                            value={mutation.description}
                            onChange={(e) => updateMutation(mutation.id, 'description', e.target.value)}
                            placeholder="What the enzyme becomes under sustained pressure..."
                            style={mutationTextareaStyle}
                          />
                        </div>
                        <div style={mutationFieldStyle}>
                          <label style={mutationLabelStyle} htmlFor={`mutation-prob-${mutation.id}`}>
                            Probability
                          </label>
                          <div style={mutationProbabilityContainerStyle}>
                            <input
                              id={`mutation-prob-${mutation.id}`}
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={mutation.probability}
                              onChange={(e) => updateMutation(mutation.id, 'probability', Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                              style={mutationProbabilityInputStyle}
                            />
                            <span style={mutationProbabilityLabelStyle}>%</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
            </>
            )
          ) : currentView === 'characters' ? (
            isMobile && !selectedCharacterId ? (
              <div style={{ padding: '16px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}>
                  <h1 style={{ fontSize: '24px', margin: 0, color: '#f5f5f5' }}>Characters</h1>
                  <button
                    onClick={() => setShowMobileCreateCharacter(true)}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: '#238636',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    + New
                  </button>
                </div>

                {characters.length === 0 ? (
                  <div style={{ color: '#8b949e', padding: '12px 0' }}>
                    No characters yet.
                  </div>
                ) : (
                  characters.map((character) => (
                    <div
                      key={character.id}
                      onClick={() => setSelectedCharacterId(character.id)}
                      style={{
                        padding: '16px',
                        backgroundColor: '#21262d',
                        borderRadius: '12px',
                        marginBottom: '12px',
                        cursor: 'pointer',
                        border: '1px solid #30363d',
                      }}
                    >
                      <div style={{ fontWeight: '600', fontSize: '16px', color: '#f5f5f5' }}>
                        {character.name || 'Unnamed Character'}
                      </div>
                      {character.description ? (
                        <div style={{ color: '#8b949e', fontSize: '14px', marginTop: '4px' }}>
                          {character.description.length > 80 ? character.description.slice(0, 80) + '…' : character.description}
                        </div>
                      ) : (
                        <div style={{ color: '#8b949e', fontSize: '14px', marginTop: '4px' }}>
                          No description
                        </div>
                      )}
                      <div style={{ color: '#6e7681', fontSize: '12px', marginTop: '6px' }}>
                        {(character.attachedEnzymes?.length || 0)} enzymes attached
                      </div>
                    </div>
                  ))
                )}

                {showMobileCreateCharacter && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.75)',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      zIndex: 2000,
                      padding: '16px',
                    }}
                    onClick={() => setShowMobileCreateCharacter(false)}
                  >
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '520px',
                        backgroundColor: '#161b22',
                        border: '1px solid #30363d',
                        borderRadius: '16px',
                        padding: '16px',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ color: '#f5f5f5', fontWeight: 700, fontSize: '1rem' }}>New Character</div>
                        <button
                          onClick={() => setShowMobileCreateCharacter(false)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#8b949e',
                            fontSize: '1.4rem',
                            cursor: 'pointer',
                            padding: '4px 8px',
                          }}
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input
                          type="text"
                          value={newCharacterName}
                          onChange={(e) => setNewCharacterName(e.target.value)}
                          placeholder="Character name"
                          style={{
                            width: '100%',
                            padding: '12px 12px',
                            borderRadius: '10px',
                            border: '1px solid #30363d',
                            backgroundColor: '#0d1117',
                            color: '#f5f5f5',
                          }}
                        />
                        <textarea
                          value={newCharacterDescription}
                          onChange={(e) => setNewCharacterDescription(e.target.value)}
                          placeholder="Description (optional)"
                          style={{
                            width: '100%',
                            padding: '12px 12px',
                            borderRadius: '10px',
                            border: '1px solid #30363d',
                            backgroundColor: '#0d1117',
                            color: '#f5f5f5',
                            minHeight: '100px',
                            resize: 'vertical',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            onClick={() => setShowMobileCreateCharacter(false)}
                            style={{
                              flex: 1,
                              padding: '12px',
                              backgroundColor: '#21262d',
                              border: '1px solid #30363d',
                              borderRadius: '10px',
                              color: '#8b949e',
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              createCharacter();
                              setShowMobileCreateCharacter(false);
                            }}
                            style={{
                              flex: 1,
                              padding: '12px',
                              backgroundColor: '#238636',
                              border: 'none',
                              borderRadius: '10px',
                              color: '#ffffff',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            Create
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Characters</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Manage characters and their enzymes</p>
              </header>

              {selectedCharacterId ? (
                <div>
                  {(() => {
                    const character = characters.find(c => c.id === selectedCharacterId);
                    if (!character) return null;
                    return (
                      <div>
                        <div style={{ marginBottom: '32px' }}>
                          <button
                            onClick={() => setSelectedCharacterId(null)}
                            style={getNewButtonStyle(buttonHover === 'back')}
                            onMouseEnter={() => setButtonHover('back')}
                            onMouseLeave={() => setButtonHover(null)}
                          >
                            ← Back to Characters
                          </button>
                        </div>

                        <div style={sectionStyle}>
                          <h2 style={sectionTitleStyle}>{character.name}</h2>
                          {character.description && (
                            <p style={sectionDescStyle}>{character.description}</p>
                          )}

                          <div style={{ marginBottom: '24px' }}>
                            <label style={labelStyle} htmlFor="attach-enzyme">
                              Attach Enzyme
                            </label>
                            <select
                              id="attach-enzyme"
                              onChange={(e) => {
                                if (e.target.value) {
                                  attachEnzymeToCharacter(character.id, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              style={enzymeSelectStyle}
                            >
                              <option value="">Select an enzyme to attach...</option>
                              {savedEnzymes
                                .filter(enzyme => !character.attachedEnzymes.some(ae => ae.enzymeId === enzyme.id))
                                .map(enzyme => (
                                  <option key={enzyme.id} value={enzyme.id}>
                                    {enzyme.characterName} - {enzyme.traitName}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div>
                            <h3 style={{ ...sectionTitleStyle, fontSize: '1.25rem', marginBottom: '16px' }}>
                              Attached Enzymes
                            </h3>
                            {character.attachedEnzymes.length === 0 ? (
                              <p style={emptyStateStyle}>No enzymes attached yet</p>
                            ) : (
                              character.attachedEnzymes.map((attachedEnzyme) => {
                                const enzyme = savedEnzymes.find(e => e.id === attachedEnzyme.enzymeId);
                                if (!enzyme) return null;
                                return (
                                  <div key={attachedEnzyme.enzymeId} style={attachedEnzymeItemStyle}>
                                    <div style={attachedEnzymeInfoStyle}>
                                      <div style={attachedEnzymeNameStyle}>{enzyme.characterName}</div>
                                      <div style={attachedEnzymeTraitStyle}>{enzyme.traitName}</div>
                                    </div>
                                    <div style={attachedEnzymeIntensityStyle}>
                                      <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={attachedEnzyme.intensity}
                                        onChange={(e) => updateAttachedEnzymeIntensity(character.id, attachedEnzyme.enzymeId, parseFloat(e.target.value))}
                                        style={intensitySliderSmallStyle}
                                      />
                                      <span style={intensityValueSmallStyle}>{attachedEnzyme.intensity.toFixed(2)}</span>
                                      <button
                                        onClick={() => detachEnzymeFromCharacter(character.id, attachedEnzyme.enzymeId)}
                                        style={getRemoveButtonStyle(removeHover === attachedEnzyme.enzymeId)}
                                        onMouseEnter={() => setRemoveHover(attachedEnzyme.enzymeId)}
                                        onMouseLeave={() => setRemoveHover(null)}
                                        aria-label="Detach enzyme"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '32px' }}>
                    <div style={sectionStyle}>
                      <h2 style={sectionTitleStyle}>Create New Character</h2>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="new-character-name">
                          Character Name
                        </label>
                        <input
                          id="new-character-name"
                          type="text"
                          value={newCharacterName}
                          onChange={(e) => setNewCharacterName(e.target.value)}
                          placeholder="Enter character name"
                          style={inputStyle}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              createCharacter();
                            }
                          }}
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="new-character-description">
                          Description
                        </label>
                        <textarea
                          id="new-character-description"
                          value={newCharacterDescription}
                          onChange={(e) => setNewCharacterDescription(e.target.value)}
                          placeholder="Enter character description (optional)"
                          style={{ ...blindspotTextareaStyle, minHeight: '100px' }}
                        />
                      </div>
                      <button
                        onClick={createCharacter}
                        style={getButtonStyle(buttonHover === 'create-character')}
                        onMouseEnter={() => setButtonHover('create-character')}
                        onMouseLeave={() => setButtonHover(null)}
                      >
                        Create Character
                      </button>
                    </div>
                  </div>

                  <div>
                    <h2 style={sectionTitleStyle}>All Characters</h2>
                    {characters.length === 0 ? (
                      <p style={emptyStateStyle}>No characters created yet. Create one above!</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {characters.map((character) => (
                          <div key={character.id} style={sectionStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flex: 1 }}>
                                {/* Character Portrait */}
                                <div style={{ flexShrink: 0 }}>
                                  {character.portrait ? (
                                    <img
                                      src={character.portrait}
                                      alt={character.name}
                                      style={{
                                        width: '100px',
                                        height: '100px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #4a4a4a',
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '100px',
                                      height: '100px',
                                      borderRadius: '50%',
                                      backgroundColor: '#2a2a2a',
                                      border: '2px solid #4a4a4a',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '2rem',
                                      fontWeight: '700',
                                      color: '#64b5f6',
                                    }}>
                                      {character.name.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    id={`portrait-upload-${character.id}`}
                                    style={{ display: 'none' }}
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        try {
                                          const dataUrl = await compressPortrait(file);
                                          setCharacters(prev => prev.map(c =>
                                            c.id === character.id ? { ...c, portrait: dataUrl } : c
                                          ));
                                        } catch (err) {
                                          console.error('Failed to process portrait:', err);
                                        }
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={() => document.getElementById(`portrait-upload-${character.id}`)?.click()}
                                    style={{
                                      marginTop: '8px',
                                      padding: '6px 12px',
                                      backgroundColor: 'transparent',
                                      border: '1px solid #4a4a4a',
                                      borderRadius: '6px',
                                      color: '#a0a0a0',
                                      fontSize: '0.8rem',
                                      cursor: 'pointer',
                                      width: '100%',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#2a2a2a';
                                      e.currentTarget.style.borderColor = '#6a6a6a';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.borderColor = '#4a4a4a';
                                    }}
                                  >
                                    {character.portrait ? 'Change' : 'Upload Portrait'}
                                  </button>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <h3 style={{ ...sectionTitleStyle, fontSize: '1.25rem', margin: 0 }}>
                                    {character.name}
                                  </h3>
                                  {character.description && (
                                    <p style={sectionDescStyle}>{character.description}</p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => deleteCharacter(character.id)}
                                style={getRemoveButtonStyle(removeHover === character.id)}
                                onMouseEnter={() => setRemoveHover(character.id)}
                                onMouseLeave={() => setRemoveHover(null)}
                                aria-label="Delete character"
                              >
                                ×
                              </button>
                            </div>
                            <div>
                              <h4 style={{ ...labelStyle, marginBottom: '12px' }}>Attached Enzymes</h4>
                              {character.attachedEnzymes.length === 0 ? (
                                <p style={{ ...emptyStateStyle, padding: '20px', fontSize: '0.9rem' }}>
                                  No enzymes attached
                                </p>
                              ) : (
                                character.attachedEnzymes.map((attachedEnzyme) => {
                                  const enzyme = savedEnzymes.find(e => e.id === attachedEnzyme.enzymeId);
                                  if (!enzyme) return null;
                                  return (
                                    <div key={attachedEnzyme.enzymeId} style={attachedEnzymeItemStyle}>
                                      <div style={attachedEnzymeInfoStyle}>
                                        <div style={attachedEnzymeNameStyle}>{enzyme.characterName}</div>
                                        <div style={attachedEnzymeTraitStyle}>{enzyme.traitName}</div>
                                      </div>
                                      <div style={attachedEnzymeIntensityStyle}>
                                        <span style={intensityValueSmallStyle}>Intensity: {attachedEnzyme.intensity.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                            <div style={{ marginTop: '16px' }}>
                              <button
                                onClick={() => setSelectedCharacterId(character.id)}
                                style={getButtonStyle(buttonHover === `edit-${character.id}`)}
                                onMouseEnter={() => setButtonHover(`edit-${character.id}`)}
                                onMouseLeave={() => setButtonHover(null)}
                              >
                                Edit Character
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
            )
          ) : currentView === 'storyworld' ? (
            interfaceMode === 'story' ? (
              renderStoryModeView()
            ) : (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Storyworld</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Global dramatic settings and tensions</p>
              </header>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                <button
                  onClick={() => setInterfaceMode('story')}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: 'transparent',
                    border: '1px solid #30363d',
                    borderRadius: '8px',
                    color: '#8b949e',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  📖 Story Mode
                </button>
              </div>

              <div style={{ marginTop: '48px' }}>
                <div style={sectionStyle}>
                  <h2 style={sectionTitleStyle}>Escalation Profile</h2>
                  <p style={sectionDescStyle}>Controls the dramatic pacing of the story</p>

                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="escalation-type">
                      Escalation Type
                    </label>
                    <select
                      id="escalation-type"
                      value={escalationProfile.type}
                      onChange={(e) => updateEscalationProfile('type', e.target.value as EscalationType)}
                      style={enzymeSelectStyle}
                    >
                      <option value="slow-burn">Slow-burn</option>
                      <option value="hair-trigger">Hair-trigger</option>
                      <option value="wave">Wave</option>
                    </select>
                  </div>

                  {escalationProfile.type === 'slow-burn' && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="slow-burn-reinforcements">
                        Reinforcements Needed Before Eruption
                      </label>
                      <input
                        id="slow-burn-reinforcements"
                        type="number"
                        min="1"
                        step="1"
                        value={escalationProfile.slowBurnReinforcements || 3}
                        onChange={(e) => updateEscalationProfile('slowBurnReinforcements', parseInt(e.target.value) || 1)}
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {escalationProfile.type === 'hair-trigger' && (
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="hair-trigger-recovery">
                        Recovery Time Between Eruptions
                      </label>
                      <input
                        id="hair-trigger-recovery"
                        type="number"
                        min="0"
                        step="0.1"
                        value={escalationProfile.hairTriggerRecoveryTime || 1}
                        onChange={(e) => updateEscalationProfile('hairTriggerRecoveryTime', parseFloat(e.target.value) || 0)}
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {escalationProfile.type === 'wave' && (
                    <>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="wave-build-duration">
                          Build Duration
                        </label>
                        <input
                          id="wave-build-duration"
                          type="number"
                          min="1"
                          step="1"
                          value={escalationProfile.waveBuildDuration || 5}
                          onChange={(e) => updateEscalationProfile('waveBuildDuration', parseInt(e.target.value) || 1)}
                          style={inputStyle}
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="wave-release-intensity">
                          Release Intensity
                        </label>
                        <div style={sliderContainerStyle}>
                          <div style={sliderWrapperStyle}>
                            <input
                              id="wave-release-intensity"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={escalationProfile.waveReleaseIntensity || 0.7}
                              onChange={(e) => updateEscalationProfile('waveReleaseIntensity', parseFloat(e.target.value))}
                              style={sliderStyle}
                            />
                            <span style={intensityValueStyle}>
                              {(escalationProfile.waveReleaseIntensity || 0.7).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ ...sectionStyle, marginTop: '32px' }}>
                  <h2 style={sectionTitleStyle}>Tension Codons</h2>
                  <p style={sectionDescStyle}>Abstract dramatic pressures active across the whole story</p>
                  <div style={{ marginBottom: '24px' }}>
                    <button
                      onClick={addTensionCodon}
                      style={getButtonStyle(buttonHover === 'add-tension-codon')}
                      onMouseEnter={() => setButtonHover('add-tension-codon')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Add Tension Codon
                    </button>
                  </div>
                  <div>
                    {tensionCodons.length === 0 ? (
                      <p style={emptyStateStyle}>No tension codons added yet</p>
                    ) : (
                      tensionCodons.map((codon) => (
                        <div key={codon.id} style={tensionCodonItemStyle}>
                          <div style={tensionCodonHeaderStyle}>
                            <h3 style={tensionCodonTitleStyle}>
                              {codon.name || 'Unnamed Tension Codon'}
                            </h3>
                            <button
                              onClick={() => removeTensionCodon(codon.id)}
                              style={getRemoveButtonStyle(removeHover === codon.id)}
                              onMouseEnter={() => setRemoveHover(codon.id)}
                              onMouseLeave={() => setRemoveHover(null)}
                              aria-label="Remove tension codon"
                            >
                              ×
                            </button>
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle} htmlFor={`codon-name-${codon.id}`}>
                              Name
                            </label>
                            <input
                              id={`codon-name-${codon.id}`}
                              type="text"
                              value={codon.name}
                              onChange={(e) => updateTensionCodon(codon.id, 'name', e.target.value)}
                              placeholder="e.g., 'Concealed Identity', 'Forbidden Desire'"
                              style={inputStyle}
                            />
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle} htmlFor={`codon-desc-${codon.id}`}>
                              Description
                            </label>
                            <textarea
                              id={`codon-desc-${codon.id}`}
                              value={codon.description}
                              onChange={(e) => updateTensionCodon(codon.id, 'description', e.target.value)}
                              placeholder="Describe this dramatic pressure..."
                              style={tensionCodonTextareaStyle}
                            />
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle} htmlFor={`codon-pressure-${codon.id}`}>
                              Pressure Level
                            </label>
                            <div style={tensionCodonPressureContainerStyle}>
                              <input
                                id={`codon-pressure-${codon.id}`}
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={codon.pressureLevel}
                                onChange={(e) => updateTensionCodon(codon.id, 'pressureLevel', parseFloat(e.target.value))}
                                style={tensionCodonPressureSliderStyle}
                              />
                              <span style={tensionCodonPressureValueStyle}>
                                {codon.pressureLevel.toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <div style={tensionCodonFieldStyle}>
                            <label style={tensionCodonLabelStyle}>
                              Affected Characters
                            </label>
                            {characters.length === 0 ? (
                              <p style={{ ...emptyStateStyle, padding: '20px', fontSize: '0.9rem' }}>
                                No characters created yet. Create characters first.
                              </p>
                            ) : (
                              <div style={affectedCharactersContainerStyle}>
                                {characters.map((character) => {
                                  const isAffected = codon.affectedCharacterIds.includes(character.id);
                                  return (
                                    <div key={character.id} style={characterCheckboxItemStyle}>
                                      <input
                                        type="checkbox"
                                        id={`codon-${codon.id}-char-${character.id}`}
                                        checked={isAffected}
                                        onChange={() => toggleCharacterInTensionCodon(codon.id, character.id)}
                                        style={characterCheckboxStyle}
                                      />
                                      <label
                                        htmlFor={`codon-${codon.id}-char-${character.id}`}
                                        style={characterCheckboxLabelStyle}
                                      >
                                        {character.name}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
            )
          ) : currentView === 'encounters' ? (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Scenes</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Create and manage dramatic scenes</p>
              </header>

              <div style={{ marginTop: '48px' }}>
                <div style={sectionStyle}>
                  <h2 style={sectionTitleStyle}>Create Scene</h2>
                  <p style={sectionDescStyle}>Generate dramatic scenes based on your characters and their patterns</p>
                  <div style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={generateEncounter}
                        disabled={encounterLoading}
                        style={{
                          ...getButtonStyle(buttonHover === 'generate-encounter'),
                          opacity: encounterLoading ? 0.6 : 1,
                          cursor: encounterLoading ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={() => !encounterLoading && setButtonHover('generate-encounter')}
                        onMouseLeave={() => setButtonHover(null)}
                      >
                        {encounterLoading ? 'Generating...' : 'Generate Encounter'}
                      </button>

                      <button
                        onClick={() => setShowEncounterForge(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 16px',
                          backgroundColor: '#6e40c9',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        <span>🔨</span>
                        <span>Encounter Forge</span>
                      </button>
                    </div>
                  </div>

                  {encounterLoading && (
                    <div style={cobotEmptyStateStyle}>
                      <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⏳</div>
                      <div style={{ fontWeight: '600', color: '#a0a0a0', marginBottom: '12px' }}>
                        Generating Encounter...
                      </div>
                      <div>
                        Claude is creating a dramatic encounter based on your characters and tensions.
                      </div>
                    </div>
                  )}

                  {generatedEncounter && !encounterLoading && (
                    <div style={{ ...encounterItemStyle, borderColor: '#64b5f6', borderWidth: '2px' }}>
                      <div style={encounterHeaderStyle}>
                        <h3 style={encounterTitleStyle}>{generatedEncounter.title}</h3>
                      </div>
                      
                      {generatedEncounter.characterIds.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>Characters Involved:</div>
                          <div style={encounterCharactersStyle}>
                            {characters
                              .filter(c => generatedEncounter.characterIds.includes(c.id))
                              .map((character) => (
                                <span key={character.id} style={encounterCharacterTagStyle}>
                                  {character.name}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}

                      {generatedEncounter.triggers && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>What Triggers It:</div>
                          <div style={encounterDescriptionStyle}>{generatedEncounter.triggers}</div>
                        </div>
                      )}

                      {generatedEncounter.whatHappens && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>What Happens:</div>
                          <div style={encounterDescriptionStyle}>{generatedEncounter.whatHappens}</div>
                        </div>
                      )}

                      {generatedEncounter.playerChoicePoints && generatedEncounter.playerChoicePoints.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ ...labelStyle, marginBottom: '8px' }}>Player Choice Points:</div>
                          <ul style={{ margin: 0, paddingLeft: '20px', color: '#d0d0d0' }}>
                            {generatedEncounter.playerChoicePoints.map((choice, index) => (
                              <li key={index} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                                {choice}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button
                          onClick={saveGeneratedEncounter}
                          style={getButtonStyle(buttonHover === 'save-encounter')}
                          onMouseEnter={() => setButtonHover('save-encounter')}
                          onMouseLeave={() => setButtonHover(null)}
                        >
                          Save Encounter
                        </button>
                        <button
                          onClick={() => setGeneratedEncounter(null)}
                          style={getNewButtonStyle(buttonHover === 'discard-encounter')}
                          onMouseEnter={() => setButtonHover('discard-encounter')}
                          onMouseLeave={() => setButtonHover(null)}
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ ...sectionStyle, marginTop: '32px' }}>
                  <h2 style={sectionTitleStyle}>Saved Encounters</h2>
                  <p style={sectionDescStyle}>Your generated encounters</p>
                  <div>
                    {encounters.length === 0 ? (
                      <p style={emptyStateStyle}>No encounters saved yet. Generate your first encounter above!</p>
                    ) : (
                      encounters.map((encounter) => {
                        const involvedCharacters = characters.filter(c => encounter.characterIds.includes(c.id));
                        return (
                          <div key={encounter.id} style={encounterItemStyle}>
                            <div style={encounterHeaderStyle}>
                              <h3 style={encounterTitleStyle}>{encounter.title || 'Untitled Encounter'}</h3>
                              <button
                                onClick={() => deleteEncounter(encounter.id)}
                                style={getRemoveButtonStyle(removeHover === encounter.id)}
                                onMouseEnter={() => setRemoveHover(encounter.id)}
                                onMouseLeave={() => setRemoveHover(null)}
                                aria-label="Delete encounter"
                              >
                                ×
                              </button>
                            </div>
                            {involvedCharacters.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>Characters Involved:</div>
                                <div style={encounterCharactersStyle}>
                                  {involvedCharacters.map((character) => (
                                    <span key={character.id} style={encounterCharacterTagStyle}>
                                      {character.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {encounter.triggers && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>What Triggers It:</div>
                                <div style={encounterDescriptionStyle}>{encounter.triggers}</div>
                              </div>
                            )}

                            {(encounter.whatHappens || encounter.description) && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>What Happens:</div>
                                <div style={encounterDescriptionStyle}>{encounter.whatHappens || encounter.description}</div>
                              </div>
                            )}

                            {encounter.playerChoicePoints && encounter.playerChoicePoints.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ ...labelStyle, marginBottom: '8px' }}>Player Choice Points:</div>
                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#d0d0d0' }}>
                                  {encounter.playerChoicePoints.map((choice, index) => (
                                    <li key={index} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                                      {choice}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : currentView === 'play' ? (
            <>
              {console.log("Play tab rendering", { playthroughActive, showCharacterSelection, characters: characters.length })}
              <header style={headerStyle}>
                <h1 style={h1Style}>Play Your Storyworld</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Experience your dramatic genome in action</p>
              </header>

              <div style={{ marginTop: '48px' }}>
                {!playthroughActive && !showCharacterSelection ? (
                  <div style={sectionStyle}>
                    <h2 style={sectionTitleStyle}>Play Your Storyworld</h2>
                    <p style={sectionDescStyle}>
                      Experience your dramatic genome in action
                    </p>
                    {characters.length === 0 ? (
                      <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#2a1a1a',
                        border: '1px solid #8a4a4a',
                        borderRadius: '8px',
                        color: '#e57373',
                        fontSize: '0.95rem',
                        lineHeight: '1.6'
                      }}>
                        ⚠️ You need at least one character to start a playthrough. Create characters in the Characters tab first.
                      </div>
                    ) : (
                      <>
                        <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                          {characters.map((character) => (
                            <div
                              key={character.id}
                              style={{
                                padding: '16px',
                                marginBottom: '12px',
                                backgroundColor: '#222222',
                                border: `1px solid ${selectedCharacterIds.has(character.id) ? '#64b5f6' : '#3a3a3a'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                              }}
                              onClick={() => {
                                const newSelected = new Set(selectedCharacterIds);
                                if (newSelected.has(character.id)) {
                                  newSelected.delete(character.id);
                                } else {
                                  newSelected.add(character.id);
                                }
                                setSelectedCharacterIds(newSelected);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#64b5f6';
                                e.currentTarget.style.backgroundColor = '#2a2a2a';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = selectedCharacterIds.has(character.id) ? '#64b5f6' : '#3a3a3a';
                                e.currentTarget.style.backgroundColor = '#222222';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedCharacterIds.has(character.id)}
                                  onChange={() => {
                                    const newSelected = new Set(selectedCharacterIds);
                                    if (newSelected.has(character.id)) {
                                      newSelected.delete(character.id);
                                    } else {
                                      newSelected.add(character.id);
                                    }
                                    setSelectedCharacterIds(newSelected);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#f5f5f5',
                                    marginBottom: '4px',
                                  }}>
                                    {character.name}
                                  </div>
                                  {character.description && (
                                    <div style={{
                                      fontSize: '0.9rem',
                                      color: '#a0a0a0',
                                      lineHeight: '1.5',
                                    }}>
                                      {character.description}
                                    </div>
                                  )}
                                  {character.attachedEnzymes.length > 0 && (
                                    <div style={{
                                      fontSize: '0.85rem',
                                      color: '#64b5f6',
                                      marginTop: '8px',
                                    }}>
                                      {character.attachedEnzymes.length} enzyme{character.attachedEnzymes.length !== 1 ? 's' : ''} attached
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button
                            onClick={beginPlaythroughWithSelectedCharacters}
                            disabled={selectedCharacterIds.size === 0}
                            style={{
                              ...getButtonStyle(buttonHover === 'start-playthrough'),
                              opacity: selectedCharacterIds.size === 0 ? 0.6 : 1,
                              cursor: selectedCharacterIds.size === 0 ? 'not-allowed' : 'pointer',
                            }}
                            onMouseEnter={() => selectedCharacterIds.size > 0 && setButtonHover('start-playthrough')}
                            onMouseLeave={() => setButtonHover(null)}
                          >
                            Start Playthrough
                          </button>
                          <button
                            onClick={startQuickPlay}
                            disabled={characters.length === 0}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: '#ffa726',
                              border: '1px solid #ffa726',
                              borderRadius: '8px',
                              color: '#ffffff',
                              fontSize: '1rem',
                              fontWeight: '600',
                              cursor: characters.length === 0 ? 'not-allowed' : 'pointer',
                              opacity: characters.length === 0 ? 0.6 : 1,
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              if (characters.length > 0) {
                                e.currentTarget.style.backgroundColor = '#ffb74d';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                setButtonHover('quick-play');
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffa726';
                              e.currentTarget.style.transform = 'translateY(0)';
                              setButtonHover(null);
                            }}
                            title="Quick test mode: Faster pace, no saving, good for testing your storyworld"
                          >
                            ⚡ Quick Play
                          </button>
                          {hasSavedPlaythrough && (
                            <button
                              onClick={continuePlaythrough}
                              style={{
                                ...getButtonStyle(buttonHover === 'continue-playthrough'),
                              }}
                              onMouseEnter={() => setButtonHover('continue-playthrough')}
                              onMouseLeave={() => setButtonHover(null)}
                            >
                              Continue Playthrough
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : showCharacterSelection ? (
                  <div style={sectionStyle}>
                    <h2 style={sectionTitleStyle}>Begin Your Story</h2>
                    <p style={sectionDescStyle}>
                      Select which characters to include in this playthrough.
                    </p>
                    {characters.length === 0 ? (
                      <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#2a1a1a',
                        border: '1px solid #8a4a4a',
                        borderRadius: '8px',
                        color: '#e57373',
                        fontSize: '0.95rem',
                      }}>
                        No characters available. Create characters in the Characters tab first.
                      </div>
                    ) : (
                      <>
                        <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                          {characters.map((character) => (
                            <div
                              key={character.id}
                              style={{
                                padding: '16px',
                                marginBottom: '12px',
                                backgroundColor: '#222222',
                                border: `1px solid ${selectedCharacterIds.has(character.id) ? '#64b5f6' : '#3a3a3a'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                              }}
                              onClick={() => {
                                const newSelected = new Set(selectedCharacterIds);
                                if (newSelected.has(character.id)) {
                                  newSelected.delete(character.id);
                                } else {
                                  newSelected.add(character.id);
                                }
                                setSelectedCharacterIds(newSelected);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#64b5f6';
                                e.currentTarget.style.backgroundColor = '#2a2a2a';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = selectedCharacterIds.has(character.id) ? '#64b5f6' : '#3a3a3a';
                                e.currentTarget.style.backgroundColor = '#222222';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedCharacterIds.has(character.id)}
                                  onChange={() => {
                                    const newSelected = new Set(selectedCharacterIds);
                                    if (newSelected.has(character.id)) {
                                      newSelected.delete(character.id);
                                    } else {
                                      newSelected.add(character.id);
                                    }
                                    setSelectedCharacterIds(newSelected);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer',
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#f5f5f5',
                                    marginBottom: '4px',
                                  }}>
                                    {character.name}
                                  </div>
                                  {character.description && (
                                    <div style={{
                                      fontSize: '0.9rem',
                                      color: '#a0a0a0',
                                      lineHeight: '1.5',
                                    }}>
                                      {character.description}
                                    </div>
                                  )}
                                  {character.attachedEnzymes.length > 0 && (
                                    <div style={{
                                      fontSize: '0.85rem',
                                      color: '#64b5f6',
                                      marginTop: '8px',
                                    }}>
                                      {character.attachedEnzymes.length} enzyme{character.attachedEnzymes.length !== 1 ? 's' : ''} attached
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button
                            onClick={beginPlaythroughWithSelectedCharacters}
                            disabled={selectedCharacterIds.size === 0}
                            style={{
                              ...getButtonStyle(buttonHover === 'begin-playthrough'),
                              opacity: selectedCharacterIds.size === 0 ? 0.6 : 1,
                              cursor: selectedCharacterIds.size === 0 ? 'not-allowed' : 'pointer',
                            }}
                            onMouseEnter={() => selectedCharacterIds.size > 0 && setButtonHover('begin-playthrough')}
                            onMouseLeave={() => setButtonHover(null)}
                          >
                            Start Playthrough
                          </button>
                          <button
                            onClick={() => {
                              setShowCharacterSelection(false);
                              setSelectedCharacterIds(new Set());
                            }}
                            style={getNewButtonStyle(buttonHover === 'cancel-selection')}
                            onMouseEnter={() => setButtonHover('cancel-selection')}
                            onMouseLeave={() => setButtonHover(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : gameEnding ? (
                  /* Ending Screen */
                  <div style={{ flex: 1, maxWidth: '900px', margin: '0 auto' }}>
                    <div style={{
                      backgroundColor: '#1a1a1a',
                      border: `3px solid ${
                        gameEnding.type === 'resolution' ? '#81c784' :
                        gameEnding.type === 'tragedy' ? '#e57373' :
                        gameEnding.type === 'collapse' ? '#757575' :
                        gameEnding.type === 'transcendence' ? '#ffd700' :
                        '#64b5f6'
                      }`,
                      borderRadius: '16px',
                      padding: '48px',
                      boxShadow: `0 8px 32px rgba(${
                        gameEnding.type === 'resolution' ? '129, 199, 132' :
                        gameEnding.type === 'tragedy' ? '229, 115, 115' :
                        gameEnding.type === 'collapse' ? '117, 117, 117' :
                        gameEnding.type === 'transcendence' ? '255, 215, 0' :
                        '100, 181, 246'
                      }, 0.3)`,
                    }}>
                      {/* Ending Icon */}
                      <div style={{
                        fontSize: '4rem',
                        textAlign: 'center',
                        marginBottom: '24px',
                      }}>
                        {gameEnding.type === 'resolution' ? '✨' :
                         gameEnding.type === 'tragedy' ? '💀' :
                         gameEnding.type === 'collapse' ? '💥' :
                         gameEnding.type === 'transcendence' ? '🌟' :
                         '📖'}
                      </div>

                      {/* Ending Title */}
                      <h1 style={{
                        fontSize: '3rem',
                        fontWeight: '700',
                        color: gameEnding.type === 'resolution' ? '#81c784' :
                               gameEnding.type === 'tragedy' ? '#e57373' :
                               gameEnding.type === 'collapse' ? '#757575' :
                               gameEnding.type === 'transcendence' ? '#ffd700' :
                               '#64b5f6',
                        textAlign: 'center',
                        marginBottom: '24px',
                        textShadow: `0 2px 8px rgba(${
                          gameEnding.type === 'resolution' ? '129, 199, 132' :
                          gameEnding.type === 'tragedy' ? '229, 115, 115' :
                          gameEnding.type === 'collapse' ? '117, 117, 117' :
                          gameEnding.type === 'transcendence' ? '255, 215, 0' :
                          '100, 181, 246'
                        }, 0.5)`,
                      }}>
                        {gameEnding.title}
                      </h1>

                      {/* Ending Message */}
                      <div style={{
                        padding: '32px',
                        backgroundColor: '#0f0f0f',
                        border: '2px solid #4a4a4a',
                        borderRadius: '12px',
                        color: '#e5e5e5',
                        lineHeight: '1.9',
                        fontSize: '1.3rem',
                        textAlign: 'center',
                        marginBottom: '40px',
                      }}>
                        {gameEnding.message}
                      </div>

                      {/* Story Summary Preview */}
                      <div style={{
                        marginBottom: '40px',
                        padding: '24px',
                        backgroundColor: '#222222',
                        borderRadius: '12px',
                        border: '1px solid #3a3a3a',
                      }}>
                        <h2 style={{
                          fontSize: '1.5rem',
                          fontWeight: '600',
                          color: '#64b5f6',
                          marginBottom: '20px',
                          textAlign: 'center',
                        }}>
                          Your Story
                        </h2>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: '16px',
                          marginBottom: '20px',
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.85rem', color: '#a0a0a0', marginBottom: '4px' }}>Encounters</div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#64b5f6' }}>
                              {storyHistory.length}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.85rem', color: '#a0a0a0', marginBottom: '4px' }}>Choices</div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#64b5f6' }}>
                              {totalChoicesMade}
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.85rem', color: '#a0a0a0', marginBottom: '4px' }}>Characters</div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#64b5f6' }}>
                              {playthroughCharacters.length}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => setShowStorySummary(true)}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: 'transparent',
                              border: '1px solid #4a6a4a',
                              borderRadius: '8px',
                              color: '#81c784',
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#1a2a1a';
                              e.currentTarget.style.borderColor = '#6a8a6a';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = '#4a6a4a';
                            }}
                          >
                            📖 View Full Story Summary
                          </button>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        justifyContent: 'center',
                      }}>
                        <button
                          onClick={() => {
                            setGameEnding(null);
                            startNewPlaythrough();
                          }}
                          style={{
                            padding: '16px 32px',
                            backgroundColor: '#64b5f6',
                            border: 'none',
                            borderRadius: '12px',
                            color: '#ffffff',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#90caf9';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(100, 181, 246, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#64b5f6';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          🎮 Start New Playthrough
                        </button>
                        <button
                          onClick={() => {
                            setGameEnding(null);
                            endPlaythrough();
                            setCurrentView('editor');
                          }}
                          style={{
                            padding: '16px 32px',
                            backgroundColor: 'transparent',
                            border: '1px solid #6a6a6a',
                            borderRadius: '12px',
                            color: '#d0d0d0',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#2a2a2a';
                            e.currentTarget.style.borderColor = '#8a8a8a';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = '#6a6a6a';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          ✏️ Return to Authoring
                        </button>
                      </div>
                    </div>
                  </div>
                ) : showStorySummary ? (
                  /* Story Summary Modal */
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    overflowY: 'auto',
                  }}
                  onClick={() => setShowStorySummary(false)}
                  >
                    <div style={{
                      backgroundColor: '#1a1a1a',
                      border: '2px solid #4a4a4a',
                      borderRadius: '16px',
                      padding: '32px',
                      maxWidth: '900px',
                      width: '100%',
                      maxHeight: '90vh',
                      overflowY: 'auto',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    >
                      {(() => {
                        const summary = generateStorySummary();
                        return (
                          <>
                            {/* Header */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '32px',
                              paddingBottom: '20px',
                              borderBottom: '2px solid #3a3a3a',
                            }}>
                              <h1 style={{
                                fontSize: '2rem',
                                fontWeight: '700',
                                color: '#f5f5f5',
                                margin: 0,
                              }}>
                                The Story of {summary.storyworldName}
                              </h1>
                              <button
                                onClick={() => setShowStorySummary(false)}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: 'transparent',
                                  border: '1px solid #6a4a4a',
                                  borderRadius: '8px',
                                  color: '#e57373',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                }}
                              >
                                ✕ Close
                              </button>
                            </div>

                            {/* Chapters */}
                            <div style={{ marginBottom: '40px' }}>
                              <h2 style={{
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                color: '#64b5f6',
                                marginBottom: '20px',
                              }}>
                                Chapters
                              </h2>
                              {summary.chapters.map((chapter, idx) => (
                                <div key={idx} style={{
                                  marginBottom: '32px',
                                  padding: '20px',
                                  backgroundColor: '#222222',
                                  borderRadius: '12px',
                                  border: '1px solid #3a3a3a',
                                }}>
                                  <h3 style={{
                                    fontSize: '1.2rem',
                                    fontWeight: '600',
                                    color: '#ffa726',
                                    marginBottom: '16px',
                                  }}>
                                    Chapter {chapter.number}
                                  </h3>
                                  {chapter.encounters.map((entry, entryIdx) => (
                                    <div key={entryIdx} style={{
                                      marginBottom: entryIdx < chapter.encounters.length - 1 ? '20px' : '0',
                                      paddingBottom: entryIdx < chapter.encounters.length - 1 ? '20px' : '0',
                                      borderBottom: entryIdx < chapter.encounters.length - 1 ? '1px solid #3a3a3a' : 'none',
                                    }}>
                                      <div style={{
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        color: '#d0d0d0',
                                        marginBottom: '8px',
                                      }}>
                                        {entry.encounterTitle}
                                      </div>
                                      <div style={{ fontSize: '0.9rem', color: '#a0a0a0', marginBottom: '8px' }}>
                                        <strong>Choice:</strong> {entry.choice}
                                      </div>
                                      <div style={{ fontSize: '0.9rem', color: '#a0a0a0', marginBottom: '8px' }}>
                                        <strong>Outcome:</strong> {entry.outcome}
                                      </div>
                                      {entry.consequences && entry.consequences.length > 0 && (
                                        <div style={{ fontSize: '0.85rem', color: '#81c784', marginTop: '8px' }}>
                                          <strong>Consequences:</strong>
                                          <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                            {entry.consequences.map((consequence, cIdx) => (
                                              <li key={cIdx} style={{ marginBottom: '4px' }}>
                                                {consequence.includes('mutation') || consequence.includes('hardened') || consequence.includes('collapsed') || consequence.includes('transcended') ? (
                                                  <span style={{ color: '#ffa726', fontWeight: '600' }}>⚡ {consequence}</span>
                                                ) : (
                                                  consequence
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>

                            {/* Character Journeys */}
                            <div style={{ marginBottom: '40px' }}>
                              <h2 style={{
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                color: '#64b5f6',
                                marginBottom: '20px',
                              }}>
                                Character Journeys
                              </h2>
                              {summary.characterJourneys.map((journey, idx) => (
                                <div key={idx} style={{
                                  marginBottom: '24px',
                                  padding: '20px',
                                  backgroundColor: '#222222',
                                  borderRadius: '12px',
                                  border: '1px solid #3a3a3a',
                                }}>
                                  <h3 style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#64b5f6',
                                    marginBottom: '12px',
                                  }}>
                                    {journey.character.name}
                                  </h3>
                                  <div style={{ fontSize: '0.9rem', color: '#a0a0a0', marginBottom: '12px' }}>
                                    <strong>Starting Enzymes:</strong> {journey.startingEnzymes.join(', ')}
                                  </div>
                                  {journey.enzymeFirings.length > 0 && (
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#ffa726', marginBottom: '8px' }}>
                                        Key Moments:
                                      </div>
                                      {journey.enzymeFirings.map((firing, fIdx) => (
                                        <div key={fIdx} style={{
                                          fontSize: '0.85rem',
                                          color: '#d0d0d0',
                                          marginBottom: '4px',
                                          paddingLeft: '12px',
                                        }}>
                                          • <strong>{firing.enzymeName}</strong> triggered during "{firing.encounter}"
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {journey.mutations.length > 0 && (
                                    <div style={{ marginBottom: '12px' }}>
                                      <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#ffa726', marginBottom: '8px' }}>
                                        Mutations:
                                      </div>
                                      {journey.mutations.map((mutation, mIdx) => (
                                        <div key={mIdx} style={{
                                          fontSize: '0.85rem',
                                          color: mutation.type === 'hardening' ? '#8b0000' :
                                                 mutation.type === 'collapsed' ? '#757575' :
                                                 '#ffd700',
                                          marginBottom: '4px',
                                          paddingLeft: '12px',
                                          fontWeight: '600',
                                        }}>
                                          ⚡ <strong>{mutation.enzymeName}</strong>: {mutation.type}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div>
                                    <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#81c784', marginBottom: '8px' }}>
                                      Current State:
                                    </div>
                                    {journey.currentState.enzymePressures.length > 0 ? (
                                      journey.currentState.enzymePressures.map((ep: any, epIdx: number) => (
                                        <div key={epIdx} style={{
                                          fontSize: '0.85rem',
                                          color: '#d0d0d0',
                                          marginBottom: '4px',
                                          paddingLeft: '12px',
                                        }}>
                                          • {ep.name}: {ep.pressure.toFixed(0)}% pressure
                                        </div>
                                      ))
                                    ) : (
                                      <div style={{ fontSize: '0.85rem', color: '#666', paddingLeft: '12px' }}>
                                        No active pressure
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Tension Resolution */}
                            {summary.resolvedTensions.length > 0 && (
                              <div style={{ marginBottom: '40px' }}>
                                <h2 style={{
                                  fontSize: '1.5rem',
                                  fontWeight: '600',
                                  color: '#64b5f6',
                                  marginBottom: '20px',
                                }}>
                                  Tension Resolution
                                </h2>
                                {summary.resolvedTensions.map((tension: any, idx: number) => (
                                  <div key={idx} style={{
                                    marginBottom: '16px',
                                    padding: '16px',
                                    backgroundColor: '#222222',
                                    borderRadius: '12px',
                                    border: '1px solid #4a8a4a',
                                  }}>
                                    <div style={{
                                      fontSize: '1rem',
                                      fontWeight: '600',
                                      color: '#81c784',
                                      marginBottom: '8px',
                                    }}>
                                      ✓ {tension.name}
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#a0a0a0' }}>
                                      {tension.description}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Statistics */}
                            <div style={{
                              marginBottom: '32px',
                              padding: '20px',
                              backgroundColor: '#222222',
                              borderRadius: '12px',
                              border: '1px solid #3a3a3a',
                            }}>
                              <h2 style={{
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                color: '#64b5f6',
                                marginBottom: '16px',
                              }}>
                                Statistics
                              </h2>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '16px',
                              }}>
                                <div>
                                  <div style={{ fontSize: '0.85rem', color: '#a0a0a0', marginBottom: '4px' }}>Total Encounters</div>
                                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#64b5f6' }}>
                                    {summary.stats.totalEncounters}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.85rem', color: '#a0a0a0', marginBottom: '4px' }}>Choices Made</div>
                                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#64b5f6' }}>
                                    {summary.stats.totalChoices}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.85rem', color: '#a0a0a0', marginBottom: '4px' }}>Enzymes Triggered</div>
                                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffa726' }}>
                                    {summary.stats.enzymesTriggered}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.85rem', color: '#a0a0a0', marginBottom: '4px' }}>Mutations Occurred</div>
                                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#e57373' }}>
                                    {summary.stats.mutationsOccurred}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div style={{
                              display: 'flex',
                              gap: '12px',
                              justifyContent: 'flex-end',
                              paddingTop: '20px',
                              borderTop: '2px solid #3a3a3a',
                            }}>
                              <button
                                onClick={shareStorySummary}
                                style={{
                                  padding: '12px 24px',
                                  backgroundColor: 'transparent',
                                  border: '1px solid #4a6a4a',
                                  borderRadius: '8px',
                                  color: '#81c784',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#1a2a1a';
                                  e.currentTarget.style.borderColor = '#6a8a6a';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.borderColor = '#4a6a4a';
                                }}
                              >
                                📋 Share
                              </button>
                              <button
                                onClick={exportStoryAsMarkdown}
                                style={{
                                  padding: '12px 24px',
                                  backgroundColor: '#64b5f6',
                                  border: 'none',
                                  borderRadius: '8px',
                                  color: '#ffffff',
                                  fontSize: '0.9rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#90caf9';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#64b5f6';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                💾 Export Story
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ) : mutationEvent && mutationEvent.active ? (
                  /* Mutation Event Screen */
                  <div style={{ flex: 1, maxWidth: '900px', margin: '0 auto' }}>
                    <div style={{
                      backgroundColor: '#1a1a1a',
                      border: '3px solid #e57373',
                      borderRadius: '16px',
                      padding: '40px',
                      boxShadow: '0 8px 32px rgba(229, 115, 115, 0.3)',
                    }}>
                      {/* Title */}
                      <div style={{
                        fontSize: '2.5rem',
                        fontWeight: '700',
                        color: '#e57373',
                        marginBottom: '24px',
                        textAlign: 'center',
                        textShadow: '0 2px 8px rgba(229, 115, 115, 0.5)',
                      }}>
                        ⚡ {mutationEvent.characterName} is at a Breaking Point
                      </div>

                      {/* Description */}
                      <div style={{
                        padding: '28px',
                        backgroundColor: '#0f0f0f',
                        border: '2px solid #4a4a4a',
                        borderRadius: '12px',
                        color: '#e5e5e5',
                        lineHeight: '1.9',
                        fontSize: '1.1rem',
                        marginBottom: '32px',
                      }}>
                        <p style={{ marginBottom: '16px' }}>
                          The pressure has been building. Through encounter after encounter, 
                          <strong style={{ color: '#64b5f6' }}> {mutationEvent.characterName}</strong>'s 
                          <strong style={{ color: '#ffa726' }}> {mutationEvent.enzymeName}</strong> has been pushed to its absolute limit.
                        </p>
                        <p style={{ marginBottom: '16px' }}>
                          Every trigger, every response, every moment of tension has accumulated. 
                          The enzyme can no longer contain the pressure. Something must give.
                        </p>
                        <p style={{ 
                          fontSize: '1.2rem',
                          fontWeight: '600',
                          color: '#ffa726',
                          marginTop: '24px',
                          paddingTop: '24px',
                          borderTop: '2px solid #4a4a4a',
                        }}>
                          The Breaking Point
                        </p>
                      </div>

                      {/* Mutation Result */}
                      <div style={{
                        padding: '32px',
                        backgroundColor: mutationEvent.mutationType === 'hardening' ? '#2a1a1a' :
                                         mutationEvent.mutationType === 'collapsed' ? '#1a1a1a' :
                                         '#2a2a1a',
                        border: `3px solid ${
                          mutationEvent.mutationType === 'hardening' ? '#8b0000' :
                          mutationEvent.mutationType === 'collapsed' ? '#757575' :
                          '#ffd700'
                        }`,
                        borderRadius: '12px',
                        marginBottom: '32px',
                        boxShadow: `0 4px 16px rgba(${
                          mutationEvent.mutationType === 'hardening' ? '139, 0, 0' :
                          mutationEvent.mutationType === 'collapsed' ? '117, 117, 117' :
                          '255, 215, 0'
                        }, 0.3)`,
                      }}>
                        <div style={{
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: mutationEvent.mutationType === 'hardening' ? '#8b0000' :
                                 mutationEvent.mutationType === 'collapsed' ? '#757575' :
                                 '#ffd700',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                        }}>
                          <span style={{ fontSize: '2rem' }}>
                            {mutationEvent.mutationType === 'hardening' ? '🛡️' :
                             mutationEvent.mutationType === 'collapsed' ? '💥' :
                             '✨'}
                          </span>
                          <span>
                            {mutationEvent.mutationType === 'hardening' ? 'Hardening' :
                             mutationEvent.mutationType === 'collapsed' ? 'Collapse' :
                             'Transcendence'}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '1.1rem',
                          color: '#e5e5e5',
                          lineHeight: '1.8',
                        }}>
                          <p style={{ marginBottom: '12px' }}>
                            <strong style={{ color: '#64b5f6' }}>{mutationEvent.characterName}</strong>'s 
                            <strong style={{ color: '#ffa726' }}> {mutationEvent.enzymeName}</strong> has
                            {mutationEvent.mutationType === 'hardening' ? ' hardened into an unyielding form' :
                             mutationEvent.mutationType === 'collapsed' ? ' shattered under the pressure' :
                             ' transcended into a new state of being'}.
                          </p>
                          <div style={{
                            padding: '16px',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '8px',
                            marginTop: '16px',
                            border: '1px solid #4a4a4a',
                          }}>
                            <div style={{
                              fontSize: '0.9rem',
                              color: '#a0a0a0',
                              fontStyle: 'italic',
                              marginBottom: '8px',
                            }}>
                              The transformation:
                            </div>
                            <div style={{
                              fontSize: '1rem',
                              color: '#d0d0d0',
                              lineHeight: '1.7',
                            }}>
                              {mutationEvent.mutationDescription}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Continue Button */}
                      <div style={{ textAlign: 'center' }}>
                        <button
                          onClick={handleMutationEventComplete}
                          style={{
                            padding: '18px 48px',
                            backgroundColor: '#64b5f6',
                            border: 'none',
                            borderRadius: '12px',
                            color: '#ffffff',
                            fontSize: '1.2rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: '0 4px 12px rgba(100, 181, 246, 0.4)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#90caf9';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(100, 181, 246, 0.5)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#64b5f6';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(100, 181, 246, 0.4)';
                          }}
                        >
                          Continue Story →
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Character Details Modal */}
                    {selectedCharacterDetails && (() => {
                      const character = playthroughCharacters.find(c => c.id === selectedCharacterDetails);
                      if (!character) return null;
                      const charState = characterStates.find(cs => cs.characterId === character.id);
                      
                      // Get emotion helpers (defined outside the encounter display)
                      const getEmotionEmojiHelper = (emotion: string) => {
                        switch (emotion) {
                          case 'angry': return '😠';
                          case 'sad': return '😢';
                          case 'happy': return '😊';
                          case 'fearful': return '😨';
                          case 'neutral': return '😐';
                          case 'suspicious': return '🤨';
                          case 'conflicted': return '😖';
                          case 'proud': return '😤';
                          case 'guilty': return '😔';
                          case 'desperate': return '😰';
                          default: return '😐';
                        }
                      };
                      
                      const getEmotionColorHelper = (emotion: string) => {
                        switch (emotion) {
                          case 'angry': return '#e57373';
                          case 'sad': return '#64b5f6';
                          case 'happy': return '#81c784';
                          case 'fearful': return '#ffb74d';
                          case 'neutral': return '#757575';
                          case 'suspicious': return '#ba68c8';
                          case 'conflicted': return '#ffa726';
                          case 'proud': return '#ff9800';
                          case 'guilty': return '#9575cd';
                          case 'desperate': return '#f06292';
                          default: return '#757575';
                        }
                      };
                      
                      const emotion = currentEmotions[character.id] || 'neutral';
                      const emotionEmoji = getEmotionEmojiHelper(emotion);
                      const emotionColor = getEmotionColorHelper(emotion);
                      
                      const renderPortrait = (char: Character, size: number) => {
                        const charEmotion = currentEmotions[char.id] || 'neutral';
                        const charEmotionColor = getEmotionColorHelper(charEmotion);
                        const charEmotionEmoji = getEmotionEmojiHelper(charEmotion);
                        
                        return (
                          <div style={{ position: 'relative', width: `${size}px`, height: `${size}px` }}>
                            {char.portrait ? (
                              <img
                                src={char.portrait}
                                alt={char.name}
                                style={{
                                  width: `${size}px`,
                                  height: `${size}px`,
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: `3px solid ${charEmotionColor}`,
                                }}
                              />
                            ) : (
                              <div style={{
                                width: `${size}px`,
                                height: `${size}px`,
                                borderRadius: '50%',
                                backgroundColor: '#1a1a1a',
                                border: `3px solid ${charEmotionColor}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: `${size * 0.4}px`,
                                fontWeight: '700',
                                color: charEmotionColor,
                              }}>
                                {char.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '-4px',
                                right: '-4px',
                                width: `${size * 0.32}px`,
                                height: `${size * 0.32}px`,
                                borderRadius: '50%',
                                backgroundColor: '#ffffff',
                                border: `2px solid ${charEmotionColor}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: `${size * 0.24}px`,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                              }}
                            >
                              {charEmotionEmoji}
                            </div>
                          </div>
                        );
                      };
                      
                      return (
                        <div
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                          }}
                          onClick={() => setSelectedCharacterDetails(null)}
                        >
                          <div
                            style={{
                              backgroundColor: '#1a1a1a',
                              border: `3px solid ${emotionColor}`,
                              borderRadius: '16px',
                              padding: '32px',
                              maxWidth: '600px',
                              maxHeight: '80vh',
                              overflowY: 'auto',
                              position: 'relative',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => setSelectedCharacterDetails(null)}
                              style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                background: 'transparent',
                                border: 'none',
                                color: '#a0a0a0',
                                fontSize: '24px',
                                cursor: 'pointer',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              ×
                            </button>
                            
                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                              {renderPortrait(character, 150)}
                              <h2 style={{ marginTop: '16px', color: '#f5f5f5', fontSize: '1.8rem' }}>
                                {character.name}
                              </h2>
                              <div style={{ marginTop: '8px', fontSize: '1.2rem' }}>
                                {emotionEmoji} {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                              </div>
                            </div>
                            
                            {character.description && (
                              <div style={{ marginBottom: '24px', color: '#a0a0a0', lineHeight: '1.6' }}>
                                {character.description}
                              </div>
                            )}
                            
                            {charState && (
                              <div>
                                <h3 style={{ color: '#64b5f6', marginBottom: '16px' }}>Enzyme Pressures</h3>
                                {character.attachedEnzymes.map(ae => {
                                  const enzyme = playthroughEnzymes.find(e => e.id === ae.enzymeId);
                                  if (!enzyme) return null;
                                  const pressure = charState.enzymePressures[enzyme.id] || 0;
                                  const mutationStatus = charState.mutationStatus[enzyme.id] || 'normal';
                                  
                                  return (
                                    <div key={enzyme.id} style={{ marginBottom: '12px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span style={{ color: '#f5f5f5' }}>{enzyme.traitName}</span>
                                        <span style={{ color: '#a0a0a0' }}>{(pressure * 100).toFixed(0)}%</span>
                                      </div>
                                      <div style={{
                                        width: '100%',
                                        height: '8px',
                                        backgroundColor: '#2a2a2a',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                      }}>
                                        <div style={{
                                          width: `${pressure * 100}%`,
                                          height: '100%',
                                          backgroundColor: pressure > 0.8 ? '#e57373' : pressure > 0.6 ? '#ffb74d' : '#64b5f6',
                                        }} />
                                      </div>
                                      {mutationStatus !== 'normal' && (
                                        <div style={{ fontSize: '0.85rem', color: '#ff9800', marginTop: '4px' }}>
                                          Status: {mutationStatus}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Mobile Play mode stacked layout */}
                    {isMobile && playthroughActive && (
                      <div style={{ padding: '16px', paddingBottom: '80px' }}>
                        {(() => {
                          const currentEncounter = encounters[currentEncounterIndex];
                          const encounterText =
                            (currentEncounter?.description || currentEncounter?.whatHappens || '').toString();
                          const involvedCharacters = currentEncounter
                            ? playthroughCharacters.filter((c) =>
                                (currentEncounter.characterIds || []).includes(c.id)
                              )
                            : [];

                          return (
                            <div>
                              {/* Encounter title */}
                              <h2 style={{ fontSize: '20px', marginBottom: '16px', color: '#f0f6fc' }}>
                                {currentEncounter?.title || (isGeneratingNextEncounter ? 'Generating...' : 'Loading...')}
                              </h2>

                              {/* Encounter content - scrollable */}
                              <div
                                style={{
                                  backgroundColor: '#21262d',
                                  borderRadius: '12px',
                                  padding: '20px',
                                  marginBottom: '16px',
                                  maxHeight: '40vh',
                                  overflowY: 'auto',
                                  border: '1px solid #30363d',
                                  color: '#f0f6fc',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {encounterText || 'Loading...'}
                              </div>

                              {/* Character status - collapsible */}
                              <button
                                onClick={() => setShowCharacterStatus(!showCharacterStatus)}
                                style={{
                                  width: '100%',
                                  padding: '12px',
                                  backgroundColor: '#161b22',
                                  border: '1px solid #30363d',
                                  borderRadius: '8px',
                                  color: '#8b949e',
                                  marginBottom: '16px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <span>👥 Characters</span>
                                <span>{showCharacterStatus ? '▲' : '▼'}</span>
                              </button>

                              {showCharacterStatus && (
                                <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                  {involvedCharacters.length === 0 ? (
                                    <div style={{ color: '#8b949e' }}>No characters for this encounter.</div>
                                  ) : (
                                    involvedCharacters.map((character) => {
                                      const charState = characterStates.find(
                                        (cs) => cs.characterId === character.id
                                      );
                                      const attached = character.attachedEnzymes
                                        .map((ae) => {
                                          const enzyme = playthroughEnzymes.find((e) => e.id === ae.enzymeId);
                                          if (!enzyme) return null;
                                          const pressure = charState?.enzymePressures?.[enzyme.id] || 0;
                                          return { enzyme, pressure };
                                        })
                                        .filter(Boolean) as Array<{ enzyme: Enzyme; pressure: number }>;

                                      return (
                                        <div
                                          key={character.id}
                                          style={{
                                            padding: '14px',
                                            backgroundColor: '#21262d',
                                            border: '1px solid #30363d',
                                            borderRadius: '12px',
                                          }}
                                        >
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            {character.portrait ? (
                                              <img
                                                src={character.portrait}
                                                alt={character.name}
                                                style={{
                                                  width: '44px',
                                                  height: '44px',
                                                  borderRadius: '50%',
                                                  objectFit: 'cover',
                                                  border: '2px solid #30363d',
                                                }}
                                              />
                                            ) : (
                                              <div
                                                style={{
                                                  width: '44px',
                                                  height: '44px',
                                                  borderRadius: '50%',
                                                  backgroundColor: '#0d1117',
                                                  border: '2px solid #30363d',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontWeight: 700,
                                                  color: '#58a6ff',
                                                }}
                                              >
                                                {(character.name || '?').charAt(0).toUpperCase()}
                                              </div>
                                            )}
                                            <div style={{ flex: 1 }}>
                                              <div style={{ color: '#f0f6fc', fontWeight: 700 }}>{character.name}</div>
                                              <div style={{ color: '#8b949e', fontSize: '12px' }}>
                                                {attached.length} enzyme{attached.length === 1 ? '' : 's'}
                                              </div>
                                            </div>
                                          </div>

                                          {attached.length > 0 && (
                                            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                              {attached.map(({ enzyme, pressure }) => (
                                                <div key={enzyme.id}>
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                    <span style={{ color: '#c9d1d9', fontSize: '12px' }}>{enzyme.traitName}</span>
                                                    <span style={{ color: '#8b949e', fontSize: '12px' }}>
                                                      {(pressure * 100).toFixed(0)}%
                                                    </span>
                                                  </div>
                                                  <div
                                                    style={{
                                                      width: '100%',
                                                      height: '8px',
                                                      backgroundColor: '#0d1117',
                                                      borderRadius: '999px',
                                                      overflow: 'hidden',
                                                      border: '1px solid #30363d',
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        width: `${Math.min(100, Math.max(0, pressure * 100))}%`,
                                                        height: '100%',
                                                        backgroundColor:
                                                          pressure > 0.8
                                                            ? '#e57373'
                                                            : pressure > 0.6
                                                              ? '#ffb74d'
                                                              : '#58a6ff',
                                                      }}
                                                    />
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              )}

                              {/* Choices - large touch targets */}
                              <div>
                                <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#8b949e' }}>CHOICES</h3>
                                {(currentEncounter?.playerChoicePoints || []).length === 0 ? (
                                  <div style={{ color: '#8b949e' }}>No choices available.</div>
                                ) : (
                                  (currentEncounter?.playerChoicePoints || []).map((choiceText, i) => {
                                    const label = choiceText.split('→')[0].trim();
                                    return (
                                      <button
                                        key={i}
                                        onClick={() => handleChoiceClick(choiceText)}
                                        disabled={isGeneratingNextEncounter}
                                        style={{
                                          width: '100%',
                                          padding: '16px',
                                          marginBottom: '12px',
                                          backgroundColor: '#21262d',
                                          border: '1px solid #30363d',
                                          borderRadius: '12px',
                                          color: '#f0f6fc',
                                          textAlign: 'left',
                                          fontSize: '16px',
                                          cursor: isGeneratingNextEncounter ? 'not-allowed' : 'pointer',
                                          minHeight: '56px',
                                          opacity: isGeneratingNextEncounter ? 0.75 : 1,
                                        }}
                                      >
                                        {label || `Choice ${i + 1}`}
                                      </button>
                                    );
                                  })
                                )}
                                {isGeneratingNextEncounter && (
                                  <div style={{ marginTop: '8px', color: '#8b949e', fontSize: '14px' }}>
                                    Generating next encounter...
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {!isMobile && (
                    <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                      {/* Main encounter display - Player View */}
                      <div style={{ flex: 1, maxWidth: '900px' }}>
                      {(() => {
                        const currentEncounter = encounters[currentEncounterIndex];
                        console.log('Render - encounters:', encounters.length);
                        console.log('Render - currentEncounterIndex:', currentEncounterIndex);
                        console.log('Render - currentEncounter:', currentEncounter ? currentEncounter.title : null);

                        if (isGeneratingNextEncounter) {
                          return (
                            <div style={{
                              padding: '40px',
                              textAlign: 'center',
                              color: '#90caf9',
                              fontSize: '1.2rem',
                              backgroundColor: '#1a1a2e',
                              borderRadius: '12px',
                              border: '2px solid #64b5f6',
                            }}>
                              <p>Generating next encounter...</p>
                            </div>
                          );
                        }
                        if (!currentEncounter) {
                          return (
                            <div style={{
                              padding: '40px',
                              textAlign: 'center',
                              color: '#f5f5f5',
                              backgroundColor: '#1a1a2e',
                              borderRadius: '12px',
                              border: '2px solid #4a4a4a',
                            }}>
                              <p style={{ marginBottom: '20px', fontSize: '1.1rem' }}>No encounter available.</p>
                              <button
                                onClick={() => nextEncounter()}
                                disabled={isGeneratingNextEncounter}
                                style={{
                                  padding: '16px 32px',
                                  backgroundColor: '#64b5f6',
                                  border: 'none',
                                  borderRadius: '12px',
                                  color: '#ffffff',
                                  fontSize: '1rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                }}
                              >
                                Generate Encounter
                              </button>
                            </div>
                          );
                        }

                        const involvedCharacters = playthroughCharacters.filter(c => 
                          (currentEncounter.characterIds || []).includes(c.id)
                        );

                          // Get emotions from encounter (default to neutral if missing)
                          const encounterEmotions = currentEncounter.emotions || {};
                          const activeEmotions: Record<string, string> = {};
                          
                          // Initialize emotions for involved characters
                          involvedCharacters.forEach(char => {
                            activeEmotions[char.id] = encounterEmotions[char.name] || currentEmotions[char.id] || 'neutral';
                          });

                          // Emotion display helpers (defined first for use in other functions)
                          const getEmotionEmoji = (emotion: string) => {
                            switch (emotion) {
                              case 'angry': return '😠';
                              case 'sad': return '😢';
                              case 'happy': return '😊';
                              case 'fearful': return '😨';
                              case 'neutral': return '😐';
                              case 'suspicious': return '🤨';
                              case 'conflicted': return '😖';
                              case 'proud': return '😤';
                              case 'guilty': return '😔';
                              case 'desperate': return '😰';
                              default: return '😐';
                            }
                          };

                          const getEmotionColor = (emotion: string) => {
                            switch (emotion) {
                              case 'angry': return '#e57373';
                              case 'sad': return '#64b5f6';
                              case 'happy': return '#81c784';
                              case 'fearful': return '#ffb74d';
                              case 'neutral': return '#757575';
                              case 'suspicious': return '#ba68c8';
                              case 'conflicted': return '#ffa726';
                              case 'proud': return '#ff9800';
                              case 'guilty': return '#9575cd';
                              case 'desperate': return '#f06292';
                              default: return '#757575';
                            }
                          };

                          // Helper function to render character portrait with emotion emoji overlay
                          const renderCharacterPortrait = (
                            character: Character,
                            size: number = 100,
                            showEmoji: boolean = true,
                            onClick?: () => void
                          ) => {
                            const emotion = activeEmotions[character.id] || 'neutral';
                            const emotionEmoji = getEmotionEmoji(emotion);
                            const emotionColor = getEmotionColor(emotion);
                            
                            return (
                              <div
                                style={{
                                  position: 'relative',
                                  width: `${size}px`,
                                  height: `${size}px`,
                                  cursor: onClick ? 'pointer' : 'default',
                                }}
                                onClick={onClick}
                              >
                                {character.portrait ? (
                                  <img
                                    src={character.portrait}
                                    alt={character.name}
                                    style={{
                                      width: `${size}px`,
                                      height: `${size}px`,
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: `3px solid ${emotionColor}`,
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: `${size}px`,
                                    height: `${size}px`,
                                    borderRadius: '50%',
                                    backgroundColor: '#1a1a1a',
                                    border: `3px solid ${emotionColor}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: `${size * 0.4}px`,
                                    fontWeight: '700',
                                    color: emotionColor,
                                  }}>
                                    {character.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                {showEmoji && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '-4px',
                                      right: '-4px',
                                      width: `${size * 0.32}px`,
                                      height: `${size * 0.32}px`,
                                      borderRadius: '50%',
                                      backgroundColor: '#ffffff',
                                      border: `2px solid ${emotionColor}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: `${size * 0.24}px`,
                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                                    }}
                                  >
                                    {emotionEmoji}
                                  </div>
                                )}
                              </div>
                            );
                          };


                          // Find active tension codons affecting involved characters
                          const activeTensionCodons = tensionCodonStates
                            .filter(tcs => {
                              const codon = playthroughTensionCodons.find(c => c.id === tcs.codonId);
                              if (!codon || tcs.resolved) return false;
                              return codon.affectedCharacterIds.some(cid => 
                                involvedCharacters.some(ic => ic.id === cid)
                              );
                            })
                            .map(tcs => {
                              const codon = playthroughTensionCodons.find(c => c.id === tcs.codonId);
                              return codon ? { ...codon, currentPressure: tcs.currentPressureLevel } : null;
                            })
                            .filter(Boolean) as Array<TensionCodon & { currentPressure: number }>;

                          return (
                            <div style={{
                              backgroundColor: '#1a1a1a',
                              border: '2px solid #4a4a4a',
                              borderRadius: '16px',
                              padding: '32px',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                            }}>
                              {/* Header */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '32px',
                                paddingBottom: '20px',
                                borderBottom: '2px solid #3a3a3a',
                              }}>
                                <div>
                                  <div style={{
                                    fontSize: '1.5rem',
                                    fontWeight: '700',
                                    color: '#f5f5f5',
                                    marginBottom: '8px',
                                    letterSpacing: '0.5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                  }}>
                                    <span>Encounter {storyHistory.length + 1}</span>
                                    {quickPlayMode && (
                                      <span style={{
                                        fontSize: '0.9rem',
                                        padding: '4px 12px',
                                        backgroundColor: '#ffa726',
                                        color: '#ffffff',
                                        borderRadius: '12px',
                                        fontWeight: '600',
                                      }}>
                                        ⚡ Quick Play Mode
                                      </span>
                                    )}
                                    <button
                                      onClick={() => setShowDebugInfo(!showDebugInfo)}
                                      style={{
                                        fontSize: '0.8rem',
                                        padding: '4px 10px',
                                        backgroundColor: showDebugInfo ? '#ff9800' : 'transparent',
                                        border: '1px solid #ff9800',
                                        borderRadius: '8px',
                                        color: showDebugInfo ? '#ffffff' : '#ff9800',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        marginLeft: 'auto',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!showDebugInfo) {
                                          e.currentTarget.style.backgroundColor = '#2a1a0a';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!showDebugInfo) {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                      }}
                                    >
                                      {showDebugInfo ? '🔍 Debug ON' : '🔍 Debug'}
                                    </button>
                                  </div>
                                  
                                  {/* Character Emotion Bar */}
                                  <div style={{
                                    display: 'flex',
                                    gap: '12px',
                                    flexWrap: 'wrap',
                                    marginBottom: '20px',
                                    padding: '12px',
                                    backgroundColor: '#1a1a1a',
                                    border: '1px solid #3a3a3a',
                                    borderRadius: '12px',
                                  }}>
                                    {involvedCharacters.map((character) => {
                                      const emotion = activeEmotions[character.id] || 'neutral';
                                      const emotionColor = getEmotionColor(emotion);
                                      
                                      return (
                                        <div
                                          key={character.id}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '8px 16px',
                                            backgroundColor: emotionColor,
                                            borderRadius: '20px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            color: '#ffffff',
                                            boxShadow: `0 2px 8px ${emotionColor}40`,
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s',
                                          }}
                                          onClick={() => setSelectedCharacterDetails(character.id)}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                          }}
                                        >
                                          {renderCharacterPortrait(character, 40, true, () => setSelectedCharacterDetails(character.id))}
                                          <span>{character.name}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                  <button
                                    onClick={() => setShowStorySummary(true)}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: 'transparent',
                                      border: '1px solid #4a6a4a',
                                      borderRadius: '8px',
                                      color: '#81c784',
                                      fontSize: '0.9rem',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#1a2a1a';
                                      e.currentTarget.style.borderColor = '#6a8a6a';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.borderColor = '#4a6a4a';
                                    }}
                                  >
                                    📖 View Full Story
                                  </button>
                                  <button
                                    onClick={endPlaythrough}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: 'transparent',
                                      border: '1px solid #6a4a4a',
                                      borderRadius: '8px',
                                      color: '#e57373',
                                      fontSize: '0.9rem',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#2a1a1a';
                                      e.currentTarget.style.borderColor = '#8a4a4a';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.borderColor = '#6a4a4a';
                                    }}
                                  >
                                    Return to Menu
                                  </button>
                                </div>
                              </div>

                              {/* Scene Description Area */}
                              <div style={{
                                marginBottom: '40px',
                              }}>
                                <h1 style={{
                                  fontSize: '2rem',
                                  fontWeight: '700',
                                  color: '#f5f5f5',
                                  marginBottom: '20px',
                                  lineHeight: '1.3',
                                }}>
                                  {currentEncounter.title}
                                </h1>

                                {/* Character Portraits with Emotions - Larger Display */}
                                <div style={{
                                  display: 'flex',
                                  gap: '16px',
                                  marginBottom: '24px',
                                  flexWrap: 'wrap',
                                }}>
                                  {involvedCharacters.map((character) => {
                                    const emotion = activeEmotions[character.id] || 'neutral';
                                    const emotionColor = getEmotionColor(emotion);
                                    
                                    return (
                                      <div
                                        key={character.id}
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          gap: '8px',
                                          padding: '12px',
                                          backgroundColor: '#2a2a2a',
                                          border: `3px solid ${emotionColor}`,
                                          borderRadius: '16px',
                                          minWidth: '120px',
                                          cursor: 'pointer',
                                          transition: 'transform 0.2s',
                                        }}
                                        onClick={() => setSelectedCharacterDetails(character.id)}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                      >
                                        {renderCharacterPortrait(character, 100, true, () => setSelectedCharacterDetails(character.id))}
                                        <div style={{
                                          fontSize: '0.9rem',
                                          fontWeight: '600',
                                          color: '#f5f5f5',
                                          textAlign: 'center',
                                        }}>
                                          {character.name}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                
                                {/* Debug Info Panel */}
                                {showDebugInfo && (
                                  <div style={{
                                    padding: '16px',
                                    backgroundColor: '#1a0a0a',
                                    border: '2px solid #ff9800',
                                    borderRadius: '8px',
                                    marginBottom: '16px',
                                    fontSize: '0.85rem',
                                    fontFamily: 'monospace',
                                    color: '#ff9800',
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                  }}>
                                    <div style={{ fontWeight: '700', marginBottom: '8px', color: '#ff9800' }}>
                                      🐛 DEBUG: Raw Encounter Data
                                    </div>
                                    <div style={{ color: '#a0a0a0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                      {JSON.stringify({
                                        id: currentEncounter.id,
                                        title: currentEncounter.title,
                                        description: currentEncounter.description,
                                        whatHappens: currentEncounter.whatHappens,
                                        characterIds: currentEncounter.characterIds,
                                        triggers: currentEncounter.triggers,
                                        playerChoicePoints: currentEncounter.playerChoicePoints,
                                        emotions: currentEncounter.emotions,
                                        activeEmotions: activeEmotions,
                                        choiceEmotionChanges: currentEncounter.choiceEmotionChanges,
                                      }, null, 2)}
                                    </div>
                                  </div>
                                )}
                                
                                <div style={{
                                  padding: '28px',
                                  backgroundColor: '#0f0f0f',
                                  border: '1px solid #4a4a4a',
                                  borderRadius: '12px',
                                  color: '#e5e5e5',
                                  lineHeight: '1.9',
                                  fontSize: '1.1rem',
                                  minHeight: '150px',
                                  marginBottom: '16px',
                                  whiteSpace: 'pre-wrap',
                                }}>
                                  {currentEncounter.whatHappens || currentEncounter.description || ''}
                                </div>

                                {/* Active Tension Codons */}
                                {activeTensionCodons.length > 0 && (
                                  <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#1a1a2a',
                                    border: '1px solid #4a4a6a',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    color: '#a0a0d0',
                                  }}>
                                    <span style={{ fontWeight: '600', color: '#b0b0e0' }}>Active Tensions: </span>
                                    {activeTensionCodons.map((codon: any, idx: number) => (
                                      <span key={codon.id}>
                                        {idx > 0 && ', '}
                                        {codon.name} ({(codon.currentPressure * 100).toFixed(0)}%)
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Choice Buttons */}
                              {!selectedChoice ? (
                                <div>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#d0d0d0',
                                    marginBottom: '20px',
                                  }}>
                                    What do you do?
                                  </div>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: '16px',
                                  }}>
                                    {(currentEncounter.playerChoicePoints || []).map((choiceText, index) => {
                                      const choice = choiceText.split('→')[0].trim();
                                      return (
                                        <button
                                          key={index}
                                          onClick={() => handleChoiceClick(choiceText)}
                                          style={{
                                            padding: '20px 24px',
                                            backgroundColor: '#2a2a2a',
                                            border: '2px solid #4a4a4a',
                                            borderRadius: '12px',
                                            color: '#f5f5f5',
                                            fontSize: '1rem',
                                            fontWeight: '500',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            position: 'relative',
                                            overflow: 'hidden',
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#3a3a3a';
                                            e.currentTarget.style.borderColor = '#64b5f6';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(100, 181, 246, 0.3)';
                                            setButtonHover(`choice-${index}`);
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#2a2a2a';
                                            e.currentTarget.style.borderColor = '#4a4a4a';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                            setButtonHover(null);
                                          }}
                                        >
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                          }}>
                                            <span style={{
                                              fontSize: '1.5rem',
                                              width: '32px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              backgroundColor: '#1a1a1a',
                                              borderRadius: '8px',
                                              flexShrink: 0,
                                            }}>
                                              {String.fromCharCode(65 + index)}
                                            </span>
                                            <span style={{ flex: 1 }}>{choice}</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div style={{
                                    padding: '28px',
                                    backgroundColor: '#1a2a1a',
                                    border: '2px solid #4a8a4a',
                                    borderRadius: '12px',
                                    color: '#e5e5e5',
                                    lineHeight: '1.9',
                                    fontSize: '1.1rem',
                                    marginBottom: '24px',
                                    boxShadow: '0 4px 16px rgba(129, 199, 132, 0.2)',
                                  }}>
                                    <div style={{
                                      fontSize: '0.9rem',
                                      fontWeight: '600',
                                      color: '#81c784',
                                      marginBottom: '12px',
                                      textTransform: 'uppercase',
                                      letterSpacing: '1px',
                                    }}>
                                      Outcome
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                      {choiceOutcome || ''}
                                    </div>
                                  </div>
                                  <button
                                    onClick={nextEncounter}
                                    disabled={isGeneratingNextEncounter}
                                    style={{
                                      padding: '16px 32px',
                                      backgroundColor: isGeneratingNextEncounter ? '#4a4a4a' : '#64b5f6',
                                      border: 'none',
                                      borderRadius: '12px',
                                      color: '#ffffff',
                                      fontSize: '1.1rem',
                                      fontWeight: '600',
                                      cursor: isGeneratingNextEncounter ? 'not-allowed' : 'pointer',
                                      transition: 'all 0.2s',
                                      width: '100%',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#90caf9';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(100, 181, 246, 0.4)';
                                      setButtonHover('next-encounter');
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#64b5f6';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                      setButtonHover(null);
                                    }}
                                  >
                                    Next Encounter →
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    )}

                    {/* Story So Far sidebar - Player View */}
                    <div style={{
                      width: '340px',
                      backgroundColor: '#1a1a1a',
                      border: '2px solid #3a3a3a',
                      borderRadius: '16px',
                      padding: '24px',
                      position: 'sticky',
                      top: '24px',
                      maxHeight: 'calc(100vh - 48px)',
                      overflowY: 'auto',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                      display: isMobile ? 'none' : 'block',
                    }}>
                      {/* Story So Far - Collapsible */}
                      <div style={{
                        marginBottom: '24px',
                        borderBottom: '2px solid #3a3a3a',
                        paddingBottom: '16px',
                      }}>
                        <button
                          onClick={() => setStorySoFarExpanded(!storySoFarExpanded)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#f5f5f5',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '8px 0',
                          }}
                        >
                          <span>📖 Story So Far</span>
                          <span style={{ fontSize: '1.2rem', color: '#64b5f6' }}>
                            {storySoFarExpanded ? '−' : '+'}
                          </span>
                        </button>
                        {storySoFarExpanded && (
                          <div style={{ marginTop: '16px' }}>
                            {storyHistory.length === 0 ? (
                              <p style={{ color: '#a0a0a0', fontSize: '0.9rem', fontStyle: 'italic' }}>
                                No choices made yet...
                              </p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {storyHistory.map((entry, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: '14px',
                                      backgroundColor: '#222222',
                                      border: '1px solid #3a3a3a',
                                      borderRadius: '8px',
                                    }}
                                  >
                                    <div style={{
                                      fontSize: '0.8rem',
                                      color: '#64b5f6',
                                      fontWeight: '600',
                                      marginBottom: '6px',
                                    }}>
                                      #{entry.encounterNumber}: {entry.encounterTitle}
                                    </div>
                                    <div style={{
                                      fontSize: '0.85rem',
                                      color: '#d0d0d0',
                                      marginBottom: '4px',
                                    }}>
                                      <strong style={{ color: '#f5f5f5' }}>→</strong> {entry.choice}
                                    </div>
                                    {entry.consequences && entry.consequences.length > 0 && (
                                      <div style={{
                                        marginTop: '6px',
                                        paddingTop: '6px',
                                        borderTop: '1px solid #3a3a3a',
                                      }}>
                                        {entry.consequences.map((consequence, idx) => (
                                          <div key={idx} style={{
                                            fontSize: '0.75rem',
                                            color: '#81c784',
                                            marginTop: '2px',
                                          }}>
                                            ⚡ {consequence}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Character Status - Collapsible */}
                      <div style={{
                        marginBottom: '24px',
                        borderBottom: '2px solid #3a3a3a',
                        paddingBottom: '16px',
                      }}>
                        <button
                          onClick={() => setCharacterStatusExpanded(!characterStatusExpanded)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#f5f5f5',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '8px 0',
                          }}
                        >
                          <span>⚡ Character Status</span>
                          <span style={{ fontSize: '1.2rem', color: '#64b5f6' }}>
                            {characterStatusExpanded ? '−' : '+'}
                          </span>
                        </button>
                        {characterStatusExpanded && (
                          <div style={{ marginTop: '16px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              {playthroughCharacters.map((character) => {
                                const charState = characterStates.find(cs => cs.characterId === character.id);
                                const attachedEnzymes = character.attachedEnzymes.map(ae => {
                                  const enzyme = playthroughEnzymes.find(e => e.id === ae.enzymeId);
                                  return enzyme ? { ...enzyme, intensity: ae.intensity } : null;
                                }).filter(Boolean);

                                return (
                                  <div
                                    key={character.id}
                                    style={{
                                      padding: '12px',
                                      backgroundColor: '#222222',
                                      border: '1px solid #3a3a3a',
                                      borderRadius: '8px',
                                    }}
                                  >
                                    {/* Character Name */}
                                    <div style={{
                                      fontSize: '0.9rem',
                                      fontWeight: '600',
                                      color: '#64b5f6',
                                      marginBottom: '10px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                    }}>
                                      <span>👤</span>
                                      <span>{character.name}</span>
                                    </div>

                                    {/* Enzyme Pressure Display */}
                                    {attachedEnzymes.length > 0 ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {attachedEnzymes.map((enz: any, idx: number) => {
                                          const pressure = charState?.enzymePressures[enz.id] || 0;
                                          const pressurePercent = pressure * 100;
                                          const mutationStatus = charState?.mutationStatus[enz.id] || 'normal';
                                          
                                          // Color coding based on pressure level
                                          let pressureColor: string;
                                          let pressureLabel: string;
                                          if (pressurePercent <= 30) {
                                            pressureColor = '#81c784'; // Green
                                            pressureLabel = 'Calm';
                                          } else if (pressurePercent <= 60) {
                                            pressureColor = '#ffeb3b'; // Yellow
                                            pressureLabel = 'Building';
                                          } else if (pressurePercent <= 80) {
                                            pressureColor = '#ff9800'; // Orange
                                            pressureLabel = 'Primed';
                                          } else {
                                            pressureColor = '#e57373'; // Red
                                            pressureLabel = 'Critical';
                                          }

                                          // Mutation status colors
                                          let mutationColor = '#ffa726';
                                          let mutationText = '';
                                          if (mutationStatus === 'hardening') {
                                            mutationColor = '#8b0000'; // Dark red
                                            mutationText = 'Hardened';
                                          } else if (mutationStatus === 'collapsed') {
                                            mutationColor = '#757575'; // Gray
                                            mutationText = 'Collapsed';
                                          } else if (mutationStatus === 'transcended') {
                                            mutationColor = '#ffd700'; // Gold
                                            mutationText = 'Transcended';
                                          }

                                          const isHighPressure = pressurePercent > 80;
                                          
                                          return (
                                            <div key={idx} style={{ 
                                              padding: '10px', 
                                              backgroundColor: '#1a1a1a', 
                                              borderRadius: '6px',
                                              border: `1px solid ${isHighPressure ? pressureColor : '#3a3a3a'}`,
                                              position: 'relative',
                                            }}>
                                              {/* Enzyme Name with Warning Indicator */}
                                              <div style={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                marginBottom: '6px',
                                              }}>
                                                <div style={{ 
                                                  fontWeight: '600', 
                                                  color: '#d0d0d0',
                                                  fontSize: '0.85rem',
                                                  flex: 1,
                                                }}>
                                                  {enz.traitName}
                                                </div>
                                                {isHighPressure && mutationStatus === 'normal' && (
                                                  <div
                                                    style={{
                                                      fontSize: '0.9rem',
                                                      color: pressureColor,
                                                      cursor: 'help',
                                                      position: 'relative',
                                                    }}
                                                    title={`${character.name}'s ${enz.traitName} is near mutation threshold`}
                                                  >
                                                    ⚠️
                                                  </div>
                                                )}
                                              </div>

                                              {/* Pressure Bar */}
                                              <div style={{ 
                                                marginBottom: '4px',
                                              }}>
                                                <div style={{ 
                                                  fontSize: '0.7rem', 
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'space-between',
                                                  marginBottom: '4px',
                                                }}>
                                                  <span style={{ color: '#a0a0a0' }}>Pressure</span>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ 
                                                      fontSize: '0.7rem',
                                                      color: '#888',
                                                      fontStyle: 'italic',
                                                    }}>
                                                      {pressureLabel}
                                                    </span>
                                                    <span style={{ 
                                                      color: pressureColor, 
                                                      fontWeight: '600',
                                                      minWidth: '40px',
                                                      textAlign: 'right',
                                                      fontSize: '0.75rem',
                                                    }}>
                                                      {pressurePercent.toFixed(0)}%
                                                    </span>
                                                  </div>
                                                </div>
                                                <div style={{
                                                  width: '100%',
                                                  height: '8px',
                                                  backgroundColor: '#2a2a2a',
                                                  borderRadius: '4px',
                                                  overflow: 'hidden',
                                                  border: `1px solid ${pressureColor}40`,
                                                }}>
                                                  <div style={{
                                                    width: `${pressurePercent}%`,
                                                    height: '100%',
                                                    backgroundColor: pressureColor,
                                                    transition: 'width 0.3s ease',
                                                    borderRadius: '4px',
                                                  }} />
                                                </div>
                                              </div>

                                              {/* Mutation Status */}
                                              {mutationStatus !== 'normal' && (
                                                <div style={{ 
                                                  fontSize: '0.75rem', 
                                                  color: mutationColor, 
                                                  marginTop: '4px',
                                                  fontWeight: '600',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '4px',
                                                }}>
                                                  <span>⚡</span>
                                                  <span>{mutationText}</span>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>
                                        No enzymes attached
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Tension Codon States */}
                      {tensionCodonStates.length > 0 && (
                        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #3a3a3a' }}>
                          <h4 style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#f5f5f5',
                            marginBottom: '12px',
                          }}>
                            Tension Codons
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {tensionCodonStates.map((codonState) => {
                              const codon = playthroughTensionCodons.find(c => c.id === codonState.codonId);
                              if (!codon) return null;
                              
                              return (
                                <div
                                  key={codonState.codonId}
                                  style={{
                                    padding: '10px',
                                    backgroundColor: '#222222',
                                    border: `1px solid ${codonState.resolved ? '#4a8a4a' : '#3a3a3a'}`,
                                    borderRadius: '6px',
                                  }}
                                >
                                  <div style={{
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: codonState.resolved ? '#81c784' : '#64b5f6',
                                    marginBottom: '4px',
                                  }}>
                                    {codon.name}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#a0a0a0' }}>
                                    Pressure: {(codonState.currentPressureLevel * 100).toFixed(0)}%
                                  </div>
                                  {codonState.resolved && (
                                    <div style={{ fontSize: '0.75rem', color: '#81c784', marginTop: '4px' }}>
                                      ✓ Resolved
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                </>
                )}
              </div>
            </>
          ) : currentView === 'settings' ? (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Settings</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Configure API keys and preferences</p>
              </header>

              <div style={{ marginTop: '48px' }}>
                {/* API Configuration Section */}
                <div style={sectionStyle}>
                  <h2 style={sectionTitleStyle}>API Configuration</h2>
                  <p style={sectionDescStyle}>Configure API keys for AI-powered features and storage</p>
                  
                  {/* Claude API Key */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '12px' }}>Claude API Key</h3>
                    <p style={sectionDescStyle}>Enter your Claude API key to enable AI-powered features</p>
                  
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="settings-api-key">
                      Claude API Key
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="settings-api-key"
                        type={showApiKey ? 'text' : 'password'}
                        value={apiKeyInput}
                        onChange={(e) => setApiKeyInput(e.target.value)}
                        placeholder="sk-ant-api03-..."
                        style={{ ...inputStyle, paddingRight: '100px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          border: '1px solid #4a4a4a',
                          borderRadius: '6px',
                          color: '#a0a0a0',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#3a3a3a';
                          e.currentTarget.style.borderColor = '#5a5a5a';
                          e.currentTarget.style.color = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#4a4a4a';
                          e.currentTarget.style.color = '#a0a0a0';
                        }}
                      >
                        {showApiKey ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                      Your API key is stored locally in your browser. Get your key from{' '}
                      <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#64b5f6' }}>
                        Anthropic Console
                      </a>
                    </p>
                  </div>

                  <div style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#2a1a0a', 
                    border: '1px solid #8b5a2b', 
                    borderRadius: '8px', 
                    marginBottom: '24px' 
                  }}>
                    <p style={{ 
                      margin: 0, 
                      color: '#ffa726', 
                      fontSize: '0.9rem', 
                      lineHeight: '1.6'
                    }}>
                      ⚠️ Keep your API key private. This is stored in your browser only.
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={saveApiKey}
                      disabled={!apiKeyInput.trim()}
                      style={{
                        ...getButtonStyle(buttonHover === 'save-api-key'),
                        opacity: !apiKeyInput.trim() ? 0.6 : 1,
                        cursor: !apiKeyInput.trim() ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={() => apiKeyInput.trim() && setButtonHover('save-api-key')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Save
                    </button>
                  </div>

                  {saveApiKeyResult && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '16px',
                      backgroundColor: saveApiKeyResult.success ? '#1a2a1a' : '#2a1a1a',
                      border: `1px solid ${saveApiKeyResult.success ? '#4a8a4a' : '#8a4a4a'}`,
                      borderRadius: '8px',
                      color: saveApiKeyResult.success ? '#81c784' : '#e57373',
                      fontSize: '0.95rem',
                      lineHeight: '1.6'
                    }}>
                      {saveApiKeyResult.message}
                    </div>
                  )}

                  <div style={{ 
                    marginTop: '20px',
                    padding: '12px',
                    backgroundColor: '#1a2a2a',
                    border: '1px solid #3a4a4a',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: '#a0a0a0',
                    lineHeight: '1.6'
                  }}>
                    ℹ️ API features will work in the Cobot panel
                  </div>

                  {claudeApiKey && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '12px',
                      backgroundColor: '#1a2a2a',
                      border: '1px solid #3a4a4a',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      color: '#81c784'
                    }}>
                      ✓ API key is saved and ready to use
                    </div>
                  )}
                  </div>
                </div>

                {/* Storage Section */}
                <div style={{ ...sectionStyle, marginTop: '48px' }}>
                  <h2 style={sectionTitleStyle}>Storage</h2>
                  <p style={sectionDescStyle}>Configure storage services for decentralized library</p>
                  
                  {/* NFT.Storage Token */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '12px' }}>NFT.Storage Token</h3>
                    <p style={sectionDescStyle}>Configure NFT.Storage for decentralized library storage</p>
                  
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="nft-storage-token">
                      NFT.Storage API Token
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="nft-storage-token"
                        type={showNftStorageToken ? 'text' : 'password'}
                        value={nftStorageTokenInput}
                        onChange={(e) => setNftStorageTokenInput(e.target.value)}
                        placeholder="Enter your NFT.Storage API token"
                        style={{ ...inputStyle, paddingRight: '100px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNftStorageToken(!showNftStorageToken)}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          border: '1px solid #4a4a4a',
                          borderRadius: '6px',
                          color: '#a0a0a0',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#3a3a3a';
                          e.currentTarget.style.borderColor = '#5a5a5a';
                          e.currentTarget.style.color = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#4a4a4a';
                          e.currentTarget.style.color = '#a0a0a0';
                        }}
                      >
                        {showNftStorageToken ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                      Get your API token from{' '}
                      <a href="https://nft.storage/" target="_blank" rel="noopener noreferrer" style={{ color: '#64b5f6' }}>
                        NFT.Storage
                      </a>
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={saveNftStorageToken}
                      disabled={!nftStorageTokenInput.trim()}
                      style={{
                        ...getButtonStyle(buttonHover === 'save-nft-storage-token'),
                        opacity: !nftStorageTokenInput.trim() ? 0.6 : 1,
                        cursor: !nftStorageTokenInput.trim() ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={() => nftStorageTokenInput.trim() && setButtonHover('save-nft-storage-token')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Save
                    </button>
                  </div>

                  {saveNftStorageTokenResult && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '16px',
                      backgroundColor: saveNftStorageTokenResult.success ? '#1a2a1a' : '#2a1a1a',
                      border: `1px solid ${saveNftStorageTokenResult.success ? '#4a8a4a' : '#8a4a4a'}`,
                      borderRadius: '8px',
                      color: saveNftStorageTokenResult.success ? '#81c784' : '#e57373',
                      fontSize: '0.95rem',
                      lineHeight: '1.6'
                    }}>
                      {saveNftStorageTokenResult.message}
                    </div>
                  )}

                  <div style={{ 
                    marginTop: '20px',
                    padding: '12px',
                    backgroundColor: '#1a2a2a',
                    border: '1px solid #3a4a4a',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: '#a0a0a0',
                    lineHeight: '1.6'
                  }}>
                    ℹ️ Used for decentralized library storage
                  </div>

                  {nftStorageToken && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '12px',
                      backgroundColor: '#1a2a2a',
                      border: '1px solid #3a4a4a',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      color: '#81c784'
                    }}>
                      ✓ NFT.Storage token is saved and ready to use
                    </div>
                  )}
                  </div>
                </div>

                <div style={{ ...sectionStyle, marginTop: '48px' }}>
                  <h2 style={sectionTitleStyle}>Library Index</h2>
                  <p style={sectionDescStyle}>Configure shared library access</p>
                  
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="library-index-url">
                      Library Index URL
                    </label>
                    <input
                      id="library-index-url"
                      type="text"
                      value={libraryIndexUrlInput}
                      onChange={(e) => setLibraryIndexUrlInput(e.target.value)}
                      placeholder="https://example.com/library-index.json"
                      style={inputStyle}
                    />
                    <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                      URL to a JSON file containing the shared library index. See Help for setup instructions.
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setLibraryIndexUrl(libraryIndexUrlInput.trim());
                        localStorage.setItem('splinewright-library-index-url', libraryIndexUrlInput.trim());
                      }}
                      disabled={!libraryIndexUrlInput.trim()}
                      style={{
                        ...getButtonStyle(buttonHover === 'save-index-url'),
                        opacity: !libraryIndexUrlInput.trim() ? 0.6 : 1,
                        cursor: !libraryIndexUrlInput.trim() ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={() => libraryIndexUrlInput.trim() && setButtonHover('save-index-url')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Save URL
                    </button>
                    {libraryIndexUrl && (
                      <button
                        onClick={fetchLibraryIndex}
                        disabled={loadingLibraryIndex}
                        style={{
                          ...getButtonStyle(buttonHover === 'fetch-index'),
                          opacity: loadingLibraryIndex ? 0.6 : 1,
                          cursor: loadingLibraryIndex ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={() => !loadingLibraryIndex && setButtonHover('fetch-index')}
                        onMouseLeave={() => setButtonHover(null)}
                      >
                        {loadingLibraryIndex ? 'Loading...' : 'Fetch Library Index'}
                      </button>
                    )}
                  </div>

                  {libraryIndexUrl && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '12px',
                      backgroundColor: '#1a2a2a',
                      border: '1px solid #3a4a4a',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      color: '#81c784'
                    }}>
                      ✓ Library Index URL: {libraryIndexUrl}
                      {remoteLibraryItems.length > 0 && (
                        <div style={{ marginTop: '8px', color: '#a0a0a0' }}>
                          {remoteLibraryItems.length} items available
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ ...sectionStyle, marginTop: '48px' }}>
                  <h2 style={sectionTitleStyle}>Pinata IPFS Storage</h2>
                  <p style={sectionDescStyle}>Configure Pinata credentials for IPFS storage of published components</p>
                  
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="pinata-api-key">
                      Pinata API Key
                    </label>
                    <input
                      id="pinata-api-key"
                      type="text"
                      value={pinataApiKeyInput}
                      onChange={(e) => setPinataApiKeyInput(e.target.value)}
                      placeholder="Enter your Pinata API Key"
                      style={inputStyle}
                    />
                    <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                      Get your API key from <a href="https://app.pinata.cloud/" target="_blank" rel="noopener noreferrer" style={{ color: '#64b5f6' }}>Pinata Dashboard</a>
                    </p>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="pinata-secret">
                      Pinata Secret
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="pinata-secret"
                        type={showPinataSecret ? 'text' : 'password'}
                        value={pinataSecretInput}
                        onChange={(e) => setPinataSecretInput(e.target.value)}
                        placeholder="Enter your Pinata Secret"
                        style={{ ...inputStyle, paddingRight: '100px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPinataSecret(!showPinataSecret)}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          border: '1px solid #4a4a4a',
                          borderRadius: '6px',
                          color: '#a0a0a0',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#3a3a3a';
                          e.currentTarget.style.borderColor = '#5a5a5a';
                          e.currentTarget.style.color = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#4a4a4a';
                          e.currentTarget.style.color = '#a0a0a0';
                        }}
                      >
                        {showPinataSecret ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                      Keep your secret key private. This is stored in your browser only.
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setPinataApiKey(pinataApiKeyInput.trim());
                        setPinataSecret(pinataSecretInput.trim());
                        localStorage.setItem('splinewright-pinata-api-key', pinataApiKeyInput.trim());
                        localStorage.setItem('splinewright-pinata-secret', pinataSecretInput.trim());
                        alert('✓ Pinata credentials saved!');
                      }}
                      disabled={!pinataApiKeyInput.trim() || !pinataSecretInput.trim()}
                      style={{
                        ...getButtonStyle(buttonHover === 'save-pinata'),
                        opacity: (!pinataApiKeyInput.trim() || !pinataSecretInput.trim()) ? 0.6 : 1,
                        cursor: (!pinataApiKeyInput.trim() || !pinataSecretInput.trim()) ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={() => (pinataApiKeyInput.trim() && pinataSecretInput.trim()) && setButtonHover('save-pinata')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Save Credentials
                    </button>
                  </div>

                  {pinataApiKey && pinataSecret && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '12px',
                      backgroundColor: '#1a2a2a',
                      border: '1px solid #3a4a4a',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      color: '#81c784'
                    }}>
                      ✓ Pinata credentials configured
                    </div>
                  )}
                </div>

                <div style={{ ...sectionStyle, marginTop: '48px' }}>
                  <h2 style={sectionTitleStyle}>Author Identity</h2>
                  <p style={sectionDescStyle}>Your unique author ID for signing published components</p>
                  
                  {authorId ? (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '16px',
                      backgroundColor: '#1a2a2a',
                      border: '1px solid #3a4a4a',
                      borderRadius: '8px',
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ ...labelStyle, marginBottom: '8px' }}>Your Author ID:</div>
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#2a2a2a',
                          border: '1px solid #4a4a4a',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontSize: '1.1rem',
                          color: '#64b5f6',
                          fontWeight: '600',
                          letterSpacing: '0.5px',
                        }}>
                          {authorId}
                        </div>
                      </div>
                      <p style={{ fontSize: '0.85rem', color: '#a0a0a0', margin: 0, lineHeight: '1.6' }}>
                        This ID is automatically generated and stored locally. Components you publish will be signed with this ID to prove authenticity.
                      </p>
                    </div>
                  ) : (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '12px',
                      backgroundColor: '#2a1a1a',
                      border: '1px solid #4a2a2a',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      color: '#e57373'
                    }}>
                      ⚠️ Author identity is being initialized...
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : currentView === 'library' ? (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Library</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Share and discover genome components</p>
              </header>

              <div style={{ marginTop: '48px' }}>
                {/* Export/Import Section */}
                <div style={sectionStyle}>
                  <h2 style={sectionTitleStyle}>Export / Import Library</h2>
                  <p style={sectionDescStyle}>Share your library or import components from others</p>
                  
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '20px' }}>
                    <button
                      onClick={exportLibrary}
                      disabled={publishedLibrary.length === 0}
                      style={{
                        ...getButtonStyle(buttonHover === 'export-library'),
                        opacity: publishedLibrary.length === 0 ? 0.6 : 1,
                        cursor: publishedLibrary.length === 0 ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={() => publishedLibrary.length > 0 && setButtonHover('export-library')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Export My Library
                    </button>
                    <button
                      onClick={importLibrary}
                      style={getButtonStyle(buttonHover === 'import-library')}
                      onMouseEnter={() => setButtonHover('import-library')}
                      onMouseLeave={() => setButtonHover(null)}
                    >
                      Import Library File
                    </button>
                  </div>

                  {publishedLibrary.length > 0 && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '12px',
                      backgroundColor: '#1a2a2a',
                      border: '1px solid #3a4a4a',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      color: '#a0a0a0',
                      lineHeight: '1.6'
                    }}>
                      Your library contains {publishedLibrary.length} component{publishedLibrary.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* Publish Section */}
                <div style={sectionStyle}>
                  <h2 style={sectionTitleStyle}>Publish</h2>
                  <p style={sectionDescStyle}>Publish your components to your local library</p>
                  
                  {!nftStorageToken && (
                    <div style={{ 
                      marginBottom: '24px',
                      padding: '12px 16px', 
                      backgroundColor: '#2a1a0a', 
                      border: '1px solid #8b5a2b', 
                      borderRadius: '8px',
                    }}>
                      <p style={{ 
                        margin: 0, 
                        color: '#ffa726', 
                        fontSize: '0.9rem', 
                        lineHeight: '1.6'
                      }}>
                        💡 Add your NFT.Storage token in Settings to publish to IPFS for decentralized sharing.
                      </p>
                    </div>
                  )}

                  {/* Enzymes */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '16px' }}>Enzymes</h3>
                    {savedEnzymes.length === 0 ? (
                      <p style={emptySidebarStyle}>No enzymes saved yet</p>
                    ) : (
                      <div style={enzymeListStyle}>
                        {savedEnzymes.map((enzyme) => (
                          <div
                            key={enzyme.id}
                            style={{
                              ...enzymeItemStyle(false, false),
                              marginBottom: '12px',
                              padding: '16px',
                            }}
                          >
                            <div style={enzymeItemHeaderStyle}>
                              <div style={enzymeNameStyle}>{enzyme.characterName} - {enzyme.traitName}</div>
                            </div>
                            <button
                              onClick={() => publishToLibrary(enzyme.id, 'Enzyme')}
                              style={{
                                ...getButtonStyle(buttonHover === `publish-${enzyme.id}`),
                                marginTop: '12px',
                                width: '100%',
                                backgroundColor: publishConfirmation?.itemId === enzyme.id ? '#81c784' : undefined,
                                borderColor: publishConfirmation?.itemId === enzyme.id ? '#81c784' : undefined,
                                transition: 'all 0.3s ease',
                              }}
                              onMouseEnter={() => setButtonHover(`publish-${enzyme.id}`)}
                              onMouseLeave={() => setButtonHover(null)}
                            >
                              {publishConfirmation?.itemId === enzyme.id ? '✓ Published!' : 'Publish to Library'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Characters */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '16px' }}>Characters</h3>
                    {characters.length === 0 ? (
                      <p style={emptySidebarStyle}>No characters created yet</p>
                    ) : (
                      <div style={enzymeListStyle}>
                        {characters.map((character) => (
                          <div
                            key={character.id}
                            style={{
                              ...enzymeItemStyle(false, false),
                              marginBottom: '12px',
                              padding: '16px',
                            }}
                          >
                            <div style={enzymeItemHeaderStyle}>
                              <div style={enzymeNameStyle}>{character.name}</div>
                            </div>
                            <button
                              onClick={() => publishToLibrary(character.id, 'Character')}
                              style={{
                                ...getButtonStyle(buttonHover === `publish-${character.id}`),
                                marginTop: '12px',
                                width: '100%',
                                backgroundColor: publishConfirmation?.itemId === character.id ? '#81c784' : undefined,
                                borderColor: publishConfirmation?.itemId === character.id ? '#81c784' : undefined,
                                transition: 'all 0.3s ease',
                              }}
                              onMouseEnter={() => setButtonHover(`publish-${character.id}`)}
                              onMouseLeave={() => setButtonHover(null)}
                            >
                              {publishConfirmation?.itemId === character.id ? '✓ Published!' : 'Publish to Library'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Tension Codons */}
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '16px' }}>Tension Codons</h3>
                    {tensionCodons.length === 0 ? (
                      <p style={emptySidebarStyle}>No tension codons created yet</p>
                    ) : (
                      <div style={enzymeListStyle}>
                        {tensionCodons.map((codon) => (
                          <div
                            key={codon.id}
                            style={{
                              ...enzymeItemStyle(false, false),
                              marginBottom: '12px',
                              padding: '16px',
                            }}
                          >
                            <div style={enzymeItemHeaderStyle}>
                              <div style={enzymeNameStyle}>{codon.name}</div>
                            </div>
                            <button
                              onClick={() => publishToLibrary(codon.id, 'Tension Codon')}
                              style={{
                                ...getButtonStyle(buttonHover === `publish-${codon.id}`),
                                marginTop: '12px',
                                width: '100%',
                                backgroundColor: publishConfirmation?.itemId === codon.id ? '#81c784' : undefined,
                                borderColor: publishConfirmation?.itemId === codon.id ? '#81c784' : undefined,
                                transition: 'all 0.3s ease',
                              }}
                              onMouseEnter={() => setButtonHover(`publish-${codon.id}`)}
                              onMouseLeave={() => setButtonHover(null)}
                            >
                              {publishConfirmation?.itemId === codon.id ? '✓ Published!' : 'Publish to Library'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {showPublishForm && (
                    <div style={{
                      marginTop: '24px',
                      padding: '24px',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                    }}>
                      <h3 style={{ ...sectionTitleStyle, marginBottom: '20px', fontSize: '1.1rem' }}>Publish Details</h3>
                      
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="publish-author">
                          Author Name *
                        </label>
                        <input
                          id="publish-author"
                          type="text"
                          value={publishAuthor}
                          onChange={(e) => setPublishAuthor(e.target.value)}
                          placeholder="Your name"
                          style={inputStyle}
                        />
                        {authorId && (
                          <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                            This component will be signed with your Author ID: <strong style={{ color: '#64b5f6', fontFamily: 'monospace' }}>{authorId}</strong>
                          </p>
                        )}
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="publish-description">
                          Description *
                        </label>
                        <textarea
                          id="publish-description"
                          value={publishDescription}
                          onChange={(e) => setPublishDescription(e.target.value)}
                          placeholder="A short description of this component..."
                          style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                        />
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="publish-tags">
                          Tags (comma separated)
                        </label>
                        <input
                          id="publish-tags"
                          type="text"
                          value={publishTags}
                          onChange={(e) => setPublishTags(e.target.value)}
                          placeholder="drama, conflict, loyalty"
                          style={inputStyle}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button
                          onClick={handlePublish}
                          style={getButtonStyle(buttonHover === 'confirm-publish')}
                          onMouseEnter={() => setButtonHover('confirm-publish')}
                          onMouseLeave={() => setButtonHover(null)}
                        >
                          Confirm Publish
                        </button>
                        <button
                          onClick={() => {
                            setShowPublishForm(false);
                            setSelectedPublishItemId(null);
                          }}
                          style={getNewButtonStyle(buttonHover === 'cancel-publish')}
                          onMouseEnter={() => setButtonHover('cancel-publish')}
                          onMouseLeave={() => setButtonHover(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Browse Section */}
                <div style={{ ...sectionStyle, marginTop: '48px' }}>
                  <h2 style={sectionTitleStyle}>Browse</h2>
                  <p style={sectionDescStyle}>Browse and import components from your published library</p>

                  <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={labelStyle} htmlFor="library-search">
                        Search:
                      </label>
                      <input
                        id="library-search"
                        type="text"
                        value={librarySearchQuery}
                        onChange={(e) => setLibrarySearchQuery(e.target.value)}
                        placeholder="Search by name or description..."
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ minWidth: '150px' }}>
                      <label style={labelStyle} htmlFor="browse-filter-type">
                        Filter by type:
                      </label>
                      <select
                        id="browse-filter-type"
                        value={browseFilterType}
                        onChange={(e) => setBrowseFilterType(e.target.value as 'all' | 'enzyme' | 'character' | 'tensionCodon')}
                        style={inputStyle}
                      >
                        <option value="all">All Types</option>
                        <option value="enzyme">Enzyme</option>
                        <option value="character">Character</option>
                        <option value="tensionCodon">Tension Codon</option>
                      </select>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '20px',
                  }}>
                    {filteredPublishedLibrary.length === 0 ? (
                      <div style={{
                        gridColumn: '1 / -1',
                        padding: '40px',
                        textAlign: 'center',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '12px',
                      }}>
                        <p style={emptySidebarStyle}>
                          {publishedLibrary.length === 0 
                            ? 'No items published yet. Publish items in the Publish section above!'
                            : 'No items match your search.'}
                        </p>
                      </div>
                    ) : (
                      filteredPublishedLibrary.map((item) => {
                        const typeLabel = item.type === 'enzyme' ? 'Enzyme' : 
                                         item.type === 'character' ? 'Character' : 
                                         'Tension Codon';
                        const isIPFSItem = item.isIPFS || (item.cid && !item.data);
                        const isLoading = isIPFSItem && loadingIPFS[item.cid || ''];
                        
                        return (
                          <div 
                            key={item.id || item.cid} 
                            style={{
                              backgroundColor: '#222222',
                              border: '1px solid #3a3a3a',
                              borderRadius: '12px',
                              padding: '20px',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#64b5f6';
                              e.currentTarget.style.backgroundColor = '#2a2a2a';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#3a3a3a';
                              e.currentTarget.style.backgroundColor = '#222222';
                            }}
                          >
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <h3 style={{ 
                                  fontSize: '1.2rem',
                                  fontWeight: '600',
                                  color: '#f5f5f5',
                                  margin: 0,
                                }}>
                                  {item.name}
                                </h3>
                                {isIPFSItem && (
                                  <span style={{
                                    padding: '2px 8px',
                                    backgroundColor: '#1a2a4a',
                                    border: '1px solid #3a4a6a',
                                    borderRadius: '8px',
                                    fontSize: '0.7rem',
                                    color: '#64b5f6',
                                    fontWeight: '600',
                                  }}>
                                    IPFS
                                  </span>
                                )}
                              </div>
                              <div style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                backgroundColor: '#1a2a2a',
                                border: '1px solid #3a4a4a',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                color: '#64b5f6',
                                fontWeight: '600',
                              }}>
                                {typeLabel}
                              </div>
                            </div>
                            
                            {isLoading ? (
                              <div style={{ 
                                marginBottom: '16px',
                                fontSize: '0.9rem',
                                color: '#64b5f6',
                                fontStyle: 'italic',
                              }}>
                                Loading from IPFS...
                              </div>
                            ) : item.description ? (
                              <div style={{ 
                                marginBottom: '16px',
                                fontSize: '0.9rem',
                                color: '#a0a0a0',
                                lineHeight: '1.5',
                                maxHeight: '60px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                {item.description.length > 100 
                                  ? item.description.substring(0, 100) + '...' 
                                  : item.description}
                              </div>
                            ) : isIPFSItem ? (
                              <div style={{ 
                                marginBottom: '16px',
                                fontSize: '0.85rem',
                                color: '#888',
                                fontStyle: 'italic',
                              }}>
                                Click Import to load from IPFS
                              </div>
                            ) : null}

                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                // If it's an IPFS item without data, fetch it first
                                if (isIPFSItem && item.cid && !item.data) {
                                  const data = await fetchFromIPFS(item.cid);
                                  if (data) {
                                    const fullItem = { ...item, data: data };
                                    importFromLibrary(fullItem);
                                  } else {
                                    alert('Failed to load item from IPFS. Please try again.');
                                  }
                                } else {
                                  importFromLibrary(item);
                                }
                              }}
                              disabled={isLoading}
                              style={{
                                ...getButtonStyle(buttonHover === `import-${item.id || item.cid}`),
                                marginTop: 'auto',
                                width: '100%',
                                opacity: isLoading ? 0.6 : 1,
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                              }}
                              onMouseEnter={() => !isLoading && setButtonHover(`import-${item.id || item.cid}`)}
                              onMouseLeave={() => setButtonHover(null)}
                            >
                              {isLoading ? 'Loading...' : 'Import to My Work'}
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : currentView === 'guide' ? (
            <>
              <header style={headerStyle}>
                <h1 style={h1Style}>Enzyme Design Guide</h1>
                <p style={{ margin: 0, color: '#a0a0a0', fontSize: '1.1rem' }}>Complete guide to designing effective behavioral enzymes</p>
              </header>

              <div style={{ marginTop: '48px', maxWidth: '900px', margin: '48px auto 0', padding: '0 24px' }}>
                {/* Search Box */}
                <div style={{ marginBottom: '32px' }}>
                  <input
                    type="text"
                    value={guideSearchQuery}
                    onChange={(e) => setGuideSearchQuery(e.target.value)}
                    placeholder="Search guide topics..."
                    style={{
                      ...inputStyle,
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '1rem',
                    }}
                  />
                </div>

                {/* Guide Sections */}
                {(() => {
                  const guideContent = {
                    whatIsEnzyme: `Enzymes are dynamic behavioral patterns that define how characters react to specific situations. Unlike static traits, enzymes create active, responsive behaviors that generate drama and conflict.

**Enzymes vs Static Traits:**
- Static traits describe what a character is (e.g., "brave," "loyal")
- Enzymes describe how a character reacts when triggered (e.g., "when authority is questioned, they become defensive and aggressive")
- Enzymes create action; static traits create description
- Enzymes generate story events; static traits generate character descriptions

**Why Enzymes Matter:**
Enzymes transform personality into plot. A character with "loyalty" as a static trait might be described as loyal, but a character with a loyalty enzyme will actively defend their chosen person, make sacrifices, and create dramatic moments through their actions.`,

                fiveComponents: `Every enzyme consists of five essential components that work together to create dynamic character behavior:

**1. Trait**
The personality aspect being modeled (e.g., "Wounded Pride," "Self-Sabotage," "Misplaced Loyalty"). The trait should have internal tension—it should be both a strength and a weakness.

**2. Triggers**
Specific situations that activate the enzyme. Triggers should be concrete and actionable, not abstract feelings. Good triggers create clear "if-then" scenarios.

**3. Responses**
How the character behaves when triggered. Responses should be patterns, not scripts—they describe behavioral tendencies, not exact dialogue or actions.

**4. Blindspots**
What the character fails to notice or understand. Blindspots create dramatic irony and opportunities for conflict. They're the gaps in the character's awareness.

**5. Mutations**
Variations that can occur under specific conditions. Mutations add complexity and prevent enzymes from becoming predictable. They represent how the trait might evolve or break down.`,

                choosingTraits: `**Good Traits with Internal Tension:**
- Wounded Pride (pride protects but also blinds)
- Self-Sabotage (self-protection that destroys)
- Misplaced Loyalty (loyalty to wrong people/ideals)
- Righteous Anger (justice that becomes vengeance)
- Protective Instinct (protection that becomes control)

**Traits to Avoid:**
- Pure virtues without flaws (e.g., "always kind")
- Pure vices without complexity (e.g., "always evil")
- Traits without internal tension
- Traits that are too abstract (e.g., "goodness")
- Traits that don't create conflict

**Intensity Levels:**
- **Low (0.2-0.4):** Subtle influence, occasional activation
- **Medium (0.5-0.7):** Regular influence, frequent activation
- **High (0.8-1.0):** Dominant trait, almost always active

Choose intensity based on how central the trait is to the character's identity and how often it should drive their behavior.`,

                designingTriggers: `**Trigger Categories:**

**Social Triggers:**
- "When someone questions their authority in front of others"
- "When excluded from a group decision"
- "When their social status is threatened"

**Status Triggers:**
- "When their achievements are overlooked"
- "When someone gets credit for their work"
- "When their position is challenged"

**Relational Triggers:**
- "When a close ally shows doubt"
- "When someone they trust betrays them"
- "When their loyalty is tested"

**Achievement Triggers:**
- "When they fail at something important"
- "When success is within reach but threatened"
- "When their competence is questioned"

**Specificity:**
Make triggers specific enough to create clear scenarios but broad enough to apply to multiple situations. "When authority is questioned" is better than "When Bob questions their authority on Tuesday."

**Sensitivity:**
More triggers = more opportunities for activation. Fewer triggers = more focused, intense responses. Balance based on the trait's role in the story.`,

                designingResponses: `**Immediate vs Delayed:**
- **Immediate:** Character reacts instantly (e.g., "immediately becomes defensive")
- **Delayed:** Character processes then reacts (e.g., "withdraws, then plans retaliation")
- Mix both types for variety

**Patterns Not Scripts:**
Responses should describe behavioral tendencies, not exact actions:
- ✅ Good: "Becomes defensive and questions the other person's motives"
- ❌ Bad: "Says 'How dare you question me!' and storms out"

**Framing:**
Frame responses to show how the character interprets the trigger:
- "Interprets the question as an attack on their competence"
- "Sees the exclusion as a personal rejection"
- "Views the challenge as a threat to their identity"

**Proportionality:**
Responses should match the trigger's intensity and the enzyme's intensity. A high-intensity enzyme with a low-intensity trigger might still create a strong response, but it should feel justified by the character's psychology.`,

                designingBlindspots: `**Types of Blindspots:**

**Self-Justification Blindspots:**
- Character doesn't see their own harmful behavior
- "Fails to notice when their protection becomes control"
- "Doesn't recognize their loyalty is misplaced"

**Relational Blindspots:**
- Character misunderstands others' motivations
- "Doesn't see that their ally is using them"
- "Fails to notice when their help is unwanted"

**Consequence Blindspots:**
- Character doesn't see the results of their actions
- "Doesn't realize their defense is pushing people away"
- "Fails to notice their protection is creating dependency"

**Crack Conditions:**
Define when the blindspot might break:
- "When the consequences become undeniable"
- "When someone they trust points it out directly"
- "When the pattern repeats too many times"

Blindspots create dramatic irony—the audience sees what the character cannot.`,

                designingMutations: `**Mutation Types:**

**Hardening:**
The trait becomes more extreme and rigid:
- "Pride becomes arrogance"
- "Protection becomes control"
- "Loyalty becomes blind obedience"
- Probability: 30-40% under stress

**Collapse:**
The trait breaks down or inverts:
- "Pride becomes self-doubt"
- "Protection becomes abandonment"
- "Loyalty becomes betrayal"
- Probability: 20-30% under extreme pressure

**Transcendence:**
The character grows beyond the trait:
- "Pride becomes humility"
- "Protection becomes empowerment"
- "Loyalty becomes wisdom"
- Probability: 10-20% with support and insight

**Setting Probabilities:**
- Higher probability = more likely to occur
- Lower probability = rare but powerful when it happens
- Total probabilities should add up to 100% or less
- Leave room for the trait to remain unchanged`,

                completeExamples: `**Example 1: Wounded Pride**

**Trait:** Wounded Pride
**Intensity:** 0.8

**Triggers:**
- When their authority is questioned in front of others
- When someone gets credit for their work
- When their competence is doubted
- When they're excluded from important decisions

**Responses:**
- Immediately becomes defensive and questions the other person's motives
- Interprets the situation as a personal attack
- Seeks to reassert dominance through knowledge or position
- May withdraw and plan a way to prove themselves

**Blindspots:**
- Fails to notice when their defensiveness pushes people away
- Doesn't see that their need to prove themselves creates conflict
- Doesn't recognize when their authority is actually being respected

**Mutations:**
- Hardening (40%): Pride becomes arrogance, dismisses all criticism
- Collapse (25%): Pride breaks, becomes self-doubt and withdrawal
- Transcendence (15%): Recognizes the pattern, seeks genuine feedback

---

**Example 2: Self-Sabotage**

**Trait:** Self-Sabotage
**Intensity:** 0.7

**Triggers:**
- When success is within reach
- When they're about to achieve something important
- When others express confidence in them
- When they're about to commit to something long-term

**Responses:**
- Creates obstacles or excuses to avoid success
- Finds reasons why they don't deserve it
- Withdraws or undermines their own progress
- Seeks out distractions or alternative paths

**Blindspots:**
- Doesn't recognize the pattern of self-sabotage
- Believes their excuses are legitimate
- Fails to see they're protecting themselves from disappointment

**Mutations:**
- Hardening (35%): Sabotage becomes more extreme, actively destroys opportunities
- Collapse (30%): Pattern breaks, they finally accept success
- Transcendence (20%): Recognizes the pattern, seeks help to change

---

**Example 3: Misplaced Loyalty**

**Trait:** Misplaced Loyalty
**Intensity:** 0.75

**Triggers:**
- When their chosen person/group is criticized
- When loyalty is tested by conflicting values
- When they see their loyalty is being exploited
- When someone questions their allegiance

**Responses:**
- Defends the person/group even when wrong
- Rationalizes harmful behavior
- Prioritizes loyalty over personal values
- Becomes hostile toward critics

**Blindspots:**
- Doesn't see when their loyalty is being exploited
- Fails to notice the harm their loyalty causes
- Doesn't recognize when the object of loyalty has changed

**Mutations:**
- Hardening (30%): Loyalty becomes blind obedience, ignores all evidence
- Collapse (35%): Loyalty breaks, feels betrayed and abandoned
- Transcendence (25%): Loyalty becomes wisdom, learns to choose wisely`,

                testingEnzymes: `**Scene Test:**
Create a scene where the enzyme would activate. Does it create interesting conflict? Does the response feel natural? Does it reveal character?

**Variation Test:**
Try the same trigger with different contexts. Does the enzyme create variety, or does it always play out the same way? Mutations should help here.

**Arc Test:**
Imagine the enzyme playing out over multiple scenes. Does it create a satisfying character arc? Does it have room to evolve or change?

**Interaction Test:**
How does this enzyme interact with other characters' enzymes? Do they create interesting dynamics? Do they generate conflict and drama?

**Questions to Ask:**
- Does this enzyme create story events, not just character description?
- Does it have internal tension (both strength and weakness)?
- Are the triggers specific enough to be actionable?
- Do the responses create conflict and reveal character?
- Do blindspots create dramatic irony?
- Do mutations add complexity without making it unpredictable?`,

                commonMistakes: `**Mistake 1: Too Abstract**
❌ "When they feel threatened"
✅ "When their authority is questioned in front of others"

**Mistake 2: Scripts Instead of Patterns**
❌ "Says 'How dare you!' and storms out"
✅ "Becomes defensive and questions the other person's motives"

**Mistake 3: No Internal Tension**
❌ "Always kind and helpful"
✅ "Protective instinct that becomes controlling"

**Mistake 4: Too Many Triggers**
Having 20+ triggers makes the enzyme activate constantly. Focus on 4-8 key triggers that create the most drama.

**Mistake 5: Responses Don't Create Conflict**
❌ "Feels sad and goes home"
✅ "Withdraws but plans to prove themselves later"

**Mistake 6: Blindspots Are Too Obvious**
Blindspots should be believable. The character should have reasons for not seeing the truth.

**Mistake 7: Mutations Don't Make Sense**
Mutations should feel like natural evolutions of the trait, not random changes.

**How to Fix:**
- Make triggers more specific and actionable
- Focus responses on behavioral patterns
- Ensure traits have internal tension
- Limit triggers to the most dramatic situations
- Design responses that create conflict
- Make blindspots believable but impactful
- Connect mutations to the trait's psychology`,

                quickReference: `**Enzyme Anatomy:**
- Trait: Personality aspect with internal tension
- Intensity: 0.0-1.0 (how strongly it influences behavior)
- Triggers: 4-8 specific situations that activate the enzyme
- Responses: 3-5 behavioral patterns (not scripts)
- Blindspots: 2-3 things the character fails to notice
- Mutations: 2-3 variations with probabilities (total ≤100%)

**Intensity Guide:**
- 0.0-0.3: Subtle, occasional influence
- 0.4-0.6: Regular, moderate influence
- 0.7-0.9: Strong, frequent influence
- 1.0: Dominant, almost always active

**Mutation Probability Templates:**
- Conservative: Hardening 40%, Collapse 20%, Transcendence 10% (70% total)
- Balanced: Hardening 35%, Collapse 30%, Transcendence 20% (85% total)
- Dynamic: Hardening 30%, Collapse 35%, Transcendence 25% (90% total)

**Trigger Categories:**
- Social (status, reputation, group dynamics)
- Status (achievement, recognition, position)
- Relational (trust, loyalty, connection)
- Achievement (success, failure, competence)

**Response Patterns:**
- Immediate (instant reaction)
- Delayed (processes then reacts)
- Escalating (starts small, builds)
- Withdrawing (pulls back, plans)

**Blindspot Types:**
- Self-justification (doesn't see own harm)
- Relational (misreads others)
- Consequence (doesn't see results)`
              };

              const renderGuideSection = (id: string, title: string, content: string, searchQuery: string) => {
                const isExpanded = expandedSections.has(id);
                const query = searchQuery.toLowerCase();
                const matchesSearch = !query || title.toLowerCase().includes(query) || content.toLowerCase().includes(query);
                
                if (!matchesSearch) return null;

                const toggleSection = () => {
                  const newExpanded = new Set(expandedSections);
                  if (isExpanded) {
                    newExpanded.delete(id);
                  } else {
                    newExpanded.add(id);
                  }
                  setExpandedSections(newExpanded);
                };

                // Format content with markdown-like formatting
                const formatContent = (text: string) => {
                  return text.split('\n').map((line, idx) => {
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <strong key={idx} style={{ color: '#64b5f6', fontWeight: '600' }}>{line.replace(/\*\*/g, '')}</strong>;
                    }
                    if (line.trim() === '') {
                      return <br key={idx} />;
                    }
                    if (line.startsWith('- ')) {
                      return (
                        <div key={idx} style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
                          • {line.substring(2)}
                        </div>
                      );
                    }
                    if (line.startsWith('✅') || line.startsWith('❌')) {
                      return (
                        <div key={idx} style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '8px' }}>
                          {line}
                        </div>
                      );
                    }
                    if (line.startsWith('---')) {
                      return <hr key={idx} style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #3a3a3a' }} />;
                    }
                    return <p key={idx} style={{ marginTop: '12px', marginBottom: '12px', lineHeight: '1.6' }}>{line}</p>;
                  });
                };

                return (
                  <div
                    key={id}
                    style={{
                      marginBottom: '24px',
                      backgroundColor: '#222222',
                      border: '1px solid #3a3a3a',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={toggleSection}
                      style={{
                        width: '100%',
                        padding: '20px 24px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#f5f5f5',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#2a2a2a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span>{title}</span>
                      <span style={{ fontSize: '1.5rem', color: '#64b5f6' }}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </button>
                    {isExpanded && (
                      <div
                        style={{
                          padding: '24px',
                          borderTop: '1px solid #3a3a3a',
                          color: '#e5e5e5',
                          lineHeight: '1.6',
                        }}
                      >
                        {formatContent(content)}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <>
                  {renderGuideSection('what-is-enzyme', 'What Is an Enzyme?', guideContent.whatIsEnzyme, guideSearchQuery)}
                  {renderGuideSection('five-components', 'The Five Components', guideContent.fiveComponents, guideSearchQuery)}
                  {renderGuideSection('choosing-traits', 'Choosing Traits', guideContent.choosingTraits, guideSearchQuery)}
                  {renderGuideSection('designing-triggers', 'Designing Triggers', guideContent.designingTriggers, guideSearchQuery)}
                  {renderGuideSection('designing-responses', 'Designing Responses', guideContent.designingResponses, guideSearchQuery)}
                  {renderGuideSection('designing-blindspots', 'Designing Blindspots', guideContent.designingBlindspots, guideSearchQuery)}
                  {renderGuideSection('designing-mutations', 'Designing Mutations', guideContent.designingMutations, guideSearchQuery)}
                  {renderGuideSection('complete-examples', 'Complete Examples', guideContent.completeExamples, guideSearchQuery)}
                  {renderGuideSection('testing-enzymes', 'Testing Your Enzymes', guideContent.testingEnzymes, guideSearchQuery)}
                  {renderGuideSection('common-mistakes', 'Common Mistakes', guideContent.commonMistakes, guideSearchQuery)}
                  {renderGuideSection('quick-reference', 'Quick Reference', guideContent.quickReference, guideSearchQuery)}
                </>
              );
            })()}
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
        </div>
      </div>

      {/* Library Preview Panel */}
      {previewLibraryItem && (
        <div style={helpModalOverlayStyle} onClick={() => setPreviewLibraryItem(null)}>
          <div style={helpModalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={helpModalHeaderStyle}>
              <h2 style={helpModalTitleStyle}>
                {previewLibraryItem?.name}
                <span style={{ fontSize: '0.9rem', fontWeight: '400', color: '#a0a0a0', marginLeft: '12px' }}>
                  ({previewLibraryItem.type})
                </span>
              </h2>
              <button
                onClick={() => setPreviewLibraryItem(null)}
                style={helpModalCloseButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a3a3a';
                  e.currentTarget.style.color = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#a0a0a0';
                }}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <div style={helpModalContentStyle}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', fontSize: '0.9rem', color: '#a0a0a0', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <strong style={{ color: '#d0d0d0' }}>Author:</strong> {previewLibraryItem.author}
                    {previewLibraryItem.authorId && previewLibraryItem.signature && (
                      <span style={{
                        padding: '2px 6px',
                        backgroundColor: '#1a2a1a',
                        border: '1px solid #4a8a4a',
                        borderRadius: '8px',
                        fontSize: '0.7rem',
                        color: '#81c784',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                      title="Verified: Component is cryptographically signed">
                        ✓ Verified
                      </span>
                    )}
                  </span>
                  {previewLibraryItem.authorId && (
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>
                      ID: {previewLibraryItem.authorId}
                    </span>
                  )}
                  <div style={{
                    padding: '4px 10px',
                    backgroundColor: (previewLibraryItem.compatibility || 0) >= 70 ? '#1a2a1a' : (previewLibraryItem.compatibility || 0) >= 40 ? '#2a2a1a' : '#2a1a1a',
                    border: `1px solid ${(previewLibraryItem.compatibility || 0) >= 70 ? '#4a8a4a' : (previewLibraryItem.compatibility || 0) >= 40 ? '#6a6a4a' : '#8a4a4a'}`,
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: (previewLibraryItem.compatibility || 0) >= 70 ? '#81c784' : (previewLibraryItem.compatibility || 0) >= 40 ? '#ffa726' : '#e57373',
                  }}>
                    {previewLibraryItem.compatibility || calculateCompatibility(previewLibraryItem)}% compatible
                  </div>
                </div>
                
                {previewLibraryItem.description && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={helpSectionTitleStyle}>Description</div>
                    <div style={helpSectionContentStyle}>{previewLibraryItem.description}</div>
                  </div>
                )}

                {previewLibraryItem.tags.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={helpSectionTitleStyle}>Tags</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {previewLibraryItem.tags.map((tag, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#2a2a2a',
                            border: '1px solid #4a4a4a',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            color: '#64b5f6',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enzyme Details */}
                {previewLibraryItem.type === 'Enzyme' && (() => {
                  const enzyme = previewLibraryItem.data as Enzyme;
                  return (
                    <div>
                      <div style={helpSectionTitleStyle}>Enzyme Details</div>
                      <div style={helpSectionContentStyle}>
                        <p><strong>Character:</strong> {enzyme.characterName}</p>
                        <p><strong>Trait:</strong> {enzyme.traitName}</p>
                        <p><strong>Intensity:</strong> {enzyme.intensity.toFixed(2)}</p>

                        {enzyme.triggers.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <strong>Triggers:</strong>
                            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                              {enzyme.triggers.map((trigger, index) => (
                                <li key={index} style={{ marginBottom: '4px' }}>{trigger.text}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {enzyme.responses.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <strong>Responses:</strong>
                            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                              {enzyme.responses.map((response, index) => (
                                <li key={index} style={{ marginBottom: '4px' }}>{response.text}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {enzyme.blindspots.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <strong>Blindspots:</strong>
                            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                              {enzyme.blindspots.map((blindspot, index) => (
                                <li key={index} style={{ marginBottom: '4px' }}>{blindspot.description}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {enzyme.mutations.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <strong>Mutations:</strong>
                            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                              {enzyme.mutations.map((mutation, index) => (
                                <li key={index} style={{ marginBottom: '4px' }}>
                                  {mutation.description} (Probability: {(mutation.probability * 100).toFixed(0)}%)
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Tension Codon Details */}
                {previewLibraryItem.type === 'Tension Codon' && (() => {
                  const codon = previewLibraryItem.data as TensionCodon;
                  return (
                    <div>
                      <div style={helpSectionTitleStyle}>Tension Codon Details</div>
                      <div style={helpSectionContentStyle}>
                        <p><strong>Name:</strong> {codon.name}</p>
                        <p><strong>Pressure Level:</strong> {codon.pressureLevel.toFixed(2)}</p>
                        {codon.description && (
                          <p style={{ marginTop: '12px' }}><strong>Description:</strong> {codon.description}</p>
                        )}
                        {codon.affectedCharacterIds.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <strong>Affects {codon.affectedCharacterIds.length} character{codon.affectedCharacterIds.length !== 1 ? 's' : ''}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Character Details */}
                {previewLibraryItem.type === 'Character' && (() => {
                  const character = previewLibraryItem.data as Character;
                  return (
                    <div>
                      <div style={helpSectionTitleStyle}>Character Details</div>
                      <div style={helpSectionContentStyle}>
                        <p><strong>Name:</strong> {character.name}</p>
                        {character.description && (
                          <p style={{ marginTop: '12px' }}><strong>Description:</strong> {character.description}</p>
                        )}
                        {character.attachedEnzymes.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <strong>Has {character.attachedEnzymes.length} attached enzyme{character.attachedEnzymes.length !== 1 ? 's' : ''}</strong>
                            <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '4px' }}>
                              Note: Attached enzymes will be reset when imported since they may not exist in your collection.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #3a3a3a' }}>
                <button
                  onClick={() => handleImport(previewLibraryItem)}
                  style={getButtonStyle(buttonHover === 'preview-import')}
                  onMouseEnter={() => setButtonHover('preview-import')}
                  onMouseLeave={() => setButtonHover(null)}
                >
                  Import
                </button>
                <button
                  onClick={() => setPreviewLibraryItem(null)}
                  style={getNewButtonStyle(buttonHover === 'preview-close')}
                  onMouseEnter={() => setButtonHover('preview-close')}
                  onMouseLeave={() => setButtonHover(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cobot Panel */}
      <div style={cobotPanelStyle}>
        <div style={cobotHeaderStyle}>
          <h2 style={cobotTitleStyle}>
            <span>🤖</span>
            Cobot
          </h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setShowApiKeySettings(!showApiKeySettings)}
              style={cobotSettingsButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
                e.currentTarget.style.color = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#a0a0a0';
              }}
              aria-label="Settings"
            >
              ⚙️
            </button>
            <button
              onClick={() => setCobotPanelOpen(false)}
              style={cobotCloseButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3a3a3a';
                e.currentTarget.style.color = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#a0a0a0';
              }}
              aria-label="Close Cobot panel"
            >
              ×
            </button>
          </div>
        </div>
        <div style={cobotContentStyle}>
          {showApiKeySettings ? (
            <div>
              <h3 style={{ ...cobotSuggestionSectionTitleStyle, marginBottom: '16px' }}>Claude API Settings</h3>
              <label style={labelStyle} htmlFor="claude-api-key">
                Claude API Key
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="claude-api-key"
                  type={showCobotApiKey ? 'text' : 'password'}
                  value={claudeApiKey}
                  onChange={(e) => setClaudeApiKey(e.target.value)}
                  placeholder="sk-ant-..."
                  style={{ ...cobotApiKeyInputStyle, paddingRight: '100px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowCobotApiKey(!showCobotApiKey)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #4a4a4a',
                    borderRadius: '6px',
                    color: '#a0a0a0',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#3a3a3a';
                    e.currentTarget.style.borderColor = '#5a5a5a';
                    e.currentTarget.style.color = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#4a4a4a';
                    e.currentTarget.style.color = '#a0a0a0';
                  }}
                >
                  {showCobotApiKey ? 'Hide' : 'Show'}
                </button>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#a0a0a0', marginTop: '8px', lineHeight: '1.5' }}>
                Your API key is stored locally in your browser. Get your key from{' '}
                <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#64b5f6' }}>
                  Anthropic Console
                </a>
              </p>
              <button
                onClick={() => {
                  setShowApiKeySettings(false);
                  setShowCobotApiKey(false);
                }}
                style={getButtonStyle(buttonHover === 'close-settings')}
                onMouseEnter={() => setButtonHover('close-settings')}
                onMouseLeave={() => setButtonHover(null)}
              >
                Done
              </button>
            </div>
          ) : cobotLoading ? (
            <div style={cobotEmptyStateStyle}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⏳</div>
              <div style={{ fontWeight: '600', color: '#a0a0a0', marginBottom: '12px' }}>
                Generating Suggestions...
              </div>
              <div>
                Claude is analyzing your enzyme and generating dramatic suggestions.
              </div>
            </div>
          ) : cobotSuggestions.triggers.length === 0 && cobotSuggestions.responses.length === 0 ? (
            <div style={cobotEmptyStateStyle}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>✨</div>
              <div style={{ fontWeight: '600', color: '#a0a0a0', marginBottom: '12px' }}>
                AI Suggestions
              </div>
              <div>
                Click "Generate Suggestions" to get AI-powered recommendations for your enzyme.
                <br />
                <br />
                {!localStorage.getItem('splinewright-claude-api-key') && (
                  <>
                    <strong>Note:</strong> You'll need to set your Claude API key in the <strong>Settings</strong> tab first.
                  </>
                )}
              </div>
            </div>
          ) : (
            <div>
              {cobotSuggestions.triggers.length > 0 && (
                <div style={cobotSuggestionSectionStyle}>
                  <h3 style={cobotSuggestionSectionTitleStyle}>Suggested Triggers</h3>
                  {cobotSuggestions.triggers.map((trigger, index) => (
                    <div
                      key={index}
                      style={{ ...cobotSuggestionItemStyle, cursor: 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span style={{ flex: 1 }}>{trigger}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          addSuggestionTrigger(trigger);
                        }}
                        style={{
                          marginLeft: '12px',
                          padding: '6px 12px',
                          backgroundColor: '#64b5f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s, transform 0.1s',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#90caf9';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#64b5f6';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        +
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {cobotSuggestions.responses.length > 0 && (
                <div style={cobotSuggestionSectionStyle}>
                  <h3 style={cobotSuggestionSectionTitleStyle}>Suggested Responses</h3>
                  {cobotSuggestions.responses.map((response, index) => (
                    <div
                      key={index}
                      style={{ ...cobotSuggestionItemStyle, cursor: 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span style={{ flex: 1 }}>{response}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          addSuggestionResponse(response);
                        }}
                        style={{
                          marginLeft: '12px',
                          padding: '6px 12px',
                          backgroundColor: '#64b5f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s, transform 0.1s',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#90caf9';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#64b5f6';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        +
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cooper Redesign Components */}
        {renderStoryAssistant()}

        {/* Conversational character creation */}
        {renderCharacterConversation()}

        {/* Mobile Robot assistant */}
        {renderRobot()}
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div style={helpModalOverlayStyle} onClick={() => setShowHelpModal(false)}>
          <div style={helpModalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={helpModalHeaderStyle}>
              <h2 style={helpModalTitleStyle}>Help & Quick Tips</h2>
              <button
                onClick={() => setShowHelpModal(false)}
                style={helpModalCloseButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#3a3a3a';
                  e.currentTarget.style.color = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#a0a0a0';
                }}
                aria-label="Close help modal"
              >
                ×
              </button>
            </div>
            <div style={helpModalContentStyle}>
              {/* Quick Tips - Enzymes */}
              <div style={helpSectionStyle}>
                <h3 style={helpSectionTitleStyle}>💡 Enzymes - Quick Tips</h3>
                <div style={helpSectionContentStyle}>
                  <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                    <li style={helpListItemStyle}>
                      <strong>Start with a trait that has internal tension:</strong> Good traits are both strengths and weaknesses (e.g., "Wounded Pride" protects but also blinds).
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Make triggers specific and actionable:</strong> Instead of "feels threatened," use "when authority is questioned in front of others."
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Design responses as patterns, not scripts:</strong> Describe behavioral tendencies, not exact dialogue.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Use blindspots strategically:</strong> They create dramatic irony and opportunities for conflict.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Add mutations for complexity:</strong> Define how the trait might evolve or break down under pressure.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Use "Generate Suggestions" for AI-powered ideas:</strong> Get help creating additional triggers and responses.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Quick Tips - Characters */}
              <div style={helpSectionStyle}>
                <h3 style={helpSectionTitleStyle}>👥 Characters - Quick Tips</h3>
                <div style={helpSectionContentStyle}>
                  <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                    <li style={helpListItemStyle}>
                      <strong>Attach multiple enzymes to each character:</strong> Different traits create complex, layered personalities.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Adjust intensity per attachment:</strong> A character might have high-intensity loyalty but low-intensity patience.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Create character descriptions:</strong> Add context about who they are and their role in the story.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Use enzymes to drive character arcs:</strong> Mutations can show character growth or breakdown over time.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Balance character complexity:</strong> Too many enzymes can be overwhelming; 2-4 per character is usually ideal.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Quick Tips - Storyworld */}
              <div style={helpSectionStyle}>
                <h3 style={helpSectionTitleStyle}>🌍 Storyworld - Quick Tips</h3>
                <div style={helpSectionContentStyle}>
                  <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                    <li style={helpListItemStyle}>
                      <strong>Create tension codons for ongoing conflicts:</strong> These affect multiple characters and create dramatic pressure.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Set pressure levels appropriately:</strong> Higher pressure means more frequent activation and stronger effects.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Link tension codons to character relationships:</strong> They work best when they create conflict between characters with opposing enzymes.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Use escalation profiles:</strong> Control how tensions build and resolve over time.
                    </li>
                    <li style={helpListItemStyle}>
                      <strong>Generate encounters to test your setup:</strong> Use the Encounters tab to create dramatic scenarios between characters.
                    </li>
                  </ul>
                </div>
              </div>

              {/* External Links */}
              <div style={helpSectionStyle}>
                <h3 style={helpSectionTitleStyle}>📚 Full Guides</h3>
                <div style={helpSectionContentStyle}>
                  <div style={{ marginBottom: '20px' }}>
                    <a
                      href="https://example.com/enzyme-design-guide"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#64b5f6',
                        textDecoration: 'none',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        display: 'inline-block',
                        marginBottom: '8px',
                        borderBottom: '2px solid #64b5f6',
                        paddingBottom: '4px',
                        transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#90caf9';
                        e.currentTarget.style.borderBottomColor = '#90caf9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#64b5f6';
                        e.currentTarget.style.borderBottomColor = '#64b5f6';
                      }}
                    >
                      📖 Full Enzyme Design Guide →
                    </a>
                    <p style={{ marginTop: '8px', color: '#a0a0a0', fontSize: '0.9rem' }}>
                      Complete guide to designing effective behavioral enzymes with detailed examples and best practices.
                    </p>
                  </div>
                  <div>
                    <a
                      href="/user-guide.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#64b5f6',
                        textDecoration: 'none',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        display: 'inline-block',
                        marginBottom: '8px',
                        borderBottom: '2px solid #64b5f6',
                        paddingBottom: '4px',
                        transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#90caf9';
                        e.currentTarget.style.borderBottomColor = '#90caf9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#64b5f6';
                        e.currentTarget.style.borderBottomColor = '#64b5f6';
                      }}
                    >
                      📘 User Guide →
                    </a>
                    <p style={{ marginTop: '8px', color: '#a0a0a0', fontSize: '0.9rem' }}>
                      Comprehensive user guide covering all features and workflows in Splinewright.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile "More" menu */}
      {isMobile && showMobileMenu && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '16px',
          }}
          onClick={() => setShowMobileMenu(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '520px',
              backgroundColor: '#161b22',
              border: '1px solid #30363d',
              borderRadius: '16px',
              padding: '16px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ color: '#f5f5f5', fontWeight: 700, fontSize: '1rem' }}>More</div>
              <button
                onClick={() => setShowMobileMenu(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#8b949e',
                  fontSize: '1.4rem',
                  cursor: 'pointer',
                  padding: '4px 8px',
                }}
                aria-label="Close menu"
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button
                onClick={() => { setCurrentView('editor'); setSelectedEnzymeId(null); setShowMobileMenu(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '12px',
                  color: '#f5f5f5',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                🧬 Patterns
              </button>
              <button
                onClick={() => { setCurrentView('settings'); setShowMobileMenu(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '12px',
                  color: '#f5f5f5',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                ⚙️ Settings
              </button>
              <button
                onClick={() => { setCurrentView('library'); setShowMobileMenu(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '12px',
                  color: '#f5f5f5',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                📚 Library
              </button>
              <button
                onClick={() => { setCurrentView('guide'); setShowMobileMenu(false); }}
                style={{
                  padding: '12px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '12px',
                  color: '#f5f5f5',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                📖 Guide
              </button>
            </div>

            <div style={{ marginTop: '12px', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { document.getElementById('open-storyworld-file-input')?.click(); setShowMobileMenu(false); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '12px',
                  color: '#8b949e',
                  cursor: 'pointer',
                }}
              >
                📥 Open Storyworld File
              </button>
              <button
                onClick={() => { document.getElementById('import-file-input')?.click(); setShowMobileMenu(false); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '12px',
                  color: '#8b949e',
                  cursor: 'pointer',
                }}
              >
                ⬇️ Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom navigation */}
      {isMobile && (
        <nav
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '64px',
            backgroundColor: '#161b22',
            borderTop: '1px solid #30363d',
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => setCurrentView('storyworld')}
            style={{
              background: 'none',
              border: 'none',
              color: currentView === 'storyworld' ? '#58a6ff' : '#8b949e',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '24px' }}>📖</span>
            <span style={{ fontSize: '10px', marginTop: '4px' }}>Story</span>
          </button>

          <button
            onClick={() => { setCurrentView('characters'); setSelectedCharacterId(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: currentView === 'characters' ? '#58a6ff' : '#8b949e',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '24px' }}>👥</span>
            <span style={{ fontSize: '10px', marginTop: '4px' }}>Characters</span>
          </button>

          <button
            onClick={() => setCurrentView('encounters')}
            style={{
              background: 'none',
              border: 'none',
              color: currentView === 'encounters' ? '#58a6ff' : '#8b949e',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '24px' }}>🎭</span>
            <span style={{ fontSize: '10px', marginTop: '4px' }}>Scenes</span>
          </button>

          <button
            onClick={() => setCurrentView('play')}
            disabled={!(storyworldAccessLevel === 'full' || storyworldAccessLevel === 'play')}
            style={{
              background: 'none',
              border: 'none',
              color:
                currentView === 'play'
                  ? '#58a6ff'
                  : !(storyworldAccessLevel === 'full' || storyworldAccessLevel === 'play')
                    ? '#4b5563'
                    : '#8b949e',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px',
              cursor: !(storyworldAccessLevel === 'full' || storyworldAccessLevel === 'play') ? 'not-allowed' : 'pointer',
            }}
            title={!(storyworldAccessLevel === 'full' || storyworldAccessLevel === 'play') ? 'Play disabled for View Only access' : undefined}
          >
            <span style={{ fontSize: '24px' }}>▶️</span>
            <span style={{ fontSize: '10px', marginTop: '4px' }}>Play</span>
          </button>

          <button
            onClick={() => setShowMobileMenu(true)}
            style={{
              background: 'none',
              border: 'none',
              color: showMobileMenu ? '#58a6ff' : '#8b949e',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: '24px' }}>≡</span>
            <span style={{ fontSize: '10px', marginTop: '4px' }}>More</span>
          </button>
        </nav>
      )}

      {/* Floating "Create with AI" button (mobile-friendly) */}
      {!isMobile && !showChatbot && (
        <button
          onClick={() => {
            setShowChatbot(true);
            if (chatMessages.length === 0) {
              setCreationStep('welcome');
              setPendingStoryworld(null);
              setChatInput('');
              const greeting: ChatMessage = {
                id: 'welcome-msg',
                role: 'assistant',
                content:
                  "Hello! I'm the Genome Architect, your AI assistant for creating storyworlds. I'll guide you through designing characters with psychological depth, behavioral patterns (enzymes), and the tensions that drive your story.\n\nWhat kind of story would you like to create? You can describe a premise, a genre, or just a feeling you want to explore.",
                timestamp: new Date(),
              };
              setChatMessages([greeting]);
            }
          }}
          style={{
            position: 'fixed',
            bottom: isMobile ? '80px' : '24px',
            right: '24px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#238636',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            zIndex: 1000,
          }}
          title="Create with AI"
        >
          🧬
        </button>
      )}

      {/* Floating "Encounter Forge" button */}
      {!isMobile && !showEncounterForge && currentStoryworld && (
        <button
          onClick={() => setShowEncounterForge(true)}
          style={{
            position: 'fixed',
            bottom: isMobile ? '80px' : '24px',
            right: '24px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#6e40c9',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            zIndex: 1000,
          }}
          title="Encounter Forge"
        >
          🔨
        </button>
      )}

      {renderEncounterForge()}

      {renderChatbot()}
    </>
  );
}

// Wrap App with ErrorBoundary for crash protection
const WrappedApp = () => (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

export default WrappedApp;

// Error Boundary to prevent white screen crashes
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Splinewright crashed:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#0d1117',
          color: '#f0f6fc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h1 style={{ fontSize: '22px', marginBottom: '12px' }}>Something went wrong</h1>
            <p style={{ color: '#8b949e', fontSize: '14px', marginBottom: '24px', lineHeight: '1.5' }}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#238636',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '15px',
                marginRight: '12px',
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => {
                window.location.reload();
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#21262d',
                border: '1px solid #30363d',
                borderRadius: '8px',
                color: '#f0f6fc',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '15px',
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
